// src/views/TradingBots/SniperBot.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Select, Switch, Tooltip, Input, Button, IconButton, HStack, SimpleGrid, Divider,
  Text, VStack, NumberInput, NumberInputStepper, NumberDecrementStepper,
  NumberIncrementStepper, NumberInputField, Alert, AlertIcon, Checkbox, Badge
} from "@chakra-ui/react";
import { ethers } from "ethers";
import Moralis from "moralis-v1";
import { useMoralis } from "react-moralis";
import PnLCalculator from "./PNL";
import { computePnL } from "../TradingBots/PNL";
import { notifyDiscordTrade, notifyDiscordBotReset, formatRegimeCompact } from "./botDiscordNotify";
import {
  DEFAULT_EXTERNAL_SIGNAL_CONFIG,
  applyExternalSizeOverlay,
  applyExternalThresholdOverlay,
  buildExternalSignalAdvice,
  createExternalMarketSignalService,
} from "./externalMarketSignal";
import {
  DEFAULT_COMPOUND_TRADE_SIZING,
  DEFAULT_MIN_BUY_SPACING_MS,
  applyExternalAssetFlowToLedger,
  calculateCompoundedTradeSize,
  calculateFlowAdjustedPnl,
  calculateRealizedCompoundingEquity,
  computeStuckStopCounterfactual,
  detectExternalBalanceFlows,
  evaluateNormalSellDuplicateGuard,
  evaluateProfitReanchor,
  getAuthoritativeAverageEntry,
  getCostAwareNormalSellFloor,
  getSafeTimeReanchorTargets,
  validateConfirmedTradeFill,
} from "./tradeRiskControls";
import {
  PRICE_HISTORY_RETENTION_MS,
  computeMarketMetrics,
  resolveMarketRegime,
  applyRegimeToAdaptiveMoves,
  evaluateRegimeBuyBlocks,
  evaluateRegimeSellAdjustments,
  getMajorDipPctForRegime,
  regimePrevFromPosition,
  regimeFieldsFromSnap,
  CAPITAL_PROTECTION_COOLDOWN_MS,
  REGIMES,
} from "./botMarketRegime";
import { FiActivity, FiRefreshCw, FiZap, FiShield, FiAlertTriangle } from "react-icons/fi";
import { MdGridOn } from "react-icons/md";
import {
  DashboardRoot,
  VersionBar,
  HeroHeader,
  CommandStrip,
  LiveTicker,
  Panel,
  BalanceStrip,
  MarketsSectionHeader,
  MarketCard,
  MetricRow,
  InsightBox,
  PnLHero,
  ActionRow,
  StyledActionButton,
  GlobalStyles,
  ArbiscanLink,
  SettingsBlock,
  FormField,
  fieldInputProps,
} from "./SniperBotDashboardUI";
import {
  CHAIN_CONFIGS,
  DEX_CONFIGS,
  getRouterAddress,
  getFactoryAddress,
  getRpcUrl,
  getWrappedNativeToken,
  getStablecoinAddress,
  getAvailableDexes,
  validateChainDex,
  getChainConfig,
  getAllChains,
  getAllDexes,
} from "../../config/chainsAndDexes";


/**
 * SniperBot.jsx (Moralis persistence)
 *
 * - Replaces localStorage with Moralis (MongoDB via Moralis SDK)
 * - Moralis helpers are included at top of file
 * - Hydrates state on account change
 */

/* ===========================
   Moralis helpers (outside component)
   =========================== */

/**
 * Truncate fractional digits for cloud payload. Prefer string ops; avoid Number().toFixed on floats.
 * Scientific notation is expanded via toFixed(maxFractionDigits) so the server gets a plain decimal.
 */
function truncateDecimalStringForPayload(raw, maxFractionDigits) {
  const s = String(raw == null ? "" : raw).trim();
  if (!s) return "0";
  if (/[eE]/.test(s)) {
    const n = Number(s);
    if (!Number.isFinite(n) || n < 0) return s;
    return n.toFixed(Math.max(0, maxFractionDigits));
  }
  if (!/^\d*\.?\d*$/.test(s)) return s;
  const [whole, frac = ""] = s.split(".");
  const f = frac.slice(0, Math.max(0, maxFractionDigits));
  return f.length ? `${whole}.${f}` : whole;
}

/**
 * Default market key when ctx.marketKey is omitted.
 * Old behavior used WBTC-${native} for any native initiator — wrong for ETH↔USDC (price/path/reverts).
 * - Selling/buying only WETH or USDC → `${wrapped}-${stable}` (e.g. ETH-USDC).
 * - Alt asset (e.g. WBTC) + native initiator → WBTC-${wrapped}; + stable initiator → WBTC-${stable}.
 */
function inferMarketKeyFromTokenAndInitiator({
  tokenToUse,
  wrappedNativeToken,
  stablecoinAddress,
  wrappedSymbol,
  stableSymbol,
  initiator,
}) {
  const w = (wrappedNativeToken || "").toLowerCase();
  const st = (stablecoinAddress || "").toLowerCase();
  const t = (tokenToUse || "").toLowerCase();
  const isWrappedOrStable = t === w || t === st;
  if (isWrappedOrStable) {
    return `${wrappedSymbol}-${stableSymbol}`;
  }
  if (initiator === stableSymbol || initiator === "USDC" || initiator === "USDC.e" || initiator === "USDT") {
    return `WBTC-${stableSymbol}`;
  }
  return `WBTC-${wrappedSymbol}`;
}

async function moralisSaveBotConfig(player, botKey, configJSON) {
  const BotConfig = Moralis.Object.extend("BotConfig");
  const q = new Moralis.Query(BotConfig);
  q.equalTo("player", player);
  q.equalTo("botKey", botKey);

  const existing = await q.first();
  if (existing) {
    existing.set("configJSON", configJSON);
    return existing.save();
  } else {
    const obj = new BotConfig();
    obj.set("player", player);
    obj.set("botKey", botKey);
    obj.set("configJSON", configJSON);
    return obj.save();
  }
}

async function moralisLoadBotConfigs(player) {
  const BotConfig = Moralis.Object.extend("BotConfig");
  const q = new Moralis.Query(BotConfig);
  q.equalTo("player", player);

  const rows = await q.find();
  const out = {};
  rows.forEach(r => {
    out[r.get("botKey")] = r.get("configJSON");
  });
  return out;
}

async function moralisSavePosition(player, botKey, positionJSON) {
  // Upsert pattern: store a single BotPosition document per player that contains
  // all bot positions in positionJSON as an object mapping botKey -> position.
  const BotPosition = Moralis.Object.extend("BotPosition");
  const q = new Moralis.Query(BotPosition);
  const playerStr = String(player);

  // normalize payload: if caller passed a per-bot object, wrap it into a full-map
  let payload = positionJSON;
  if (botKey && typeof positionJSON !== "object") {
    // legacy callsite: moralisSavePosition(player, botKey, pos)
    payload = { [botKey]: positionJSON };
  }

  // If caller already passed the full positions map, use it directly.
  if (typeof payload === "string") {
    try { payload = JSON.parse(payload); } catch (e) { payload = { raw: payload }; }
  }

  // Sanitize keys: MongoDB/Parse doesn't allow '.' or '$' in nested keys
  // Replace '.' with '_DOT_' and '$' with '_DOLLAR_' for safe storage
  const sanitizeKeys = (obj) => {
    if (typeof obj !== "object" || obj === null || Array.isArray(obj)) {
      return obj;
    }
    const sanitized = {};
    for (const [key, value] of Object.entries(obj)) {
      const sanitizedKey = key.replace(/\./g, "_DOT_").replace(/\$/g, "_DOLLAR_");
      sanitized[sanitizedKey] = typeof value === "object" && value !== null && !Array.isArray(value)
        ? sanitizeKeys(value)
        : value;
    }
    return sanitized;
  };

  const sanitizedPayload = sanitizeKeys(payload);

  q.equalTo("player", playerStr);
  // prevent concurrent creates from this client by using an in-memory lock
  try {
    if (!window.__moralisCreateLocks) window.__moralisCreateLocks = {};
    const lockKey = `botpos:${playerStr}`;
    // wait for any existing lock to release
    while (window.__moralisCreateLocks[lockKey]) {
      await new Promise((res) => setTimeout(res, 50));
    }
    window.__moralisCreateLocks[lockKey] = true;

    // re-check existing after acquiring lock
  const existing = await q.first();
  if (existing) {
      existing.set("positionJSON", sanitizedPayload);
      const res = await existing.save();
      return res;
    }

    // create new if still not found
    const obj = new BotPosition();
    obj.set("player", playerStr);
    obj.set("positionJSON", sanitizedPayload);
    const res2 = await obj.save();
    return res2;
  } catch (err) {
    console.warn("[moralisSavePosition] upsert failed (locked):", err?.message || err);
    // if create failed due to race, try to find existing and update
    try {
      const maybe = await q.first();
      if (maybe) {
        maybe.set("positionJSON", sanitizedPayload);
        const r2 = await maybe.save();
        console.log("[moralisSavePosition] upsert retry saved after failure, id:", r2.id);
        return r2;
      }
    } catch (e2) {
      console.error("[moralisSavePosition] upsert retry also failed:", e2);
    }
    throw err;
  } finally {
    try {
      if (window.__moralisCreateLocks) {
        const lockKey = `botpos:${playerStr}`;
        window.__moralisCreateLocks[lockKey] = false;
      }
    } catch (e) {}
  }
}
async function moralisLoadPositions(player) {
  const BotPosition = Moralis.Object.extend("BotPosition");
  const q = new Moralis.Query(BotPosition);
  const playerStr = String(player);
  q.equalTo("player", playerStr);
  // Load player-level BotPosition (single document containing all bots)
  const row = await q.first();
  if (!row) {
    console.log("[moralisLoadPositions] no position doc found for", playerStr);
    return {};
  }
  const raw = row.get("positionJSON");
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    
    // Desanitize keys: restore '.' and '$' characters that were replaced for MongoDB
    const desanitizeKeys = (obj) => {
      if (typeof obj !== "object" || obj === null || Array.isArray(obj)) {
        return obj;
      }
      const desanitized = {};
      for (const [key, value] of Object.entries(obj)) {
        const desanitizedKey = key.replace(/_DOT_/g, ".").replace(/_DOLLAR_/g, "$");
        desanitized[desanitizedKey] = typeof value === "object" && value !== null && !Array.isArray(value)
          ? desanitizeKeys(value)
          : value;
      }
      return desanitized;
    };

    const desanitized = desanitizeKeys(parsed);
    console.log("[moralisLoadPositions] loaded player positions for", playerStr, "keys:", Object.keys(desanitized || {}));
    return desanitized || {};
  } catch (e) {
    console.error("[moralisLoadPositions] failed to parse player positions raw:", raw, "error:", e);
    return raw || {};
  }
}

async function moralisSaveBotAccounting(player, accountingJSON) {
  const BotAccounting = Moralis.Object.extend("BotAccounting");
  const q = new Moralis.Query(BotAccounting);
  q.equalTo("player", String(player));
  const obj = (await q.first()) || new BotAccounting();
  obj.set("player", String(player));
  obj.set("accountingJSON", accountingJSON);
  obj.set("updatedAtMs", Date.now());
  return obj.save();
}

async function moralisLoadBotAccounting(player) {
  const BotAccounting = Moralis.Object.extend("BotAccounting");
  const q = new Moralis.Query(BotAccounting);
  q.equalTo("player", String(player));
  const row = await q.first();
  if (!row) return null;
  const raw = row.get("accountingJSON");
  if (!raw) return null;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch (error) {
      console.warn("moralisLoadBotAccounting failed to parse accountingJSON", error);
      return null;
    }
  }
  return raw;
}

async function moralisAddCapitalFlow(player, flow) {
  const BotCapitalFlow = Moralis.Object.extend("BotCapitalFlow");
  const obj = new BotCapitalFlow();
  obj.set("player", String(player));
  obj.set("marketKey", flow.marketKey || "");
  obj.set("eventId", flow.eventId || "");
  obj.set("eventType", flow.type || "UNKNOWN_EXTERNAL_FLOW");
  obj.set("token", flow.token || "");
  obj.set("amount", Number(flow.amount) || 0);
  obj.set("usdValue", Number(flow.usdValue) || 0);
  obj.set("timestamp", Number(flow.timestamp) || Date.now());
  obj.set("txHash", flow.txHash || "");
  obj.set("blockNumber", flow.blockNumber == null ? null : Number(flow.blockNumber));
  obj.set("source", flow.source || "balance-reconciliation");
  obj.set("affectsPnl", false);
  return obj.save();
}

async function moralisSaveCustomBots(player, customBotsArray /* [{key, tokenAddress, pairAddress}] */) {
  const CustomBot = Moralis.Object.extend("CustomBot");

  // delete old
  const q = new Moralis.Query(CustomBot);
  q.equalTo("player", player);
  const oldRows = await q.find();
  if (oldRows.length) {
    try { await Moralis.Object.destroyAll(oldRows); } catch (e) { console.warn("delete old custom bots failed", e); }
  }

  // save new
  const toSave = customBotsArray.map(b => {
    const obj = new CustomBot();
    obj.set("player", player);
    obj.set("botKey", b.key);
    obj.set("tokenAddress", b.tokenAddress);
    obj.set("pairAddress", b.pairAddress);
    return obj;
  });
  if (toSave.length) await Moralis.Object.saveAll(toSave);
}

async function moralisLoadCustomBots(player) {
  const CustomBot = Moralis.Object.extend("CustomBot");
  const q = new Moralis.Query(CustomBot);
  q.equalTo("player", player);
  const rows = await q.find();
  return rows.map(r => ({
    key: r.get("botKey"),
    tokenAddress: r.get("tokenAddress"),
    pairAddress: r.get("pairAddress"),
  }));
}

async function moralisAddTradeHistory(player, entry /* {ts, side, amount, token, initiator, marketKey, txHash} */) {
  const TradeHistory = Moralis.Object.extend("TradeHistory");
  const obj = new TradeHistory();
  obj.set("player", player);
  obj.set("marketKey", entry.marketKey);
  obj.set("side", entry.side);
  obj.set("amount", entry.amount);
  obj.set("token", entry.token || "");
  obj.set("initiator", entry.initiator || "");
  obj.set("txHash", entry.txHash || "");
  obj.set("timestamp", entry.ts || Date.now());
  if (Number(entry.actualAmountIn) > 0) obj.set("actualAmountIn", Number(entry.actualAmountIn));
  if (Number(entry.actualAmountOut) > 0) obj.set("actualAmountOut", Number(entry.actualAmountOut));
  if (entry.blockNumber != null) obj.set("blockNumber", Number(entry.blockNumber));
  if (entry.externalSignal) obj.set("externalSignal", entry.externalSignal);
  if (entry.externalSignalDecisionId) obj.set("externalSignalDecisionId", entry.externalSignalDecisionId);
  return obj.save();
}

async function moralisAddExternalSignalDecision(player, decision) {
  const ExternalSignalDecision = Moralis.Object.extend("ExternalSignalDecision");
  const obj = new ExternalSignalDecision();
  obj.set("player", String(player));
  obj.set("marketKey", decision.marketKey || "");
  obj.set("side", decision.side || "");
  obj.set("mode", decision.mode || "shadow");
  obj.set("status", decision.status || "unavailable");
  obj.set("decisionId", decision.decisionId || "");
  obj.set("timestamp", decision.timestamp || Date.now());
  obj.set("changedAnything", decision.changedAnything === true);
  obj.set("decision", decision);
  return obj.save();
}

async function moralisPatchExternalSignalDecision(player, decisionId, patch = {}, outcomeKey = "") {
  if (!player || !decisionId) return null;
  const ExternalSignalDecision = Moralis.Object.extend("ExternalSignalDecision");
  const q = new Moralis.Query(ExternalSignalDecision);
  q.equalTo("player", String(player));
  q.equalTo("decisionId", String(decisionId));
  const obj = await q.first();
  if (!obj) return null;
  const current = obj.get("decision") || {};
  const next = { ...current, ...patch };
  if (outcomeKey) {
    next.outcomes = {
      ...(current.outcomes || {}),
      [outcomeKey]: patch.outcome,
    };
    delete next.outcome;
  }
  obj.set("decision", next);
  if (patch.txHash) obj.set("txHash", patch.txHash);
  if (patch.executedAt) obj.set("executedAt", Number(patch.executedAt));
  obj.set("outcomeUpdatedAt", Date.now());
  return obj.save();
}

async function moralisUpsertStuckStopEvaluation(player, evaluation) {
  const StuckStopEvaluation = Moralis.Object.extend("StuckStopEvaluation");
  const q = new Moralis.Query(StuckStopEvaluation);
  q.equalTo("player", String(player));
  q.equalTo("eventId", evaluation.eventId);
  const obj = (await q.first()) || new StuckStopEvaluation();
  obj.set("player", String(player));
  obj.set("marketKey", evaluation.marketKey || "");
  obj.set("eventId", evaluation.eventId || "");
  obj.set("tier", evaluation.tier || "");
  obj.set("status", evaluation.status || "monitoring");
  obj.set("timestamp", Number(evaluation.confirmedAt || evaluation.triggeredAt || Date.now()));
  obj.set("evaluation", evaluation);
  return obj.save();
}

async function moralisLoadOpenStuckStopEvaluations(player, { limit = 20 } = {}) {
  const StuckStopEvaluation = Moralis.Object.extend("StuckStopEvaluation");
  const q = new Moralis.Query(StuckStopEvaluation);
  q.equalTo("player", String(player));
  q.equalTo("status", "monitoring");
  q.descending("timestamp");
  q.limit(limit);
  const rows = await q.find();
  return rows
    .map((row) => row.get("evaluation"))
    .filter((evaluation) => evaluation?.eventId && evaluation?.marketKey);
}

async function moralisLoadTradeHistory(player, { limit = 200 } = {}) {
  const TradeHistory = Moralis.Object.extend("TradeHistory");
  const q = new Moralis.Query(TradeHistory);
  q.equalTo("player", player);
  q.descending("timestamp");
  q.limit(limit);
  const rows = await q.find();
  return rows.map(r => ({
    ts: r.get("timestamp"),
    side: r.get("side"),
    amount: r.get("amount"),
    marketKey: r.get("marketKey"),
    token: r.get("token"),
    initiator: r.get("initiator"),
    txHash: r.get("txHash"),
    actualAmountIn: r.get("actualAmountIn"),
    actualAmountOut: r.get("actualAmountOut"),
    blockNumber: r.get("blockNumber"),
    externalSignal: r.get("externalSignal"),
    externalSignalDecisionId: r.get("externalSignalDecisionId"),
  }));
}

async function moralisClearTradeHistoryForPlayer(player) {
  const TradeHistory = Moralis.Object.extend("TradeHistory");
  const q = new Moralis.Query(TradeHistory);
  q.equalTo("player", player);
  const rows = await q.find();
  if (rows.length) {
    try { await Moralis.Object.destroyAll(rows); } catch (e) { console.warn("clear trade history failed", e); }
  }
}

async function moralisDeleteTradeHistoryForMarket(player, marketKey) {
  const TradeHistory = Moralis.Object.extend("TradeHistory");
  const q = new Moralis.Query(TradeHistory);
  q.equalTo("player", player);
  q.equalTo("marketKey", marketKey);
  const rows = await q.find();
  if (rows.length) {
    try { await Moralis.Object.destroyAll(rows); } catch (e) { console.warn("delete market history failed", e); }
  }
}

async function moralisLoadAllPlayerState(player) {
  const [configs, positions, customBots, history, stuckStopEvaluations, accounting] = await Promise.all([
    moralisLoadBotConfigs(player),
    moralisLoadPositions(player),
    moralisLoadCustomBots(player),
    moralisLoadTradeHistory(player, { limit: 200 }),
    moralisLoadOpenStuckStopEvaluations(player).catch((error) => {
      console.warn("moralisLoadOpenStuckStopEvaluations failed", error);
      return [];
    }),
    moralisLoadBotAccounting(player).catch((error) => {
      console.warn("moralisLoadBotAccounting failed", error);
      return null;
    }),
  ]);
  return { configs, positions, customBots, history, stuckStopEvaluations, accounting };
}

/* ===========================
   Constants & Utilities
   =========================== */

// Default configuration (will be overridden by user selection)
// CRITICAL FIX: Make Arbitrum V3 pools default for better liquidity and lower fees
const DEFAULT_CHAIN = "arbitrum";
const DEFAULT_DEX = "uniswapV3";

const ERC20_META_ABI = [
  "function symbol() view returns (string)",
  "function name() view returns (string)",
  "function decimals() view returns (uint8)",
];
const ERC20_ABI = [
  "function approve(address spender, uint256 amount) external returns (bool)",
  "function allowance(address owner, address spender) external view returns (uint256)",
  "function balanceOf(address account) external view returns (uint256)",
];
const ROUTER_ABI = [
  // V2 Router functions
  "function swapExactTokensForTokens(uint256 amountIn,uint256 amountOutMin,address[] calldata path,address to,uint256 deadline) external returns (uint256[] memory amounts)",
  "function swapExactTokensForTokens(uint256 amountIn,uint256 amountOutMin,address[] calldata path,address to) external returns (uint256[] memory amounts)",
  "function swapExactETHForTokens(uint256 amountOutMin,address[] calldata path,address to,uint256 deadline) external payable returns (uint256[] memory amounts)",
  "function swapExactTokensForETH(uint256 amountIn,uint256 amountOutMin,address[] calldata path,address to,uint256 deadline) external returns (uint256[] memory amounts)",
  "function getAmountsOut(uint256 amountIn, address[] calldata path) external view returns (uint256[] memory amounts)",
  // V3 Router functions (SwapRouter02) - only including functions we actually use
  "function exactInputSingle((address tokenIn, address tokenOut, uint24 fee, address recipient, uint256 deadline, uint256 amountIn, uint256 amountOutMinimum, uint160 sqrtPriceLimitX96)) external payable returns (uint256 amountOut)",
  "function exactOutputSingle((address tokenIn, address tokenOut, uint24 fee, address recipient, uint256 deadline, uint256 amountOut, uint256 amountInMaximum, uint160 sqrtPriceLimitX96)) external payable returns (uint256 amountIn)",
  // Universal Router (V4) functions - specify both overloads explicitly
  "function execute(bytes calldata commands, bytes[] calldata inputs) external payable",
  "function execute(bytes calldata commands, bytes[] calldata inputs, uint256 deadline) external payable",
];
const PAIR_ABI = [
  "event Swap(address indexed sender, uint amount0In, uint amount1In, uint amount0Out, uint amount1Out, address indexed to)",
  "event Sync(uint112 reserve0, uint112 reserve1)",
  "function getReserves() external view returns (uint112 reserve0, uint112 reserve1, uint32 blockTimestampLast)",
  "function token0() external view returns (address)",
  "function token1() external view returns (address)",
];

// Uniswap V3 Pool ABI
const V3_POOL_ABI = [
  "event Swap(address indexed sender, address indexed recipient, int256 amount0, int256 amount1, uint160 sqrtPriceX96, uint128 liquidity, int24 tick)",
  "function slot0() external view returns (uint160 sqrtPriceX96, int24 tick, uint16 observationIndex, uint16 observationCardinality, uint16 observationCardinalityNext, uint8 feeProtocol, bool unlocked)",
  "function liquidity() external view returns (uint128)",
  "function token0() external view returns (address)",
  "function token1() external view returns (address)",
  "function fee() external view returns (uint24)",
];
const FACTORY_ABI = [
  "event PairCreated(address indexed token0, address indexed token1, address pair, uint)",
  "function getPair(address tokenA, address tokenB) external view returns (address pair)",
  // Uniswap V3 factory methods
  "function getPool(address tokenA, address tokenB, uint24 fee) external view returns (address pool)",
];

const clamp = (x, min, max) => Math.max(min, Math.min(x, max));
/** Same idea as cloud normalizeSlippageFraction — UI may store 0.5 meaning 50% or 5 meaning 5%. */
const normalizeClientSlippageFraction = (raw) => {
  let s = Number(raw);
  if (!Number.isFinite(s) || s < 0) s = 0.005;
  if (s > 1) s = s / 100;
  return Math.min(Math.max(s, 0), 0.5);
};
const DUST = 1e-12;
const PNL_ASSET_DUST = 1e-10;
const PNL_LEDGER_SCHEMA_VERSION = 3;

const finitePnlNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const normalizePnlRecord = (record = {}) => ({
  ...record,
  schemaVersion: PNL_LEDGER_SCHEMA_VERSION,
  costBasis: Math.max(0, finitePnlNumber(record.costBasis)),
  holdings: Math.max(0, finitePnlNumber(record.holdings)),
  realizedPnL: finitePnlNumber(record.realizedPnL),
  totalTrades: Math.max(0, finitePnlNumber(record.totalTrades)),
  buyCount: Math.max(0, finitePnlNumber(record.buyCount)),
  sellCount: Math.max(0, finitePnlNumber(record.sellCount)),
  totalBuyBase: Math.max(0, finitePnlNumber(record.totalBuyBase)),
  totalBuyAsset: Math.max(0, finitePnlNumber(record.totalBuyAsset)),
  totalSellBase: Math.max(0, finitePnlNumber(record.totalSellBase)),
  totalSellAsset: Math.max(0, finitePnlNumber(record.totalSellAsset)),
  grossProfit: Math.max(0, finitePnlNumber(record.grossProfit)),
  grossLoss: Math.max(0, finitePnlNumber(record.grossLoss)),
  totalGasUsd: Math.max(0, finitePnlNumber(record.totalGasUsd)),
  accountingCheckpointAt: Math.max(0, finitePnlNumber(record.accountingCheckpointAt)),
  accountingBaselineEquityUsd: Math.max(0, finitePnlNumber(record.accountingBaselineEquityUsd)),
  reportingRealizedPnlOffset: finitePnlNumber(record.reportingRealizedPnlOffset),
  reportingGasUsdOffset: Math.max(0, finitePnlNumber(record.reportingGasUsdOffset)),
  netExternalDepositsUsd: Math.max(0, finitePnlNumber(record.netExternalDepositsUsd)),
  netExternalWithdrawalsUsd: Math.max(0, finitePnlNumber(record.netExternalWithdrawalsUsd)),
  lastObservedStableBalance: Math.max(0, finitePnlNumber(record.lastObservedStableBalance)),
  lastObservedAssetBalance: Math.max(0, finitePnlNumber(record.lastObservedAssetBalance)),
  lastObservedPrice: Math.max(0, finitePnlNumber(record.lastObservedPrice)),
  lastObservedAt: Math.max(0, finitePnlNumber(record.lastObservedAt)),
  capitalFlows: Array.isArray(record.capitalFlows) ? record.capitalFlows.slice(-100) : [],
  sellReturnsPct: Array.isArray(record.sellReturnsPct)
    ? record.sellReturnsPct.map(finitePnlNumber).filter(Number.isFinite).slice(-200)
    : [],
  processedTxHashes: Array.isArray(record.processedTxHashes)
    ? record.processedTxHashes.filter(Boolean).slice(-200)
    : [],
});

const applyConfirmedTradeToPnlRecord = (record, side, amountIn, amountOut, txHash = "") => {
  const current = normalizePnlRecord(record);
  const normalizedSide = String(side || "").toUpperCase();
  const inAmount = finitePnlNumber(amountIn);
  const outAmount = finitePnlNumber(amountOut);
  const txKey = String(txHash || "").toLowerCase();

  if (!(inAmount > 0) || !(outAmount > 0) || !["BUY", "SELL"].includes(normalizedSide)) {
    return { record: current, applied: false, reason: "invalid confirmed amounts" };
  }
  if (txKey && current.processedTxHashes.includes(txKey)) {
    return { record: current, applied: false, reason: "duplicate txHash" };
  }

  let costBasis = current.costBasis;
  let holdings = current.holdings;
  let realizedPnL = current.realizedPnL;
  let tradePnL = 0;
  let realizedCostRemoved = 0;
  let coveredAssetAmount = normalizedSide === "SELL" ? 0 : inAmount;
  let uncoveredAssetAmount = 0;
  let excludedProceedsUsd = 0;
  let costBasisCoverageRatio = 1;

  if (normalizedSide === "BUY") {
    costBasis += inAmount;
    holdings += outAmount;
  } else if (holdings > PNL_ASSET_DUST && costBasis > 0) {
    coveredAssetAmount = Math.min(inAmount, holdings);
    uncoveredAssetAmount = Math.max(0, inAmount - coveredAssetAmount);
    costBasisCoverageRatio = inAmount > 0 ? coveredAssetAmount / inAmount : 0;
    const soldFraction = Math.min(1, coveredAssetAmount / holdings);
    const costBasisSold = costBasis * soldFraction;
    const coveredProceedsUsd = outAmount * costBasisCoverageRatio;
    realizedCostRemoved = costBasisSold;
    tradePnL = coveredProceedsUsd - costBasisSold;
    excludedProceedsUsd = Math.max(0, outAmount - coveredProceedsUsd);
    realizedPnL += tradePnL;
    costBasis = Math.max(0, costBasis - costBasisSold);
    holdings = Math.max(0, holdings - coveredAssetAmount);
    if (holdings <= PNL_ASSET_DUST) {
      holdings = 0;
      costBasis = 0;
    }
  } else {
    coveredAssetAmount = 0;
    uncoveredAssetAmount = inAmount;
    excludedProceedsUsd = outAmount;
    costBasisCoverageRatio = 0;
  }

  const fullyCostCovered =
    normalizedSide !== "SELL" ||
    uncoveredAssetAmount <= Math.max(0.00001, inAmount * 0.005);

  const nextRecord = {
    ...current,
    costBasis,
    holdings,
    realizedPnL,
    totalTrades: current.totalTrades + 1,
    buyCount: current.buyCount + (normalizedSide === "BUY" ? 1 : 0),
    sellCount: current.sellCount + (normalizedSide === "SELL" ? 1 : 0),
    totalBuyBase: current.totalBuyBase + (normalizedSide === "BUY" ? inAmount : 0),
    totalBuyAsset: current.totalBuyAsset + (normalizedSide === "BUY" ? outAmount : 0),
    totalSellBase: current.totalSellBase + (normalizedSide === "SELL" ? outAmount : 0),
    totalSellAsset: current.totalSellAsset + (normalizedSide === "SELL" ? inAmount : 0),
    grossProfit: current.grossProfit + (tradePnL > 0 ? tradePnL : 0),
    grossLoss: current.grossLoss + (tradePnL < 0 ? Math.abs(tradePnL) : 0),
    accountingStatus: fullyCostCovered ? "confirmed" : "incomplete-cost-basis",
    lastCostBasisCoverageRatio: costBasisCoverageRatio,
    excludedExternalSaleProceedsUsd:
      Math.max(0, finitePnlNumber(current.excludedExternalSaleProceedsUsd)) + excludedProceedsUsd,
    sellReturnsPct:
      normalizedSide === "SELL" && realizedCostRemoved > 0 && fullyCostCovered
        ? [
            ...current.sellReturnsPct,
            (tradePnL / realizedCostRemoved) * 100,
          ].slice(-200)
        : current.sellReturnsPct,
    processedTxHashes: txKey
      ? [...current.processedTxHashes.filter((hash) => hash !== txKey), txKey].slice(-200)
      : current.processedTxHashes,
    lastUpdated: Date.now(),
  };

  return {
    record: nextRecord,
    applied: true,
    tradePnL,
    realizedCostRemoved,
    coveredAssetAmount,
    uncoveredAssetAmount,
    excludedProceedsUsd,
    costBasisCoverageRatio,
    fullyCostCovered,
  };
};

const getPnlAverageEntry = (record) => {
  const normalized = normalizePnlRecord(record);
  return normalized.holdings > PNL_ASSET_DUST
    ? normalized.costBasis / normalized.holdings
    : 0;
};

const getUnrealizedPnlForRecord = (record, price) => {
  const normalized = normalizePnlRecord(record);
  const spot = finitePnlNumber(price);
  if (!(normalized.holdings > PNL_ASSET_DUST) || !(spot > 0)) return 0;
  return normalized.holdings * spot - normalized.costBasis;
};
const ZERO_HOLDINGS_THRESHOLD_USD = 5; // Reset anchor if remaining holdings worth less than $5 USD
const ARBITRUM_WETH_USDC_POOL_ADDRESS =
  (typeof process !== "undefined" && (process.env.REACT_APP_WETH_USDC_POOL_ADDRESS || process.env.WETH_USDC_POOL_ADDRESS)) ||
  "0xc6962004f452be9203591991d15f6b388e09e8d0";
const ARBITRUM_NATIVE_USDC_ADDRESS = "0xaf88d065e77c8cC2239327C5EDb3A432268e5831";
const PRICE_SCALE_18 = ethers.BigNumber.from("1000000000000000000");
const Q192 = ethers.BigNumber.from(2).pow(192);
const WETH_PRICE_LOGGING_ENABLED =
  typeof process === "undefined" ||
  process.env.REACT_APP_WETH_PRICE_LOGGING_ENABLED !== "false";
const BALANCE_MISMATCH_LOGGING_ENABLED =
  typeof process === "undefined" ||
  process.env.REACT_APP_BALANCE_MISMATCH_LOGGING_ENABLED !== "false";
const GRID_RESET_COOLDOWN_SECONDS = Number(
  typeof process !== "undefined" && process.env.REACT_APP_GRID_RESET_COOLDOWN_SECONDS
) || 300;
const GRID_RESET_RECOVERY_COOLDOWN_SECONDS = Number(
  typeof process !== "undefined" && process.env.REACT_APP_GRID_RESET_RECOVERY_COOLDOWN_SECONDS
) || 7200;
const GRID_RESET_MIN_PRICE_MOVE_BPS = Number(
  typeof process !== "undefined" && process.env.REACT_APP_GRID_RESET_MIN_PRICE_MOVE_BPS
) || 30;
const GRID_RESET_RECOVERY_MIN_PRICE_MOVE_BPS = Number(
  typeof process !== "undefined" && process.env.REACT_APP_GRID_RESET_RECOVERY_MIN_PRICE_MOVE_BPS
) || 100;
const GRID_RESET_HYSTERESIS_BPS = Number(
  typeof process !== "undefined" && process.env.REACT_APP_GRID_RESET_HYSTERESIS_BPS
) || 20;
const GRID_RESET_MIN_TARGET_CHANGE_BPS = Number(
  typeof process !== "undefined" && process.env.REACT_APP_GRID_RESET_MIN_TARGET_CHANGE_BPS
) || 25;
const GRID_RESET_RECOVERY_MIN_TARGET_CHANGE_BPS = Number(
  typeof process !== "undefined" && process.env.REACT_APP_GRID_RESET_RECOVERY_MIN_TARGET_CHANGE_BPS
) || 75;
const GRID_RESET_RECOVERY_HYSTERESIS_BPS = Number(
  typeof process !== "undefined" && process.env.REACT_APP_GRID_RESET_RECOVERY_HYSTERESIS_BPS
) || 50;
const GRID_RESET_RECOVERY_MAX_PER_6H = Number(
  typeof process !== "undefined" && process.env.REACT_APP_GRID_RESET_RECOVERY_MAX_PER_6H
) || 2;
const SAFE_REGIME_MIN_MOVES = {
  BULL_ACTIVE: { buy: 0.002, sell: 0.003 },
  NORMAL_CHOP: { buy: 0.003, sell: 0.004 },
  RECOVERY_BREAKOUT: { buy: 0.004, sell: 0.0055 },
  DEFENSIVE: { buy: 0.006, sell: 0.0075 },
  CAPITAL_PROTECTION: { buy: 0.008, sell: 0.01 },
};
const STUCK_STOP_HORIZONS_MS = {
  m15: 15 * 60 * 1000,
  h1: 60 * 60 * 1000,
  h4: 4 * 60 * 60 * 1000,
  h24: 24 * 60 * 60 * 1000,
};

/** Drawdown vs cost basis (0.05 = 5% underwater). */
const getDrawdownPct = (avgEntry, price) => {
  if (!(avgEntry > 0) || !(price > 0)) return 0;
  return Math.max(0, 1 - price / avgEntry);
};

const computeGridTargets = ({ price, avgEntry, buyPct, sellPct, useSpotAnchor }) => {
  if (!(price > 0)) return { priceTargetBuy: 0, priceTargetSell: 0 };
  if (useSpotAnchor) {
    return {
      priceTargetBuy: price * buyPct,
      priceTargetSell: price * sellPct,
    };
  }
  const entry = avgEntry > 0 ? avgEntry : price;
  const buyFromPrice = price * buyPct;
  const buyFromEntry = entry * buyPct;
  const sellFromPrice = price * sellPct;
  const sellFromEntry = entry * sellPct;
  return {
    priceTargetBuy: avgEntry > 0 ? Math.min(buyFromPrice, buyFromEntry) : buyFromPrice,
    priceTargetSell: avgEntry > 0 ? Math.max(sellFromPrice, sellFromEntry) : sellFromPrice,
  };
};

const getNormalSellFloorPrice = ({ avgEntry, minProfitBps = 30 }) => {
  const entry = Number(avgEntry) || 0;
  if (!(entry > 0)) return 0;
  return entry * (1 + Math.max(0, Number(minProfitBps) || 0) / 10000);
};

const getProgressiveBuyStepPct = (marketRegime, consecBuys) => {
  const ladders = {
    BULL_ACTIVE: [0, 0.0035, 0.005, 0.007, 0.009],
    NORMAL_CHOP: [0, 0.004, 0.0055, 0.0075, 0.01],
    RECOVERY_BREAKOUT: [0, 0.0045, 0.007, 0.0095, 0.0125],
    DEFENSIVE: [0, 0.005, 0.0075, 0.01, 0.0125],
    CAPITAL_PROTECTION: [0, 0.006, 0.009, 0.012, 0.015],
  };
  const ladder = ladders[marketRegime] || ladders.NORMAL_CHOP;
  return ladder[Math.min(Math.max(0, Number(consecBuys) || 0), ladder.length - 1)] || 0;
};

const shouldUseSpotGridMode = ({ cfg, pos, price, baseBalanceUsd, totalBookUsd }) => {
  if (cfg?.hybridGridEnabled === false) return false;
  const threshold = Number(cfg?.underwaterSpotPct ?? defaultBotCfg.underwaterSpotPct);
  const reservePct = Number(cfg?.minUsdcReservePct ?? defaultBotCfg.minUsdcReservePct);
  const dd = getDrawdownPct(pos?.avgEntry, price);
  if (dd >= threshold && (pos?.holdings || 0) > DUST) return true;
  if (totalBookUsd > 0 && baseBalanceUsd / totalBookUsd < reservePct) return true;
  return false;
};

const getMarketBook = (key, pos, price, positions, chainConfig) => {
  if (!chainConfig || !(price > 0)) {
    return { baseBalance: 0, holdingsUsd: 0, totalBookUsd: 0 };
  }
  const wrappedSymbol = chainConfig.nativeCurrency.symbol;
  const stableSymbol = chainConfig.defaultStablecoin;
  const nativeSymbol = wrappedSymbol;
  let baseBalance = 0;
  if (key === `${wrappedSymbol}-${stableSymbol}` || key === `WBTC-${stableSymbol}`) {
    const baseKey = `${wrappedSymbol}-${stableSymbol}_BASE`;
    baseBalance = positions[baseKey]?.holdings || 0;
  } else if (key === `WBTC-${wrappedSymbol}`) {
    baseBalance = positions[nativeSymbol]?.holdings || 0;
  }
  const holdingsUsd = (pos?.holdings || 0) * price;
  return {
    baseBalance,
    holdingsUsd,
    totalBookUsd: baseBalance + holdingsUsd,
  };
};

function computeWethBenchmark({
  benchmark,
  currentTotalUsd,
  currentWethPrice
}) {
  const initialTotalValueInWeth = Number(benchmark?.initialTotalValueInWeth) || 0;
  const price = Number(currentWethPrice) || 0;
  const totalUsd = Number(currentTotalUsd) || 0;
  if (!(initialTotalValueInWeth > 0) || !(price > 0) || !(totalUsd > 0)) return null;

  const currentTotalValueInWeth = totalUsd / price;
  const wethBenchmarkDelta = currentTotalValueInWeth - initialTotalValueInWeth;
  const wethBenchmarkDeltaPct = initialTotalValueInWeth > 0
    ? wethBenchmarkDelta / initialTotalValueInWeth
    : 0;
  const hodlWethValueNowUsd = initialTotalValueInWeth * price;
  const botVsWethHoldUsd = totalUsd - hodlWethValueNowUsd;
  const botVsWethHoldPct = hodlWethValueNowUsd > 0
    ? botVsWethHoldUsd / hodlWethValueNowUsd
    : 0;

  return {
    initialTotalValueInWeth,
    currentTotalValueInWeth,
    wethBenchmarkDelta,
    wethBenchmarkDeltaPct,
    hodlWethValueNowUsd,
    botVsWethHoldUsd,
    botVsWethHoldPct,
  };
}

const resolvePostSellTargetUpdate = ({
  price,
  newHoldings,
  remainingHoldingsUsd,
  pos,
  cfg,
  buyPct,
  sellPct,
  useSpotGrid,
  resetWhenDepleted = true,
}) => {
  if (newHoldings <= DUST || remainingHoldingsUsd < ZERO_HOLDINGS_THRESHOLD_USD) {
    if (!resetWhenDepleted) {
      if (!(Number(pos.flatSince) > 0)) {
        const t = computeGridTargets({
          price,
          avgEntry: 0,
          buyPct,
          sellPct,
          useSpotAnchor: true,
        });
        return {
          newAvgEntry: 0,
          priceTargetBuy: t.priceTargetBuy,
          priceTargetSell: t.priceTargetSell,
          buyCounter: 1,
          sellCounter: (pos.sellCounter || 1) + 1,
          depletedWithoutReset: true,
          fullExitTransition: true,
        };
      }
      return {
        newAvgEntry: pos.avgEntry || 0,
        priceTargetBuy: pos.priceTargetBuy || price * buyPct,
        priceTargetSell: pos.priceTargetSell || price * sellPct,
        buyCounter: pos.buyCounter || 1,
        sellCounter: (pos.sellCounter || 1) + 1,
        depletedWithoutReset: true,
      };
    }
    const t = computeGridTargets({ price, avgEntry: 0, buyPct, sellPct, useSpotAnchor: true });
    return {
      newAvgEntry: 0,
      priceTargetBuy: t.priceTargetBuy,
      priceTargetSell: t.priceTargetSell,
      buyCounter: 1,
      sellCounter: 1,
    };
  }
  const newAvgEntry = newHoldings > 0 ? (pos.avgEntry || 0) : 0;
  const dd = getDrawdownPct(newAvgEntry, price);
  const reanchor =
    useSpotGrid ||
    (cfg.reanchorOnSellWhenUnderwater !== false &&
      dd >= Number(cfg.underwaterSpotPct ?? defaultBotCfg.underwaterSpotPct));
  const t = computeGridTargets({
    price,
    avgEntry: newAvgEntry,
    buyPct,
    sellPct,
    useSpotAnchor: reanchor,
  });
  return {
    newAvgEntry,
    priceTargetBuy: t.priceTargetBuy,
    priceTargetSell: t.priceTargetSell,
    buyCounter: 1,
    sellCounter: (pos.sellCounter || 1) + 1,
  };
};

/** WETH qty to sell so wrapped USD share moves toward target (e.g. 50%). */
const computeRebalanceSellQty = ({
  holdings,
  price,
  baseBalanceUsd,
  targetAssetPct = 0.5,
  tolerancePct = 0.02,
}) => {
  if (!(holdings > DUST) || !(price > 0)) return 0;
  const holdingsUsd = holdings * price;
  const totalBookUsd = holdingsUsd + Math.max(0, baseBalanceUsd);
  if (!(totalBookUsd > 0)) return 0;
  const currentPct = holdingsUsd / totalBookUsd;
  if (currentPct <= targetAssetPct + tolerancePct) return 0;
  const targetHoldingsUsd = totalBookUsd * targetAssetPct;
  const excessUsd = holdingsUsd - targetHoldingsUsd;
  if (!(excessUsd > 0)) return 0;
  return Math.min(holdings, excessUsd / price);
};

const resolveStopLossTargetUpdate = ({ price, newHoldings, pos, buyPct, sellPct }) => {
  const preservedEntry = newHoldings > DUST ? (pos.avgEntry || 0) : 0;
  const t = computeGridTargets({
    price,
    avgEntry: preservedEntry,
    buyPct,
    sellPct,
    useSpotAnchor: true,
  });
  return {
    newAvgEntry: preservedEntry,
    priceTargetBuy: t.priceTargetBuy,
    priceTargetSell: t.priceTargetSell,
    buyCounter: 1,
    sellCounter: 1,
  };
};

/**
 * BUY-side protection against overbuying during downtrends/recovery.
 *
 * Notes:
 * - Uses existing `buyCounter` as consecutive-buy counter since it already resets to 1 on SELL / resets.
 *   Consecutive buys = max(0, (buyCounter||1) - 1).
 * - Does NOT affect SELL behavior.
 */
const getBuyProtectionStatus = ({
  cfg,
  pos,
  currentPrice,
  anchorPrice,
  recentRangePct,
  marketRegime,
}) => {
  const enabled = cfg?.buyProtectionEnabled !== false;
  if (!enabled) {
    return {
      enabled,
      blocked: false,
      kind: null,
      consecBuys: Math.max(0, (pos?.buyCounter || 1) - 1),
      maxBuys: Number(cfg?.buyProtectionMaxConsecutiveBuys ?? 4),
      recoveryBuyBlockedUntil: Number(pos?.recoveryBuyBlockedUntil || 0),
      majorDipRequiredPrice: null,
      majorDipRequiredPct: null,
    };
  }

  const now = Date.now();
  const recoveryBuyBlockedUntil = Number(pos?.recoveryBuyBlockedUntil || 0);
  const consecBuys = Math.max(0, (pos?.buyCounter || 1) - 1);
  const maxBuys = Math.max(1, Number(cfg?.buyProtectionMaxConsecutiveBuys ?? 4));

  // Hard rule: never allow 5th+ consecutive buy
  if (now < recoveryBuyBlockedUntil) {
    return {
      enabled,
      blocked: true,
      kind: "recoveryCooldown",
      consecBuys,
      maxBuys,
      recoveryBuyBlockedUntil,
      majorDipRequiredPrice: null,
      majorDipRequiredPct: null,
    };
  }

  if (consecBuys >= maxBuys) {
    return {
      enabled,
      blocked: true,
      kind: "maxConsecutive",
      consecBuys,
      maxBuys,
      recoveryBuyBlockedUntil,
      majorDipRequiredPrice: null,
      majorDipRequiredPct: null,
    };
  }

  const progressiveStepPct = getProgressiveBuyStepPct(marketRegime, consecBuys);
  const lastBuyPrice = Number(pos?.lastBuyPrice) || 0;
  if (consecBuys > 0 && progressiveStepPct > 0 && lastBuyPrice > 0) {
    const progressiveRequiredPrice = lastBuyPrice * (1 - progressiveStepPct);
    if (Number(currentPrice) > progressiveRequiredPrice) {
      return {
        enabled,
        blocked: true,
        kind: "progressiveSpacing",
        consecBuys,
        maxBuys,
        recoveryBuyBlockedUntil,
        progressiveRequiredPrice,
        progressiveStepPct,
        majorDipRequiredPrice: null,
        majorDipRequiredPct: null,
      };
    }
  }

  // Major-dip rule for the 4th buy (i.e., 3 buys already done)
  if (consecBuys === maxBuys - 1) {
    const hiVolDipPct = Number(cfg?.buyProtectionMajorDipHighVolPct ?? 0.01); // 1.0%
    let baseDipPct = Number(cfg?.buyProtectionMajorDipFourthBuyPct ?? 0.0075); // 0.75%
    if (cfg?.regimeEngineEnabled !== false && marketRegime) {
      baseDipPct = getMajorDipPctForRegime(marketRegime);
    }
    const isHighVol = Number(recentRangePct || 0) >= 0.01;
    const dipPct = isHighVol ? Math.max(baseDipPct, hiVolDipPct) : baseDipPct;

    const ref =
      (Number(pos?.lastBuyPrice) > 0 && Number.isFinite(Number(pos?.lastBuyPrice)))
        ? Number(pos?.lastBuyPrice)
        : (Number(pos?.avgEntry) > 0 && Number.isFinite(Number(pos?.avgEntry)))
        ? Number(pos?.avgEntry)
        : (Number(anchorPrice) > 0 && Number.isFinite(Number(anchorPrice)))
        ? Number(anchorPrice)
        : Number(currentPrice);

    const required = ref > 0 ? ref * (1 - dipPct) : 0;
    const ok = required > 0 ? Number(currentPrice) <= required : false;
    if (!ok) {
      return {
        enabled,
        blocked: true,
        kind: "fourthMajorDip",
        consecBuys,
        maxBuys,
        recoveryBuyBlockedUntil,
        majorDipRequiredPrice: required,
        majorDipRequiredPct: dipPct,
        majorDipRefPrice: ref,
        majorDipIsHighVol: isHighVol,
      };
    }

    return {
      enabled,
      blocked: false,
      kind: null,
      consecBuys,
      maxBuys,
      recoveryBuyBlockedUntil,
      majorDipRequiredPrice: required,
      majorDipRequiredPct: dipPct,
      majorDipRefPrice: ref,
      majorDipIsHighVol: isHighVol,
    };
  }

  return {
    enabled,
    blocked: false,
    kind: null,
    consecBuys,
    maxBuys,
    recoveryBuyBlockedUntil,
    majorDipRequiredPrice: null,
    majorDipRequiredPct: null,
  };
};

/** Scenario table for WETH-USDC style books (holdings in WETH, price in USDC per WETH). */
const buildPriceScenarios = ({ holdings, avgEntry, currentPrice, buyPct, sellPct, useSpotNow }) => {
  const prices = [2050, 2150, 2250];
  const entry = avgEntry > 0 ? avgEntry : currentPrice;
  return prices.map((p) => {
    const spotT = computeGridTargets({ price: p, avgEntry: entry, buyPct, sellPct, useSpotAnchor: true });
    const entryT = computeGridTargets({ price: p, avgEntry: entry, buyPct, sellPct, useSpotAnchor: false });
    const valueUsd = holdings * p;
    const unrealized = holdings > 0 && entry > 0 ? (p - entry) * holdings : 0;
    const active = useSpotNow ? spotT : entryT;
    const canSell = holdings > 0 && p > active.priceTargetSell;
    const canBuy = p < active.priceTargetBuy;
    return {
      price: p,
      valueUsd,
      unrealized,
      sellTarget: active.priceTargetSell,
      buyTarget: active.priceTargetBuy,
      canSell,
      canBuy,
      spotSell: spotT.priceTargetSell,
      entrySell: entryT.priceTargetSell,
    };
  });
};
const safeFix = (v, n = 6) => (typeof v === "number" && isFinite(v) ? v.toFixed(n) : "—");

// Format number with compact notation for large/small values
const formatCompact = (value, decimals = 2) => {
  if (typeof value !== "number" || !isFinite(value)) return "—";
  if (value === 0) return "0";
  // Only use compact notation for values >= 10,000 to avoid showing "0.40K" for 400
  if (Math.abs(value) >= 1e6) return `${(value / 1e6).toFixed(decimals)}M`;
  if (Math.abs(value) >= 1e4) return `${(value / 1e3).toFixed(decimals)}K`;
  // For small numbers, show first 5 significant digits instead of scientific notation
  if (Math.abs(value) < 0.01 && value !== 0) {
    const absValue = Math.abs(value);
    const sign = value < 0 ? "-" : "";
    // Find the order of magnitude (how many zeros after decimal)
    let order = 0;
    let temp = absValue;
    while (temp < 1 && temp > 0) {
      temp *= 10;
      order++;
    }
    // Show 5 significant digits: need (order + 4) decimal places
    // e.g., 0.00123 needs 5 decimal places to show 5 digits: 0.00123
    const decimalPlaces = order + 4;
    return sign + absValue.toFixed(Math.min(decimalPlaces, 15));
  }
  return value.toFixed(decimals);
};
const toDecimalStringSafe = (amt, decimals) => {
  const s = typeof amt === "number" ? String(amt) : (amt || "0");
  if (!s.includes(".")) return s;
  const parts = s.split(".");
  const i = parts[0];
  const fRaw = parts[1] || "";
  const f = fRaw.replace(/[^\d]/g, "");
  const fTrunc = f.slice(0, Math.max(0, decimals));
  return fTrunc.length ? `${i}.${fTrunc}` : i;
};

/** Normalize Moralis Cloud executeTokenTrade response to a receipt-like object */
const extractTradereceiptFromCloudResponse = (resp) => {
  if (!resp || typeof resp !== "object") return resp;
  if (resp.tradereceipt != null) {
    const tr = resp.tradereceipt;
    return Array.isArray(tr) ? tr[0] || tr : tr;
  }
  if (resp.receipt != null) return resp.receipt;
  if (resp.transactionReceipt != null) return resp.transactionReceipt;
  return resp;
};

/**
 * True if backend trade clearly failed or returned a useless receipt (common when tx reverts).
 * Note: A common cloud bug is amountOutMin > expected output (swap always reverts) — see docs/executeTokenTrade-minOut.md
 */
const isBackendTradeFailedOrInvalidReceipt = (resp, receipt) => {
  if (resp && typeof resp === "object") {
    if (resp.success === false) return true;
    if (resp.ok === false) return true;
    if (typeof resp.error === "string" && resp.error.trim()) return true;
  }
  const r = receipt;
  if (!r || typeof r !== "object") return true;
  if (r.status === 0 || r.status === false) return true;
  const hash = r.transactionHash || r.hash || "";
  const logCount = Array.isArray(r.logs) ? r.logs.length : 0;
  if (!hash && logCount === 0) return true;
  return false;
};

const defaultBotCfg = {
  riskAccountingRevision: 2,
  usePercentMode: true,
  manualBuy: 0,
  manualSell: 0,
  minBuySpacingMs: DEFAULT_MIN_BUY_SPACING_MS,
   buyTarget : 0.999,             // Static-mode fallback: 0.1% below the anchor
   sellTarget : 1.001,            // Static-mode fallback: 0.1% above the anchor
   maxBaseCap : 500,             // 500 CRO ≈ $50–60, perfect
   maxSellCap : 1,               // ← THIS MUST BE 1 (or remove field), NOT 0.4
   buyMultiplier : 0.20,        // Flow-adjusted compound base; capped at 25% of strategy equity
   sellMultiplier : 0.3,        // 30% of holdings per sell (increased for faster profit realization)
   // targetNetPct removed - redundant with sellTarget, was blocking trades unnecessarily
   minTradeUsd : 50,             // Economic floor; never overrides the percentage risk cap
   maxTradeUsd : 500,            // Absolute ceiling; compound sizing applies the lower 25% equity cap
   compoundSizingEnabled: true,
   compoundBaseTradePct: DEFAULT_COMPOUND_TRADE_SIZING.baseTradePct,
   compoundMaxTradePct: DEFAULT_COMPOUND_TRADE_SIZING.maxTradePct,
   compoundEconomicMinTradeUsd: DEFAULT_COMPOUND_TRADE_SIZING.economicMinTradeUsd,
   compoundAbsoluteMaxTradeUsd: DEFAULT_COMPOUND_TRADE_SIZING.absoluteMaxTradeUsd,
   compoundRealizedOnly: true,
   strategyMode: "adaptive",     // Volatility-adjusted thresholds with regime protections
   adaptiveBaseMovePct: 0.0012,  // Base adaptive buy distance: 0.12% below anchor
   adaptiveSellExtraPct: 0.0006, // Extra sell distance: base + 0.06% (roughly 0.18% in calm markets)
   adaptiveVolMultiplier: 0.08,  // Adds 8% of recent range to threshold distance
   adaptiveMinMovePct: 0.0010,   // Minimum adaptive buy distance: 0.10%
   adaptiveMaxMovePct: 0.0035,   // Maximum adaptive buy distance: 0.35%
   adaptiveBuyDistanceFactor: 0.90, // Evidence-backed 10% tighter BUY spacing; SELL spacing is unchanged

   // Stuck-position / inactivity risk controls. These are portfolio-flow controls,
   // not normal tiny stop-losses: they only trigger after the bot has been idle
   // while carrying an underwater position.
   stuckStopEnabled: true,
   maxConsecutiveBuys: 5,         // Legacy preset field; active buy protection uses its own limit
   softStuckHours: 24,
   softStuckLossPct: 0.015,
   softStuckLossUsd: 0,           // Percentage/history aligned by default; optional USD floor remains supported
   softStuckSellPct: 0.33,
   hardStuckHours: 36,
   hardStuckLossPct: 0.025,
   hardStuckLossUsd: 0,
   hardStuckSellPct: 0.66,
   emergencyStuckHours: 48,
   emergencyStuckLossPct: 0.04,
   emergencyStuckLossUsd: 0,
   emergencyStuckSellPct: 1,

   // Hybrid grid: spot-centered targets when underwater or USDC reserve is low.
   hybridGridEnabled: true,
   minUsdcReservePct: 0.22,       // Keep at least 22% of book in USDC (stable base)
   underwaterSpotPct: 0.03,       // Switch to spot grid when >= 3% below avgEntry
   reanchorOnSellWhenUnderwater: true,
   reanchorStuckOnPartial: true,

   // Drawdown stop-loss: rebalance to target WETH/USDC split, spot grid, PnL unchanged.
   stopLossEnabled: true,
   stopLossDrawdownPct: 0.05,     // Trigger when price is this far below avgEntry (5%)
   stopLossTargetWethPct: 0.5,    // Target book allocation in WETH (50% = half WETH / half USDC)
   stopLossCooldownHours: 4,      // Min hours between stop-loss rebalances per market

   // BUY protection (anti-overbuy) — does not affect SELLs.
   buyProtectionEnabled: true,
   recoveryBuyCooldownMs: 20 * 60 * 1000, // 20 minutes after RECOVERY_GRID
   buyProtectionMaxConsecutiveBuys: 5,
   buyProtectionMajorDipFourthBuyPct: 0.0075, // 0.75%
   buyProtectionMajorDipHighVolPct: 0.01, // 1.0% when 1h range is high

   // Normal-cycle execution economics. Explicit stop-loss/stuck-stop/manual sells bypass this floor.
   normalSellMinProfitBps: 30,
   normalSellExitFeeBps: 5,
   normalSellExpectedSlippageBps: 3,
   normalSellExpectedPriceImpactBps: 1,
   normalSellRequiredNetProfitBps: 10,
   normalSellFallbackGasUsd: 0.10,
   normalSellDuplicateCooldownMs: 90 * 1000,
   normalSellDuplicateMoveBps: 12,
   postSellBuyCooldownMs: 15 * 60 * 1000,

   // Multi-timeframe regime engine (metrics, RECOVERY_BREAKOUT, exposure caps)
   regimeEngineEnabled: true,
   capitalProtectionCooldownMs: 90 * 60 * 1000,

   // Guarded re-entry after a confirmed full exit. This is separate from grid-reset behavior.
   flatReentryEnabled: true,
   flatReentryMonitorHours: 3,
   flatReentryNearMissBps: 12,
   flatReentryConfirmMinutes: 5,
   flatReentryCooldownHours: 4,
   flatReentryForcedReviewHours: 8,
   flatReentryBullMaxHours: 12,

   // External CEX advisor. Shadow mode is deliberately the default: calculations
   // and MongoDB observations run, while the proven strategy keeps its exact sizing.
   externalSignalEnabled: true,
   externalSignalShadowMode: true,
   externalSignalPhase1SizingEnabled: false,
   externalSignalPhase2ThresholdEnabled: true,
   externalSignalPhase2MaxAdjustmentPct: DEFAULT_EXTERNAL_SIGNAL_CONFIG.maxThresholdAdjustmentPct,
   externalSignalPhase2MinSamples: 2,
   externalSignalPhase2MinPersistenceMs: 2000,
   externalSignalPhase2MaxSampleGapMs: 15000,
   externalSignalMinQuality: DEFAULT_EXTERNAL_SIGNAL_CONFIG.minSignalQuality,
   externalSignalMinSizeMultiplier: DEFAULT_EXTERNAL_SIGNAL_CONFIG.minSizeMultiplier,
   externalSignalMaxSizeMultiplier: DEFAULT_EXTERNAL_SIGNAL_CONFIG.maxSizeMultiplier,
   externalSignalMinDirectionalQuality: DEFAULT_EXTERNAL_SIGNAL_CONFIG.minDirectionalQuality,
   externalSignalMinDirectionalLeadBps: DEFAULT_EXTERNAL_SIGNAL_CONFIG.minDirectionalLeadBps,
   externalSignalMinDirectionalMomentumBps: DEFAULT_EXTERNAL_SIGNAL_CONFIG.minDirectionalMomentumBps,
   externalSignalMinDirectionalAgreement: DEFAULT_EXTERNAL_SIGNAL_CONFIG.minDirectionalAgreement,
   externalSignalMinMomentumConsistency: DEFAULT_EXTERNAL_SIGNAL_CONFIG.minMomentumConsistency,
   externalSignalExecutionToleranceBps: DEFAULT_EXTERNAL_SIGNAL_CONFIG.executionToleranceBps,
   externalSignalMaxUpsizeWithoutNetOpportunity: DEFAULT_EXTERNAL_SIGNAL_CONFIG.maxUpsizeWithoutNetOpportunity,
   externalSignalEstimatedExitCostBps: DEFAULT_EXTERNAL_SIGNAL_CONFIG.estimatedExitCostBps,
   externalSignalSafetyMarginBps: DEFAULT_EXTERNAL_SIGNAL_CONFIG.safetyMarginBps,
   externalSignalFallbackGasUsd: DEFAULT_EXTERNAL_SIGNAL_CONFIG.fallbackGasUsd,

};

const secondaryBotDefaultCfg = {
  ...defaultBotCfg,
  riskAccountingRevision: 3,
  minTradeUsd: 10,
  compoundEconomicMinTradeUsd: 10,
};

const SNIPER_BOT_VERSION = "1.13.3";
const SNIPER_BOT_VERSION_DATE = "2026-09-20";
const SNIPER_BOT_VERSION_NOTE = "Small-account buy sizing respects the percentage cap.";

const TEMPLATE_LABELS = {
  aggressive: "Aggressive",
  balanced: "Balanced",
  conservative: "Conservative",
};

const BOT_TEMPLATES = {
  aggressive: {
    description: "Scalping posture for low volatility and rising markets. Keeps thin thresholds and light safety nets.",
    strategyMode: "adaptive",
    adaptiveBaseMovePct: 0.0012,
    adaptiveSellExtraPct: 0.0006,
    adaptiveVolMultiplier: 0.08,
    minUsdcReservePct: 0.22,
    maxConsecutiveBuys: 5,
    stopLossEnabled: true,
    stopLossDrawdownPct: 0.05,
    buyProtectionEnabled: true,
    recoveryBuyCooldownMs: 20 * 60 * 1000,
    buyProtectionMaxConsecutiveBuys: 5,
    buyProtectionMajorDipFourthBuyPct: 0.0075,
    buyProtectionMajorDipHighVolPct: 0.01,
    hybridGridEnabled: true,
    regimeEngineEnabled: true,
  },
  balanced: {
    description: "Balanced posture for moderate conditions. Preserves scalping while adding modest protection.",
    strategyMode: "adaptive",
    adaptiveBaseMovePct: 0.0014,
    adaptiveSellExtraPct: 0.0005,
    adaptiveVolMultiplier: 0.12,
    minUsdcReservePct: 0.25,
    maxConsecutiveBuys: 4,
    stopLossEnabled: true,
    stopLossDrawdownPct: 0.05,
    buyProtectionEnabled: true,
    recoveryBuyCooldownMs: 30 * 60 * 1000,
    buyProtectionMaxConsecutiveBuys: 4,
    buyProtectionMajorDipFourthBuyPct: 0.0085,
    buyProtectionMajorDipHighVolPct: 0.012,
    hybridGridEnabled: true,
    regimeEngineEnabled: true,
  },
  conservative: {
    description: "Conservative posture for high-risk or choppy conditions. Widens thresholds and increases reserves.",
    strategyMode: "adaptive",
    adaptiveBaseMovePct: 0.0016,
    adaptiveSellExtraPct: 0.0007,
    adaptiveVolMultiplier: 0.15,
    minUsdcReservePct: 0.30,
    maxConsecutiveBuys: 3,
    stopLossEnabled: true,
    stopLossDrawdownPct: 0.06,
    buyProtectionEnabled: true,
    recoveryBuyCooldownMs: 40 * 60 * 1000,
    buyProtectionMaxConsecutiveBuys: 3,
    buyProtectionMajorDipFourthBuyPct: 0.01,
    buyProtectionMajorDipHighVolPct: 0.015,
    hybridGridEnabled: true,
    regimeEngineEnabled: true,
  },
};

const CONVERGENCE_FALLBACK_HORIZONS_MS = [250, 500, 1000, 2000, 5000, 15000, 30000, 60000, 180000];
const CONVERGENCE_GATE_LABELS = {
  enoughCandidates: "samples",
  enoughDays: "days",
  holdoutMeanAboveMinimum: "holdout mean",
  holdoutMedianPositive: "holdout median",
  holdoutProfitFactor: "profit factor",
  holdoutBootstrapPositive: "confidence",
};

function convergenceNumeric(value) {
  if (value == null || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function formatConvergenceUsd(value, digits = 2) {
  const numeric = convergenceNumeric(value);
  if (numeric == null) return "N/A";
  const sign = numeric < 0 ? "-$" : "$";
  return `${sign}${Math.abs(numeric).toFixed(digits)}`;
}

function formatConvergenceBps(value) {
  const numeric = convergenceNumeric(value);
  if (numeric == null) return "N/A";
  return `${numeric > 0 ? "+" : ""}${numeric.toFixed(1)} bps`;
}

function formatConvergencePct(value) {
  const numeric = convergenceNumeric(value);
  if (numeric == null) return "N/A";
  return `${(numeric * 100).toFixed(0)}%`;
}

function formatConvergenceDuration(ms) {
  const numeric = convergenceNumeric(ms);
  if (numeric == null) return "N/A";
  if (numeric < 1000) return `${numeric}ms`;
  if (numeric < 60000) return `${numeric / 1000}s`;
  return `${numeric / 60000}m`;
}

function formatConvergenceAge(timestamp) {
  const numeric = convergenceNumeric(timestamp);
  if (numeric == null || numeric <= 0) return "Waiting";
  const seconds = Math.max(0, Math.round((Date.now() - numeric) / 1000));
  if (seconds < 10) return "Now";
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  return `${Math.floor(minutes / 60)}h ago`;
}

function ConvergenceMetric({ label, value, color = "white", hint }) {
  const content = (
    <Box minW={0} py={1}>
      <Text fontSize="xs" color="gray.500" textTransform="uppercase">
        {label}
      </Text>
      <Text mt={1} fontSize="sm" fontWeight="700" color={color} overflowWrap="anywhere">
        {value}
      </Text>
    </Box>
  );
  return hint ? <Tooltip label={hint}>{content}</Tooltip> : content;
}

function ConvergenceOpportunityRow({ side, opportunity }) {
  const sideColor = side === "BUY" ? "green.300" : "red.300";
  if (!opportunity) {
    return (
      <HStack justify="space-between" py={2}>
        <Text fontSize="sm" fontWeight="700" color={sideColor}>{side}</Text>
        <Text fontSize="sm" color="gray.500">Waiting for quotes</Text>
      </HStack>
    );
  }

  const poolGapBps = opportunity.poolPrice > 0 && opportunity.fairPrice > 0
    ? ((opportunity.fairPrice / opportunity.poolPrice) - 1) * 10000
    : null;
  return (
    <Box py={2}>
      <HStack justify="space-between" align="start" spacing={3}>
        <HStack spacing={2} minW={0}>
          <Text fontSize="sm" fontWeight="800" color={sideColor}>{side}</Text>
          <Tooltip
            label={
              opportunity.eligible
                ? "All current quality and executable-edge gates pass"
                : (opportunity.rejectionReasons || []).join(", ") || "Current gates do not pass"
            }
          >
            <Badge colorScheme={opportunity.eligible ? "green" : "gray"}>
              {opportunity.eligible ? "eligible" : "below gate"}
            </Badge>
          </Tooltip>
        </HStack>
        <Text
          fontSize="md"
          fontWeight="800"
          color={opportunity.netEdgeBps > 0 ? "green.300" : "orange.300"}
        >
          {formatConvergenceBps(opportunity.netEdgeBps)}
        </Text>
      </HStack>
      <Text mt={1} fontSize="xs" color="gray.400" overflowWrap="anywhere">
        {opportunity.venueName || opportunity.venueId || "DEX"} | {formatConvergenceUsd(opportunity.sizeUsd, 0)} |
        {` CEX ${formatConvergenceBps(poolGapBps)} vs pool`}
      </Text>
    </Box>
  );
}

function ConvergenceResearchPanel({
  statusPayload,
  reportPayload,
  loading,
  errors,
  horizonMs,
  onHorizonChange,
  onRefresh,
}) {
  const status = statusPayload?.status || null;
  const config = statusPayload?.config || {};
  const report = reportPayload?.report || null;
  const fair = status?.fairPrice || null;
  const paper = status?.paperState || reportPayload?.paperState || null;
  const opportunities = Array.isArray(status?.bestOpportunities) ? status.bestOpportunities : [];
  const buyOpportunity = opportunities.find((row) => row?.side === "BUY") || null;
  const sellOpportunity = opportunities.find((row) => row?.side === "SELL") || null;
  const connections = status?.feedConnections || {};
  const health = status?.health || {};
  const counters = status?.counters || {};
  const dexHealth = status?.dexQuoteHealth || {};
  const connectionValues = Object.values(connections);
  const openFeeds = connectionValues.filter((value) => value === "open").length;
  const totalFeeds = Math.max(connectionValues.length, fair?.sourceCount || 0, 4);
  const fairPrice = convergenceNumeric(fair?.fairWethUsdc);
  const paperEquity = paper && fairPrice != null
    ? Number(paper.usdcBalance || 0) + Number(paper.wethBalance || 0) * fairPrice
    : null;
  const paperPnl = paperEquity != null
    ? paperEquity - Number(paper.initialCapitalUsd || 0)
    : null;
  const paperTrades = Number(paper?.trades || 0);
  const paperWinRate = paperTrades > 0 ? Number(paper?.wins || 0) / paperTrades : null;
  const gateEntries = report?.gates ? Object.entries(report.gates) : [];
  const passedGates = gateEntries.filter(([, passed]) => passed).length;
  const horizons = Array.isArray(config.markoutHorizonsMs) && config.markoutHorizonsMs.length
    ? config.markoutHorizonsMs
    : CONVERGENCE_FALLBACK_HORIZONS_MS;
  const errorMessage = [status?.lastError, ...Object.values(errors || {})].filter(Boolean).join(" | ");
  const serviceColor = status?.running && !status?.lastError && health.dexQuotesHealthy !== false
    ? "green"
    : status?.lastError || health.dexQuotesHealthy === false
      ? "red"
      : "orange";
  const qualityColor = fair?.status === "healthy" ? "green.300" : fair?.status === "degraded" ? "orange.300" : "gray.300";

  return (
    <Panel
      title="Convergence research"
      subtitle="Arbitrum WETH/USDC shadow evidence and paper execution"
      icon={FiActivity}
      accent="cyan"
    >
      <HStack justify="space-between" align="center" spacing={3} flexWrap="wrap">
        <HStack spacing={2} flexWrap="wrap">
          <Badge colorScheme={serviceColor}>{status?.running ? "collector running" : "collector stopped"}</Badge>
          <Badge colorScheme="blue">shadow only</Badge>
          <Badge colorScheme={fair?.status === "healthy" ? "green" : fair?.status === "degraded" ? "orange" : "gray"}>
            {fair?.status || "warming"}
          </Badge>
          <Badge colorScheme={health.dexQuotesHealthy === true ? "green" : health.dexQuotesHealthy === false ? "red" : "gray"}>
            DEX {health.dexQuotesHealthy === true ? "healthy" : health.dexQuotesHealthy === false ? "unhealthy" : "warming"}
          </Badge>
          {Number(status?.backoffRemainingMs || 0) > 0 && (
            <Badge colorScheme="orange">RPC backoff {formatConvergenceDuration(status.backoffRemainingMs)}</Badge>
          )}
          <Text fontSize="xs" color="gray.500">Auto 15s</Text>
        </HStack>
        <HStack spacing={2}>
          <Tooltip label="Markout horizon used by the evidence report">
            <Select
              size="sm"
              width="110px"
              value={horizonMs}
              onChange={(event) => onHorizonChange(Number(event.target.value))}
              aria-label="Evidence horizon"
            >
              {horizons.map((value) => (
                <option key={value} value={value}>{formatConvergenceDuration(value)}</option>
              ))}
            </Select>
          </Tooltip>
          <Tooltip label="Refresh convergence data">
            <IconButton
              size="sm"
              variant="outline"
              icon={<FiRefreshCw />}
              aria-label="Refresh convergence data"
              isLoading={loading}
              onClick={onRefresh}
            />
          </Tooltip>
        </HStack>
      </HStack>

      {errorMessage && (
        <Alert status={status ? "warning" : "error"} mt={3} borderRadius="md">
          <AlertIcon />
          <Text fontSize="xs" overflowWrap="anywhere">{errorMessage}</Text>
        </Alert>
      )}

      {loading && !status && !report ? (
        <Text py={6} color="gray.400">Loading convergence data...</Text>
      ) : (
        <>
          <SimpleGrid columns={[2, 4, 8]} spacingX={5} spacingY={3} mt={4}>
            <ConvergenceMetric label="CEX fair price" value={formatConvergenceUsd(fairPrice)} color="cyan.200" />
            <ConvergenceMetric label="Signal quality" value={formatConvergencePct(fair?.signalQuality)} color={qualityColor} />
            <ConvergenceMetric label="Agreement" value={formatConvergencePct(fair?.agreement)} />
            <ConvergenceMetric label="Dispersion" value={formatConvergenceBps(fair?.dispersionBps)} />
            <ConvergenceMetric label="Momentum" value={formatConvergenceBps(fair?.momentumBps)} />
            <ConvergenceMetric label="CEX feeds" value={`${openFeeds}/${totalFeeds} open`} />
            <ConvergenceMetric label="Feed freshness" value={formatConvergenceDuration(fair?.feedFreshnessMs)} />
            <ConvergenceMetric label="Last quote sweep" value={formatConvergenceAge(status?.lastSuccessfulTickAt)} />
            <ConvergenceMetric
              label="DEX venues"
              value={`${Number(dexHealth.lastSuccessfulVenueCount || 0)}/${Number(dexHealth.expectedVenueCount || 0)} healthy`}
            />
            <ConvergenceMetric
              label="Quote calls"
              value={`${Number(counters.successfulQuotes || 0)}/${Number(counters.quoteAttempts || 0)} ok`}
              color={Number(counters.failedQuotes || 0) > 0 ? "orange.300" : "gray.200"}
            />
          </SimpleGrid>

          <Divider my={4} borderColor="gray.700" />
          <SimpleGrid columns={[1, 2]} spacing={6}>
            <Box minW={0}>
              <HStack justify="space-between" mb={1}>
                <Text fontSize="sm" fontWeight="800" color="gray.200">Executable edge</Text>
                <Text fontSize="xs" color="gray.500">{Number(status?.candidates || 0)} confirmed</Text>
              </HStack>
              <ConvergenceOpportunityRow side="BUY" opportunity={buyOpportunity} />
              <Divider borderColor="gray.700" />
              <ConvergenceOpportunityRow side="SELL" opportunity={sellOpportunity} />
            </Box>

            <Box minW={0}>
              <HStack justify="space-between" mb={3}>
                <Text fontSize="sm" fontWeight="800" color="gray.200">Paper account</Text>
                <Badge colorScheme={paper?.position ? "cyan" : "gray"}>
                  {paper?.position?.direction || "flat"}
                </Badge>
              </HStack>
              <SimpleGrid columns={2} spacingX={5} spacingY={3}>
                <ConvergenceMetric label="Equity" value={formatConvergenceUsd(paperEquity)} />
                <ConvergenceMetric
                  label="Paper PnL"
                  value={formatConvergenceUsd(paperPnl)}
                  color={paperPnl > 0 ? "green.300" : paperPnl < 0 ? "red.300" : "gray.200"}
                />
                <ConvergenceMetric label="Closed trades" value={paper ? paperTrades : "N/A"} />
                <ConvergenceMetric label="Win rate" value={paperWinRate == null ? "N/A" : formatConvergencePct(paperWinRate)} />
                <ConvergenceMetric label="Realized" value={formatConvergenceUsd(paper?.realizedPnlUsd)} />
                <ConvergenceMetric label="Max drawdown" value={formatConvergenceUsd(paper?.maxDrawdownUsd)} color="orange.300" />
              </SimpleGrid>
            </Box>
          </SimpleGrid>

          <Divider my={4} borderColor="gray.700" />
          <HStack justify="space-between" align="center" spacing={3} flexWrap="wrap">
            <HStack spacing={2}>
              <Text fontSize="sm" fontWeight="800" color="gray.200">Evidence</Text>
              <Badge colorScheme={report?.promotionReady ? "green" : report ? "orange" : "gray"}>
                {report ? `${passedGates}/${gateEntries.length} gates` : "collecting"}
              </Badge>
            </HStack>
            <Text fontSize="xs" color="gray.500">
              {report ? `${Number(report.observedDays || 0).toFixed(1)} days observed` : "No completed markouts yet"}
            </Text>
          </HStack>
          <SimpleGrid columns={[2, 4, 8]} spacingX={5} spacingY={3} mt={3}>
            <ConvergenceMetric label="Samples" value={report?.total?.count ?? "N/A"} />
            <ConvergenceMetric label="Mean" value={formatConvergenceBps(report?.total?.meanPnlBps)} />
            <ConvergenceMetric label="Median" value={formatConvergenceBps(report?.total?.medianPnlBps)} />
            <ConvergenceMetric label="Win rate" value={report ? formatConvergencePct(report.total?.winRate) : "N/A"} />
            <ConvergenceMetric label="Expectancy" value={formatConvergenceUsd(report?.total?.expectancyUsd)} />
            <ConvergenceMetric label="Holdout mean" value={formatConvergenceBps(report?.holdout?.meanPnlBps)} />
            <ConvergenceMetric label="Holdout factor" value={report ? Number(report.holdout?.profitFactor || 0).toFixed(2) : "N/A"} />
            <ConvergenceMetric
              label="95% lower mean"
              value={formatConvergenceBps(report?.holdout?.bootstrapLower95MeanBps)}
              hint="Bootstrap lower bound for the holdout mean. Positive is the strongest evidence gate."
            />
          </SimpleGrid>
          {gateEntries.length > 0 && (
            <HStack spacing={2} mt={3} flexWrap="wrap">
              {gateEntries.map(([name, passed]) => (
                <Tooltip key={name} label={name.replace(/([A-Z])/g, " $1").toLowerCase()}>
                  <Badge colorScheme={passed ? "green" : "gray"}>
                    {CONVERGENCE_GATE_LABELS[name] || name}: {passed ? "pass" : "pending"}
                  </Badge>
                </Tooltip>
              ))}
            </HStack>
          )}
        </>
      )}
    </Panel>
  );
}




/* ===========================
   SniperBot component
   =========================== */

const BOT_INSTANCE_IDS = new Set(["primary", "secondary"]);
const SECONDARY_BUY_ENTRY_OFFSET_BPS = 12;

const normalizeBotInstanceId = (value) => {
  const normalized = String(value || "primary").trim().toLowerCase();
  return BOT_INSTANCE_IDS.has(normalized) ? normalized : "primary";
};

const getBotStorageKey = (baseKey, botInstanceId) =>
  botInstanceId === "primary" ? baseKey : `${baseKey}:${botInstanceId}`;

function SniperBotInstance({ selectedTokenAddress, botInstanceId: requestedBotInstanceId = "primary" }) {
  const { account } = useMoralis();
  const botInstanceId = normalizeBotInstanceId(requestedBotInstanceId);
  const isSecondaryBot = botInstanceId === "secondary";
  const instanceDefaultBotCfg = isSecondaryBot ? secondaryBotDefaultCfg : defaultBotCfg;
  const [walletProfile, setWalletProfile] = useState(null);
  const [walletProfileError, setWalletProfileError] = useState("");
  const [walletProfileLoading, setWalletProfileLoading] = useState(false);
  const [secondaryAuthError, setSecondaryAuthError] = useState("");
  const [secondaryAuthLoading, setSecondaryAuthLoading] = useState(false);
  const [secondaryAuthorization, setSecondaryAuthorization] = useState(null);
  const secondarySessionAuthorized = !isSecondaryBot || (
    !!account &&
    secondaryAuthorization?.ownerAddress === String(account).toLowerCase() &&
    secondaryAuthorization.expiresAt > Date.now() &&
    !!secondaryAuthorization.message &&
    !!secondaryAuthorization.signature
  );

  const authorizeSecondaryBot = async () => {
    if (!account || secondaryAuthLoading) return;
    setSecondaryAuthLoading(true);
    setSecondaryAuthError("");
    try {
      const challenge = await Moralis.Cloud.run("getSecondaryBotAuthorizationChallenge");
      if (!challenge?.message || String(challenge.ownerAddress).toLowerCase() !== String(account).toLowerCase()) {
        throw new Error("Connect the primary wallet 0x7081...e6D6 to authorize secondary trades.");
      }
      if (walletProfile?.address &&
          String(challenge.executionWalletAddress).toLowerCase() !== String(walletProfile.address).toLowerCase()) {
        throw new Error("The secondary execution wallet changed. Refresh the bot before authorizing.");
      }
      const signer = Moralis.getInternalWeb3Provider()?.signer;
      if (!signer?.signMessage || !signer?.getAddress) {
        throw new Error("Connect the primary wallet before signing the secondary authorization.");
      }
      const signerAddress = String(await signer.getAddress()).toLowerCase();
      if (signerAddress !== String(account).toLowerCase()) {
        throw new Error("The connected signer does not match the primary wallet.");
      }
      const signature = await signer.signMessage(challenge.message);
      if (ethers.utils.verifyMessage(challenge.message, signature).toLowerCase() !== signerAddress) {
        throw new Error("The wallet signature could not be verified.");
      }
      const expiresAt = Number(challenge.expiresAt);
      if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
        throw new Error("The authorization challenge expired. Try again.");
      }
      setSecondaryAuthorization({
        message: challenge.message,
        signature,
        ownerAddress: signerAddress,
        expiresAt,
      });
    } catch (error) {
      setSecondaryAuthError(error?.message || "Secondary wallet authorization failed.");
    } finally {
      setSecondaryAuthLoading(false);
    }
  };

  useEffect(() => {
    if (!isSecondaryBot || !secondaryAuthorization) return;
    const remaining = secondaryAuthorization.expiresAt - Date.now();
    if (remaining <= 0) {
      setSecondaryAuthorization(null);
      return;
    }
    const timer = setTimeout(() => setSecondaryAuthorization(null), remaining);
    return () => clearTimeout(timer);
  }, [isSecondaryBot, secondaryAuthorization]);

  useEffect(() => {
    let cancelled = false;
    if (!account) {
      setWalletProfile(null);
      setWalletProfileError("");
      setWalletProfileLoading(false);
      return () => { cancelled = true; };
    }
    setWalletProfileLoading(true);
    setWalletProfileError("");
    Moralis.Cloud.run("getTradingWalletProfile", { botInstanceId })
      .then((profile) => {
        if (cancelled) return;
        setWalletProfile(profile || null);
        if (profile?.configured === false) {
          setWalletProfileError(
            botInstanceId === "secondary"
              ? "Secondary backend wallet is not configured. Add MORALIS_PRIVATE_KEY_SECONDARY on Heroku."
              : "Primary backend wallet is not configured."
          );
        }
      })
      .catch((error) => {
        if (cancelled) return;
        setWalletProfile(null);
        setWalletProfileError(error?.message || "Unable to read backend wallet profile.");
      })
      .finally(() => {
        if (!cancelled) setWalletProfileLoading(false);
      });
    return () => { cancelled = true; };
  }, [account, botInstanceId]);

  // Chain and DEX selection state
  const [selectedChain, setSelectedChain] = useState(DEFAULT_CHAIN);
  const [selectedDex, setSelectedDex] = useState(DEFAULT_DEX);
  const [configError, setConfigError] = useState(null);

  // Get current chain config
  const chainConfig = useMemo(() => {
    try {
      return getChainConfig(selectedChain);
    } catch (e) {
      setConfigError(`Invalid chain: ${e.message}`);
      return null;
    }
  }, [selectedChain]);

  // Get available DEXes for selected chain
  const availableDexes = useMemo(() => {
    if (!chainConfig) return [];
    return getAvailableDexes(selectedChain);
  }, [selectedChain, chainConfig]);

  // Validate chain + DEX combination
  useEffect(() => {
    if (!chainConfig) return;
    const validation = validateChainDex(selectedChain, selectedDex);
    if (!validation.valid) {
      setConfigError(validation.error);
      // Auto-select first available DEX if current selection is invalid
      if (availableDexes.length > 0 && !availableDexes.includes(selectedDex)) {
        // CRITICAL FIX: Prefer uniswapV3 for Arbitrum, otherwise use first available
        const preferredDex = selectedChain === "arbitrum" && availableDexes.includes("uniswapV3")
          ? "uniswapV3"
          : availableDexes[0];
        setSelectedDex(preferredDex);
      }
    } else {
      setConfigError(null);
    }
  }, [selectedChain, selectedDex, chainConfig, availableDexes]);

  // CRITICAL FIX: Auto-select uniswapV3 when Arbitrum is selected (if current DEX is invalid)
  useEffect(() => {
    if (selectedChain === "arbitrum" && availableDexes.includes("uniswapV3")) {
      const validation = validateChainDex(selectedChain, selectedDex);
      // Only auto-select V3 if current DEX is invalid for Arbitrum
      // This ensures V3 is default but doesn't override user's manual selection
      if (!validation.valid) {
        setSelectedDex("uniswapV3");
        console.log("🎯 Auto-selected Uniswap V3 for Arbitrum (better liquidity, lower fees)");
      }
    }
  }, [selectedChain, availableDexes, selectedDex]);

  // Dynamic addresses based on chain + DEX
  const currentConfig = useMemo(() => {
    if (!chainConfig || configError) return null;
    try {
      const wrappedNative = getWrappedNativeToken(selectedChain);
      const stablecoin = getStablecoinAddress(selectedChain);
      const router = getRouterAddress(selectedChain, selectedDex);
      const factory = getFactoryAddress(selectedChain, selectedDex);
      const rpcUrl = getRpcUrl(selectedChain);

      return {
        wrappedNativeToken: wrappedNative,
        stablecoinAddress: stablecoin,
        routerAddress: router,
        factoryAddress: factory,
        rpcUrl: rpcUrl,
        chainId: chainConfig.id,
        nativeSymbol: chainConfig.nativeCurrency.symbol,
        stablecoinSymbol: chainConfig.defaultStablecoin,
      };
    } catch (e) {
      setConfigError(`Configuration error: ${e.message}`);
      return null;
    }
  }, [selectedChain, selectedDex, chainConfig, configError]);

  // provider (dynamic based on chain)
  const providerRef = useRef(null);
  useEffect(() => {
    if (!currentConfig) return;
    try {
      providerRef.current = new ethers.providers.JsonRpcProvider(currentConfig.rpcUrl);
      console.log(`✅ Provider initialized for ${chainConfig.name} (${currentConfig.rpcUrl})`);
    } catch (e) {
      console.error("❌ Provider initialization failed:", e);
      setConfigError(`RPC connection failed: ${e.message}`);
    }
  }, [currentConfig, chainConfig]);

  const provider = providerRef.current;

  // live contract refs to detach listeners
  const liveContractsRef = useRef([]);
  const factoryContractRef = useRef(null);

  // pricing refs
  const lastPriceRef = useRef({}); // marketKey -> price
  const priceHistoryRef = useRef({}); // marketKey -> [{ t, p }] used by adaptive + regime (25h)
  const marketRegimeRef = useRef({}); // marketKey -> latest regime snapshot
  const recoveryGridHistoryRef = useRef({}); // marketKey -> [timestamps] for CAPITAL_PROTECTION rule
  const lastRegimePersistRef = useRef({}); // marketKey -> timestamp
  const lastRegimeLogRef = useRef({}); // marketKey -> regime (console base vs effective)
  const wcroPerWbtcRef = useRef(0);
  const usdPerWcroRef = useRef(0);
  const lastPoolWethPriceRef = useRef({ price: 0, blockNumber: null, poolAddress: "" });
  const externalSignalServiceRef = useRef(null);
  if (!externalSignalServiceRef.current) {
    externalSignalServiceRef.current = createExternalMarketSignalService();
  }
  const externalThresholdStateRef = useRef({});
  const externalDecisionLogGateRef = useRef({});
  const externalOutcomeTimersRef = useRef(new Set());

  // trade control refs
  const tradeLockRef = useRef({});
  const prevPriceRef = useRef({});
  const lastBuyAtRef = useRef({});
    const lastSellAtRef = useRef({});
  const lastDeRiskAtRef = useRef({});
  const lastStuckStopAtRef = useRef({});
  const stuckStopEvaluationsRef = useRef({});
  const recentGasCostUsdRef = useRef({});
  const lastStopLossAtRef = useRef({});
  const lastDiscordResetNotifyRef = useRef({});
  const lastRecoveryDecisionLogRef = useRef({});
  const lastGridResetGateRef = useRef((() => {
    try {
      if (typeof localStorage === "undefined") return {};
      const key = getBotStorageKey("botGridResetGateState", botInstanceId);
      return JSON.parse(localStorage.getItem(key) || "{}") || {};
    } catch {
      return {};
    }
  })());
  const processedTradeTxHashesRef = useRef({});
  const lastTradeAtRef = useRef({}); // Track last trade (buy or sell) timestamp per market
  const lastTradeReceiptAtRef = useRef({}); // Track last trade receipt confirmation timestamp per market
  const lastTradeFailureAtRef = useRef({}); // Short deterministic-failure cooldown per market/side
  const lastReanchorAtRef = useRef({}); // Track last re-anchor timestamp per market
  const lastRealizedProfitPctRef = useRef({}); // Track last realized profit % per market (for profit-based re-anchoring)
  const pendingTradeSubmissionsRef = useRef({}); // Track pending trade submissions: { marketKey: { side, submittedAt, amount } }
  const tradeNoticeTimerRef = useRef(null);

  // pending optimistic updates
  const pendingTradesRef = useRef({});

  // caches
  const decimalsCacheRef = useRef({}); // tokenAddr -> decimals
  const exactTokenBalanceRef = useRef({}); // marketKey -> { raw base-unit string, decimals }
  
  // debounce timer for position saves
  const savePositionsTimeoutRef = useRef(null);

  // app state (defaults; we'll hydrate on account)
  const [address, setAddress] = useState("");
  const [hydratedPlayer, setHydratedPlayer] = useState("");
  const [killSwitch, setKillSwitch] = useState(true);
  const [showDebugUI, setShowDebugUI] = useState(true);

  const [isListening, setIsListening] = useState(false);
  const [useBackendWallet, setUseBackendWallet] = useState(() => isSecondaryBot);
  const effectiveUseBackendWallet = isSecondaryBot || useBackendWallet;
  const backendWalletAddress = walletProfile?.configured && walletProfile?.address
    ? String(walletProfile.address)
    : "";
  const executionAddress = isSecondaryBot
    ? backendWalletAddress
    : effectiveUseBackendWallet
      ? (backendWalletAddress || String(account || ""))
      : String(account || "");
  // Preserve all existing primary MongoDB records under the logged-in address.
  // Secondary records use its execution address and therefore remain fully isolated.
  const persistencePlayer = isSecondaryBot ? backendWalletAddress : String(account || "");
  const [sniperStatus, setSniperStatus] = useState("");
  const [tradeNotice, setTradeNotice] = useState(null);

  const showTradeNotice = (message, details = {}) => {
    const text = String(message || "Trade failed");
    setTradeNotice({
      status: details.status || "error",
      title: details.title || "Trade not executed",
      message: text.length > 700 ? `${text.slice(0, 700)}...` : text,
      marketKey: details.marketKey || "",
      side: details.side || "",
      createdAt: Date.now(),
    });
    if (tradeNoticeTimerRef.current) clearTimeout(tradeNoticeTimerRef.current);
    tradeNoticeTimerRef.current = setTimeout(() => {
      setTradeNotice(null);
      tradeNoticeTimerRef.current = null;
    }, 12000);
  };

  const dismissTradeNotice = () => {
    if (tradeNoticeTimerRef.current) clearTimeout(tradeNoticeTimerRef.current);
    tradeNoticeTimerRef.current = null;
    setTradeNotice(null);
  };

  useEffect(() => () => {
    if (tradeNoticeTimerRef.current) clearTimeout(tradeNoticeTimerRef.current);
  }, []);

  const [TOKEN_ADDRESS, setTOKEN_ADDRESS] = useState("0x062E66477Faf219F25D27dCED647BF57C3107d52"); // Default WBTC on Cronos
  const [tokenPairAddress, setTokenPairAddress] = useState("");
  const [tokenDecimals, setTokenDecimals] = useState(8);

  const [tokenSymbol, setTokenSymbol] = useState("");
  const [tokenName, setTokenName] = useState("");

  const [slippagePercent, setSlippagePercent] = useState(0.005); // 0.5% slippage (reduced from 0.6% for tighter grids and high liquidity pool)
  // Dynamic gas settings based on chain (lower for most chains, higher for Cronos)
  const [maxFeePerGasAmount, setMaxFeePerGasAmount] = useState(() => {
    // Default gas prices vary by chain
    const chainDefaults = {
      cronos: "8000",
      ethereum: "50",
      bsc: "5",
      polygon: "30",
      arbitrum: "0.1",
      optimism: "0.1",
      avalanche: "30",
    };
    return chainDefaults[selectedChain] || "50";
  });
  const [maxPriorityFeePerGasAmount, setMaxPriorityFeePerGasAmount] = useState(() => {
    const chainDefaults = {
      cronos: "100",
      ethereum: "2",
      bsc: "1",
      polygon: "1",
      arbitrum: "0.01",
      optimism: "0.01",
      avalanche: "1",
    };
    return chainDefaults[selectedChain] || "2";
  });

  // Update gas defaults when chain changes
  useEffect(() => {
    const chainDefaults = {
      cronos: { maxFee: "8000", priority: "100" },
      ethereum: { maxFee: "50", priority: "2" },
      bsc: { maxFee: "5", priority: "1" },
      polygon: { maxFee: "30", priority: "1" },
      arbitrum: { maxFee: "0.1", priority: "0.01" },
      optimism: { maxFee: "0.1", priority: "0.01" },
      avalanche: { maxFee: "30", priority: "1" },
    };
    const defaults = chainDefaults[selectedChain] || { maxFee: "50", priority: "2" };
    setMaxFeePerGasAmount(defaults.maxFee);
    setMaxPriorityFeePerGasAmount(defaults.priority);
  }, [selectedChain]);

  // custom bots
  const [customBots, setCustomBots] = useState([]);

  // positions (will be initialized based on current chain config)
  // Initialize with safe defaults to prevent undefined errors
  const [positions, setPositions] = useState({
    "CRO": { holdings: 0, price: 0 },
    "WCRO-USDC": { holdings: 0, avgEntry: 0, priceTargetBuy: 0, priceTargetSell: 0, buyCounter: 1, sellCounter: 1, enabled: true },
    "WBTC-WCRO": { holdings: 0, avgEntry: 0, priceTargetBuy: 0, priceTargetSell: 0, buyCounter: 1, sellCounter: 1, enabled: true },
    "WBTC-USDC": { holdings: 0, avgEntry: 0, priceTargetBuy: 0, priceTargetSell: 0, buyCounter: 1, sellCounter: 1, enabled: true },
  });

  // Initialize positions when chain changes
  useEffect(() => {
    if (!currentConfig) return;
    const wrappedSymbol = chainConfig.nativeCurrency.symbol;
    const stableSymbol = chainConfig.defaultStablecoin;
    const nativeSymbol = chainConfig.nativeCurrency.symbol;
    
    setPositions(prev => ({
      ...prev, // Preserve existing positions
      [`${wrappedSymbol}-${stableSymbol}`]: prev[`${wrappedSymbol}-${stableSymbol}`] || { 
        holdings: 0, avgEntry: 0, priceTargetBuy: 0, priceTargetSell: 0, 
        buyCounter: 1, sellCounter: 1, enabled: true 
      },
      [`WBTC-${wrappedSymbol}`]: prev[`WBTC-${wrappedSymbol}`] || { 
        holdings: 0, avgEntry: 0, priceTargetBuy: 0, priceTargetSell: 0, 
        buyCounter: 1, sellCounter: 1, enabled: true 
      },
      [`WBTC-${stableSymbol}`]: prev[`WBTC-${stableSymbol}`] || { 
        holdings: 0, avgEntry: 0, priceTargetBuy: 0, priceTargetSell: 0, 
        buyCounter: 1, sellCounter: 1, enabled: true 
      },
      [nativeSymbol]: prev[nativeSymbol] || { holdings: 0, price: 0 },
    }));
  }, [currentConfig, chainConfig]);

  // markets (will be initialized based on current chain config)
  const [markets, setMarkets] = useState([]);

  // Initialize markets when chain/DEX changes
  useEffect(() => {
    if (!currentConfig) return;
    const wrappedSymbol = chainConfig.nativeCurrency.symbol;
    const stableSymbol = chainConfig.defaultStablecoin;
    
    setMarkets([
      { 
        key: `${wrappedSymbol}-${stableSymbol}`, 
        price: 0, 
        // Must be wrapped native (WETH/WCRO): holdings & sells are in this token, not USDC
        token: currentConfig.wrappedNativeToken, 
        initiator: wrappedSymbol 
      },
      { 
        key: `WBTC-${wrappedSymbol}`, 
        price: 0, 
        token: TOKEN_ADDRESS, 
        initiator: chainConfig.nativeCurrency.symbol 
      },
      { 
        key: `WBTC-${stableSymbol}`, 
        price: 0, 
        token: TOKEN_ADDRESS, 
        initiator: stableSymbol 
      },
    ]);
  }, [currentConfig, chainConfig, TOKEN_ADDRESS]);

  // per-bot configs (persisted)
  const [botConfigs, setBotConfigs] = useState({});

  // Read-only convergence research monitor. These public Cloud calls cannot submit trades.
  const [convergenceStatusPayload, setConvergenceStatusPayload] = useState(null);
  const [convergenceReportPayload, setConvergenceReportPayload] = useState(null);
  const [convergenceMonitorLoading, setConvergenceMonitorLoading] = useState(true);
  const [convergenceMonitorErrors, setConvergenceMonitorErrors] = useState({ status: "", report: "" });
  const [convergenceReportHorizonMs, setConvergenceReportHorizonMs] = useState(5000);
  const convergenceMonitorMountedRef = useRef(false);
  const convergenceMonitorRequestRef = useRef({ status: 0, report: 0, refresh: 0 });

  useEffect(() => {
    convergenceMonitorMountedRef.current = true;
    return () => {
      convergenceMonitorMountedRef.current = false;
    };
  }, []);

  const loadConvergenceStatus = useCallback(async () => {
    const requestId = ++convergenceMonitorRequestRef.current.status;
    try {
      const payload = await Moralis.Cloud.run("getConvergenceShadowStatus");
      if (!convergenceMonitorMountedRef.current || requestId !== convergenceMonitorRequestRef.current.status) return;
      setConvergenceStatusPayload(payload || null);
      setConvergenceMonitorErrors((prev) => ({ ...prev, status: "" }));
    } catch (error) {
      if (!convergenceMonitorMountedRef.current || requestId !== convergenceMonitorRequestRef.current.status) return;
      setConvergenceMonitorErrors((prev) => ({
        ...prev,
        status: `Status unavailable: ${error?.message || String(error)}`,
      }));
    }
  }, []);

  const loadConvergenceReport = useCallback(async () => {
    const requestId = ++convergenceMonitorRequestRef.current.report;
    try {
      const payload = await Moralis.Cloud.run("getConvergenceShadowReport", {
        horizonMs: convergenceReportHorizonMs,
      });
      if (!convergenceMonitorMountedRef.current || requestId !== convergenceMonitorRequestRef.current.report) return;
      setConvergenceReportPayload(payload || null);
      setConvergenceMonitorErrors((prev) => ({ ...prev, report: "" }));
    } catch (error) {
      if (!convergenceMonitorMountedRef.current || requestId !== convergenceMonitorRequestRef.current.report) return;
      setConvergenceMonitorErrors((prev) => ({
        ...prev,
        report: `Report unavailable: ${error?.message || String(error)}`,
      }));
    }
  }, [convergenceReportHorizonMs]);

  const refreshConvergenceMonitor = useCallback(async () => {
    const refreshId = ++convergenceMonitorRequestRef.current.refresh;
    if (convergenceMonitorMountedRef.current) setConvergenceMonitorLoading(true);
    await Promise.allSettled([loadConvergenceStatus(), loadConvergenceReport()]);
    if (
      convergenceMonitorMountedRef.current &&
      refreshId === convergenceMonitorRequestRef.current.refresh
    ) {
      setConvergenceMonitorLoading(false);
    }
  }, [loadConvergenceReport, loadConvergenceStatus]);

  useEffect(() => {
    if (isSecondaryBot) {
      setConvergenceMonitorLoading(false);
      return undefined;
    }
    refreshConvergenceMonitor();
    const canPoll = () => typeof document === "undefined" || !document.hidden;
    const statusTimer = setInterval(() => {
      if (canPoll()) loadConvergenceStatus();
    }, 15000);
    const reportTimer = setInterval(() => {
      if (canPoll()) loadConvergenceReport();
    }, 120000);
    return () => {
      clearInterval(statusTimer);
      clearInterval(reportTimer);
    };
  }, [isSecondaryBot, loadConvergenceReport, loadConvergenceStatus, refreshConvergenceMonitor]);

  useEffect(() => {
    const service = externalSignalServiceRef.current;
    const primaryKey = chainConfig
      ? `${chainConfig.nativeCurrency.symbol}-${chainConfig.defaultStablecoin}`
      : "ETH-USDC";
    const cfg = { ...defaultBotCfg, ...(botConfigs[primaryKey] || {}) };
    if (isListening && selectedChain === "arbitrum" && cfg.externalSignalEnabled !== false) {
      service.start();
    } else {
      service.stop();
    }
    return () => service.stop();
  }, [isListening, selectedChain, chainConfig, botConfigs]);

  useEffect(() => () => {
    externalOutcomeTimersRef.current.forEach((timer) => clearTimeout(timer));
    externalOutcomeTimersRef.current.clear();
  }, []);

  // sniper settings
  const [sniperMode, setSniperMode] = useState("ambush");
  const [ambushToken, setAmbushToken] = useState("");
  const [ambushTickerFilter, setAmbushTickerFilter] = useState("");
  const [ambushMinWcro, setAmbushMinWcro] = useState(100);
  const [ambushAutoBuyWcro, setAmbushAutoBuyWcro] = useState(5);
  const [ambushCooldownMs, setAmbushCooldownMs] = useState(5000);

  const [recklessTickerFilter, setRecklessTickerFilter] = useState("");
  const [recklessMinWcro, setRecklessMinWcro] = useState(200);
  const [recklessAutoBuyWcro, setRecklessAutoBuyWcro] = useState(4);
  const [recklessCooldownMs, setRecklessCooldownMs] = useState(5000);

  const ambushActiveRef = useRef(false);
  const recklessActiveRef = useRef(false);
  const watchedPairsRef = useRef({});

  // manual trade panel
  const [manualTokenAddr, setManualTokenAddr] = useState("");
  const [manualInitiator, setManualInitiator] = useState("CRO");
  const [manualTradeAmount, setManualTradeAmount] = useState(0);
  const [manualTradeSide, setManualTradeSide] = useState("BUY");

  const [sellTarget, setSellTarget] = useState(defaultBotCfg.sellTarget);
  const [buyTarget,  setBuyTarget ] = useState(defaultBotCfg.buyTarget);
  const [strategyMode, setStrategyMode] = useState(defaultBotCfg.strategyMode);
  const [adaptiveBaseMovePct, setAdaptiveBaseMovePct] = useState(defaultBotCfg.adaptiveBaseMovePct);
  const [adaptiveSellExtraPct, setAdaptiveSellExtraPct] = useState(defaultBotCfg.adaptiveSellExtraPct);
  const [adaptiveVolMultiplier, setAdaptiveVolMultiplier] = useState(defaultBotCfg.adaptiveVolMultiplier);
  const [stuckStopEnabled, setStuckStopEnabled] = useState(defaultBotCfg.stuckStopEnabled);
  const [maxConsecutiveBuys, setMaxConsecutiveBuys] = useState(defaultBotCfg.maxConsecutiveBuys);
  const [softStuckHours, setSoftStuckHours] = useState(defaultBotCfg.softStuckHours);
  const [softStuckLossPct, setSoftStuckLossPct] = useState(defaultBotCfg.softStuckLossPct);
  const [softStuckLossUsd, setSoftStuckLossUsd] = useState(defaultBotCfg.softStuckLossUsd);
  const [softStuckSellPct, setSoftStuckSellPct] = useState(defaultBotCfg.softStuckSellPct);
  const [hardStuckHours, setHardStuckHours] = useState(defaultBotCfg.hardStuckHours);
  const [hardStuckLossPct, setHardStuckLossPct] = useState(defaultBotCfg.hardStuckLossPct);
  const [hardStuckLossUsd, setHardStuckLossUsd] = useState(defaultBotCfg.hardStuckLossUsd);
  const [hardStuckSellPct, setHardStuckSellPct] = useState(defaultBotCfg.hardStuckSellPct);
  const [emergencyStuckHours, setEmergencyStuckHours] = useState(defaultBotCfg.emergencyStuckHours);
  const [emergencyStuckLossPct, setEmergencyStuckLossPct] = useState(defaultBotCfg.emergencyStuckLossPct);
  const [emergencyStuckLossUsd, setEmergencyStuckLossUsd] = useState(defaultBotCfg.emergencyStuckLossUsd);
  const [emergencyStuckSellPct, setEmergencyStuckSellPct] = useState(defaultBotCfg.emergencyStuckSellPct);
  const [hybridGridEnabled, setHybridGridEnabled] = useState(defaultBotCfg.hybridGridEnabled);
  const [minUsdcReservePct, setMinUsdcReservePct] = useState(defaultBotCfg.minUsdcReservePct);
  const [underwaterSpotPct, setUnderwaterSpotPct] = useState(defaultBotCfg.underwaterSpotPct);
  const [stopLossEnabled, setStopLossEnabled] = useState(defaultBotCfg.stopLossEnabled);
  const [stopLossDrawdownPct, setStopLossDrawdownPct] = useState(defaultBotCfg.stopLossDrawdownPct);
  const [stopLossTargetWethPct, setStopLossTargetWethPct] = useState(defaultBotCfg.stopLossTargetWethPct);
  const [stopLossCooldownHours, setStopLossCooldownHours] = useState(defaultBotCfg.stopLossCooldownHours);
  const [buyProtectionEnabled, setBuyProtectionEnabled] = useState(defaultBotCfg.buyProtectionEnabled);
  const [recoveryBuyCooldownMinutes, setRecoveryBuyCooldownMinutes] = useState(
    Math.round((defaultBotCfg.recoveryBuyCooldownMs || (30 * 60 * 1000)) / (60 * 1000))
  );
  const [buyProtectionMaxConsecutiveBuys, setBuyProtectionMaxConsecutiveBuys] = useState(defaultBotCfg.buyProtectionMaxConsecutiveBuys);
  const [buyProtectionMajorDipFourthBuyPct, setBuyProtectionMajorDipFourthBuyPct] = useState(defaultBotCfg.buyProtectionMajorDipFourthBuyPct);
  const [buyProtectionMajorDipHighVolPct, setBuyProtectionMajorDipHighVolPct] = useState(defaultBotCfg.buyProtectionMajorDipHighVolPct);
  const [regimeEngineEnabled, setRegimeEngineEnabled] = useState(defaultBotCfg.regimeEngineEnabled);
  const [expertMode, setExpertMode] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("aggressive");
  const [autoApplyRecommendedTemplate, setAutoApplyRecommendedTemplate] = useState(true);
  const [priceHistoryTick, setPriceHistoryTick] = useState(0);

  // history
  const [tradeHistory, setTradeHistory] = useState([]);
  
  // Manual PnL input for sanity checking (compare with Arbiscan)
  const [manualPnL, setManualPnL] = useState("");
  const [manualPnLEnabled, setManualPnLEnabled] = useState(false);
  
  // PnL tracking: per-market cost basis and realized PnL
  const [pnlData, setPnlData] = useState(() => {
    // Load from localStorage if available
    try {
      const saved = localStorage.getItem(getBotStorageKey("botPnlData", botInstanceId));
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });
  const pnlDataRef = useRef(pnlData);
  const pnlLedgerRepairRef = useRef({ fingerprint: "", status: "idle" });
  const accountingHydratedRef = useRef(false);
  const pnlPersistenceTimeoutRef = useRef(null);

  useEffect(() => {
    pnlDataRef.current = pnlData;
  }, [pnlData]);

  const [benchmarkData, setBenchmarkData] = useState(() => {
    try {
      const saved = localStorage.getItem(getBotStorageKey("botWethBenchmarkData", botInstanceId));
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const saveBenchmarkData = (updater) => {
    setBenchmarkData((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      try {
        localStorage.setItem(
          getBotStorageKey("botWethBenchmarkData", botInstanceId),
          JSON.stringify(next)
        );
      } catch (e) {
        console.warn("Failed to save WETH benchmark data:", e);
      }
      return next;
    });
  };

  // deriveds
// Removed targetNetPct - redundant with sellTarget, was blocking trades unnecessarily
// const targetNetPct = 0; // Profit gate removed - sellTarget already ensures profit

  const TIMEFRAME_WINDOWS = [
    { key: "15m", ms: 15 * 60 * 1000 },
    { key: "1h", ms: 60 * 60 * 1000 },
    { key: "4h", ms: 4 * 60 * 60 * 1000 },
    { key: "24h", ms: 24 * 60 * 60 * 1000 },
  ];

  const getMarketTimeframeMetrics = (key) => {
    const samples = priceHistoryRef.current[key] || [];
    const now = Date.now();
    const lastPrice = samples.length ? Number(samples[samples.length - 1].p) : 0;
    const metrics = { lastPrice };

    TIMEFRAME_WINDOWS.forEach(({ key: tfKey, ms }) => {
      const window = samples.filter((sample) => sample.t >= now - ms && sample.p > 0);
      const prices = window.map((sample) => Number(sample.p));
      const open = prices.length ? prices[0] : lastPrice;
      const high = prices.length ? Math.max(...prices) : lastPrice;
      const low = prices.length ? Math.min(...prices) : lastPrice;

      metrics[`open${tfKey}`] = open;
      metrics[`high${tfKey}`] = high;
      metrics[`low${tfKey}`] = low;
      metrics[`range${tfKey}`] = lastPrice > 0 ? (high - low) / lastPrice : 0;
      metrics[`trend${tfKey}`] = open > 0 ? (lastPrice - open) / open : 0;
      metrics[`pctFrom${tfKey}High`] = high > 0 ? (high - lastPrice) / high : 0;
      metrics[`pctFrom${tfKey}Low`] = low > 0 ? (lastPrice - low) / low : 0;
    });

    return metrics;
  };

  const isWrappedStableMarket = (key) => {
    if (!chainConfig) return false;
    const wrappedSymbol = chainConfig.nativeCurrency.symbol;
    const stableSymbol = chainConfig.defaultStablecoin;
    return key === `${wrappedSymbol}-${stableSymbol}`;
  };

  const getWethBenchmarkSnapshot = (key, priceOverride) => {
    const market = markets.find((m) => m.key === key);
    const currentWethPrice = Number(priceOverride ?? market?.price ?? lastPriceRef.current[key] ?? 0);
    const pos = positions[key] || {};
    const book = getMarketBook(key, pos, currentWethPrice, positions, chainConfig);
    return {
      currentTotalUsd: Number(book.totalBookUsd) || 0,
      currentWethPrice,
      baseBalance: Number(book.baseBalance) || 0,
      holdingsUsd: Number(book.holdingsUsd) || 0,
    };
  };

  const startWethBenchmark = (key, manualLabel = "") => {
    const snap = getWethBenchmarkSnapshot(key);
    if (!isWrappedStableMarket(key) || !(snap.currentTotalUsd > 0) || !(snap.currentWethPrice > 0)) {
      console.warn("[WETH Benchmark] Cannot start benchmark", { key, ...snap });
      alert("Benchmark unavailable: missing WETH price or book value.");
      return;
    }
    const nextBenchmark = {
      startedAt: new Date().toISOString(),
      initialTotalUsd: snap.currentTotalUsd,
      initialWethPrice: snap.currentWethPrice,
      initialTotalValueInWeth: snap.currentTotalUsd / snap.currentWethPrice,
      manualLabel,
    };
    saveBenchmarkData((prev) => ({ ...prev, [key]: nextBenchmark }));
  };

  const resetWethBenchmark = (key) => {
    startWethBenchmark(key, benchmarkData[key]?.manualLabel || "");
  };

  const getWethBenchmarkResult = (key, priceOverride) => {
    const benchmark = benchmarkData[key];
    const snap = getWethBenchmarkSnapshot(key, priceOverride);
    const result = computeWethBenchmark({
      benchmark,
      currentTotalUsd: snap.currentTotalUsd,
      currentWethPrice: snap.currentWethPrice,
    });
    return { benchmark, snapshot: snap, result };
  };

  const recommendTemplateForMarket = (key) => {
    const metrics = getMarketTimeframeMetrics(key);
    if (!metrics.lastPrice || !metrics.high24h || !metrics.low24h) return "balanced";

    const closeTo24hHigh = metrics.pctFrom24hHigh <= 0.03;
    const closeTo24hLow = metrics.pctFrom24hLow <= 0.03;
    const highVol = metrics.range1h >= 0.04 || metrics.range4h >= 0.06;
    const strongUptrend = metrics.trend4h >= 0.03;
    const moderateDowntrend = metrics.trend4h < -0.015;

    if (closeTo24hHigh || highVol || moderateDowntrend) return "conservative";
    if (closeTo24hLow && metrics.range4h <= 0.03 && !highVol) return "aggressive";
    if (strongUptrend && metrics.range1h <= 0.03) return "aggressive";
    return "balanced";
  };

  const primaryMarketKey = useMemo(() => {
    const wrappedSymbol = chainConfig?.nativeCurrency?.symbol || "";
    const stableSymbol = chainConfig?.defaultStablecoin || "USDC";
    return `${wrappedSymbol}-${stableSymbol}`;
  }, [chainConfig]);

  const recommendedTemplate = useMemo(() => {
    const primary = markets.find((m) => m.key === primaryMarketKey)?.key || markets[0]?.key;
    if (!primary) return "balanced";
    return recommendTemplateForMarket(primary);
  }, [markets, primaryMarketKey, priceHistoryTick]);

  const applyTemplateToLocalSettings = (templateName) => {
    const template = BOT_TEMPLATES[templateName];
    if (!template) return;
    setSelectedTemplate(templateName);
    setStrategyMode(template.strategyMode);
    setAdaptiveBaseMovePct(template.adaptiveBaseMovePct);
    setAdaptiveSellExtraPct(template.adaptiveSellExtraPct);
    setAdaptiveVolMultiplier(template.adaptiveVolMultiplier);
    setMinUsdcReservePct(template.minUsdcReservePct);
    setMaxConsecutiveBuys(template.maxConsecutiveBuys);
    setStopLossEnabled(template.stopLossEnabled);
    setStopLossDrawdownPct(template.stopLossDrawdownPct);
    setBuyProtectionEnabled(template.buyProtectionEnabled);
    setRecoveryBuyCooldownMinutes(Math.round(template.recoveryBuyCooldownMs / (60 * 1000)));
    setBuyProtectionMaxConsecutiveBuys(template.buyProtectionMaxConsecutiveBuys);
    setBuyProtectionMajorDipFourthBuyPct(template.buyProtectionMajorDipFourthBuyPct);
    setBuyProtectionMajorDipHighVolPct(template.buyProtectionMajorDipHighVolPct);
    setHybridGridEnabled(template.hybridGridEnabled);
    setRegimeEngineEnabled(template.regimeEngineEnabled);
  };

  useEffect(() => {
    if (!autoApplyRecommendedTemplate || expertMode) return;
    if (recommendedTemplate && recommendedTemplate !== selectedTemplate) {
      applyTemplateToLocalSettings(recommendedTemplate);
    }
  }, [autoApplyRecommendedTemplate, expertMode, recommendedTemplate]);

  const clampNumber = (value, min, max) => Math.min(max, Math.max(min, Number(value) || 0));

  const recordPriceSample = (key, price) => {
    if (!key || !(price > 0)) return;
    const now = Date.now();
    const arr = priceHistoryRef.current[key] || [];
    arr.push({ t: now, p: Number(price) });
    const cutoff = now - PRICE_HISTORY_RETENTION_MS;
    priceHistoryRef.current[key] = arr.filter((x) => x.t >= cutoff && x.p > 0).slice(-3000);
    if (priceHistoryRef.current[key].length % 5 === 0) {
      setPriceHistoryTick((tick) => tick + 1);
    }
  };

  const getRecentRangePct = (key) => {
    const arr = priceHistoryRef.current[key] || [];
    const now = Date.now();
    const oneHourAgo = now - 60 * 60 * 1000;
    const inHour = arr.filter((x) => x.t >= oneHourAgo && x.p > 0);
    if (inHour.length < 4) return 0;
    const prices = inHour.map((x) => Number(x.p));
    const last = prices[prices.length - 1];
    const hi = Math.max(...prices);
    const lo = Math.min(...prices);
    return last > 0 ? (hi - lo) / last : 0;
  };

  const getMarketPnLPercent = (key) => {
    const marketPnl = normalizePnlRecord(pnlDataRef.current?.[key] || pnlData[key]);
    if (!marketPnl || !(marketPnl.costBasis > 0)) return 0;
    const realized =
      marketPnl.realizedPnL -
      marketPnl.reportingRealizedPnlOffset -
      Math.max(0, marketPnl.totalGasUsd - marketPnl.reportingGasUsdOffset);
    return realized / marketPnl.costBasis;
  };

  const positionsRef = useRef(positions);
  useEffect(() => {
    positionsRef.current = positions;
  }, [positions]);

  const getPositionForDecisions = (key) => {
    const position = positionsRef.current[key] || {};
    const ledger = normalizePnlRecord(pnlDataRef.current?.[key]);
    const accounting = getAuthoritativeAverageEntry({
      costBasis: ledger.costBasis,
      ledgerHoldings: ledger.holdings,
      positionAvgEntry: position.avgEntry,
    });
    return accounting.source === "confirmed-fill-ledger"
      ? { ...position, avgEntry: accounting.avgEntry, accountingSource: accounting.source }
      : position;
  };

  const getCompoundSizingPlan = ({ key, book, cfg, availableUsd, multiplier = 1 }) => {
    const currentEquityUsd = Math.max(0, Number(book?.totalBookUsd) || 0);
    const ledger = normalizePnlRecord(pnlDataRef.current?.[key]);
    if (cfg.compoundSizingEnabled === false || !isWrappedStableMarket(key)) {
      const legacyMin = Math.max(0, Number(cfg.minTradeUsd ?? defaultBotCfg.minTradeUsd));
      const legacyMax = Math.max(legacyMin, Number(cfg.maxTradeUsd ?? defaultBotCfg.maxTradeUsd));
      return {
        allowed: true,
        reason: "legacy fixed-dollar sizing",
        sizingEquityUsd: currentEquityUsd,
        baseTradeUsd: Math.min(legacyMax, Math.max(legacyMin, currentEquityUsd * Number(cfg.buyMultiplier || 0))),
        finalTradeUsd: 0,
        economicMinTradeUsd: legacyMin,
        hardMaxUsd: Math.min(Math.max(0, Number(availableUsd) || 0), legacyMax),
        appliedMultiplier: 1,
      };
    }

    const baselineEquityUsd = ledger.accountingCheckpointAt > 0
      ? ledger.accountingBaselineEquityUsd
      : currentEquityUsd;
    const compoundEquity = calculateRealizedCompoundingEquity({
      currentEquityUsd,
      baselineEquityUsd,
      netDepositsUsd: ledger.netExternalDepositsUsd,
      netWithdrawalsUsd: ledger.netExternalWithdrawalsUsd,
      realizedPnlUsd: ledger.realizedPnL - ledger.totalGasUsd,
      realizedPnlOffsetUsd: ledger.accountingCheckpointAt > 0
        ? ledger.reportingRealizedPnlOffset - ledger.reportingGasUsdOffset
        : ledger.realizedPnL - ledger.totalGasUsd,
    });
    const sizingEquityUsd = cfg.compoundRealizedOnly === false
      ? currentEquityUsd
      : compoundEquity.sizingEquityUsd;
    return {
      ...calculateCompoundedTradeSize({
        sizingEquityUsd,
        availableUsd,
        baseTradePct: cfg.compoundBaseTradePct,
        maxTradePct: cfg.compoundMaxTradePct,
        economicMinTradeUsd: cfg.compoundEconomicMinTradeUsd,
        absoluteMaxTradeUsd: cfg.compoundAbsoluteMaxTradeUsd,
        multiplier,
      }),
      flowAdjustedBaselineUsd: compoundEquity.flowAdjustedBaselineUsd,
      realizedSinceCheckpointUsd: compoundEquity.realizedSinceCheckpointUsd,
    };
  };

  const isMarketTradeInFlight = (key) =>
    !!pendingTradeSubmissionsRef.current[key] ||
    (pendingTradesRef.current[key] || []).length > 0;

  const isRegimeEngineEnabled = (key) => {
    const cfg = botConfigs[key] || {};
    if (cfg.regimeEngineEnabled === false) return false;
    if (cfg.regimeEngineEnabled === true) return true;
    return regimeEngineEnabled !== false;
  };

  const syncMarketRegime = (key, price) => {
    if (!isRegimeEngineEnabled(key)) return null;

    const pos = getPositionForDecisions(key);
    const samples = priceHistoryRef.current[key] || [];
    const metrics = computeMarketMetrics(samples);
    const gridHist = recoveryGridHistoryRef.current[key] || [];
    const sixHAgo = Date.now() - 6 * 60 * 60 * 1000;
    const recentRecoveryGridCount = gridHist.filter((t) => t >= sixHAgo).length;

    const prev = regimePrevFromPosition(pos, marketRegimeRef.current[key] || {});

    const snap = resolveMarketRegime({
      metrics,
      currentPrice: price,
      pos,
      recentRecoveryGridCount,
      totalPnLPercent: getMarketPnLPercent(key),
      prev,
    });

    marketRegimeRef.current[key] = snap;

    const regimeChanged = prev.regime !== snap.regime;
    const lastPersist = lastRegimePersistRef.current[key] || 0;
    if (regimeChanged || Date.now() - lastPersist > 60 * 1000) {
      lastRegimePersistRef.current[key] = Date.now();
      updatePosition(key, regimeFieldsFromSnap(snap));
    }

    return snap;
  };

  /**
   * Returns the actual trading parameters used by the bot.
   * - static: current fixed buyTarget/sellTarget behavior.
   * - adaptive: widens/narrows thresholds from recent 1h intraday range + optional regime.
   */
  const getEffectiveBotCfg = (key) => {
    const cfg = { ...instanceDefaultBotCfg, ...(botConfigs[key] || {}) };
    const instanceBuyOffsetPct = isSecondaryBot
      ? SECONDARY_BUY_ENTRY_OFFSET_BPS / 10000
      : 0;
    const regimeOn = isRegimeEngineEnabled(key);
    const regimeSnap = marketRegimeRef.current[key];

    const userMinReserve = cfg.minUsdcReservePct ?? defaultBotCfg.minUsdcReservePct;
    const userBaseMove = cfg.adaptiveBaseMovePct;
    const userSellExtra = cfg.adaptiveSellExtraPct;

    if (regimeOn && regimeSnap?.limits) {
      cfg.minUsdcReservePct = regimeSnap.limits.minUsdcReservePct;
      cfg.maxWethExposurePct = regimeSnap.limits.maxWethExposurePct;
      cfg.marketRegime = regimeSnap.regime;
      cfg.regimeAdaptiveBase = regimeSnap.adaptive?.baseMove;
      cfg.regimeAdaptiveSellExtra = regimeSnap.adaptive?.sellExtra;
    }

    if (cfg.strategyMode !== "adaptive") {
      cfg.regimeEngineEnabled = regimeOn;
      cfg.buyTarget = Math.max(0.5, Number(cfg.buyTarget) - instanceBuyOffsetPct);
      cfg.instanceBuyOffsetBps = isSecondaryBot ? SECONDARY_BUY_ENTRY_OFFSET_BPS : 0;
      return cfg;
    }

    const recentRangePct = getRecentRangePct(key);
    let baseMove = clampNumber(cfg.adaptiveBaseMovePct, 0.0001, 0.02);
    let sellExtra = clampNumber(cfg.adaptiveSellExtraPct, 0, 0.02);
    let volMultiplier = clampNumber(cfg.adaptiveVolMultiplier, 0, 2);

    if (regimeOn && regimeSnap?.adaptive) {
      const regimeMoves = applyRegimeToAdaptiveMoves(cfg, regimeSnap, recentRangePct, clampNumber);
      baseMove = regimeMoves.baseMove;
      sellExtra = regimeMoves.sellExtra;
      volMultiplier = regimeMoves.volMultiplier;
    }

    const minMove = clampNumber(cfg.adaptiveMinMovePct, 0.0001, 0.02);
    const maxMove = clampNumber(cfg.adaptiveMaxMovePct, minMove, 0.05);

    let buyMove = clampNumber(baseMove + recentRangePct * volMultiplier, minMove, maxMove);
    let sellMove = clampNumber(buyMove + sellExtra, minMove, maxMove + sellExtra);
    const buyDistanceFactor = clampNumber(
      cfg.adaptiveBuyDistanceFactor ?? defaultBotCfg.adaptiveBuyDistanceFactor,
      0.8,
      1
    );
    buyMove = clampNumber(buyMove * buyDistanceFactor, minMove, maxMove);
    const safeMin = regimeOn && regimeSnap?.regime ? SAFE_REGIME_MIN_MOVES[regimeSnap.regime] : null;
    if (safeMin && (!recentRangePct || recentRangePct <= 0.0001)) {
      buyMove = Math.max(buyMove, safeMin.buy);
      sellMove = Math.max(sellMove, safeMin.sell);
    }

    if (regimeOn && regimeSnap && lastRegimeLogRef.current[key] !== regimeSnap.regime) {
      lastRegimeLogRef.current[key] = regimeSnap.regime;
      console.log(
        `[RegimeCfg] ${key} ${regimeSnap.regime} | base buy ${(userBaseMove * 100).toFixed(3)}% → effective ${(buyMove * 100).toFixed(3)}% | sell ${(userSellExtra * 100).toFixed(3)}% → ${(sellMove * 100).toFixed(3)}% | reserve ${(userMinReserve * 100).toFixed(0)}% → ${((cfg.minUsdcReservePct || 0) * 100).toFixed(0)}% | max WETH ${((cfg.maxWethExposurePct || 0) * 100).toFixed(0)}%`
      );
    }

    return {
      ...cfg,
      regimeEngineEnabled: regimeOn,
      buyTarget: 1 - buyMove - instanceBuyOffsetPct,
      sellTarget: 1 + sellMove,
      adaptiveRecentRangePct: recentRangePct,
      adaptiveBuyMovePct: buyMove,
      adaptiveSellMovePct: sellMove,
      adaptiveBuyDistanceFactor: buyDistanceFactor,
      adaptiveBaseMoveUser: userBaseMove,
      adaptiveSellExtraUser: userSellExtra,
      instanceBuyOffsetBps: isSecondaryBot ? SECONDARY_BUY_ENTRY_OFFSET_BPS : 0,
    };
  };

    // regime / risk management tuning
  const MAX_DRIFT_FROM_ANCHOR = 0.125;     // 12.5% move from avgEntry ⇒ reset regime (reduced from 30% for faster adaptation)
  const MAX_UNREALIZED_LOSS = 0.45;        // 45% drawdown ⇒ de-risk
  const COUNTER_DECAY_MS = 10 * 60 * 1000; // Sell counter decay only; BUY count persists until a confirmed sell
  const TIME_BASED_REANCHOR_MS = 8 * 60 * 60 * 1000; // 8 hours inactivity ⇒ reset anchor before stuck-position stops
  const PROFIT_BASED_REANCHOR_PCT = 0.025; // 2.5% realized profit ⇒ reset anchor (middle of 2-3%)

  // --------------------------
  // Persistence helpers (use Moralis)
  // --------------------------

  const persistPnlSnapshot = (nextData, { immediate = false } = {}) => {
    try {
      localStorage.setItem(
        getBotStorageKey("botPnlData", botInstanceId),
        JSON.stringify(nextData)
      );
    } catch (error) {
      console.warn("Failed to save local PnL ledger", error);
    }
    if (!persistencePlayer) return;
    if (pnlPersistenceTimeoutRef.current) clearTimeout(pnlPersistenceTimeoutRef.current);
    const save = async () => {
      try {
        await moralisSaveBotAccounting(persistencePlayer, {
          schemaVersion: PNL_LEDGER_SCHEMA_VERSION,
          markets: nextData,
          updatedAt: Date.now(),
        });
      } catch (error) {
        console.warn("moralisSaveBotAccounting failed", error?.message || error);
      }
    };
    if (immediate) {
      pnlPersistenceTimeoutRef.current = null;
      void save();
    } else {
      pnlPersistenceTimeoutRef.current = setTimeout(save, 300);
    }
  };

  // Save botConfigs: optimistic setState + persist each config
  const saveBotConfigs = (updater) => {
    setBotConfigs(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      // persist in background
      (async () => {
        try {
          if (!persistencePlayer) return;
          const player = persistencePlayer;
          const tasks = Object.entries(next).map(([botKey, cfg]) =>
            moralisSaveBotConfig(player, botKey, cfg)
          );
          await Promise.all(tasks);
        } catch (e) {
          console.error("❌ moralisSaveBotConfig batch failed:", e);
        }
      })();
      return next;
    });
  };

  const setBotConfig = (key, patch) =>
    saveBotConfigs((prev) => ({ ...prev, [key]: { ...(prev[key] || instanceDefaultBotCfg), ...patch } }));

  // Save positions: optimistic setState + persist all positions in single document
  // Note: Don't save _BASE keys to Moralis (they're balance tracking, not positions)
  // Uses debouncing to avoid saving too frequently
  const savePositions = (updater) => {
    setPositions(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      positionsRef.current = next;
      
      // Debounce saves to avoid too frequent database writes
      if (savePositionsTimeoutRef.current) {
        clearTimeout(savePositionsTimeoutRef.current);
      }
      savePositionsTimeoutRef.current = setTimeout(async () => {
        try {
          if (!persistencePlayer) return;
          const hasUnconfirmedTrade = Object.values(pendingTradesRef.current || {})
            .some((entries) => Array.isArray(entries) && entries.length > 0);
          if (hasUnconfirmedTrade) {
            console.log("[BotPosition] Pending trade snapshot not persisted before confirmation");
            return;
          }
          const player = persistencePlayer;
          // Filter out _BASE keys - these are balance tracking, not positions to persist
          const positionsToSave = Object.fromEntries(
            Object.entries(next)
            .filter(([botKey]) => !botKey.endsWith('_BASE'))
            );
          // Save all positions in a single document (upsert pattern)
          await moralisSavePosition(player, null, positionsToSave);
        } catch (e) {
          console.error("❌ moralisSavePosition (upsert) failed:", e);
        }
      }, 500); // 500ms debounce
      
      return next;
    });
  };

  // Save custom bots: optimistic setState + persist full list (replace-all)
  const saveCustomBots = (updater) => {
    setCustomBots(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      (async () => {
        try {
          if (!persistencePlayer) return;
          const player = persistencePlayer;
          await moralisSaveCustomBots(player, next);
        } catch (e) {
          console.error("❌ moralisSaveCustomBots failed:", e);
        }
      })();
      return next;
    });
  };

  // Add trade history entry: update local state then persist record
  const addTradeToHistory = (entry) => {
    try {
      setTradeHistory(prev => {
        const next = [entry, ...prev].slice(0, 200);
        return next;
      });
    } catch (e) {
      console.error("update tradeHistory local failed", e);
    }

    (async () => {
      try {
        if (!persistencePlayer) return;
        const player = persistencePlayer;
        await moralisAddTradeHistory(player, entry);
      } catch (e) {
        console.error("❌ moralisAddTradeHistory failed:", e);
      }
    })();
  };

  const recordExternalSignalDecision = (decision) => {
    const withId = {
      decisionId: decision.decisionId || `ext-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
      botInstanceId,
      ...decision,
    };
    const isPreviewDecision = !withId.txHash;
    const logGateKey = `${withId.marketKey || ""}:${withId.side || ""}:${withId.candidateKind || "candidate"}`;
    const logSignature = [
      withId.status || "",
      withId.signalConfirmed === true ? "confirmed" : "unconfirmed",
      withId.thresholdPersistent === true ? "persistent" : "forming",
      withId.thresholdDirection || "neutral",
      withId.originalWouldTrigger === true ? "original-trigger" : "original-wait",
      withId.recommendedWouldTrigger === true ? "recommended-trigger" : "recommended-wait",
    ].join(":");
    const previousLog = externalDecisionLogGateRef.current[logGateKey];
    const suppressPreviewPersistence =
      isPreviewDecision &&
      previousLog?.signature === logSignature &&
      Date.now() - Number(previousLog.at || 0) < 5 * 60 * 1000;
    if (!suppressPreviewPersistence) {
      externalDecisionLogGateRef.current[logGateKey] = { signature: logSignature, at: Date.now() };
    }
    withId.persistedToMongoDb = !suppressPreviewPersistence;
    const sanitized = JSON.parse(JSON.stringify(withId, (_key, value) =>
      typeof value === "number" && !Number.isFinite(value) ? null : value
    ));
    if (!suppressPreviewPersistence) {
      console.log("[EXTERNAL SIGNAL DECISION]", sanitized);
    }
    (async () => {
      try {
        if (!persistencePlayer || suppressPreviewPersistence) return;
        await moralisAddExternalSignalDecision(persistencePlayer, sanitized);
      } catch (error) {
        console.error("moralisAddExternalSignalDecision failed", error);
      }
    })();
    return sanitized;
  };

  const persistStuckStopEvaluation = async (evaluation) => {
    if (!evaluation?.eventId) return;
    const sanitized = JSON.parse(JSON.stringify(evaluation, (_key, value) =>
      typeof value === "number" && !Number.isFinite(value) ? null : value
    ));
    stuckStopEvaluationsRef.current[sanitized.eventId] = sanitized;
    console.log("[STUCK STOP EVALUATION]", sanitized);
    try {
      if (persistencePlayer) {
        await moralisUpsertStuckStopEvaluation(persistencePlayer, sanitized);
      }
    } catch (error) {
      console.error("moralisUpsertStuckStopEvaluation failed", error);
    }
  };

  const getStuckStopEvaluationAtPrice = (evaluation, price) =>
    computeStuckStopCounterfactual({
      soldWeth: evaluation.soldWeth,
      receivedUsdc: evaluation.receivedUsdc,
      reboughtWeth: evaluation.rebuy?.weth || 0,
      rebuyUsdc: evaluation.rebuy?.usdc || 0,
      currentPrice: price,
    });

  const recordConfirmedStuckStop = ({ marketKey, txHash, blockNumber, currentPrice, tradeAmounts, context }) => {
    if (!context?.fromStuckStop || !(tradeAmounts?.amountIn > 0) || !(tradeAmounts?.amountOut > 0)) return;
    const snapshot = context.stuckStopSnapshot || {};
    const confirmedAt = Date.now();
    const eventId = `stuck-${String(txHash || `${marketKey}-${confirmedAt}`).toLowerCase()}`;
    const evaluation = {
      eventId,
      marketKey,
      tier: context.stuckStopTier || snapshot.tier || "UNKNOWN",
      status: "monitoring",
      triggeredAt: Number(snapshot.triggeredAt) || confirmedAt,
      confirmedAt,
      txHash: txHash || "",
      blockNumber: blockNumber ?? null,
      triggerPrice: Number(snapshot.triggerPrice) || Number(currentPrice) || 0,
      avgEntry: Number(snapshot.avgEntry) || 0,
      preStopHoldings: Number(snapshot.preStopHoldings) || 0,
      drawdownPct: Number(snapshot.drawdownPct) || 0,
      hoursIdle: Number(snapshot.hoursIdle) || 0,
      sellPct: Number(snapshot.sellPct) || 0,
      soldWeth: Number(tradeAmounts.amountIn),
      receivedUsdc: Number(tradeAmounts.amountOut),
      observations: {},
    };
    evaluation.atConfirmation = getStuckStopEvaluationAtPrice(evaluation, currentPrice);
    void persistStuckStopEvaluation(evaluation);
  };

  const recordFirstRebuyAfterStuckStop = ({ marketKey, txHash, blockNumber, currentPrice, tradeAmounts }) => {
    if (!(tradeAmounts?.amountIn > 0) || !(tradeAmounts?.amountOut > 0)) return;
    const candidates = Object.values(stuckStopEvaluationsRef.current)
      .filter((evaluation) =>
        evaluation?.marketKey === marketKey &&
        evaluation?.status === "monitoring" &&
        !evaluation?.rebuy
      )
      .sort((a, b) => Number(b.confirmedAt || 0) - Number(a.confirmedAt || 0));
    const evaluation = candidates[0];
    if (!evaluation) return;

    evaluation.rebuy = {
      timestamp: Date.now(),
      txHash: txHash || "",
      blockNumber: blockNumber ?? null,
      price: Number(currentPrice) || 0,
      usdc: Number(tradeAmounts.amountIn),
      weth: Math.min(Number(evaluation.soldWeth) || 0, Number(tradeAmounts.amountOut)),
    };
    evaluation.rebuy.counterfactual = getStuckStopEvaluationAtPrice(evaluation, currentPrice);
    void persistStuckStopEvaluation(evaluation);
  };

  const observeOpenStuckStops = (marketKey, price) => {
    if (!(price > 0)) return;
    const now = Date.now();
    Object.values(stuckStopEvaluationsRef.current).forEach((evaluation) => {
      if (evaluation?.marketKey !== marketKey || evaluation?.status !== "monitoring") return;
      const confirmedAt = Number(evaluation.confirmedAt || evaluation.triggeredAt) || 0;
      if (!confirmedAt) return;
      let changed = false;
      evaluation.observations = evaluation.observations || {};
      Object.entries(STUCK_STOP_HORIZONS_MS).forEach(([label, horizonMs]) => {
        if (!evaluation.observations[label] && now - confirmedAt >= horizonMs) {
          evaluation.observations[label] = {
            targetAt: confirmedAt + horizonMs,
            observedAt: now,
            lateByMs: Math.max(0, now - (confirmedAt + horizonMs)),
            price,
            counterfactual: getStuckStopEvaluationAtPrice(evaluation, price),
          };
          changed = true;
        }
      });
      if (evaluation.observations.h24) {
        evaluation.status = "complete";
        evaluation.completedAt = now;
        changed = true;
      }
      if (changed) void persistStuckStopEvaluation(evaluation);
    });
  };

  // --------------------------
  // Ensure defaults & migration once
  // --------------------------
  useEffect(() => {
    // ensure buyTarget/sellTarget exist for configs (local state)
    setBotConfigs(prev => {
      const next = { ...prev };
      let changed = false;
      for (const k of Object.keys(next)) {
        const cfg = next[k] || {};
        if (cfg.buyTarget == null) { cfg.buyTarget = defaultBotCfg.buyTarget; changed = true; }
        if (cfg.sellTarget == null) { cfg.sellTarget = defaultBotCfg.sellTarget; changed = true; }
        next[k] = { ...instanceDefaultBotCfg, ...cfg };
      }
      if (changed) {
        // persist changed set
        (async () => {
          try {
            if (!persistencePlayer) return;
            const player = persistencePlayer;
            const tasks = Object.entries(next).map(([botKey, cfg]) =>
              moralisSaveBotConfig(player, botKey, cfg)
            );
            await Promise.all(tasks);
          } catch (e) {
            console.error("❌ moralisSaveBotConfig migration failed:", e);
          }
        })();
        return next;
      }
      return prev;
    });
    // run once
  }, []);

  // --------------------------
  // Hydrate from Moralis on account
  // --------------------------
  useEffect(() => {
    (async () => {
      if (!persistencePlayer) {
        // clear local state when account is removed
        setAddress("");
        setHydratedPlayer("");
        stuckStopEvaluationsRef.current = {};
        accountingHydratedRef.current = false;
        return;
      }
      accountingHydratedRef.current = false;
      setHydratedPlayer("");
      setAddress(executionAddress);
      try {
        const player = persistencePlayer;
        const {
          configs,
          positions: loadedPositions,
          customBots: loadedCustomBots,
          history,
          stuckStopEvaluations,
          accounting,
        } =
          await moralisLoadAllPlayerState(player);

        const loadedAccountingMarkets = accounting?.markets;
        if (loadedAccountingMarkets && typeof loadedAccountingMarkets === "object") {
          const normalizedAccounting = Object.fromEntries(
            Object.entries(loadedAccountingMarkets).map(([key, value]) => [key, normalizePnlRecord(value)])
          );
          pnlDataRef.current = normalizedAccounting;
          setPnlData(normalizedAccounting);
          try {
            localStorage.setItem(
              getBotStorageKey("botPnlData", botInstanceId),
              JSON.stringify(normalizedAccounting)
            );
          } catch (error) {
            console.warn("Failed to cache MongoDB accounting locally", error);
          }
        }

        stuckStopEvaluationsRef.current = Object.fromEntries(
          (stuckStopEvaluations || []).map((evaluation) => [evaluation.eventId, evaluation])
        );

        if (configs && Object.keys(configs).length) {
          // merge with defaults
          const normalized = {};
          const migrated = [];
          Object.keys(configs).forEach(k => {
            const stored = configs[k] || {};
            const needsRiskAccountingMigration = Number(stored.riskAccountingRevision || 0) < 2;
            const needsSmallBalanceMigration = isSecondaryBot && Number(stored.riskAccountingRevision || 0) < 3;
            const storedCompoundMin = Number(stored.compoundEconomicMinTradeUsd);
            const storedLegacyMin = Number(stored.minTradeUsd);
            normalized[k] = {
              ...instanceDefaultBotCfg,
              ...stored,
              ...(needsRiskAccountingMigration
                ? {
                    riskAccountingRevision: 2,
                    minBuySpacingMs: Math.max(
                      DEFAULT_MIN_BUY_SPACING_MS,
                      Number(stored.minBuySpacingMs) || 0
                    ),
                    compoundSizingEnabled: true,
                    compoundBaseTradePct: DEFAULT_COMPOUND_TRADE_SIZING.baseTradePct,
                    compoundMaxTradePct: DEFAULT_COMPOUND_TRADE_SIZING.maxTradePct,
                    compoundEconomicMinTradeUsd: DEFAULT_COMPOUND_TRADE_SIZING.economicMinTradeUsd,
                    compoundAbsoluteMaxTradeUsd: DEFAULT_COMPOUND_TRADE_SIZING.absoluteMaxTradeUsd,
                    compoundRealizedOnly: true,
                    minTradeUsd: DEFAULT_COMPOUND_TRADE_SIZING.economicMinTradeUsd,
                    maxTradeUsd: DEFAULT_COMPOUND_TRADE_SIZING.absoluteMaxTradeUsd,
                    buyMultiplier: DEFAULT_COMPOUND_TRADE_SIZING.baseTradePct,
                  }
                : {}),
              ...(needsSmallBalanceMigration
                ? {
                    riskAccountingRevision: 3,
                    compoundEconomicMinTradeUsd:
                      Number.isFinite(storedCompoundMin) && storedCompoundMin > 0 &&
                      storedCompoundMin !== DEFAULT_COMPOUND_TRADE_SIZING.economicMinTradeUsd
                        ? storedCompoundMin
                        : secondaryBotDefaultCfg.compoundEconomicMinTradeUsd,
                    minTradeUsd:
                      Number.isFinite(storedLegacyMin) && storedLegacyMin > 0 &&
                      storedLegacyMin !== DEFAULT_COMPOUND_TRADE_SIZING.economicMinTradeUsd
                        ? storedLegacyMin
                        : secondaryBotDefaultCfg.minTradeUsd,
                  }
                : {}),
            };
            if (needsRiskAccountingMigration || needsSmallBalanceMigration) migrated.push(k);
          });
          setBotConfigs(prev => ({ ...prev, ...normalized }));
          if (migrated.length) {
            try {
              await Promise.all(
                migrated.map((botKey) => moralisSaveBotConfig(player, botKey, normalized[botKey]))
              );
              console.log(`[BotConfig migration][${botInstanceId.toUpperCase()}] Applied sizing defaults`, {
                markets: migrated,
                minBuySpacingMs: DEFAULT_MIN_BUY_SPACING_MS,
                economicMinTradeUsd: instanceDefaultBotCfg.compoundEconomicMinTradeUsd,
              });
            } catch (migrationError) {
              console.warn("[BotConfig migration] Runtime defaults applied; persistence failed", {
                error: migrationError?.message || migrationError,
              });
            }
          }
        }

        if (loadedPositions && Object.keys(loadedPositions).length) {
          // Merge loaded positions, but don't overwrite _BASE keys (they're from fresh RPC calls)
          // Also, don't overwrite holdings if they were just updated from RPC
          // CRITICAL FIX: Reset avgEntry if holdings are 0 (stale Moralis data)
          setPositions(prev => {
            const merged = { ...prev };
            Object.entries(loadedPositions).forEach(([key, pos]) => {
              // Don't load _BASE keys from Moralis - they should come from fresh RPC calls
              if (key.endsWith('_BASE')) return;

              if (pos.marketRegime) {
                marketRegimeRef.current[key] = {
                  ...(marketRegimeRef.current[key] || {}),
                  regime: pos.marketRegime,
                  regimeSince: pos.regimeSince,
                  recoveryBreakoutActive: !!pos.recoveryBreakoutActive,
                  recoveryBreakoutStartedAt: pos.recoveryBreakoutStartedAt,
                  recoveryBreakoutAnchorPrice: pos.recoveryBreakoutAnchorPrice,
                  lastLocalLowPrice: pos.lastLocalLowPrice,
                  higherLowConfirmed: !!pos.higherLowConfirmed,
                };
              }
              
              // CRITICAL FIX: If holdings are 0 but avgEntry exists, reset avgEntry to prevent stale data
              // This prevents Case 1 sync logic from initializing costBasis from stale avgEntry
              const freshHoldings = prev[key]?.holdings !== undefined ? prev[key].holdings : pos.holdings;
              const shouldResetAvgEntry = freshHoldings === 0 && pos.avgEntry > 0;
              
              // Only merge if the key doesn't exist or if it's not a balance key
              // Balance keys (holdings) should come from fresh RPC calls, not Moralis
              if (!merged[key] || (prev[key] && prev[key].holdings !== undefined && prev[key].holdings !== pos.holdings)) {
                // Preserve holdings from fresh RPC if they exist
                merged[key] = {
                  ...pos,
                  holdings: freshHoldings,
                  // Reset avgEntry if holdings are 0 (stale data)
                  avgEntry: shouldResetAvgEntry ? 0 : pos.avgEntry
                };
                if (shouldResetAvgEntry) {
                  console.log(`[Position Load] Reset stale avgEntry for ${key}: holdings=0 but avgEntry=${pos.avgEntry} (likely stale Moralis data)`);
                  console.log(`[Position Load Validation] Successfully reset avgEntry to 0 for ${key} to prevent stale data initialization`);
                }
              } else {
                merged[key] = { 
                  ...prev[key], 
                  ...pos,
                  // Reset avgEntry if holdings are 0 (stale data)
                  avgEntry: shouldResetAvgEntry ? 0 : pos.avgEntry
                };
                if (shouldResetAvgEntry) {
                  console.log(`[Position Load] Reset stale avgEntry for ${key}: holdings=0 but avgEntry=${pos.avgEntry} (likely stale Moralis data)`);
                }
              }
            });
            positionsRef.current = merged;
            return merged;
          });
        }

        if (Array.isArray(loadedCustomBots) && loadedCustomBots.length) {
          setCustomBots(loadedCustomBots);
          const extraMarkets = loadedCustomBots.map(b => ({
            key: b.key, price: 0, token: b.tokenAddress, initiator: "CRO",
          }));
          setMarkets(prev => {
            const merged = [...prev];
            for (const m of extraMarkets) {
              if (!merged.find(x => x.key === m.key)) merged.push(m);
            }
            return merged;
          });
        }

        if (Array.isArray(history) && history.length) {
          setTradeHistory(history);
        }
        accountingHydratedRef.current = true;
        setHydratedPlayer(player);
        setTimeout(() => {
          void getTokenBalance(player).catch((balanceError) => {
            console.warn("[ACCOUNTING CHECKPOINT] Post-hydration balance refresh failed", balanceError?.message || balanceError);
          });
        }, 0);
      } catch (e) {
        console.error("❌ Failed to hydrate state from Moralis:", e);
        accountingHydratedRef.current = true;
        setHydratedPlayer(player);
      }
    })();
  }, [persistencePlayer, executionAddress, botInstanceId]);

  // --------------------------
  // Core helpers (unchanged logic mostly)
  // --------------------------

  const updatePosition = (key, updates) => {
    positionsRef.current = {
      ...positionsRef.current,
      [key]: { ...(positionsRef.current[key] || {}), ...updates },
    };
    savePositions((prev) => ({ ...prev, [key]: { ...prev[key], ...updates } }));
  };

  // CRITICAL FIX: Optimized version that only updates specific market
  // IMPORTANT: Only seeds targets when they're missing, NOT when price changes
  // Targets should remain fixed relative to anchor (avgEntry) unless explicitly reset
  const seedTargetsForMarket = (key, price) => {
    if (!chainConfig || !price || price <= 0) return;
    const wrappedSymbol = chainConfig.nativeCurrency.symbol;
    const stableSymbol = chainConfig.defaultStablecoin;
    const nativeStableKey = `${wrappedSymbol}-${stableSymbol}`;
    
    const pos = positions[key] || {};
    const isWcroUsdc = key === nativeStableKey;
    const hasPos   = (pos.holdings ?? 0) > 0 && (pos.avgEntry ?? 0) > 0;
    
    const needsInitialAnchor = !hasPos && !(pos.avgEntry > 0);
    const needsAvgEntryFix = (pos.holdings > 0) && !(pos.avgEntry > 0);

    const invalidBuy  = !(pos.priceTargetBuy  > 0);
    const invalidSell = !(pos.priceTargetSell > 0);

    // CRITICAL FIX: Early return if targets are valid and avgEntry exists - prevent any recalculation
    // This prevents targets from being recalculated on every price update
    if (!invalidBuy && !invalidSell && pos.avgEntry > 0) {
      // Targets exist and are valid, avgEntry exists - skip this market completely
      return;
    }

    // CRITICAL FIX: Derive anchor from existing targets if they exist, not from current price
    // This prevents targets from drifting with price changes
    let anchor;
    if (hasPos) {
      // Use avgEntry if we have valid holdings and entry
      anchor = pos.avgEntry;
    } else if (needsInitialAnchor) {
      // First time setup: use current price
      anchor = price;
    } else if (needsAvgEntryFix && (pos.priceTargetBuy > 0 || pos.priceTargetSell > 0)) {
      // If avgEntry needs fixing but targets exist, derive anchor from targets
      // This preserves target stability even when avgEntry is missing
      const cfg = getEffectiveBotCfg(key);
      const buyPct  = cfg.buyTarget  ?? defaultBotCfg.buyTarget;
      const sellPct = cfg.sellTarget ?? defaultBotCfg.sellTarget;
      // Derive anchor from buy target (more reliable than sell target)
      anchor = pos.priceTargetBuy > 0 ? pos.priceTargetBuy / buyPct : (pos.priceTargetSell > 0 ? pos.priceTargetSell / sellPct : price);
    } else {
      // Fallback: use existing avgEntry or current price
      anchor = pos.avgEntry || price;
    }

    // CRITICAL FIX: Only update targets if they're actually missing
    // Don't recalculate targets just because price changed or avgEntry needs fixing
    // If avgEntry needs fixing but targets exist, only fix avgEntry, don't recalculate targets
    if (invalidBuy || invalidSell) {
      const cfg = getEffectiveBotCfg(key);
      const buyPct  = cfg.buyTarget  ?? defaultBotCfg.buyTarget;
      const sellPct = cfg.sellTarget ?? defaultBotCfg.sellTarget;
      
      updatePosition(key, {
        avgEntry: needsAvgEntryFix ? anchor : (pos.avgEntry || (needsInitialAnchor ? price : 0)),
        // Only set targets if they're missing - preserve existing targets otherwise
        priceTargetBuy:  invalidBuy  ? anchor * buyPct  : pos.priceTargetBuy,
        priceTargetSell: invalidSell ? anchor * sellPct : pos.priceTargetSell,
      });
    } else if (needsAvgEntryFix) {
      // CRITICAL FIX: When fixing avgEntry, derive it from existing targets (don't recalculate targets)
      // The anchor was already derived from existing targets (line 990-997), so targets are correct
      // Only update avgEntry to sync it with the targets, preserving target stability
      // This prevents target drift on every price update
      updatePosition(key, {
        avgEntry: anchor, // Anchor derived from existing targets, so targets stay fixed
      });
    }
  };

  const updateMarketPrice = (key, price) => {
    if (!isFinite(price) || price <= 0) return;
    lastPriceRef.current[key] = price;
    recordPriceSample(key, price);
    syncMarketRegime(key, price);
    setMarkets((prev) => {
      const updated = prev.map((m) => (m.key === key ? { ...m, price } : m));
      // CRITICAL FIX: Use requestAnimationFrame to defer position checks until after React state updates
      // This prevents reading stale state that might override targets just updated by trades
      requestAnimationFrame(() => {
        const market = updated.find(m => m.key === key);
        // Read fresh positions state using functional update to get latest state
        setPositions((currentPositions) => {
          const pos = currentPositions[key] || {};
          // CRITICAL FIX: Only seed if targets are missing, NOT if avgEntry needs fixing
          // If targets exist but avgEntry is missing, we'll fix avgEntry separately without recalculating targets
          // This prevents target drift on every price update
          const needsSeeding = !(pos.priceTargetBuy > 0) || !(pos.priceTargetSell > 0);
          const needsAvgEntryFixOnly = (pos.holdings > 0) && !(pos.avgEntry > 0) && (pos.priceTargetBuy > 0 || pos.priceTargetSell > 0);
          
          if (market && market.price > 0 && needsSeeding) {
            seedTargetsForMarket(key, market.price);
          } else if (market && market.price > 0 && needsAvgEntryFixOnly) {
            // CRITICAL FIX: If only avgEntry needs fixing (targets exist), fix it without recalculating targets
            // This prevents target drift on every price update
            const cfg = getEffectiveBotCfg(key);
            const buyPct  = cfg.buyTarget  ?? defaultBotCfg.buyTarget;
            const sellPct = cfg.sellTarget ?? defaultBotCfg.sellTarget;
            // Derive avgEntry from existing targets to preserve target stability
            const derivedAvgEntry = pos.priceTargetBuy > 0 ? pos.priceTargetBuy / buyPct : (pos.priceTargetSell > 0 ? pos.priceTargetSell / sellPct : market.price);
            updatePosition(key, { avgEntry: derivedAvgEntry });
          }
          return currentPositions; // Return unchanged - we're just reading state, not updating it
        });
      });
      return updated;
    });
  };

  const startPending = (marketKey, prevSnapshot) => {
    const id = Math.random().toString(36).slice(2);
    if (!pendingTradesRef.current[marketKey]) pendingTradesRef.current[marketKey] = [];
    pendingTradesRef.current[marketKey].push({ id, prev: prevSnapshot });
    return id;
  };

  const commitPending = (marketKey, id) => {
    const arr = pendingTradesRef.current[marketKey] || [];
    const hadPendingEntry = arr.some((x) => x.id === id);
    pendingTradesRef.current[marketKey] = arr.filter((x) => x.id !== id);
    if (hadPendingEntry) {
      // The optimistic UI state is now confirmed and may be persisted.
      savePositions((prev) => ({ ...prev }));
    }
  };

  const rollbackPending = (marketKey, id) => {
    const arr = pendingTradesRef.current[marketKey] || [];
    const entry = arr.find((x) => x.id === id);
    if (!entry) return;
    pendingTradesRef.current[marketKey] = arr.filter((x) => x.id !== id);
    updatePosition(marketKey, { ...entry.prev });
  };

  const rollbackAllPendingForMarket = (marketKey) => {
    const arr = pendingTradesRef.current[marketKey] || [];
    if (!arr.length) return;
    const earliest = arr[0];
    pendingTradesRef.current[marketKey] = [];
    updatePosition(marketKey, { ...earliest.prev });
  };

  const detachAllListeners = () => {
    try {
      // CRITICAL FIX: Ensure all listeners are properly cleaned up to prevent memory leaks
      liveContractsRef.current.forEach((c) => {
        try {
          if (c && typeof c.removeAllListeners === 'function') {
          c.removeAllListeners();
          }
        } catch (err) {
          console.warn('[detachAllListeners] Error removing listener:', err);
        }
      });
      liveContractsRef.current = [];
    } catch (err) {
      console.warn('[detachAllListeners] Error cleaning up contracts:', err);
    }
    if (window.__sniperHeartbeats?.[botInstanceId]) {
      clearInterval(window.__sniperHeartbeats[botInstanceId]);
      delete window.__sniperHeartbeats[botInstanceId];
    }
    // CRITICAL FIX: Also clear any pending trade submissions to prevent memory leaks
    pendingTradeSubmissionsRef.current = {};
  };

  const normalize = (bn, decimals) => Number(bn) / Math.pow(10, decimals);

  const readPoolTokenDecimals = async (tokenAddress, blockTag) => {
    const k = (tokenAddress || "").toLowerCase();
    if (!k) return 18;
    if (decimalsCacheRef.current[k] != null) return decimalsCacheRef.current[k];
    const token = new ethers.Contract(tokenAddress, ERC20_META_ABI, provider);
    const overrides = blockTag != null ? { blockTag } : {};
    const raw = await token.decimals(overrides);
    const decimals = Math.max(0, Math.min(18, Number(raw)));
    decimalsCacheRef.current[k] = decimals;
    return decimals;
  };

  const getWethUsdcPoolPrice = async ({ blockTag, usage = "unknown" } = {}) => {
    if (!provider || !currentConfig) {
      throw new Error("[WETH PRICE] Provider/config not ready");
    }
    const poolAddress = ARBITRUM_WETH_USDC_POOL_ADDRESS;
    const pool = new ethers.Contract(poolAddress, V3_POOL_ABI, provider);
    const overrides = blockTag != null ? { blockTag } : {};
    const [token0Raw, token1Raw, slot0] = await Promise.all([
      pool.token0(overrides),
      pool.token1(overrides),
      pool.slot0(overrides),
    ]);

    const token0 = token0Raw.toLowerCase();
    const token1 = token1Raw.toLowerCase();
    const weth = currentConfig.wrappedNativeToken?.toLowerCase();
    const configuredStable = currentConfig.stablecoinAddress?.toLowerCase();
    const nativeUsdc = ARBITRUM_NATIVE_USDC_ADDRESS.toLowerCase();
    const stableCandidates = new Set([configuredStable, nativeUsdc].filter(Boolean));
    const poolHasWeth = token0 === weth || token1 === weth;
    const stableToken = stableCandidates.has(token0) ? token0 : stableCandidates.has(token1) ? token1 : "";

    if (!poolHasWeth || !stableToken) {
      throw new Error(
        `[WETH PRICE] Configured pool is not WETH/USDC. pool=${poolAddress} token0=${token0Raw} token1=${token1Raw}`
      );
    }

    const [decimals0, decimals1] = await Promise.all([
      readPoolTokenDecimals(token0Raw, blockTag),
      readPoolTokenDecimals(token1Raw, blockTag),
    ]);
    const sqrtPriceX96 = slot0.sqrtPriceX96 || slot0[0];
    if (!sqrtPriceX96 || !ethers.BigNumber.from(sqrtPriceX96).gt(0)) {
      throw new Error(`[WETH PRICE] Invalid slot0.sqrtPriceX96 for pool ${poolAddress}`);
    }

    // Uniswap V3 slot0 gives sqrt(token1/token0) in Q96 fixed point.
    // Scale the token1-per-token0 adjusted price to 1e18 before converting at the display boundary.
    const ten = ethers.BigNumber.from(10);
    const price1Per0Scaled = ethers.BigNumber.from(sqrtPriceX96)
      .mul(ethers.BigNumber.from(sqrtPriceX96))
      .mul(ten.pow(decimals0))
      .mul(PRICE_SCALE_18)
      .div(Q192)
      .div(ten.pow(decimals1));

    let usdcPerWethScaled;
    if (token0 === weth && token1 === stableToken) {
      usdcPerWethScaled = price1Per0Scaled;
    } else if (token1 === weth && token0 === stableToken) {
      if (price1Per0Scaled.isZero()) {
        throw new Error(`[WETH PRICE] Cannot invert zero V3 price for pool ${poolAddress}`);
      }
      usdcPerWethScaled = PRICE_SCALE_18.mul(PRICE_SCALE_18).div(price1Per0Scaled);
    } else {
      throw new Error(
        `[WETH PRICE] Pool token order is not recognized. pool=${poolAddress} token0=${token0Raw} token1=${token1Raw}`
      );
    }

    const usdcPerWeth = Number(ethers.utils.formatUnits(usdcPerWethScaled, 18));
    if (!Number.isFinite(usdcPerWeth) || usdcPerWeth <= 0) {
      throw new Error(`[WETH PRICE] Calculated invalid USDC per WETH: ${usdcPerWeth}`);
    }

    lastPoolWethPriceRef.current = {
      price: usdcPerWeth,
      blockNumber: blockTag ?? "latest",
      poolAddress,
    };

    if (WETH_PRICE_LOGGING_ENABLED) {
      console.log("[WETH PRICE]", {
        usage,
        poolAddress,
        blockNumber: blockTag ?? "latest",
        token0: token0Raw,
        token1: token1Raw,
        decimals0,
        decimals1,
        usdcPerWeth,
        configuredStable,
        poolStable: stableToken,
      });
    }

    return usdcPerWeth;
  };

  const getWethUsdcPriceForCore = async ({ marketKey, blockTag, usage, fallbackPrice = 0 } = {}) => {
    if (!isWrappedStableMarket(marketKey)) return Number(fallbackPrice) || 0;
    if (Number(currentConfig?.chainId) !== 42161) return Number(fallbackPrice) || 0;
    try {
      return await getWethUsdcPoolPrice({ blockTag, usage });
    } catch (sameBlockError) {
      if (blockTag != null) {
        console.warn("[WETH PRICE] Same-block pool price failed; using latest confirmed fallback", {
          usage,
          blockTag,
          error: sameBlockError?.message || sameBlockError,
        });
        try {
          return await getWethUsdcPoolPrice({ usage: `${usage}:latest-fallback` });
        } catch (latestError) {
          console.warn("[WETH PRICE] Latest fallback failed", latestError?.message || latestError);
        }
      } else {
        console.warn("[WETH PRICE] Pool price failed", sameBlockError?.message || sameBlockError);
      }
    }
    return Number(fallbackPrice) || Number(lastPoolWethPriceRef.current.price) || 0;
  };

  const scheduleExternalSignalOutcomes = ({
    decision,
    marketKey,
    side,
    txHash,
    blockNumber,
    actualAmountIn,
    actualAmountOut,
    executionPrice,
    executedAt,
  }) => {
    if (!persistencePlayer || !decision?.decisionId || !isWrappedStableMarket(marketKey)) return;
    const player = persistencePlayer;
    const normalizedSide = String(side).toUpperCase();
    const startPrice = Number(executionPrice) || Number(decision.poolPrice) || 0;
    const execution = {
      txHash,
      blockNumber: blockNumber == null ? null : Number(blockNumber),
      executedAt,
      actualAmountIn: Number(actualAmountIn) || 0,
      actualAmountOut: Number(actualAmountOut) || 0,
      executionPrice: startPrice,
      phase1Applied: decision.phase1ChangedAnything === true,
      phase2Applied: decision.phase2ChangedAnything === true,
    };
    moralisPatchExternalSignalDecision(player, decision.decisionId, {
      txHash,
      executedAt,
      tradeExecution: execution,
    }).catch((error) => console.warn("External signal execution-link update failed", error));

    [5, 15, 30, 60].forEach((horizonMinutes) => {
      const targetAt = executedAt + horizonMinutes * 60 * 1000;
      const timer = setTimeout(async () => {
        externalOutcomeTimersRef.current.delete(timer);
        try {
          const markPrice = await getWethUsdcPriceForCore({
            marketKey,
            usage: `external-signal:${decision.decisionId}:${horizonMinutes}m`,
            fallbackPrice: lastPoolWethPriceRef.current.price,
          });
          if (!(startPrice > 0) || !(markPrice > 0)) return;
          const poolMoveBps = ((markPrice / startPrice) - 1) * 10000;
          const sideFavorableMoveBps = normalizedSide === "BUY" ? poolMoveBps : -poolMoveBps;
          const recommendationSign = Number(decision.recommendedThresholdAdjustmentBps) < 0
            ? 1
            : Number(decision.recommendedThresholdAdjustmentBps) > 0
              ? -1
              : 0;
          const advisorSignedOutcomeBps = recommendationSign * sideFavorableMoveBps;
          const samples = (priceHistoryRef.current[marketKey] || []).filter(
            (sample) => sample.t >= executedAt && sample.t <= Date.now() && sample.p > 0
          );
          const signedPath = samples.map((sample) => {
            const moveBps = ((sample.p / startPrice) - 1) * 10000;
            const sideMoveBps = normalizedSide === "BUY" ? moveBps : -moveBps;
            return recommendationSign * sideMoveBps;
          });

          const baseTradeUsd = Number(decision.baseTradeSizeUsd) || 0;
          const finalTradeUsd = Number(decision.finalTradeSizeUsd) || 0;
          const ratio = finalTradeUsd > 0 ? baseTradeUsd / finalTradeUsd : 1;
          let phase1CounterfactualImpactUsd = 0;
          if (decision.phase1ChangedAnything === true && actualAmountIn > 0 && actualAmountOut > 0) {
            if (normalizedSide === "BUY") {
              const cashDifference = -actualAmountIn + baseTradeUsd;
              const wethDifference = actualAmountOut - actualAmountOut * ratio;
              phase1CounterfactualImpactUsd = cashDifference + wethDifference * markPrice;
            } else {
              const cashDifference = actualAmountOut - actualAmountOut * ratio;
              const wethDifference = -actualAmountIn + actualAmountIn * ratio;
              phase1CounterfactualImpactUsd = cashDifference + wethDifference * markPrice;
            }
          }

          const observedAt = Date.now();
          await moralisPatchExternalSignalDecision(
            player,
            decision.decisionId,
            {
              outcome: {
                horizonMinutes,
                targetAt,
                observedAt,
                observationDelayMs: Math.max(0, observedAt - targetAt),
                startPrice,
                markPrice,
                poolMoveBps,
                sideFavorableMoveBps,
                advisorSignedOutcomeBps,
                recommendationCorrect: recommendationSign === 0 ? null : advisorSignedOutcomeBps > 0,
                maxFavorableAdvisorMoveBps: signedPath.length ? Math.max(...signedPath) : null,
                maxAdverseAdvisorMoveBps: signedPath.length ? Math.min(...signedPath) : null,
                phase1CounterfactualImpactUsd,
              },
            },
            `m${horizonMinutes}`
          );
        } catch (error) {
          console.warn("External signal outcome update failed", {
            decisionId: decision.decisionId,
            horizonMinutes,
            error: error?.message || error,
          });
        }
      }, Math.max(0, targetAt - Date.now()));
      externalOutcomeTimersRef.current.add(timer);
    });
  };

  const gridTargetChangeBps = (nextTarget, currentTarget) => {
    if (!(currentTarget > 0) || !(nextTarget > 0)) return Infinity;
    return Math.abs(nextTarget - currentTarget) / currentTarget * 10000;
  };

  const shouldSkipGridReset = ({
    key,
    reason,
    price,
    proposedBuy,
    proposedSell,
    cooldownSeconds = GRID_RESET_COOLDOWN_SECONDS,
    minPriceMoveBps = GRID_RESET_MIN_PRICE_MOVE_BPS,
    minTargetChangeBps = GRID_RESET_MIN_TARGET_CHANGE_BPS,
    hysteresisBps = GRID_RESET_HYSTERESIS_BPS,
    eventKey = "",
  }) => {
    const pos = getPositionForDecisions(key);
    const now = Date.now();
    const gate = lastGridResetGateRef.current[key] || {};
    const cooldownRemainingMs = gate.lastResetAt
      ? Math.max(0, cooldownSeconds * 1000 - (now - gate.lastResetAt))
      : 0;
    const priceMoveBps = gate.lastResetPrice > 0 && price > 0
      ? Math.abs(price - gate.lastResetPrice) / gate.lastResetPrice * 10000
      : Infinity;
    const effectiveMinPriceMoveBps =
      gate.lastResetReason === reason
        ? minPriceMoveBps + hysteresisBps
        : minPriceMoveBps;
    const buyChangeBps = gridTargetChangeBps(proposedBuy, pos.priceTargetBuy);
    const sellChangeBps = gridTargetChangeBps(proposedSell, pos.priceTargetSell);

    let skipReason = "";
    if (eventKey && gate.lastResetEventKey === eventKey) {
      skipReason = "duplicate event";
    } else if (cooldownRemainingMs > 0) {
      skipReason = "cooldown";
    } else if (priceMoveBps < effectiveMinPriceMoveBps) {
      skipReason = "price move below threshold";
    } else if (buyChangeBps < minTargetChangeBps && sellChangeBps < minTargetChangeBps) {
      skipReason = "targets materially unchanged";
    }

    if (skipReason) {
      console.log("[GRID RESET SKIPPED]", {
        marketKey: key,
        reason,
        skipReason,
        currentPrice: price,
        lastResetPrice: gate.lastResetPrice,
        priceMoveBps,
        minPriceMoveBps: effectiveMinPriceMoveBps,
        cooldownRemainingSeconds: Math.ceil(cooldownRemainingMs / 1000),
        proposedBuyTarget: proposedBuy,
        currentBuyTarget: pos.priceTargetBuy,
        proposedSellTarget: proposedSell,
        currentSellTarget: pos.priceTargetSell,
        buyTargetChangeBps: buyChangeBps,
        sellTargetChangeBps: sellChangeBps,
        eventKey,
      });
      return true;
    }

    return false;
  };

  const recordGridReset = ({ key, reason, price, newBuy, newSell, regime, eventKey = "" }) => {
    const prev = lastGridResetGateRef.current[key] || {};
    const priceMoveBps = prev.lastResetPrice > 0 && price > 0
      ? Math.abs(price - prev.lastResetPrice) / prev.lastResetPrice * 10000
      : Infinity;
    lastGridResetGateRef.current[key] = {
      lastResetAt: Date.now(),
      lastResetPrice: price,
      lastResetReason: reason,
      lastResetBuyTarget: newBuy,
      lastResetSellTarget: newSell,
      lastResetEventKey: eventKey,
    };
    try {
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(
          getBotStorageKey("botGridResetGateState", botInstanceId),
          JSON.stringify(lastGridResetGateRef.current)
        );
      }
    } catch (e) {
      console.warn("[GRID RESET] Failed to persist gate state", e?.message || e);
    }
    console.log("[GRID RESET EXECUTED]", {
      marketKey: key,
      resetReason: reason,
      currentPrice: price,
      oldBuyTarget: positionsRef.current[key]?.priceTargetBuy,
      oldSellTarget: positionsRef.current[key]?.priceTargetSell,
      newBuyTarget: newBuy,
      newSellTarget: newSell,
      priceMoveBps,
      regime,
      eventKey,
    });
  };

  const readCoreTradeBalances = async (owner) => {
    if (!owner || !provider || !currentConfig || !chainConfig) return null;
    const [stableDecimals, wrappedDecimals] = await Promise.all([
      readPoolTokenDecimals(currentConfig.stablecoinAddress),
      readPoolTokenDecimals(currentConfig.wrappedNativeToken),
    ]);
    const stable = new ethers.Contract(currentConfig.stablecoinAddress, ERC20_ABI, provider);
    const wrapped = new ethers.Contract(currentConfig.wrappedNativeToken, ERC20_ABI, provider);
    const [stableRaw, wrappedRaw] = await Promise.all([
      stable.balanceOf(owner),
      wrapped.balanceOf(owner),
    ]);
    return {
      stableBalance: Number(ethers.utils.formatUnits(stableRaw, stableDecimals)),
      wrappedBalance: Number(ethers.utils.formatUnits(wrappedRaw, wrappedDecimals)),
      stableDecimals,
      wrappedDecimals,
    };
  };

  const logBalanceMismatchDiagnostics = async ({
    marketKey,
    side,
    txHash,
    blockNumber,
    tradeAmounts,
    preStableBalance,
    preWrappedBalance,
  }) => {
    if (!BALANCE_MISMATCH_LOGGING_ENABLED || !isWrappedStableMarket(marketKey) || !tradeAmounts || !executionAddress) return;
    try {
      const fresh = await readCoreTradeBalances(executionAddress);
      if (!fresh) return;
      const expectedStableDelta = side === "BUY" ? -tradeAmounts.amountIn : tradeAmounts.amountOut;
      const expectedWrappedDelta = side === "BUY" ? tradeAmounts.amountOut : -tradeAmounts.amountIn;
      const actualStableDelta = Number.isFinite(preStableBalance) ? fresh.stableBalance - preStableBalance : null;
      const actualWrappedDelta = Number.isFinite(preWrappedBalance) ? fresh.wrappedBalance - preWrappedBalance : null;
      const stableDiff = actualStableDelta == null ? null : actualStableDelta - expectedStableDelta;
      const wrappedDiff = actualWrappedDelta == null ? null : actualWrappedDelta - expectedWrappedDelta;
      const aboveTolerance =
        (stableDiff != null && Math.abs(stableDiff) > 0.01) ||
        (wrappedDiff != null && Math.abs(wrappedDiff) > 0.00001);
      const payload = {
        marketKey,
        side,
        txHash,
        blockNumber,
        preTradeUSDCBalance: preStableBalance,
        postTradeUSDCBalance: fresh.stableBalance,
        expectedUSDCDelta: expectedStableDelta,
        actualUSDCDelta: actualStableDelta,
        usdcDifference: stableDiff,
        preTradeWETHBalance: preWrappedBalance,
        postTradeWETHBalance: fresh.wrappedBalance,
        expectedWETHDelta: expectedWrappedDelta,
        actualWETHDelta: actualWrappedDelta,
        wethDifference: wrappedDiff,
        optimisticStateReconciled: actualStableDelta != null || actualWrappedDelta != null,
        aboveTolerance,
      };
      if (aboveTolerance) {
        console.warn("[BALANCE MISMATCH]", payload);
      } else {
        console.log(
          actualStableDelta == null && actualWrappedDelta == null
            ? "[Balance Snapshot]"
            : "[Balance Reconciled]",
          payload
        );
      }
    } catch (e) {
      console.warn("[BALANCE MISMATCH] diagnostics failed", e?.message || e);
    }
  };

  const reconcileExternalCapitalBalances = ({
    marketKey,
    stableBalance,
    assetBalance,
    price,
    preStableBalance,
    preAssetBalance,
    expectedStableDelta = 0,
    expectedAssetDelta = 0,
    txHash = "",
    blockNumber = null,
    source = "balance-refresh",
  }) => {
    if (!accountingHydratedRef.current || !isWrappedStableMarket(marketKey)) return null;
    const stable = Math.max(0, Number(stableBalance) || 0);
    const asset = Math.max(0, Number(assetBalance) || 0);
    const poolPrice = Math.max(0, Number(price) || 0);
    if (!(poolPrice > 0)) return null;

    const current = normalizePnlRecord(pnlDataRef.current?.[marketKey]);
    let next = { ...current };
    const now = Date.now();
    const newFlows = [];

    if (!(current.accountingCheckpointAt > 0)) {
      const quantityTolerance = Math.max(0.00001, asset * 0.002);
      const ledgerQuantityMatches = Math.abs(current.holdings - asset) <= quantityTolerance;
      const positionHoldings = Math.max(0, Number(positionsRef.current?.[marketKey]?.holdings) || 0);
      const positionQuantityMatches = Math.abs(positionHoldings - asset) <= quantityTolerance;
      const currentLedgerAverage =
        ledgerQuantityMatches && current.holdings > PNL_ASSET_DUST && current.costBasis > 0
        ? current.costBasis / current.holdings
        : 0;
      const positionAverage = positionQuantityMatches
        ? Math.max(0, Number(positionsRef.current?.[marketKey]?.avgEntry) || 0)
        : 0;
      const checkpointAverage = currentLedgerAverage || positionAverage || poolPrice;
      const checkpointBasisSource = currentLedgerAverage > 0
        ? "matching-confirmed-ledger"
        : positionAverage > 0
          ? "matching-position"
          : "pool-price-checkpoint";
      next = {
        ...current,
        schemaVersion: PNL_LEDGER_SCHEMA_VERSION,
        holdings: asset,
        costBasis: asset * checkpointAverage,
        accountingCheckpointAt: now,
        accountingBaselineEquityUsd: stable + asset * poolPrice,
        reportingRealizedPnlOffset: current.realizedPnL,
        reportingGasUsdOffset: current.totalGasUsd,
        legacyRealizedPnlAtCheckpoint: current.realizedPnL,
        legacyStatisticsAtCheckpoint: {
          totalTrades: current.totalTrades,
          buyCount: current.buyCount,
          sellCount: current.sellCount,
          totalBuyBase: current.totalBuyBase,
          totalBuyAsset: current.totalBuyAsset,
          totalSellBase: current.totalSellBase,
          totalSellAsset: current.totalSellAsset,
          grossProfit: current.grossProfit,
          grossLoss: current.grossLoss,
        },
        totalTrades: 0,
        buyCount: 0,
        sellCount: 0,
        totalBuyBase: 0,
        totalBuyAsset: 0,
        totalSellBase: 0,
        totalSellAsset: 0,
        grossProfit: 0,
        grossLoss: 0,
        sellReturnsPct: [],
        netExternalDepositsUsd: 0,
        netExternalWithdrawalsUsd: 0,
        lastObservedStableBalance: stable,
        lastObservedAssetBalance: asset,
        lastObservedPrice: poolPrice,
        lastObservedAt: now,
        flowAdjustedPnlUsd: 0,
        accountingStatus: "checkpointed",
      };
      console.log("[ACCOUNTING CHECKPOINT CREATED]", {
        marketKey,
        baselineEquityUsd: next.accountingBaselineEquityUsd,
        stableBalance: stable,
        assetBalance: asset,
        checkpointAverage,
        checkpointBasisSource,
        archivedRealizedPnl: current.realizedPnL,
      });
    } else {
      const detectionSteps = [];
      const hasPreBalances =
        Number.isFinite(Number(preStableBalance)) && Number.isFinite(Number(preAssetBalance));
      if (hasPreBalances) {
        if (current.lastObservedAt > 0) {
          detectionSteps.push(detectExternalBalanceFlows({
            previousStableBalance: current.lastObservedStableBalance,
            previousAssetBalance: current.lastObservedAssetBalance,
            currentStableBalance: Number(preStableBalance),
            currentAssetBalance: Number(preAssetBalance),
            assetPrice: poolPrice,
          }));
        }
        detectionSteps.push(detectExternalBalanceFlows({
          previousStableBalance: Number(preStableBalance),
          previousAssetBalance: Number(preAssetBalance),
          currentStableBalance: stable,
          currentAssetBalance: asset,
          expectedStableDelta,
          expectedAssetDelta,
          assetPrice: poolPrice,
        }));
      } else if (current.lastObservedAt > 0) {
        detectionSteps.push(detectExternalBalanceFlows({
          previousStableBalance: current.lastObservedStableBalance,
          previousAssetBalance: current.lastObservedAssetBalance,
          currentStableBalance: stable,
          currentAssetBalance: asset,
          assetPrice: poolPrice,
        }));
      }

      for (const detection of detectionSteps) {
        for (const detected of detection.flows) {
          const flow = {
            ...detected,
            eventId: `capital-flow-${now}-${newFlows.length}`,
            marketKey,
            timestamp: now,
            txHash,
            blockNumber,
            source,
            affectsPnl: false,
          };
          newFlows.push(flow);
          if (flow.type.endsWith("_DEPOSIT")) {
            next.netExternalDepositsUsd += flow.usdValue;
          } else if (flow.type.endsWith("_WITHDRAWAL")) {
            next.netExternalWithdrawalsUsd += flow.usdValue;
          }
          if (flow.token === "WETH") {
            const adjusted = applyExternalAssetFlowToLedger({
              holdings: next.holdings,
              costBasis: next.costBasis,
              signedAssetDelta: flow.signedTokenDelta,
              assetPrice: poolPrice,
            });
            next.holdings = adjusted.holdings;
            next.costBasis = adjusted.costBasis;
            flow.costBasisTransferred = adjusted.costBasisTransferred;
          }
        }
      }

      const flowAdjusted = calculateFlowAdjustedPnl({
        currentStableBalance: stable,
        currentAssetBalance: asset,
        assetPrice: poolPrice,
        baselineEquityUsd: next.accountingBaselineEquityUsd,
        netDepositsUsd: next.netExternalDepositsUsd,
        netWithdrawalsUsd: next.netExternalWithdrawalsUsd,
        totalGasUsd: Math.max(0, next.totalGasUsd - next.reportingGasUsdOffset),
      });
      next = {
        ...next,
        lastObservedStableBalance: stable,
        lastObservedAssetBalance: asset,
        lastObservedPrice: poolPrice,
        lastObservedAt: now,
        flowAdjustedPnlUsd: flowAdjusted.pnlUsd,
        accountingStatus: newFlows.length ? "external-flow-reconciled" : next.accountingStatus || "confirmed",
        capitalFlows: [...(next.capitalFlows || []), ...newFlows].slice(-100),
      };
    }

    const nextData = { ...(pnlDataRef.current || {}), [marketKey]: normalizePnlRecord(next) };
    pnlDataRef.current = nextData;
    setPnlData(nextData);
    persistPnlSnapshot(nextData, { immediate: newFlows.length > 0 });

    if (newFlows.length) {
      console.log("[EXTERNAL CAPITAL FLOW RECONCILED]", {
        marketKey,
        flows: newFlows,
        netDepositsUsd: next.netExternalDepositsUsd,
        netWithdrawalsUsd: next.netExternalWithdrawalsUsd,
        flowAdjustedPnlUsd: next.flowAdjustedPnlUsd,
      });
      for (const flow of newFlows) {
        void moralisAddCapitalFlow(persistencePlayer, flow).catch((error) => {
          console.warn("moralisAddCapitalFlow failed", error?.message || error);
        });
      }
    }

    if (newFlows.some((flow) => flow.token === "WETH") || !(current.accountingCheckpointAt > 0)) {
      const normalizedNext = normalizePnlRecord(next);
      const avgEntry = getPnlAverageEntry(normalizedNext);
      updatePosition(marketKey, {
        holdings: normalizedNext.holdings,
        avgEntry,
        accountingReconciledAt: now,
        accountingSource: newFlows.length ? "external-capital-flow" : "accounting-checkpoint",
      });
    }
    return normalizePnlRecord(next);
  };

  const fetchTokenInfo = async (addr) => {
    if (!ethers.utils.isAddress(addr)) return;
    try {
      const c = new ethers.Contract(addr, ERC20_META_ABI, provider);
      const [symbol, name, decimalsRaw] = await Promise.all([c.symbol(), c.name(), c.decimals()]);
      setTokenSymbol(symbol || "");
      setTokenName(name || "");
      const dec = Number(decimalsRaw);
      setTokenDecimals(Number.isFinite(dec) ? clamp(dec, 0, 18) : 8);
    } catch {
      setTokenDecimals((d) => d ?? 8);
    }
  };

  const getFactory = () => {
    if (!currentConfig || !provider) {
      throw new Error("Configuration not ready");
    }
    if (factoryContractRef.current && factoryContractRef.current.address === currentConfig.factoryAddress) {
      return factoryContractRef.current;
    }
    factoryContractRef.current = new ethers.Contract(currentConfig.factoryAddress, FACTORY_ABI, provider);
    return factoryContractRef.current;
  };

  // Preferred pool addresses for better liquidity (chain-specific)
  // Format: "chainId-token0-token1": poolAddress
  // Note: token0 and token1 are sorted by address (lower address = token0)
  // For Arbitrum: WETH=0x82aF..., USDC.e=0xFF97..., Native USDC=0xaf88...
  // The pool 0xc696... is WETH/Native USDC (0xaf88...), NOT WETH/USDC.e (0xFF97...)
  // WETH < Native USDC (0x82af < 0xaf88), so WETH=token0, Native USDC=token1
  const PREFERRED_POOLS = {
    // WETH-Native USDC pool (0xaf88...) - This is the high liquidity pool
    "42161-0x82aF49447D8a07e3bd95BD0d56f35241523fBab1-0xaf88d065e77c8cC2239327C5EDb3A432268e5831": "0xc6962004f452be9203591991d15f6b388e09e8d0", // Arbitrum WETH-USDC (native, 0.05%, $76.68M liquidity)
    "42161-0xaf88d065e77c8cC2239327C5EDb3A432268e5831-0x82aF49447D8a07e3bd95BD0d56f35241523fBab1": "0xc6962004f452be9203591991d15f6b388e09e8d0", // Reverse order
    // Note: WETH/USDC.e pool (0xFF97...) uses a different pool address (0xC31E...)
    // If you want to trade WETH/USDC.e, you'd need to use the factory-discovered pool
  };

  // Helper function to get pair/pool address for both V2 and V3
  const getPairOrPoolAddress = async (tokenA, tokenB) => {
    try {
      if (!currentConfig || !provider) {
        return "";
      }
      const factory = getFactory();
      const addrA = ethers.utils.getAddress(tokenA);
      const addrB = ethers.utils.getAddress(tokenB);
      
      // Check if this is Uniswap V3 or V4
      const isV3 = selectedDex === "uniswapV3";
      const isV4 = selectedDex === "uniswapV4";
      
      if (isV3) {
        // V3 uses getPool(token0, token1, fee) on V3 factory
        // V3 requires tokens to be sorted: token0 < token1 (by address)
        const [token0, token1] = addrA.toLowerCase() < addrB.toLowerCase() 
          ? [addrA, addrB] 
          : [addrB, addrA];
        
        // Check if there's a preferred pool for this token pair on this chain
        const chainId = currentConfig?.chainId || (await provider.getNetwork())?.chainId;
        
        // Normalize addresses to checksummed format for consistent comparison
        const normalizedToken0 = ethers.utils.getAddress(token0);
        const normalizedToken1 = ethers.utils.getAddress(token1);
        
        // Try both token orderings since PREFERRED_POOLS key might be in either order
        const preferredPoolKey1 = `${chainId}-${normalizedToken0}-${normalizedToken1}`;
        const preferredPoolKey2 = `${chainId}-${normalizedToken1}-${normalizedToken0}`;
        const preferredPoolAddress = PREFERRED_POOLS[preferredPoolKey1] || PREFERRED_POOLS[preferredPoolKey2];
        
        // Debug logging
        if (preferredPoolAddress || Object.keys(PREFERRED_POOLS).some(k => k.includes(normalizedToken0) && k.includes(normalizedToken1))) {
          console.log(`[Pool Discovery] Checking preferred pools for chain ${chainId}:`);
          console.log(`   token0: ${normalizedToken0}, token1: ${normalizedToken1}`);
          console.log(`   Key1: ${preferredPoolKey1}`);
          console.log(`   Key2: ${preferredPoolKey2}`);
          console.log(`   Available keys:`, Object.keys(PREFERRED_POOLS));
        }
        
        if (preferredPoolAddress) {
          // Verify the preferred pool exists and is valid
          try {
            const poolContract = new ethers.Contract(
              preferredPoolAddress,
              ["function token0() external view returns (address)", "function token1() external view returns (address)", "function fee() external view returns (uint24)"],
              provider
            );
            const [poolToken0, poolToken1, poolFee] = await Promise.all([
              poolContract.token0(),
              poolContract.token1(),
              poolContract.fee()
            ]);
            
            // Normalize pool tokens for comparison
            const normalizedPoolToken0 = ethers.utils.getAddress(poolToken0);
            const normalizedPoolToken1 = ethers.utils.getAddress(poolToken1);
            
            // Verify it matches our token pair (check both orderings)
            const matchesOrder1 = normalizedPoolToken0.toLowerCase() === normalizedToken0.toLowerCase() && normalizedPoolToken1.toLowerCase() === normalizedToken1.toLowerCase();
            const matchesOrder2 = normalizedPoolToken0.toLowerCase() === normalizedToken1.toLowerCase() && normalizedPoolToken1.toLowerCase() === normalizedToken0.toLowerCase();
            
            if (matchesOrder1 || matchesOrder2) {
              const feePercent = poolFee === 500 ? "0.05%" : poolFee === 3000 ? "0.3%" : "1%";
              console.log(`✅ Using preferred V3 pool (${feePercent}, higher liquidity): ${preferredPoolAddress}`);
              console.log(`   Pool token0: ${normalizedPoolToken0}, token1: ${normalizedPoolToken1}`);
              return preferredPoolAddress;
            } else {
              // Pool tokens don't match - this preferred pool is for a different token pair
              // Don't log as error, just skip it silently and fall back to factory discovery
              // The preferred pool might be for WETH/Native USDC while we're looking for WETH/USDC.e
              console.log(`ℹ️ Preferred pool ${preferredPoolAddress} is for different tokens (${normalizedPoolToken0}/${normalizedPoolToken1}), using factory discovery`);
            }
          } catch (e) {
            console.warn(`⚠️ Preferred pool verification failed, falling back to factory discovery:`, e.message);
          }
        }
        
        // Fallback: Try different fee tiers: 0.05% (500), 0.3% (3000), 1% (10000)
        const feeTiers = [500, 3000, 10000];
        for (const feeTier of feeTiers) {
          try {
            const pool = await factory.getPool(token0, token1, feeTier);
            if (pool && pool !== ethers.constants.AddressZero) {
              const feePercent = feeTier === 500 ? "0.05%" : feeTier === 3000 ? "0.3%" : "1%";
              console.log(`✅ Found V3 pool (${feePercent}): ${pool}`);
              return pool;
            }
          } catch (e) {
            // Continue to next fee tier if this one fails
            if (feeTier === feeTiers[feeTiers.length - 1]) {
              // Only log warning on last attempt
              console.warn(`V3 getPool failed for all fee tiers (${token0}/${token1}):`, e.message || e);
            }
          }
        }
        return "";
      }
      
      if (isV4) {
        // V4 uses PoolManager which doesn't have getPool method
        // V4 pools are identified by PoolKey (currencies, fee, tickSpacing, hooks)
        // For price monitoring, we can't directly get a pool address
        // The Universal Router will handle routing, so we return empty
        // Price updates will need to come from swap events or a different source
        console.log("⚠️ V4 pools don't have individual addresses - using Universal Router for routing");
        return ""; // V4 pools are managed by PoolManager, no individual addresses
      }
      
      // V2: use getPair (order doesn't matter for V2)
      // Only call getPair if factory has this method (V2 factories)
      try {
        const pair = await factory.getPair(addrA, addrB);
        if (!pair || pair === ethers.constants.AddressZero) return "";
        return pair;
      } catch (e) {
        // If getPair fails, it might be a V3 factory being used incorrectly
        console.warn("getPair failed (might be V3 factory):", e);
        return "";
      }
    } catch (e) {
      console.error("getPairOrPoolAddress error:", e);
      return "";
    }
  };

  const getPairAddress = async (tokenAddress) => {
    try {
      if (!currentConfig || !provider) {
        console.warn("⚠️ Configuration not ready");
        return "";
      }
      return await getPairOrPoolAddress(currentConfig.wrappedNativeToken, tokenAddress);
    } catch (e) {
      console.error("getPairAddress error:", e);
      return "";
    }
  };

  const getTokenBalance = async (userAddress, options = {}) => {
    try {
      if (!userAddress || !ethers.utils.isAddress(userAddress)) {
        console.warn("⚠️ Invalid wallet address:", userAddress);
        return;
      }

      const balances = {};

      const providerLocal = provider;

      const tokenAddr = selectedTokenAddress || TOKEN_ADDRESS;
      const token = new ethers.Contract(tokenAddr, ERC20_ABI, providerLocal);
      const wrappedNative = new ethers.Contract(currentConfig.wrappedNativeToken, ERC20_ABI, providerLocal);
      
      // For V3 pools, detect the actual stablecoin address from the pool
      // On Arbitrum, prefer Native USDC (higher liquidity pool)
      let stablecoinAddress = currentConfig.stablecoinAddress;
      if (selectedDex === "uniswapV3") {
        try {
          const wrappedSymbol = chainConfig.nativeCurrency.symbol;
          const stableSymbol = chainConfig.defaultStablecoin;
          const poolAddress = await getPairOrPoolAddress(
            currentConfig.wrappedNativeToken,
            currentConfig.stablecoinAddress
          );
          if (poolAddress) {
            const pool = new ethers.Contract(poolAddress, V3_POOL_ABI, providerLocal);
            const poolToken0 = await pool.token0();
            const poolToken1 = await pool.token1();
            const wrappedNativeLower = currentConfig.wrappedNativeToken.toLowerCase();
            // The stablecoin is whichever token is NOT the wrapped native
            const detectedStablecoin = poolToken0.toLowerCase() === wrappedNativeLower 
              ? poolToken1 
              : poolToken0;
            if (detectedStablecoin && detectedStablecoin !== ethers.constants.AddressZero) {
              // On Arbitrum, prefer Native USDC (detected) over USDC.e for better liquidity
              const configuredAddressLower = currentConfig.stablecoinAddress.toLowerCase();
              const detectedAddressLower = detectedStablecoin.toLowerCase();
              
              if (detectedAddressLower === configuredAddressLower) {
                // Detected address matches configured - use it
                stablecoinAddress = detectedStablecoin;
                console.log(`[getTokenBalance] Detected stablecoin from V3 pool matches config: ${stablecoinAddress}`);
              } else if (selectedChain === "arbitrum" && stableSymbol === "USDC") {
                // On Arbitrum with Native USDC, prefer detected Native USDC (higher liquidity pool)
                stablecoinAddress = detectedStablecoin;
                console.log(`[getTokenBalance] On Arbitrum: Using detected Native USDC ${detectedStablecoin} (higher liquidity pool)`);
              } else {
                // Use detected address for other chains
              stablecoinAddress = detectedStablecoin;
              console.log(`[getTokenBalance] Detected stablecoin from V3 pool: ${stablecoinAddress} (was: ${currentConfig.stablecoinAddress})`);
              }
            }
          }
        } catch (e) {
          console.warn("Failed to detect stablecoin from V3 pool, using config address:", e);
        }
      }
      
      const stablecoin = new ethers.Contract(stablecoinAddress, ERC20_ABI, providerLocal);

      const nativeDecimals = chainConfig.nativeCurrency.decimals;
      const stablecoinDecimals = (chainConfig.defaultStablecoin === "USDC" || chainConfig.defaultStablecoin === "USDC.e") ? 6 : 
                                 chainConfig.defaultStablecoin === "USDT" ? 6 : 18;

      const [nativeWei, tokenRaw, wrappedNativeRaw, stablecoinRaw] = await Promise.all([
        providerLocal.getBalance(userAddress),
        token.balanceOf(userAddress).catch(() => ethers.BigNumber.from(0)),
        wrappedNative.balanceOf(userAddress).catch(() => ethers.BigNumber.from(0)),
        stablecoin.balanceOf(userAddress).catch(() => ethers.BigNumber.from(0)),
      ]);

      const nativeSymbol = chainConfig.nativeCurrency.symbol;
      const wrappedSymbol = chainConfig.nativeCurrency.symbol;
      const stableSymbol = chainConfig.defaultStablecoin;
      const wrappedStableMarketKey = `${wrappedSymbol}-${stableSymbol}`;

      exactTokenBalanceRef.current[wrappedStableMarketKey] = {
        raw: wrappedNativeRaw.toString(),
        decimals: nativeDecimals,
      };
      exactTokenBalanceRef.current[`WBTC-${wrappedSymbol}`] = {
        raw: tokenRaw.toString(),
        decimals: tokenDecimals,
      };
      exactTokenBalanceRef.current[`WBTC-${stableSymbol}`] = {
        raw: tokenRaw.toString(),
        decimals: tokenDecimals,
      };

      const nativeBal = Math.max(0, Number(ethers.utils.formatEther(nativeWei)));
      const tokenBal = Math.max(0, Number(ethers.utils.formatUnits(tokenRaw, tokenDecimals)));
      const wrappedNativeBal = Math.max(0, Number(ethers.utils.formatUnits(wrappedNativeRaw, nativeDecimals)));
      const stablecoinBal = Math.max(0, Number(ethers.utils.formatUnits(stablecoinRaw, stablecoinDecimals)));

      balances[nativeSymbol] = nativeBal;
      balances[`WBTC-${wrappedSymbol}`] = tokenBal;
      balances[`WBTC-${stableSymbol}`] = tokenBal;
      // For WCRO-USDC market: holdings should be WCRO (what you hold after buying with USDC)
      // We also need to track USDC balance separately for the baseBalance calculation
      balances[`${wrappedSymbol}-${stableSymbol}`] = wrappedNativeBal; // WETH/WCRO holdings
      const baseKey = `${wrappedSymbol}-${stableSymbol}_BASE`;
      balances[baseKey] = stablecoinBal; // USDC.e balance for buying

      const accountingContext = options.accountingContext || {};
      const accountingPrice =
        Math.max(0, Number(options.priceOverride) || 0) ||
        Math.max(0, Number(markets.find((market) => market.key === wrappedStableMarketKey)?.price) || 0) ||
        Math.max(0, Number(lastPriceRef.current[wrappedStableMarketKey]) || 0);
      if (accountingPrice > 0 && options.skipAccounting !== true) {
        reconcileExternalCapitalBalances({
          marketKey: wrappedStableMarketKey,
          stableBalance: stablecoinBal,
          assetBalance: wrappedNativeBal,
          price: accountingPrice,
          preStableBalance: accountingContext.preStableBalance,
          preAssetBalance: accountingContext.preAssetBalance,
          expectedStableDelta: accountingContext.expectedStableDelta,
          expectedAssetDelta: accountingContext.expectedAssetDelta,
          txHash: accountingContext.txHash || "",
          blockNumber: accountingContext.blockNumber ?? null,
          source: accountingContext.source || "wallet-balance-refresh",
        });
      }
      
      console.log(`[getTokenBalance] Balance summary:`, {
        nativeSymbol,
        nativeBal,
        wrappedSymbol,
        wrappedNativeBal,
        stableSymbol,
        stablecoinBal,
        baseKey,
        stablecoinAddress: currentConfig.stablecoinAddress,
        stablecoinDecimals,
        rawStablecoin: stablecoinRaw.toString()
      });

      for (const market of markets) {
        const { key, token } = market;
        if (!token || !ethers.utils.isAddress(token)) continue;
        if (balances[key] !== undefined) continue;

        try {
          const erc20 = new ethers.Contract(token, ERC20_ABI, providerLocal);
          let decimals = 18;
          try { decimals = await erc20.decimals(); } catch {}
          const rawBalance = await erc20.balanceOf(userAddress);
          const balance = Math.max(0, Number(ethers.utils.formatUnits(rawBalance, decimals)));
          balances[key] = balance;
          exactTokenBalanceRef.current[key] = {
            raw: rawBalance.toString(),
            decimals: Number(decimals),
          };
        } catch (err) {
          console.warn(`⚠️ Failed to fetch balance for ${key}:`, err?.message);
        }
      }

      // Update positions with fresh RPC balances
      // Use setPositions directly to ensure fresh balances always take precedence over Moralis data
      const positionsWithFreshBalances = { ...positionsRef.current };
      Object.entries(balances).forEach(([key, amount]) => {
        positionsWithFreshBalances[key] = {
          ...(positionsWithFreshBalances[key] || {}),
          holdings: amount,
        };
      });
      positionsRef.current = positionsWithFreshBalances;
      setPositions(prev => {
        const updated = { ...prev };
        Object.entries(balances).forEach(([key, amount]) => {
          updated[key] = {
            ...prev[key],
            holdings: amount
          };
        });
        positionsRef.current = updated;
        return updated;
      });

      console.log("✅ [getTokenBalance] Balances fetched via RPC:", balances);
      return balances;
    } catch (error) {
      console.error("❌ [getTokenBalance] Error:", error?.message || error);
    }
  };

  const seedTargetsIfUnset = () => {
    if (!chainConfig) return;
    const wrappedSymbol = chainConfig.nativeCurrency.symbol;
    const stableSymbol = chainConfig.defaultStablecoin;
    const nativeStableKey = `${wrappedSymbol}-${stableSymbol}`;
    
    const priceByKey = Object.fromEntries(markets.map(m => [m.key, m.price || 0]));
    
    // CRITICAL FIX: Only seed targets when they're actually missing
    // Don't recalculate targets on every call - they should stay fixed relative to anchor
    for (const { key } of markets) {
      const price = priceByKey[key];
      if (!(price > 0)) continue;

      const pos = positions[key] || {};
      const hasPos   = (pos.holdings ?? 0) > 0 && (pos.avgEntry ?? 0) > 0;
      
      // Only use current price as anchor if no holdings and no avgEntry (first time setup)
      // Otherwise, use existing avgEntry as anchor
      const needsInitialAnchor = !hasPos && !(pos.avgEntry > 0);
      const needsAvgEntryFix = (pos.holdings > 0) && !(pos.avgEntry > 0);

      const invalidBuy  = !(pos.priceTargetBuy  > 0);
      const invalidSell = !(pos.priceTargetSell > 0);

      // CRITICAL FIX: Early return if targets are valid and avgEntry exists - prevent any recalculation
      // This prevents targets from being recalculated on every price update
      if (!invalidBuy && !invalidSell && pos.avgEntry > 0) {
        // Targets exist and are valid, avgEntry exists - skip this market completely
        continue;
      }

      // CRITICAL FIX: Derive anchor from existing targets if they exist, not from current price
      // This prevents targets from drifting with price changes
      let anchor;
      if (hasPos) {
        // Use avgEntry if we have valid holdings and entry
        anchor = pos.avgEntry;
      } else if (needsInitialAnchor) {
        // First time setup: use current price
        anchor = price;
      } else if (needsAvgEntryFix && (pos.priceTargetBuy > 0 || pos.priceTargetSell > 0)) {
        // If avgEntry needs fixing but targets exist, derive anchor from targets
        // This preserves target stability even when avgEntry is missing
      const cfg = getEffectiveBotCfg(key);
        const buyPct  = cfg.buyTarget  ?? defaultBotCfg.buyTarget;
        const sellPct = cfg.sellTarget ?? defaultBotCfg.sellTarget;
        // Derive anchor from buy target (more reliable than sell target)
        anchor = pos.priceTargetBuy > 0 ? pos.priceTargetBuy / buyPct : (pos.priceTargetSell > 0 ? pos.priceTargetSell / sellPct : price);
      } else {
        // Fallback: use existing avgEntry or current price
        anchor = pos.avgEntry || price;
      }

      // CRITICAL FIX: Only update targets if they're actually missing
      // Don't recalculate targets just because price changed or avgEntry needs fixing
      // If avgEntry needs fixing but targets exist, only fix avgEntry, don't recalculate targets
      if (invalidBuy || invalidSell) {
        const cfg = getEffectiveBotCfg(key);
        const buyPct  = cfg.buyTarget  ?? defaultBotCfg.buyTarget;
        const sellPct = cfg.sellTarget ?? defaultBotCfg.sellTarget;
        
        updatePosition(key, {
          avgEntry: needsAvgEntryFix ? anchor : (pos.avgEntry || (needsInitialAnchor ? price : 0)),
          // Only set targets if they're missing - preserve existing targets otherwise
          priceTargetBuy:  invalidBuy  ? anchor * buyPct  : pos.priceTargetBuy,
          priceTargetSell: invalidSell ? anchor * sellPct : pos.priceTargetSell,
        });
      } else if (needsAvgEntryFix) {
        // CRITICAL FIX: When fixing avgEntry, derive it from existing targets (don't recalculate targets)
        // This preserves target stability - targets are already correct, we just need to sync avgEntry
        updatePosition(key, {
          avgEntry: anchor, // Anchor derived from existing targets, so targets stay fixed (no recalculation)
        });
      }
    }
  };

  useEffect(() => {
    if (!isListening || !executionAddress || hydratedPlayer !== persistencePlayer ||
        (isSecondaryBot && !secondarySessionAuthorized)) return;

    // CRITICAL FIX: Use for...of instead of forEach to properly handle async operations
    // This prevents race conditions and ensures proper error handling
    (async () => {
      for (const { key, price, token, initiator } of markets) {
        try {
      syncMarketRegime(key, price);
      const cfg = getEffectiveBotCfg(key);
          const pos = getPositionForDecisions(key);
          if (!pos || pos.enabled === false) continue;
          if (!(price > 0)) continue;
          observeOpenStuckStops(key, price);

          // Keep all target, risk, and execution decisions serialized until the
          // prior receipt has been accounted for and wallet balances refreshed.
          if (isMarketTradeInFlight(key)) {
            prevPriceRef.current[key] = price;
            continue;
          }

      // 2) regime reset when price has moved far from avgEntry
      maybeResetRegime(key, price);

      // 2b) hybrid recovery: re-center entry-anchored targets to spot when underwater / low USDC
      maybeSyncRecoveryGrid(key, price);

          // 3) time-based re-anchoring: reset anchor if no trades for 4-6 hours
          maybeTimeBasedReanchor(key, price);

      // 4) drawdown stop-loss → rebalance to 50/50 WETH/USDC, spot grid (PnL preserved)
          const stopLossTriggered = await maybeStopLossRebalance(key, price);
          if (stopLossTriggered) {
            prevPriceRef.current[key] = price;
            continue;
          }

      // 5) controlled de-risk on large drawdowns (extreme backup; skipped when stop-loss handles book)
      if (!getEffectiveBotCfg(key).stopLossEnabled) {
        maybeDeRisk(key, price);
      }

          // 6) stuck-position stop: free inventory after long inactivity + underwater position.
          const stuckStopTriggered = await maybeStuckPositionStop(key, price);
          if (stuckStopTriggered) {
            prevPriceRef.current[key] = price;
            continue;
          }

          if (!currentConfig || !chainConfig) continue;
      const wrappedSymbol = chainConfig.nativeCurrency.symbol;
      const stableSymbol = chainConfig.defaultStablecoin;
      const nativeSymbol = chainConfig.nativeCurrency.symbol;
      const isWcroUsdc = key === `${wrappedSymbol}-${stableSymbol}`;
          // For stablecoin initiator, use _BASE key (stablecoin balance), not wrapped holdings
          // For WETH-USDC.e market: baseBalance should be USDC.e balance (_BASE key)
          // holdings key tracks wrapped native (WETH), _BASE key tracks stablecoin (USDC.e)
          let baseBalance;
          // Check market key first (more reliable than initiator field)
          if (key === `${wrappedSymbol}-${stableSymbol}` || key === `WBTC-${stableSymbol}`) {
            // For wrapped-native/stablecoin or WBTC/stablecoin markets, base is always stablecoin
            const baseKey = `${wrappedSymbol}-${stableSymbol}_BASE`;
            baseBalance = positionsRef.current[baseKey]?.holdings || 0;
          } else if (initiator === stableSymbol || initiator === "USDC" || initiator === "USDC.e") {
            // Fallback: check initiator for other markets
            const baseKey = `${wrappedSymbol}-${stableSymbol}_BASE`;
            baseBalance = positionsRef.current[baseKey]?.holdings || 0;
          } else {
            // For native token initiator markets (e.g., WBTC-WETH)
            baseBalance = positionsRef.current[nativeSymbol]?.holdings || 0;
          }

      const prev = prevPriceRef.current[key];
          
          // CRITICAL FIX: Add epsilon tolerance for floating point comparisons to prevent precision issues
          const PRICE_EPSILON = 0.000001; // Very small tolerance for price comparisons
          const isPriceEqual = (a, b) => Math.abs(a - b) < PRICE_EPSILON;
          const isPriceGreater = (a, b) => a - b > PRICE_EPSILON;
          const isPriceLess = (a, b) => b - a > PRICE_EPSILON;
      
      // Price is on the correct side of the target (allows buying)
      // For WETH-USDC: Buy when price < buyTarget (price drops below target)
      // For other markets: Buy when price < buyTarget (price drops below target)
      // CRITICAL FIX: When holdings are zero, allow buys when price is at or below anchor
      // This ensures bot can restart trading after holdings are depleted
      // When holdings exist: Require price < buyTarget (normal grid trading)
      // When holdings zero: Allow price <= anchor (avgEntry) so bot can start new position
      const hasHoldings = (pos.holdings || 0) > DUST;
      const anchor = pos.avgEntry > 0 ? pos.avgEntry : price; // Use avgEntry as anchor, fallback to current price
      const originalBuyTarget = Number(pos.priceTargetBuy) || 0;
      let effectiveBuyTarget = originalBuyTarget;
      let externalBuyPreview = null;
      
      // Price condition: Normal case requires price < buyTarget, zero holdings allows price <= anchor
      let priceConditionOk = hasHoldings
        ? isPriceLess(price, effectiveBuyTarget)  // Normal: price must be below buy target
        : (effectiveBuyTarget > 0 ? (isPriceLess(price, effectiveBuyTarget) || isPriceLess(price, anchor) || isPriceEqual(price, anchor)) : true); // Zero holdings: allow if below target OR at/below anchor
      
      // Price noise filter threshold: Only trade on significant moves (>= 0.05%) to avoid false signals and save gas
      // BUT allow trades when target is crossed or significantly past target
      const PRICE_NOISE_THRESHOLD = 0.0005; // 0.05%
      const priceChange = prev !== undefined ? Math.abs((price - prev) / prev) : 0;
      
      // Check if price just crossed the edge (from wrong side to correct side)
      // This is the ONLY condition that allows buys (prevents consecutive buys)
      // For all markets: Buy when price crosses from above target to below target
      // Use epsilon-aware comparisons
      let justCrossedEdge = prev !== undefined
        ? (isPriceGreater(prev, effectiveBuyTarget) || isPriceEqual(prev, effectiveBuyTarget)) && isPriceLess(price, effectiveBuyTarget)
        : false;

      // Allow buy ONLY if price just crossed the edge (prevents consecutive buys)
      // The "price is already on correct side" logic was causing consecutive buys
      let crossedEdge = justCrossedEdge;
      
      // Check if price is significantly below the target (allows trades when price is already below target)
      // This handles cases where price is already below target and stays below
      // Distance is positive when price is below target (for buys)
      let distanceFromTarget = priceConditionOk && effectiveBuyTarget > 0
        ? (effectiveBuyTarget - price) / effectiveBuyTarget  // Positive when price < target
        : 0;
      let isSignificantlyBelowTarget = distanceFromTarget >= PRICE_NOISE_THRESHOLD;
      
      // CRITICAL FIX: When holdings are zero, allow buys more liberally
      // This ensures bot can restart trading after holdings are depleted
      // Normal case: Require significant move or crossing to prevent consecutive buys
      // Zero holdings: Allow if price condition met (below target or at/below anchor) to restart position
      // Note: The Math.min logic in buy target calculation already prevents negative percentages
      let isSignificantMove = hasHoldings
        ? (justCrossedEdge || isSignificantlyBelowTarget || prev === undefined)  // Normal: require crossing or significant move
        : (priceConditionOk || prev === undefined);  // Zero holdings: allow if price condition met (ready to start new position)

      // Check spacing based on receipt confirmation time, not submission time
      // This ensures we wait for the previous trade to be confirmed before starting a new one
      const lastBuyReceiptTs = lastTradeReceiptAtRef.current[key] || lastBuyAtRef.current[key] || 0;
      const minBuySpacingMs = Math.max(10000, Number(cfg.minBuySpacingMs) || DEFAULT_MIN_BUY_SPACING_MS);
      const timeSinceLastBuyReceipt = Date.now() - lastBuyReceiptTs;
      const timeOk = timeSinceLastBuyReceipt >= minBuySpacingMs;
      
      // Also check if there's a pending trade submission that hasn't been confirmed yet
      const pendingTrade = pendingTradeSubmissionsRef.current[key];
      const hasPendingTrade = !!pendingTrade;
      
      const isTradeLocked = tradeLockRef.current[key] === true || hasPendingTrade;

      const book = getMarketBook(key, pos, price, positionsRef.current, chainConfig);
      const spotGrid = shouldUseSpotGridMode({
        cfg,
        pos,
        price,
        baseBalanceUsd: book.baseBalance,
        totalBookUsd: book.totalBookUsd,
      });
      const reservePct = Number(cfg.minUsdcReservePct ?? defaultBotCfg.minUsdcReservePct);
      const usdcReserveOk =
        book.totalBookUsd <= 0 || book.baseBalance / book.totalBookUsd >= reservePct;

      // ---------- BUY ----------
      // CRITICAL FIX: Allow buys when price is significantly below target, not just when crossing
      // Spacing checks (timeOk) prevent consecutive buys, so we can allow buys when already below target
      const recentRangePct = Number(cfg.adaptiveRecentRangePct ?? getRecentRangePct(key) ?? 0);
      const regimeSnap = marketRegimeRef.current[key];
      const buyProtection = getBuyProtectionStatus({
        cfg,
        pos,
        currentPrice: price,
        anchorPrice: anchor,
        recentRangePct,
        marketRegime: regimeSnap?.regime,
      });

      const buyMultiplierPctEst = cfg.buyMultiplier || 0.1;
      const compoundBuySizingEstimate =
        cfg.usePercentMode && isWrappedStableMarket(key)
          ? getCompoundSizingPlan({
              key,
              book,
              cfg,
              availableUsd: book.baseBalance,
            })
          : null;
      let plannedBuyUsdEst = 0;
      if (cfg.usePercentMode && book.totalBookUsd > 0) {
        plannedBuyUsdEst = compoundBuySizingEstimate
          ? compoundBuySizingEstimate.baseTradeUsd
          : Math.min(
              Number(cfg.maxTradeUsd || defaultBotCfg.maxTradeUsd),
              Math.max(Number(cfg.minTradeUsd || defaultBotCfg.minTradeUsd), book.baseBalance * buyMultiplierPctEst)
            );
      } else {
        plannedBuyUsdEst = Number(cfg.manualBuy || 0) * (key.includes(stableSymbol) ? 1 : price);
      }
      const regimeBuyGate =
        isRegimeEngineEnabled(key) && regimeSnap
          ? evaluateRegimeBuyBlocks({
              regimeSnap,
              currentPrice: price,
              consecBuys: buyProtection.consecBuys,
              book,
              plannedBuyUsd: plannedBuyUsdEst,
              pos,
            })
          : { blocked: false, blocks: [] };

      const flatNow = Date.now();
      const flatBook = !hasHoldings && cfg.flatReentryEnabled !== false;
      const derivedFlatSince = flatBook
        ? Number(pos.flatSince || pos.lastSellAt || pos.lastTradeAt || 0)
        : 0;
      const flatAgeMs = derivedFlatSince > 0 ? flatNow - derivedFlatSince : 0;
      const flatMonitorMs =
        Math.max(0, Number(cfg.flatReentryMonitorHours ?? defaultBotCfg.flatReentryMonitorHours)) *
        60 * 60 * 1000;
      const flatForcedReviewMs =
        Math.max(0, Number(cfg.flatReentryForcedReviewHours ?? defaultBotCfg.flatReentryForcedReviewHours)) *
        60 * 60 * 1000;
      const flatBullMaxMs =
        Math.max(0, Number(cfg.flatReentryBullMaxHours ?? defaultBotCfg.flatReentryBullMaxHours)) *
        60 * 60 * 1000;
      const flatCooldownMs =
        Math.max(0, Number(cfg.flatReentryCooldownHours ?? defaultBotCfg.flatReentryCooldownHours)) *
        60 * 60 * 1000;
      const nearMissBps = Math.max(
        0,
        Number(cfg.flatReentryNearMissBps ?? defaultBotCfg.flatReentryNearMissBps)
      );
      const targetGapBps =
        pos.priceTargetBuy > 0 && price > pos.priceTargetBuy
          ? ((price - pos.priceTargetBuy) / pos.priceTargetBuy) * 10000
          : 0;
      const inNearMissBand =
        flatBook &&
        pos.priceTargetBuy > 0 &&
        price > pos.priceTargetBuy &&
        targetGapBps <= nearMissBps;
      const storedNearMissSince = Number(pos.flatReentryNearMissSince || 0);
      const effectiveNearMissSince = inNearMissBand
        ? (storedNearMissSince || flatNow)
        : 0;
      const nearMissConfirmed =
        effectiveNearMissSince > 0 &&
        flatNow - effectiveNearMissSince >=
          Math.max(
            0,
            Number(cfg.flatReentryConfirmMinutes ?? defaultBotCfg.flatReentryConfirmMinutes)
          ) *
            60 *
            1000;

      const flatStatePatch = {};
      if (flatBook && derivedFlatSince > 0 && !(Number(pos.flatSince) > 0)) {
        flatStatePatch.flatSince = derivedFlatSince;
        flatStatePatch.fullExitPrice = Number(pos.fullExitPrice || pos.avgEntry || price);
      }
      if (inNearMissBand && !storedNearMissSince) {
        flatStatePatch.flatReentryNearMissSince = flatNow;
      } else if (!inNearMissBand && storedNearMissSince) {
        flatStatePatch.flatReentryNearMissSince = 0;
      }
      if (Object.keys(flatStatePatch).length > 0) {
        updatePosition(key, flatStatePatch);
      }

      const history15m = (priceHistoryRef.current[key] || []).filter(
        (sample) => sample.t >= flatNow - 15 * 60 * 1000 && sample.p > 0
      );
      const timeframeMetrics = getMarketTimeframeMetrics(key);
      const midpoint15m =
        timeframeMetrics.high15m > 0 && timeframeMetrics.low15m > 0
          ? (timeframeMetrics.high15m + timeframeMetrics.low15m) / 2
          : 0;
      const above15mMidpoint =
        history15m.length >= 4 && midpoint15m > 0 && price >= midpoint15m;
      const trend15m = Number(
        regimeSnap?.metrics?.m15?.changePct ?? timeframeMetrics.trend15m ?? 0
      );
      const oneHourPosition = Number(
        regimeSnap?.metrics?.m1h?.positionInRange ??
          (
            timeframeMetrics.high1h > timeframeMetrics.low1h
              ? (price - timeframeMetrics.low1h) /
                (timeframeMetrics.high1h - timeframeMetrics.low1h)
              : 0.5
          )
      );
      const flatRegimeAllowed = [
        REGIMES.BULL_ACTIVE,
        REGIMES.RECOVERY_BREAKOUT,
        REGIMES.NORMAL_CHOP,
      ].includes(regimeSnap?.regime);
      const flatSafetyConfirmed =
        flatRegimeAllowed &&
        above15mMidpoint &&
        !regimeSnap?.fresh15mLow &&
        trend15m >= -0.0015 &&
        oneHourPosition > 0.45;
      const flatCooldownsClear =
        flatNow >= Number(pos.recoveryBuyBlockedUntil || 0) &&
        flatNow >= Number(pos.capitalProtectionBuyBlockedUntil || 0) &&
        flatNow - Number(pos.lastFlatReentryAt || 0) >= flatCooldownMs;
      const flatNearMissSignal =
        flatAgeMs >= flatMonitorMs && nearMissConfirmed;
      const flatForcedSignal =
        flatAgeMs >= flatForcedReviewMs &&
        (
          regimeSnap?.regime === REGIMES.RECOVERY_BREAKOUT ||
          trend15m >= 0
        );
      const flatBullMaxSignal =
        flatAgeMs >= flatBullMaxMs &&
        regimeSnap?.regime === REGIMES.BULL_ACTIVE;
      const flatReentryReady =
        flatBook &&
        isWrappedStableMarket(key) &&
        derivedFlatSince > 0 &&
        (cfg.compoundSizingEnabled !== false && compoundBuySizingEstimate
          ? compoundBuySizingEstimate.allowed
          : book.baseBalance >= Number(cfg.minTradeUsd ?? defaultBotCfg.minTradeUsd)) &&
        flatSafetyConfirmed &&
        flatCooldownsClear &&
        (flatNearMissSignal || flatForcedSignal || flatBullMaxSignal);
      const flatReentryReason = flatBullMaxSignal
        ? "BULL_MAX_WAIT"
        : flatForcedSignal
        ? "FORCED_REVIEW"
        : flatNearMissSignal
        ? "NEAR_MISS"
        : null;

      if (regimeBuyGate.blocked && priceConditionOk) {
        console.log(`[BUY BLOCKED][${botInstanceId.toUpperCase()}] ${key} regime gate:`, {
          regime: regimeSnap?.regime,
          blocks: regimeBuyGate.blocks?.map((b) => b.message).join("; "),
        });
      }

      if (buyProtection.enabled && buyProtection.blocked && priceConditionOk) {
        if (buyProtection.kind === "recoveryCooldown") {
          console.log(
            `[BUY BLOCKED][${botInstanceId.toUpperCase()}] ${key} recovery buy cooldown active until ${new Date(
              buyProtection.recoveryBuyBlockedUntil
            ).toLocaleString()}`
          );
        } else if (buyProtection.kind === "maxConsecutive") {
          console.log(`[BUY BLOCKED][${botInstanceId.toUpperCase()}] ${key} max consecutive buys reached. Waiting for SELL or reset.`, {
            consecBuys: buyProtection.consecBuys,
            maxBuys: buyProtection.maxBuys,
            lastBuyPrice: pos.lastBuyPrice,
          });
        } else if (buyProtection.kind === "fourthMajorDip") {
          console.log(
            `[BUY BLOCKED][${botInstanceId.toUpperCase()}] ${key} 4th consecutive buy requires major dip. current=${safeFix(
              price
            )}, required<=${safeFix(buyProtection.majorDipRequiredPrice)}, lastBuy=${safeFix(
              pos.lastBuyPrice || buyProtection.majorDipRefPrice
            )}`
          );
        } else if (buyProtection.kind === "progressiveSpacing") {
          console.log(
            `[BUY BLOCKED][${botInstanceId.toUpperCase()}] ${key} progressive ladder requires ${(buyProtection.progressiveStepPct * 100).toFixed(2)}% below last buy (need <= ${safeFix(buyProtection.progressiveRequiredPrice)}, now ${safeFix(price)})`
          );
        }
      }

      const buyProtectionBlocksBuy =
        buyProtection.enabled &&
        buyProtection.blocked &&
        (!flatBook || buyProtection.kind === "recoveryCooldown");
      const postSellCooldownMs = Math.max(
        0,
        Number(cfg.postSellBuyCooldownMs ?? defaultBotCfg.postSellBuyCooldownMs) || 0
      );
      const lastSellTs = Math.max(
        Number(pos.lastSellAt || 0),
        Number(lastSellAtRef.current[key] || 0)
      );
      const lastSellPrice = Number(pos.lastSellPrice || 0);
      const fullBuyStepPct = Math.max(
        0.001,
        Number(cfg.adaptiveBuyMovePct || (1 - Number(cfg.buyTarget || defaultBotCfg.buyTarget))) || 0
      );
      const postSellPriceBypass =
        lastSellPrice > 0 && price <= lastSellPrice * (1 - fullBuyStepPct);
      const postSellCooldownClear =
        lastSellTs <= 0 ||
        Date.now() - lastSellTs >= postSellCooldownMs ||
        postSellPriceBypass;
      const phase2BuyMaxNudgeBps =
        Math.max(0, Number(cfg.adaptiveBuyMovePct) || 0) *
        10000 *
        clampNumber(
          Number(cfg.externalSignalPhase2MaxAdjustmentPct ?? defaultBotCfg.externalSignalPhase2MaxAdjustmentPct),
          0,
          0.1
        );
      const phase2BuyCandidate =
        isWrappedStableMarket(key) &&
        cfg.externalSignalEnabled !== false &&
        cfg.externalSignalPhase2ThresholdEnabled !== false &&
        !flatBook &&
        cfg.usePercentMode &&
        originalBuyTarget > 0 &&
        price <= originalBuyTarget * (1 + phase2BuyMaxNudgeBps / 10000);
      const phase2BuyNonPriceReady =
        !killSwitch &&
        baseBalance > 0 &&
        usdcReserveOk &&
        !buyProtectionBlocksBuy &&
        !regimeBuyGate.blocked &&
        postSellCooldownClear &&
        timeOk &&
        !isTradeLocked &&
        !hasPendingTrade;
      if (phase2BuyCandidate && phase2BuyNonPriceReady && plannedBuyUsdEst > 0) {
        let phase2BuyAvailableUsd = Math.max(0, book.baseBalance);
        if (book.totalBookUsd > 0) {
          const reserveFloorUsd = Math.max(0, Number(cfg.minUsdcReservePct) || 0) * book.totalBookUsd;
          phase2BuyAvailableUsd = Math.min(
            phase2BuyAvailableUsd,
            Math.max(0, book.baseBalance - reserveFloorUsd)
          );
          const maxExposurePct = Number(cfg.maxWethExposurePct);
          if (maxExposurePct > 0 && maxExposurePct <= 1) {
            phase2BuyAvailableUsd = Math.min(
              phase2BuyAvailableUsd,
              Math.max(0, maxExposurePct * book.totalBookUsd - book.holdingsUsd)
            );
          }
        }
        externalBuyPreview = await evaluateExternalTradeSizing({
          marketKey: key,
          side: "BUY",
          baseTradeAmount: plannedBuyUsdEst,
          baseTradeUsd: plannedBuyUsdEst,
          poolPrice: price,
          availableUsd: phase2BuyAvailableUsd,
          cfg,
          thresholdContext: {
            originalTargetPrice: originalBuyTarget,
            candidateKind: "near-buy-target",
            regime: regimeSnap?.regime,
            avgEntry: pos.avgEntry,
            holdingsWeth: pos.holdings,
            usdcBalance: book.baseBalance,
            costBasis: pnlDataRef.current[key]?.costBasis,
          },
        });
        effectiveBuyTarget = Number(externalBuyPreview.adjustedTargetPrice) || originalBuyTarget;
        priceConditionOk = hasHoldings
          ? isPriceLess(price, effectiveBuyTarget)
          : (effectiveBuyTarget > 0
              ? (isPriceLess(price, effectiveBuyTarget) || isPriceLess(price, anchor) || isPriceEqual(price, anchor))
              : true);
        justCrossedEdge = prev !== undefined
          ? (isPriceGreater(prev, effectiveBuyTarget) || isPriceEqual(prev, effectiveBuyTarget)) &&
            isPriceLess(price, effectiveBuyTarget)
          : false;
        crossedEdge = justCrossedEdge;
        distanceFromTarget = priceConditionOk && effectiveBuyTarget > 0
          ? (effectiveBuyTarget - price) / effectiveBuyTarget
          : 0;
        isSignificantlyBelowTarget = distanceFromTarget >= PRICE_NOISE_THRESHOLD;
        isSignificantMove = hasHoldings
          ? (justCrossedEdge || isSignificantlyBelowTarget || prev === undefined)
          : (priceConditionOk || prev === undefined);
      }
      const normalBuySignal = priceConditionOk && isSignificantMove;
      const buyReady =
        !killSwitch &&
        baseBalance > 0 &&
        usdcReserveOk &&
        !buyProtectionBlocksBuy &&
        !regimeBuyGate.blocked &&
        postSellCooldownClear &&
        (normalBuySignal || flatReentryReady) &&
        timeOk &&
        !isTradeLocked &&
        !hasPendingTrade;

      // Report why BUY is blocked if price condition is met but trade isn't happening
      // Only log if price is actually below buy target (priceConditionOk = true)
      if ((priceConditionOk || flatReentryReady) && !buyReady) {
        const reasons = [];
        if (killSwitch) reasons.push("Kill switch is ON");
        if (baseBalance <= 0) reasons.push(`No base balance (${baseBalance.toFixed(6)} ${key.includes('USDC') ? stableSymbol : nativeSymbol})`);
        if (!usdcReserveOk) {
          reasons.push(
            `USDC reserve below ${(reservePct * 100).toFixed(0)}% (${((book.baseBalance / Math.max(book.totalBookUsd, 1)) * 100).toFixed(1)}% of book)`
          );
        }
        if (buyProtectionBlocksBuy) {
          if (buyProtection.kind === "recoveryCooldown") {
            reasons.push(
              `Recovery buy cooldown: ${Math.max(0, Math.ceil((buyProtection.recoveryBuyBlockedUntil - Date.now()) / 60000))}m remaining`
            );
          } else if (buyProtection.kind === "maxConsecutive") {
            reasons.push(`Max consecutive buys reached (${buyProtection.consecBuys}/${buyProtection.maxBuys})`);
          } else if (buyProtection.kind === "fourthMajorDip") {
            reasons.push(
              `4th buy requires major dip (need <= ${safeFix(buyProtection.majorDipRequiredPrice)}, now ${safeFix(price)})`
            );
          } else if (buyProtection.kind === "progressiveSpacing") {
            reasons.push(
              `Progressive buy step ${(buyProtection.progressiveStepPct * 100).toFixed(2)}% (need <= ${safeFix(buyProtection.progressiveRequiredPrice)})`
            );
          } else {
            reasons.push("Buy protection blocked");
          }
        }
        if (!postSellCooldownClear) {
          reasons.push(
            `Post-sell cooldown: ${Math.max(0, Math.ceil((postSellCooldownMs - (Date.now() - lastSellTs)) / 60000))}m remaining`
          );
        }
        if (regimeBuyGate.blocked && regimeBuyGate.blocks?.length) {
          regimeBuyGate.blocks.forEach((b) => reasons.push(b.message));
        }
        if (!crossedEdge) {
          if (!priceConditionOk) {
            const edgeStatus = isWcroUsdc 
              ? `Price below target (current: ${price.toFixed(6)}, target: ${pos.priceTargetBuy.toFixed(6)})`
              : `Price above target (current: ${price.toFixed(6)}, target: ${pos.priceTargetBuy.toFixed(6)})`;
            reasons.push(edgeStatus);
          } else if (prev === undefined) {
            reasons.push("No previous price data (waiting for first price update)");
          } else {
            reasons.push(`Price condition met but edge check failed (prev: ${prev?.toFixed(6)}, current: ${price.toFixed(6)}, target: ${pos.priceTargetBuy.toFixed(6)})`);
          }
        }
        if (!timeOk) {
          const remainingMs = minBuySpacingMs - timeSinceLastBuyReceipt;
          const remainingSec = (remainingMs / 1000).toFixed(1);
          reasons.push(`Waiting for cooldown: ${remainingSec}s remaining (${minBuySpacingMs}ms required)`);
        }
        if (isTradeLocked) reasons.push("Trade settlement lock active");
        if (!isSignificantMove && prev !== undefined) {
          const priceChangePct = (priceChange * 100).toFixed(3);
          reasons.push(`Price move too small (${priceChangePct}% < ${(PRICE_NOISE_THRESHOLD * 100).toFixed(2)}% threshold)`);
        }
        
          if (reasons.length > 0) {
            // Debug: Calculate actual values to understand why buys are blocked
            const debugDistance = priceConditionOk && pos.priceTargetBuy > 0
              ? (pos.priceTargetBuy - price) / pos.priceTargetBuy
              : 0;
            const debugIsSignificantlyBelow = debugDistance >= PRICE_NOISE_THRESHOLD;
            
            console.log(`[BUY BLOCKED][${botInstanceId.toUpperCase()}] ${key} - Price below target but trade blocked:`, {
              botInstanceId,
              price: price.toFixed(6),
              target: pos.priceTargetBuy.toFixed(6),
              prev: prev !== undefined ? prev.toFixed(6) : "undefined",
              justCrossedEdge,
              priceConditionOk,
              distanceFromTarget: (debugDistance * 100).toFixed(3) + "%",
              isSignificantlyBelowTarget: debugIsSignificantlyBelow,
              isSignificantMove,
              priceChange: (priceChange * 100).toFixed(3) + "%",
              buyProtection: buyProtection.enabled
                ? {
                    blocked: buyProtection.blocked,
                    kind: buyProtection.kind,
                    consecBuys: buyProtection.consecBuys,
                    maxBuys: buyProtection.maxBuys,
                    recoveryBuyBlockedUntil: buyProtection.recoveryBuyBlockedUntil,
                    lastBuyPrice: pos.lastBuyPrice,
                    majorDipRequiredPrice: buyProtection.majorDipRequiredPrice,
                    majorDipRequiredPct: buyProtection.majorDipRequiredPct,
                  }
                : { enabled: false },
              regime: regimeSnap?.regime,
              regimeBuyBlocked: regimeBuyGate.blocked,
              regimeBuyBlocks: regimeBuyGate.blocks?.map((b) => b.kind),
              reasons: reasons.join("; ")
            });
          }
          
          // Debug logging moved to after amountToBuy is calculated (line ~1640)
      }

      if (buyReady) {
        if (!isTradeLocked) {
          tradeLockRef.current[key] = true;
          // Reduced from 7000ms to 2500ms for Arbitrum's fast block times (~250ms blocks, 1 confirmation + buffer)
          setTimeout(() => { tradeLockRef.current[key] = false; }, 2500);
          
          // CRITICAL FIX: Calculate amountToBuy BEFORE using it (fixes temporal dead zone error)
          // Use buyMultiplier directly (no tiers)
          const buyMultiplierPct = cfg.buyMultiplier || 0.1;

          let amountToBuy;
          let compoundBuySizing = null;
          if (cfg.usePercentMode) {
            // USD-based trade limits for percent mode
            const wrappedSymbol = chainConfig.nativeCurrency.symbol;
            const stableSymbol = chainConfig.defaultStablecoin;
            
            // Determine base token price in USD
            let basePriceUsd = 1; // Default for stablecoins (USDC.e = $1)
            if (key === `${wrappedSymbol}-${stableSymbol}` || key === `WBTC-${stableSymbol}`) {
              // Base is stablecoin (USDC.e), price is $1
              basePriceUsd = 1;
            } else {
              // Base is native token, get price from positions
              const nativePrice = positions[nativeSymbol]?.price || 0;
              basePriceUsd = nativePrice > 0 ? nativePrice : 1;
            }
            
            // Calculate base balance in USD
            const baseBalanceUsd = baseBalance * basePriceUsd;
            
            let clampedUsd;
            if (isWrappedStableMarket(key) && cfg.compoundSizingEnabled !== false) {
              compoundBuySizing = getCompoundSizingPlan({
                key,
                book,
                cfg,
                availableUsd: baseBalanceUsd,
              });
              clampedUsd = compoundBuySizing.allowed
                ? flatReentryReady
                  ? compoundBuySizing.minimumRelaxed
                    ? compoundBuySizing.baseTradeUsd
                    : compoundBuySizing.effectiveMinTradeUsd
                  : compoundBuySizing.baseTradeUsd
                : 0;
              console.log(`[COMPOUND SIZING][${botInstanceId.toUpperCase()}] BUY`, {
                marketKey: key,
                sizingEquityUsd: compoundBuySizing.sizingEquityUsd,
                realizedSinceCheckpointUsd: compoundBuySizing.realizedSinceCheckpointUsd,
                baseTradeUsd: compoundBuySizing.baseTradeUsd,
                hardMaxUsd: compoundBuySizing.hardMaxUsd,
                configuredMinTradeUsd: compoundBuySizing.economicMinTradeUsd,
                effectiveMinTradeUsd: compoundBuySizing.effectiveMinTradeUsd,
                allowed: compoundBuySizing.allowed,
                reason: compoundBuySizing.reason,
              });
            } else {
              const percentUsd = baseBalanceUsd * buyMultiplierPct;
              const minTradeUsd = Number(cfg.minTradeUsd || defaultBotCfg.minTradeUsd);
              const maxTradeUsd = Number(cfg.maxTradeUsd || defaultBotCfg.maxTradeUsd);
              clampedUsd = flatReentryReady
                ? Math.min(minTradeUsd, maxTradeUsd)
                : Math.min(Math.max(minTradeUsd, percentUsd), maxTradeUsd);
            }
            
            // Convert USD back to token amount
            amountToBuy = clampedUsd / basePriceUsd;
          } else {
            amountToBuy = Number(cfg.manualBuy || 0);
          }

          if (cfg.maxBaseCap && Number(cfg.maxBaseCap) > 0) {
            amountToBuy = Math.min(amountToBuy, Number(cfg.maxBaseCap));
          }

          amountToBuy = Math.max(0, Math.min(amountToBuy, baseBalance));
          if (!(amountToBuy > 0)) {
            console.log(`[BUY SKIPPED][${botInstanceId.toUpperCase()}] ${key} - no executable amount`, {
              baseBalance,
              sizingReason: compoundBuySizing?.reason,
              sizingEquityUsd: compoundBuySizing?.sizingEquityUsd,
              economicMinTradeUsd: compoundBuySizing?.economicMinTradeUsd,
              hardMaxUsd: compoundBuySizing?.hardMaxUsd,
            });
            tradeLockRef.current[key] = false;
            prevPriceRef.current[key] = price;
            return;
          }
          const stableMarketKey = `${chainConfig.nativeCurrency.symbol}-${chainConfig.defaultStablecoin}`;
          const buyBasePriceUsd = key === stableMarketKey ? 1 : Math.max(1, Number(positions[nativeSymbol]?.price) || 1);
          let externalBuyAvailableUsd = baseBalance * buyBasePriceUsd;
          if (key === stableMarketKey && book.totalBookUsd > 0) {
            const reserveFloorUsd = Math.max(0, Number(cfg.minUsdcReservePct) || 0) * book.totalBookUsd;
            externalBuyAvailableUsd = Math.min(
              externalBuyAvailableUsd,
              Math.max(0, book.baseBalance - reserveFloorUsd)
            );
            const maxExposurePct = Number(cfg.maxWethExposurePct);
            if (maxExposurePct > 0 && maxExposurePct <= 1) {
              externalBuyAvailableUsd = Math.min(
                externalBuyAvailableUsd,
                Math.max(0, maxExposurePct * book.totalBookUsd - book.holdingsUsd)
              );
            }
          }
          const externalBuy = await evaluateExternalTradeSizing({
            marketKey: key,
            side: "BUY",
            baseTradeAmount: amountToBuy,
            baseTradeUsd: amountToBuy * buyBasePriceUsd,
            poolPrice: price,
            availableUsd: externalBuyAvailableUsd,
            cfg: compoundBuySizing
              ? {
                  ...cfg,
                  minTradeUsd: compoundBuySizing.effectiveMinTradeUsd,
                  maxTradeUsd: compoundBuySizing.hardMaxUsd,
                }
              : cfg,
            bypassReason: flatReentryReady
              ? "Guarded flat re-entry sizing is preserved."
              : !cfg.usePercentMode
                ? "Manual amount mode is never externally resized."
                : "",
            thresholdContext: {
              originalTargetPrice: originalBuyTarget,
              candidateKind: "buy-trigger",
              regime: regimeSnap?.regime,
              avgEntry: pos.avgEntry,
              holdingsWeth: pos.holdings,
              usdcBalance: book.baseBalance,
              costBasis: pnlDataRef.current[key]?.costBasis,
            },
          });
          if (
            !flatReentryReady &&
            Number(externalBuy.adjustedTargetPrice) > 0 &&
            !isPriceLess(price, Number(externalBuy.adjustedTargetPrice))
          ) {
            tradeLockRef.current[key] = false;
            prevPriceRef.current[key] = price;
            console.log("[PHASE 2 BUY DELAYED]", {
              marketKey: key,
              price,
              originalTarget: originalBuyTarget,
              adjustedTarget: externalBuy.adjustedTargetPrice,
              adjustmentBps: externalBuy.decision?.thresholdAdjustmentBps || 0,
              episodeId: externalBuy.decision?.candidateEpisodeId || "",
            });
            return;
          }
          amountToBuy = Math.min(externalBuy.finalTradeUsd, externalBuyAvailableUsd) / buyBasePriceUsd;
          if (cfg.maxBaseCap && Number(cfg.maxBaseCap) > 0) {
            amountToBuy = Math.min(amountToBuy, Number(cfg.maxBaseCap));
          }
          amountToBuy = Math.max(0, Math.min(amountToBuy, baseBalance));
          if (!(amountToBuy > 0)) {
            console.log(`[BUY SKIPPED][${botInstanceId.toUpperCase()}] ${key} - no amount after sizing and exposure limits`, {
              baseBalance,
              availableUsd: externalBuyAvailableUsd,
              finalTradeUsd: externalBuy.finalTradeUsd,
              sizingReason: compoundBuySizing?.reason,
            });
            tradeLockRef.current[key] = false;
            prevPriceRef.current[key] = price;
            return;
          }
          if (flatReentryReady) {
            console.log("[FLAT REENTRY TRIGGERED]", {
              marketKey: key,
              reason: flatReentryReason,
              flatHours: flatAgeMs / (60 * 60 * 1000),
              price,
              buyTarget: pos.priceTargetBuy,
              targetGapBps,
              regime: regimeSnap?.regime,
              trend15m,
              oneHourPosition,
              amountUsd: amountToBuy,
            });
          }

          // CRITICAL FIX: Track trade submission AFTER amountToBuy is calculated
          // This fixes the "Cannot access 'amountToBuy' before initialization" error
          pendingTradeSubmissionsRef.current[key] = {
            side: "BUY",
            submittedAt: Date.now(),
            amount: amountToBuy,
          };

          // Debug: Log when buy executes to track target changes
          const debugBuyPct = cfg.buyTarget ?? defaultBotCfg.buyTarget;
          const estimatedQty = amountToBuy / Math.max(price, DUST);
          const estimatedNewHoldings = (pos.holdings || 0) + estimatedQty;
          const estimatedNewAvgEntry = (pos.holdings || 0) > 0
            ? ((pos.avgEntry || 0) * (pos.holdings || 0) + price * estimatedQty) / Math.max(estimatedNewHoldings, DUST)
            : price;
          const debugAnchorForTargets = estimatedNewAvgEntry > 0 ? estimatedNewAvgEntry : (pos.avgEntry || price);
          const estimatedNewTarget = debugAnchorForTargets * debugBuyPct;
          
          console.log(`[BUY TRIGGERED] ${key}:`, {
            price: price.toFixed(6),
            currentTarget: pos.priceTargetBuy.toFixed(6),
            prev: prev !== undefined ? prev.toFixed(6) : "undefined",
            justCrossedEdge,
            amountToBuy: amountToBuy.toFixed(6),
            estimatedNewTarget: estimatedNewTarget.toFixed(6),
            targetChange: pos.priceTargetBuy > 0 ? ((estimatedNewTarget - pos.priceTargetBuy) / pos.priceTargetBuy * 100).toFixed(3) + "%" : "N/A"
          });

          // Create snapshot for rollback capability (matches forced trades)
          const snapshot = {
            holdings: pos.holdings, avgEntry: pos.avgEntry,
            priceTargetBuy: pos.priceTargetBuy, priceTargetSell: pos.priceTargetSell,
            buyCounter: pos.buyCounter, sellCounter: pos.sellCounter,
            lastTradeAt: pos.lastTradeAt,
            lastBuyAt: pos.lastBuyAt,
            lastBuyPrice: pos.lastBuyPrice,
            lastSellAt: pos.lastSellAt,
            lastSellPrice: pos.lastSellPrice,
            flatSince: pos.flatSince,
            fullExitPrice: pos.fullExitPrice,
            fullExitTxHash: pos.fullExitTxHash,
            flatReentryNearMissSince: pos.flatReentryNearMissSince,
            lastFlatReentryAt: pos.lastFlatReentryAt,
          };
          const pendingId = startPending(key, snapshot);
          const priorLastBuyRef = Number(lastBuyAtRef.current[key] || 0);
          const priorLastTradeRef = Number(lastTradeAtRef.current[key] || 0);

          // Determine actualInitiator and tokenToUse (matches forced trades)
          // Note: wrappedSymbol, stableSymbol, and nativeSymbol are already defined above
          let actualInitiator;
          let tokenToUse;
          
          if (key === `${wrappedSymbol}-${stableSymbol}`) {
            // Buying wrapped native (WETH/WCRO) with stablecoin (USDC)
            actualInitiator = stableSymbol;
            tokenToUse = currentConfig.wrappedNativeToken;
          } else if (key === `WBTC-${wrappedSymbol}`) {
            // Buying WBTC with wrapped native
            actualInitiator = nativeSymbol;
            tokenToUse = token;
          } else if (key === `WBTC-${stableSymbol}`) {
            // Buying WBTC with stablecoin
            actualInitiator = stableSymbol;
            tokenToUse = token;
          } else {
            // Fallback
            actualInitiator = initiator;
            tokenToUse = token;
          }

          // Calculate quantity received: For WETH-USDC, amountToBuy is in USDC, price is USDC per WETH
          // So: WETH received = USDC spent / (USDC per WETH) = USDC / price
          // For other markets: amountToBuy is in base token, price is target per base, so divide
          const qty = amountToBuy / Math.max(price, DUST);
          const newHoldings = (pos.holdings || 0) + qty;
          const newAvgEntry =
            (pos.holdings || 0) > 0
              ? ((pos.avgEntry || 0) * (pos.holdings || 0) + price * qty) / Math.max(newHoldings, DUST)
              : price;

          const buyPct  = cfg.buyTarget  ?? defaultBotCfg.buyTarget;
          const sellPct = cfg.sellTarget ?? defaultBotCfg.sellTarget;
          const postBuyBook = getMarketBook(
            key,
            { ...pos, holdings: newHoldings, avgEntry: newAvgEntry },
            price,
            positionsRef.current,
            chainConfig
          );
          const postBuySpot = shouldUseSpotGridMode({
            cfg,
            pos: { ...pos, holdings: newHoldings, avgEntry: newAvgEntry },
            price,
            baseBalanceUsd: postBuyBook.baseBalance,
            totalBookUsd: postBuyBook.totalBookUsd,
          });
          const gridAfterBuy = computeGridTargets({
            price,
            avgEntry: newAvgEntry,
            buyPct,
            sellPct,
            useSpotAnchor: false,
          });
          if (postBuySpot) {
            const spotBuyGrid = computeGridTargets({
              price,
              avgEntry: newAvgEntry,
              buyPct,
              sellPct,
              useSpotAnchor: true,
            });
            gridAfterBuy.priceTargetBuy = spotBuyGrid.priceTargetBuy;
          }
          gridAfterBuy.priceTargetSell = Math.max(
            gridAfterBuy.priceTargetSell,
            getNormalSellFloorPrice({
              avgEntry: newAvgEntry,
              minProfitBps: cfg.normalSellMinProfitBps,
            })
          );

          updatePosition(key, {
            holdings: newHoldings,
            avgEntry: newAvgEntry,
            priceTargetBuy: gridAfterBuy.priceTargetBuy,
            priceTargetSell: gridAfterBuy.priceTargetSell,
            buyCounter: (pos.buyCounter || 1) + 1,
            sellCounter: 1,
            lastBuyPrice: price,
            flatSince: 0,
            flatReentryNearMissSince: 0,
            lastFlatReentryAt: flatReentryReady ? Date.now() : (pos.lastFlatReentryAt || 0),
          });

          lastBuyAtRef.current[key] = Date.now();
          lastTradeAtRef.current[key] = Date.now(); // Track last trade timestamp

          // Log the decision point for debugging
          const baseKeyStable = `${wrappedSymbol}-${stableSymbol}_BASE`;
          console.log("[AUTO BUY]", {
            key,
            initiator,
            actualInitiator,
            baseBalance,
            baseKeyStable,
            baseStable: positions[baseKeyStable]?.holdings,
            baseWrapped: positions[`${wrappedSymbol}-${stableSymbol}`]?.holdings,
            buyMultiplierPct,
            amountToBuy,
            tokenToUse,
            flatReentry: flatReentryReady,
            flatReentryReason,
            flatHours: flatAgeMs > 0 ? flatAgeMs / (60 * 60 * 1000) : 0,
            targetGapBps,
          });

          // Execute trade with correct initiator and token (matches forced trades)
          try {
            const succeeded = await executeBlockchainTrade("BUY", amountToBuy.toString(), tokenToUse, actualInitiator, {
              marketKey: key,
              pendingId,
              fromFlatReentry: flatReentryReady,
              flatReentryReason,
              externalSignal: externalBuy.decision,
            });
            if (!succeeded) {
              lastBuyAtRef.current[key] = priorLastBuyRef;
              lastTradeAtRef.current[key] = priorLastTradeRef;
            }
          } catch (error) {
            console.error(`[Automated BUY] Error executing trade:`, error);
            if (pendingId) rollbackPending(key, pendingId);
            lastBuyAtRef.current[key] = priorLastBuyRef;
            lastTradeAtRef.current[key] = priorLastTradeRef;
            // Clear pending trade submission on failure
            delete pendingTradeSubmissionsRef.current[key];
            console.log(`[Automated BUY] Failed attempt rolled back; confirmed position and targets were preserved`);
          }
        }
        prevPriceRef.current[key] = price;
        return;
      }

      // ---------- SELL ----------
      // For WCRO-USDC: Sell when price > priceTargetSell (price rises, CRO gets expensive)
      // For other markets: Sell when price > priceTargetSell (normal)
      // Removed profitGateOk - redundant with sellTarget which already ensures profit
      const originalSellTarget = Number(pos.priceTargetSell) || 0;
      let effectiveSellTarget = originalSellTarget;
      let externalSellPreview = null;
      let targetSellCrossed = price > effectiveSellTarget;
      const pnlHoldingsForFloor = Number(pnlDataRef.current[key]?.holdings) || 0;
      const pnlCostBasisForFloor = Number(pnlDataRef.current[key]?.costBasis) || 0;
      const pnlAvgEntryForFloor =
        pnlHoldingsForFloor > DUST && pnlCostBasisForFloor > 0
          ? pnlCostBasisForFloor / pnlHoldingsForFloor
          : 0;
      const economicAvgEntry = pnlAvgEntryForFloor > 0
        ? pnlAvgEntryForFloor
        : Number(pos.avgEntry) || 0;
      const wrappedStableMarket = isWrappedStableMarket(key);
      const estimatedHoldingsUsdForSell = wrappedStableMarket
        ? Math.max(0, Number(pos.holdings) || 0) * price
        : 0;
      const compoundSellSizing =
        wrappedStableMarket && cfg.usePercentMode && cfg.compoundSizingEnabled !== false
          ? getCompoundSizingPlan({
              key,
              book,
              cfg,
              availableUsd: estimatedHoldingsUsdForSell,
            })
          : null;
      const sellEconomicMinUsd = compoundSellSizing
        ? compoundSellSizing.economicMinTradeUsd
        : Number(cfg.minTradeUsd || defaultBotCfg.minTradeUsd);
      // Sells reduce exposure, so a small account may still sell the economic
      // minimum even when that is above the normal 25% position-size ceiling.
      const sellTradeMaxUsd = compoundSellSizing
        ? Math.min(
            estimatedHoldingsUsdForSell,
            Math.max(sellEconomicMinUsd, compoundSellSizing.hardMaxUsd)
          )
        : Number(cfg.maxTradeUsd || defaultBotCfg.maxTradeUsd);
      const estimatedNormalSellUsd = wrappedStableMarket
        ? Math.min(
            estimatedHoldingsUsdForSell,
            sellTradeMaxUsd,
            Math.max(
              sellEconomicMinUsd,
              estimatedHoldingsUsdForSell * Number(cfg.sellMultiplier || defaultBotCfg.sellMultiplier)
            )
          )
        : 0;
      const recentGas = recentGasCostUsdRef.current[key];
      const recentGasUsd =
        recentGas && Date.now() - Number(recentGas.observedAt || 0) <= 24 * 60 * 60 * 1000
          ? Number(recentGas.gasUsd)
          : Number(cfg.normalSellFallbackGasUsd ?? defaultBotCfg.normalSellFallbackGasUsd);
      const normalSellEconomics = wrappedStableMarket
        ? getCostAwareNormalSellFloor({
            avgEntry: economicAvgEntry,
            tradeUsd: estimatedNormalSellUsd,
            estimatedGasUsd: recentGasUsd,
            baseMinProfitBps: cfg.normalSellMinProfitBps,
            exitFeeBps: cfg.normalSellExitFeeBps,
            expectedSlippageBps: cfg.normalSellExpectedSlippageBps,
            expectedPriceImpactBps: cfg.normalSellExpectedPriceImpactBps,
            requiredNetProfitBps: cfg.normalSellRequiredNetProfitBps,
            fallbackGasUsd: cfg.normalSellFallbackGasUsd,
          })
        : {
            floorPrice: getNormalSellFloorPrice({
              avgEntry: economicAvgEntry,
              minProfitBps: cfg.normalSellMinProfitBps,
            }),
            requiredProfitBps: Number(cfg.normalSellMinProfitBps) || 0,
            exitCostBps: 0,
            gasBps: 0,
            gasUsd: 0,
            tradeUsd: 0,
          };
      const normalSellFloorPrice = normalSellEconomics.floorPrice;
      const normalSellFloorOk =
        !wrappedStableMarket ||
        !(normalSellFloorPrice > 0) ||
        price >= normalSellFloorPrice;
      let coreSellOk = targetSellCrossed && normalSellFloorOk;
      
      // Check spacing based on receipt confirmation time, not submission time
      const lastSellReceiptTs = lastTradeReceiptAtRef.current[key] || lastSellAtRef.current[key] || 0;
      const minSellSpacingMs = Math.max(4000, 4000); // Minimum 4 seconds for Arbitrum's fast blocks (~250ms blocks, 1 confirmation + buffer)
      const timeSinceLastSellReceipt = Date.now() - lastSellReceiptTs;
      const sellTimeOk = timeSinceLastSellReceipt >= minSellSpacingMs;

      const normalSellWillClearBook =
        wrappedStableMarket &&
        estimatedHoldingsUsdForSell <= sellTradeMaxUsd + 0.01;
      const duplicateSellGuard = wrappedStableMarket
        ? evaluateNormalSellDuplicateGuard({
            now: Date.now(),
            lastSellAt: pos.lastNormalSellAt,
            lastSellPrice: pos.lastNormalSellPrice,
            currentPrice: price,
            cooldownMs: cfg.normalSellDuplicateCooldownMs,
            minAdvanceBps: cfg.normalSellDuplicateMoveBps,
            bypass: normalSellWillClearBook,
          })
        : { allowed: true, bypassed: true };
      
      // Also check if there's a pending trade submission that hasn't been confirmed yet
      const pendingSellTrade = pendingTradeSubmissionsRef.current[key];
      const hasPendingSellTrade = pendingSellTrade?.side === "SELL";
      
      const isSellTradeLocked = tradeLockRef.current[key] === true || hasPendingSellTrade;
      const sellFailureKey = `${key}:SELL`;
      const sellFailureCooldownMs = 10000;
      const sellFailureCooldownRemainingMs = Math.max(
        0,
        sellFailureCooldownMs - (Date.now() - Number(lastTradeFailureAtRef.current[sellFailureKey] || 0))
      );
      const sellFailureCooldownActive = sellFailureCooldownRemainingMs > 0;
      
      // Price noise filter for sells: Only trade on significant moves (>= 0.05%)
      // BUT allow if price just crossed target OR is significantly past target
      // Note: PRICE_NOISE_THRESHOLD is already defined above for buys
      const sellPriceChange = prev !== undefined ? Math.abs((price - prev) / prev) : 0;
      
      // Check if price just crossed the sell target (from below to above)
      let justCrossedSellEdge = prev !== undefined && effectiveSellTarget > 0
        ? (prev <= effectiveSellTarget && price > effectiveSellTarget)
        : false;
      
      // Check if price is significantly past the sell target (allows trades even with small tick changes)
      let distanceFromSellTarget = coreSellOk && effectiveSellTarget > 0
        ? Math.abs((price - effectiveSellTarget) / effectiveSellTarget)
        : 0;
      let isSignificantlyPastSellTarget = distanceFromSellTarget >= PRICE_NOISE_THRESHOLD;
      
      // Allow trade if: significant tick change OR just crossed target OR significantly past target
      // Note: The Math.max logic in sell target calculation already prevents negative percentages
      let isSignificantSellMove = sellPriceChange >= PRICE_NOISE_THRESHOLD || prev === undefined || justCrossedSellEdge || isSignificantlyPastSellTarget;

      const phase2SellMaxNudgeBps =
        Math.max(0, Number(cfg.adaptiveSellMovePct) || 0) *
        10000 *
        clampNumber(
          Number(cfg.externalSignalPhase2MaxAdjustmentPct ?? defaultBotCfg.externalSignalPhase2MaxAdjustmentPct),
          0,
          0.1
        );
      const phase2SellCandidate =
        wrappedStableMarket &&
        cfg.externalSignalEnabled !== false &&
        cfg.externalSignalPhase2ThresholdEnabled !== false &&
        cfg.usePercentMode &&
        !normalSellWillClearBook &&
        originalSellTarget > 0 &&
        price >= originalSellTarget * (1 - phase2SellMaxNudgeBps / 10000);
      const phase2SellNonPriceReady =
        !killSwitch &&
        (pos.holdings || 0) > 0 &&
        (pos.sellCounter || 1) < 10 &&
        normalSellFloorOk &&
        sellTimeOk &&
        duplicateSellGuard.allowed &&
        !isSellTradeLocked &&
        !sellFailureCooldownActive;
      if (phase2SellCandidate && phase2SellNonPriceReady && estimatedNormalSellUsd > 0) {
        externalSellPreview = await evaluateExternalTradeSizing({
          marketKey: key,
          side: "SELL",
          baseTradeAmount: estimatedNormalSellUsd / Math.max(price, DUST),
          baseTradeUsd: estimatedNormalSellUsd,
          poolPrice: price,
          availableUsd: estimatedHoldingsUsdForSell,
          cfg: compoundSellSizing
            ? {
                ...cfg,
                minTradeUsd: sellEconomicMinUsd,
                maxTradeUsd: sellTradeMaxUsd,
              }
            : cfg,
          thresholdContext: {
            originalTargetPrice: originalSellTarget,
            economicFloorPrice: normalSellFloorPrice,
            candidateKind: "near-sell-target",
            regime: marketRegimeRef.current[key]?.regime,
            avgEntry: economicAvgEntry,
            holdingsWeth: pos.holdings,
            usdcBalance: book.baseBalance,
            costBasis: pnlCostBasisForFloor,
          },
        });
        effectiveSellTarget = Number(externalSellPreview.adjustedTargetPrice) || originalSellTarget;
        targetSellCrossed = price > effectiveSellTarget;
        coreSellOk = targetSellCrossed && normalSellFloorOk;
        justCrossedSellEdge = prev !== undefined && effectiveSellTarget > 0
          ? (prev <= effectiveSellTarget && price > effectiveSellTarget)
          : false;
        distanceFromSellTarget = coreSellOk && effectiveSellTarget > 0
          ? Math.abs((price - effectiveSellTarget) / effectiveSellTarget)
          : 0;
        isSignificantlyPastSellTarget = distanceFromSellTarget >= PRICE_NOISE_THRESHOLD;
        isSignificantSellMove =
          sellPriceChange >= PRICE_NOISE_THRESHOLD ||
          prev === undefined ||
          justCrossedSellEdge ||
          isSignificantlyPastSellTarget;
      }

      const sellReady =
        !killSwitch &&
        (pos.holdings || 0) > 0 &&
        (pos.sellCounter || 1) < 10 &&
        coreSellOk &&
        sellTimeOk &&
        duplicateSellGuard.allowed &&
        !isSellTradeLocked &&
        !sellFailureCooldownActive &&
        isSignificantSellMove; // Only trade on significant price moves (filters noise)

      // Report why SELL is blocked if target is crossed but trade isn't happening
      if (targetSellCrossed && !sellReady) {
        const reasons = [];
        if (killSwitch) reasons.push("Kill switch is ON");
        if ((pos.holdings || 0) <= 0) reasons.push(`No holdings (${(pos.holdings || 0).toFixed(6)})`);
        if ((pos.sellCounter || 1) >= 10) reasons.push(`Sell counter limit reached (${pos.sellCounter || 1}/10)`);
        if (!normalSellFloorOk) {
          reasons.push(
            `Cost-aware sell floor not met (need >= ${safeFix(normalSellFloorPrice)}, now ${safeFix(price)}, ${normalSellEconomics.requiredProfitBps.toFixed(1)} bps)`
          );
        }
        if (!sellTimeOk) reasons.push(`Receipt spacing active (${Math.max(0, minSellSpacingMs - timeSinceLastSellReceipt)}ms remaining)`);
        if (!duplicateSellGuard.allowed) {
          reasons.push(
            `Duplicate normal sell guard (${Math.ceil(duplicateSellGuard.cooldownRemainingMs / 1000)}s remaining, ${duplicateSellGuard.priceAdvanceBps.toFixed(1)}/${Number(cfg.normalSellDuplicateMoveBps).toFixed(1)} bps advance)`
          );
        }
        if (isSellTradeLocked) reasons.push("Trade settlement lock active");
        if (sellFailureCooldownActive) {
          reasons.push(`Previous SELL attempt failed (${Math.ceil(sellFailureCooldownRemainingMs / 1000)}s retry cooldown)`);
        }
        if (!isSignificantSellMove && prev !== undefined) {
          const priceChangePct = (sellPriceChange * 100).toFixed(3);
          reasons.push(`Price move too small (${priceChangePct}% < ${(PRICE_NOISE_THRESHOLD * 100).toFixed(2)}% threshold)`);
        }
        
        if (reasons.length > 0) {
          console.log(`[SELL BLOCKED] ${key} - Target crossed but trade blocked:`, {
            price: price.toFixed(6),
            target: pos.priceTargetSell.toFixed(6),
            avgEntry: (pos.avgEntry || 0).toFixed(6),
            holdings: (pos.holdings || 0).toFixed(6),
            reasons: reasons.join("; ")
          });
        }
      }

      if (sellReady) {
        if (!isSellTradeLocked) {
          tradeLockRef.current[key] = true;
          // Reduced from 7000ms to 2500ms for Arbitrum's fast block times (~250ms blocks, 1 confirmation + buffer)
          setTimeout(() => { tradeLockRef.current[key] = false; }, 2500);
          
          // CRITICAL FIX: Calculate amountToSell BEFORE using it (fixes temporal dead zone error)
          // Use sellMultiplier directly (no tiers)
          let sellMultiplierPct = cfg.sellMultiplier || 0.3;
          if (cfg.maxSellCap != null && isFinite(Number(cfg.maxSellCap))) {
            sellMultiplierPct = Math.min(sellMultiplierPct, Number(cfg.maxSellCap));
          }

          const sellBookPre = getMarketBook(key, pos, price, positionsRef.current, chainConfig);
          const sellRegimeSnap = marketRegimeRef.current[key];
          const sellAdj =
            isRegimeEngineEnabled(key) && sellRegimeSnap
              ? evaluateRegimeSellAdjustments({
                  regimeSnap: sellRegimeSnap,
                  book: sellBookPre,
                  totalPnLPercent: getMarketPnLPercent(key),
                  baseSellMultiplier: sellMultiplierPct,
                })
              : { sellMultiplierFactor: 1, maxSellFractionOfHoldings: null, notes: [] };
          sellMultiplierPct *= sellAdj.sellMultiplierFactor || 1;
          if (sellAdj.notes?.length) {
            console.log(`[RegimeSell] ${key}:`, sellAdj.notes.join("; "));
          }

          let amountToSell;
          let outputPriceUsd = 1;
          if (cfg.usePercentMode) {
            // USD-based trade limits for percent mode
            const wrappedSymbol = chainConfig.nativeCurrency.symbol;
            const stableSymbol = chainConfig.defaultStablecoin;
            
            // Determine output token price in USD (what we're receiving)
            if (key === `${wrappedSymbol}-${stableSymbol}`) {
              // Selling WETH, getting USDC.e (price is in USDC.e per WETH)
              outputPriceUsd = 1; // USDC.e = $1
            } else if (key === `WBTC-${stableSymbol}`) {
              // Selling WBTC, getting USDC.e
              outputPriceUsd = 1; // USDC.e = $1
            } else {
              // Selling WBTC, getting WETH
              const nativePrice = positions[nativeSymbol]?.price || 0;
              outputPriceUsd = nativePrice > 0 ? nativePrice : 1;
            }
            
            // Calculate holdings value in USD (what we're selling)
            const holdingsUsd = (pos.holdings || 0) * price * outputPriceUsd;
            
            // Calculate percentage of holdings in USD
            const percentUsd = holdingsUsd * sellMultiplierPct;
            
            // Keep the established inventory distribution rate, bounded by the
            // same flow-adjusted ceiling used by normal buy sizing.
            const minTradeUsd = sellEconomicMinUsd;
            const maxTradeUsd = sellTradeMaxUsd;
            const clampedUsd = Math.min(holdingsUsd, Math.max(minTradeUsd, Math.min(percentUsd, maxTradeUsd)));
            if (compoundSellSizing) {
              console.log(`[COMPOUND SIZING][${botInstanceId.toUpperCase()}] SELL`, {
                marketKey: key,
                sizingEquityUsd: compoundSellSizing.sizingEquityUsd,
                realizedSinceCheckpointUsd: compoundSellSizing.realizedSinceCheckpointUsd,
                holdingsPercentTradeUsd: percentUsd,
                finalBaseTradeUsd: clampedUsd,
                hardMaxUsd: maxTradeUsd,
              });
            }
            
            // Convert USD back to token amount (what we're selling)
            // clampedUsd = amountToSell * price * outputPriceUsd
            // amountToSell = clampedUsd / (price * outputPriceUsd)
            amountToSell = price > 0 && outputPriceUsd > 0 
              ? clampedUsd / (price * outputPriceUsd)
              : (pos.holdings || 0) * sellMultiplierPct; // Fallback to percentage
          } else {
            amountToSell = Number(cfg.manualSell || 0);
          }

          amountToSell = Math.max(0, Math.min(amountToSell, (pos.holdings || 0)));
          if (sellAdj.maxSellFractionOfHoldings != null && (pos.holdings || 0) > 0) {
            amountToSell = Math.min(
              amountToSell,
              (pos.holdings || 0) * sellAdj.maxSellFractionOfHoldings
            );
          }
          if (!(amountToSell > 0)) return;
          let plannedSellUsd = amountToSell * price * outputPriceUsd;
          const totalHoldingsUsd = (pos.holdings || 0) * price * outputPriceUsd;
          const minNormalSellUsd = sellEconomicMinUsd;
          const isFinalDustSell =
            amountToSell >= (pos.holdings || 0) * 0.999999 ||
            totalHoldingsUsd < minNormalSellUsd;
          const externalSell = await evaluateExternalTradeSizing({
            marketKey: key,
            side: "SELL",
            baseTradeAmount: amountToSell,
            baseTradeUsd: plannedSellUsd,
            poolPrice: price,
            availableUsd: totalHoldingsUsd,
            cfg: compoundSellSizing
              ? {
                  ...cfg,
                  minTradeUsd: sellEconomicMinUsd,
                  maxTradeUsd: sellTradeMaxUsd,
                }
              : cfg,
            bypassReason: isFinalDustSell
              ? "Final or dust sell sizing is preserved."
              : !cfg.usePercentMode
                ? "Manual amount mode is never externally resized."
                : "",
            thresholdContext: {
              originalTargetPrice: originalSellTarget,
              economicFloorPrice: normalSellFloorPrice,
              candidateKind: "sell-trigger",
              regime: sellRegimeSnap?.regime,
              avgEntry: economicAvgEntry,
              holdingsWeth: pos.holdings,
              usdcBalance: sellBookPre.baseBalance,
              costBasis: pnlCostBasisForFloor,
            },
          });
          if (
            Number(externalSell.adjustedTargetPrice) > 0 &&
            price <= Number(externalSell.adjustedTargetPrice)
          ) {
            tradeLockRef.current[key] = false;
            prevPriceRef.current[key] = price;
            console.log("[PHASE 2 SELL DELAYED]", {
              marketKey: key,
              price,
              originalTarget: originalSellTarget,
              adjustedTarget: externalSell.adjustedTargetPrice,
              adjustmentBps: externalSell.decision?.thresholdAdjustmentBps || 0,
              episodeId: externalSell.decision?.candidateEpisodeId || "",
            });
            return;
          }
          amountToSell = price > 0 && outputPriceUsd > 0
            ? externalSell.finalTradeUsd / (price * outputPriceUsd)
            : amountToSell;
          amountToSell = Math.max(0, Math.min(amountToSell, (pos.holdings || 0)));
          if (sellAdj.maxSellFractionOfHoldings != null && (pos.holdings || 0) > 0) {
            amountToSell = Math.min(
              amountToSell,
              (pos.holdings || 0) * sellAdj.maxSellFractionOfHoldings
            );
          }
          plannedSellUsd = amountToSell * price * outputPriceUsd;
          const sellEntirePosition =
            (pos.holdings || 0) > 0 &&
            amountToSell >= (pos.holdings || 0) * (1 - 1e-12);
          let executionAmount = amountToSell.toString();
          const exactSellBalance = exactTokenBalanceRef.current[key];
          if (sellEntirePosition && exactSellBalance?.raw != null) {
            try {
              executionAmount = ethers.utils.formatUnits(
                ethers.BigNumber.from(exactSellBalance.raw),
                exactSellBalance.decimals
              );
              console.log("[SELL EXACT BALANCE] Using the last raw RPC balance for full-position payload", {
                marketKey: key,
                amountRaw: exactSellBalance.raw,
                amount: executionAmount,
              });
            } catch (error) {
              console.warn("[SELL EXACT BALANCE] Raw balance formatting failed; backend balanceOf clamp remains active", {
                marketKey: key,
                error: error?.message || error,
              });
            }
          }
          if (
            cfg.usePercentMode &&
            plannedSellUsd + 0.01 < minNormalSellUsd &&
            !isFinalDustSell
          ) {
            console.log("[SELL BLOCKED] Normal partial sell below minimum", {
              marketKey: key,
              plannedSellUsd,
              minNormalSellUsd,
              totalHoldingsUsd,
              holdings: pos.holdings,
            });
            prevPriceRef.current[key] = price;
            return;
          }

          console.log("[NORMAL SELL ECONOMICS]", {
            marketKey: key,
            price,
            economicAvgEntry,
            floorPrice: normalSellFloorPrice,
            requiredProfitBps: normalSellEconomics.requiredProfitBps,
            exitCostBps: normalSellEconomics.exitCostBps,
            gasBps: normalSellEconomics.gasBps,
            gasUsd: normalSellEconomics.gasUsd,
            plannedSellUsd,
            duplicateGuard: duplicateSellGuard,
          });

          // CRITICAL FIX: Track trade submission AFTER amountToSell is calculated
          // This fixes the "Cannot access 'amountToSell' before initialization" error
          pendingTradeSubmissionsRef.current[key] = {
            side: "SELL",
            submittedAt: Date.now(),
            amount: amountToSell,
          };

          // Create snapshot for rollback capability (matches forced trades)
          const snapshot = {
            holdings: pos.holdings, avgEntry: pos.avgEntry,
            priceTargetBuy: pos.priceTargetBuy, priceTargetSell: pos.priceTargetSell,
            buyCounter: pos.buyCounter, sellCounter: pos.sellCounter,
            lastSellAt: pos.lastSellAt,
            lastSellPrice: pos.lastSellPrice,
            lastNormalSellAt: pos.lastNormalSellAt,
            lastNormalSellPrice: pos.lastNormalSellPrice,
            flatSince: pos.flatSince,
            fullExitPrice: pos.fullExitPrice,
            fullExitTxHash: pos.fullExitTxHash,
            flatReentryNearMissSince: pos.flatReentryNearMissSince,
            lastFlatReentryAt: pos.lastFlatReentryAt,
            lastTradeAt: pos.lastTradeAt,
            lastBuyPrice: pos.lastBuyPrice,
            recoveryModeActive: pos.recoveryModeActive,
            lastRecoveryGridAt: pos.lastRecoveryGridAt,
            recoveryBuyBlockedUntil: pos.recoveryBuyBlockedUntil,
            capitalProtectionBuyBlockedUntil: pos.capitalProtectionBuyBlockedUntil,
          };
          const pendingId = startPending(key, snapshot);
          const priorLastSellRef = Number(lastSellAtRef.current[key] || 0);
          const priorLastTradeRef = Number(lastTradeAtRef.current[key] || 0);

          // Determine actualInitiator and tokenToUse (matches forced trades)
          const wrappedSymbol = chainConfig.nativeCurrency.symbol;
          const stableSymbol = chainConfig.defaultStablecoin;
          const nativeSymbol = chainConfig.nativeCurrency.symbol;
          let actualInitiator;
          let tokenToUse;
          
          if (key === `${wrappedSymbol}-${stableSymbol}`) {
            // Selling WCRO to get USDC
            actualInitiator = wrappedSymbol; // We're selling WCRO
            tokenToUse = currentConfig.wrappedNativeToken; // The token we're selling
          } else if (key === `WBTC-${wrappedSymbol}`) {
            // Selling WBTC to get WCRO
            actualInitiator = nativeSymbol;
            tokenToUse = token; // WBTC
          } else if (key === `WBTC-${stableSymbol}`) {
            // Selling WBTC to get USDC
            actualInitiator = stableSymbol;
            tokenToUse = token; // WBTC
          } else {
            // Fallback
            actualInitiator = initiator;
            tokenToUse = token;
          }

          const buyPct  = cfg.buyTarget  ?? defaultBotCfg.buyTarget;
          const sellPct = cfg.sellTarget ?? defaultBotCfg.sellTarget;
          const newHoldings = (pos.holdings || 0) - Number(amountToSell);
          
          // Calculate USD value of remaining holdings to determine if we should reset anchor
          // Zero-holdings reset threshold: Reset if holdings are effectively zero or worth less than threshold
          let remainingHoldingsUsd = 0;
          if (key === `${wrappedSymbol}-${stableSymbol}`) {
            // For WETH-USDC: holdings are in WETH, price is USDC per WETH
            // USD value = holdings * price (since USDC = $1)
            remainingHoldingsUsd = newHoldings * price;
          } else if (key === `WBTC-${stableSymbol}`) {
            // For WBTC-USDC: holdings are in WBTC, price is USDC per WBTC
            remainingHoldingsUsd = newHoldings * price;
          } else {
            // For WBTC-WETH: holdings are in WBTC, need ETH price
            const ethPrice = positions[nativeSymbol]?.price || 0;
            if (ethPrice > 0) {
              remainingHoldingsUsd = newHoldings * price * ethPrice;
            }
            // If ETH price unavailable, we'll still reset if holdings <= DUST (zero check)
          }
          
          const sellBook = getMarketBook(key, pos, price, positionsRef.current, chainConfig);
          const sellSpotGrid = shouldUseSpotGridMode({
            cfg,
            pos,
            price,
            baseBalanceUsd: sellBook.baseBalance,
            totalBookUsd: sellBook.totalBookUsd,
          });
          const postSell = resolvePostSellTargetUpdate({
            price,
            newHoldings,
            remainingHoldingsUsd,
            pos,
            cfg,
            buyPct,
            sellPct,
            useSpotGrid: sellSpotGrid,
            resetWhenDepleted: false,
          });
          const sellStateAt = Date.now();
          const fullExitTransition = postSell.fullExitTransition === true;
          let confirmedSellReset = null;

          let capitalProtectionBuyBlockedUntil = pos.capitalProtectionBuyBlockedUntil || 0;
          if (postSell.newAvgEntry === 0 && !postSell.depletedWithoutReset) {
            const capCooldownMs =
              Number(cfg.capitalProtectionCooldownMs ?? defaultBotCfg.capitalProtectionCooldownMs) ||
              CAPITAL_PROTECTION_COOLDOWN_MS;
            capitalProtectionBuyBlockedUntil = Date.now() + capCooldownMs;
            confirmedSellReset = {
              kind: "FULL_EXIT_RESET",
              payload: {
                buyTarget: postSell.priceTargetBuy,
                sellTarget: postSell.priceTargetSell,
                details: `Holdings depleted — ${Math.round(capCooldownMs / 60000)}m post-exit buy cooldown`,
                cooldownMs: 60 * 1000,
                capitalProtectionBuyBlockedUntil,
              },
            };
            console.log(
              `[SELL PLANNED] ${key} will reset depleted holdings after confirmation at ${safeFix(price)}`
            );
          } else if (fullExitTransition) {
            console.log(
              `[FLAT BOOK STARTED] ${key} exited near ${safeFix(price)}; monitoring guarded re-entry without reset spam.`
            );
          } else if (postSell.depletedWithoutReset) {
            console.log(
              `[${key}] Holdings remain depleted; preserving the one-time full-exit target.`
            );
          } else if (sellSpotGrid || postSell.priceTargetSell <= price * sellPct * 1.01) {
            console.log(
              `🔄 [${key}] Post-sell spot/entry hybrid targets — sell ${safeFix(postSell.priceTargetSell)}, buy ${safeFix(postSell.priceTargetBuy)}`
            );
          }

          updatePosition(key, {
            holdings: newHoldings,
            avgEntry: postSell.newAvgEntry,
            priceTargetBuy: postSell.priceTargetBuy,
            priceTargetSell: postSell.priceTargetSell,
            buyCounter: postSell.buyCounter,
            sellCounter: postSell.sellCounter,
            lastTradeAt: Date.now(),
            lastSellAt: Date.now(),
            lastSellPrice: price,
            lastNormalSellAt: sellStateAt,
            lastNormalSellPrice: price,
            lastBuyPrice: 0,
            recoveryModeActive: false,
            lastRecoveryGridAt: postSell.newAvgEntry === 0 ? 0 : (pos.lastRecoveryGridAt || 0),
            recoveryBuyBlockedUntil: postSell.newAvgEntry === 0 ? 0 : (pos.recoveryBuyBlockedUntil || 0),
            capitalProtectionBuyBlockedUntil,
            flatSince: fullExitTransition ? sellStateAt : (pos.flatSince || 0),
            fullExitPrice: fullExitTransition ? price : (pos.fullExitPrice || 0),
            fullExitTxHash: fullExitTransition ? "" : (pos.fullExitTxHash || ""),
            flatReentryNearMissSince: fullExitTransition ? 0 : (pos.flatReentryNearMissSince || 0),
          });

          lastSellAtRef.current[key] = Date.now();
          lastTradeAtRef.current[key] = Date.now(); // Track last trade timestamp
          lastTradeAtRef.current[key] = Date.now(); // Track last trade timestamp

          // Execute trade with correct initiator and token (matches forced trades)
          try {
            const succeeded = await executeBlockchainTrade("SELL", executionAmount, tokenToUse, actualInitiator, {
              marketKey: key,
              pendingId,
              sellEntirePosition,
              fullExitTransition,
              fullExitAt: fullExitTransition ? sellStateAt : 0,
              fullExitPrice: fullExitTransition ? price : 0,
              externalSignal: externalSell.decision,
            });
            if (!succeeded) {
              lastTradeFailureAtRef.current[sellFailureKey] = Date.now();
              lastSellAtRef.current[key] = priorLastSellRef;
              lastTradeAtRef.current[key] = priorLastTradeRef;
            } else {
              if (confirmedSellReset) {
                emitBotResetDiscord(key, confirmedSellReset.kind, confirmedSellReset.payload);
              }
              delete lastTradeFailureAtRef.current[sellFailureKey];
            }
          } catch (error) {
            console.error(`[Automated SELL] Error executing trade:`, error);
            if (pendingId) rollbackPending(key, pendingId);
            lastTradeFailureAtRef.current[sellFailureKey] = Date.now();
            lastSellAtRef.current[key] = priorLastSellRef;
            lastTradeAtRef.current[key] = priorLastTradeRef;
            // Clear pending trade submission on failure
            delete pendingTradeSubmissionsRef.current[key];
            console.log(`[Automated SELL] Failed attempt rolled back; confirmed position and targets were preserved`);
          }
        }
      }

      prevPriceRef.current[key] = price;
        } catch (error) {
          // CRITICAL FIX: Error boundary - prevent single market error from crashing entire bot
          console.error(`[Market Processing Error] ${key}:`, error);
          console.error(`[Market Processing Error] Stack:`, error.stack);
          console.error(`[Market Processing Error] Context:`, { key, price, hasPos: !!positions[key], hasCfg: !!botConfigs[key] });
          // Clear any pending trade submissions for this market to prevent getting stuck
          if (pendingTradeSubmissionsRef.current[key]) {
            console.warn(`[Market Processing Error] Clearing pending trade submission for ${key} due to error`);
            delete pendingTradeSubmissionsRef.current[key];
          }
          if ((pendingTradesRef.current[key] || []).length) {
            console.warn(`[Market Processing Error] Rolling back unconfirmed position state for ${key}`);
            rollbackAllPendingForMarket(key);
          }
          // Continue processing other markets
        }
      }
    })();
  }, [isListening, executionAddress, persistencePlayer, hydratedPlayer, killSwitch, markets, positions, botConfigs, customBots.length, secondarySessionAuthorized]);

  useEffect(() => {
    console.log(isListening ? "🟢  Listeners started." : "🔴  Listeners stopped.");
  }, [isListening]);

  const fetchTokenPrice3 = async () => {
    if (!currentConfig || !provider) {
      console.warn("⚠️ Configuration not ready for price fetching");
      return;
    }

    if (!window.__attachedPairsByBot) window.__attachedPairsByBot = {};
    if (!window.__attachedPairsByBot[botInstanceId]) {
      window.__attachedPairsByBot[botInstanceId] = {};
    }
    const attachedPairs = window.__attachedPairsByBot[botInstanceId];

    const wrappedSymbol = chainConfig.nativeCurrency.symbol;
    const stableSymbol = chainConfig.defaultStablecoin;
    
    // Get stablecoin pair address dynamically (WCRO/USDC)
    let stablecoinPairAddress = "";
    try {
      stablecoinPairAddress = await getPairOrPoolAddress(
        currentConfig.wrappedNativeToken,
        currentConfig.stablecoinAddress
      );
      if (!stablecoinPairAddress) {
        console.warn(`⚠️ No pair found for ${wrappedSymbol}/${stableSymbol}`);
      }
    } catch (e) {
      console.warn("Failed to get stablecoin pair:", e);
    }
    if (selectedDex === "uniswapV3" && Number(currentConfig.chainId) === 42161) {
      stablecoinPairAddress = ARBITRUM_WETH_USDC_POOL_ADDRESS;
      console.log(`[WETH PRICE] Using configured Arbitrum USDC/WETH pool for ${wrappedSymbol}/${stableSymbol} pricing: ${stablecoinPairAddress}`);
    }

    // Get WBTC/WCRO pair address if not already set
    let wbtcWrappedPairAddress = tokenPairAddress;
    if (!wbtcWrappedPairAddress && TOKEN_ADDRESS && ethers.utils.isAddress(TOKEN_ADDRESS)) {
      try {
        const pair = await getPairOrPoolAddress(
          TOKEN_ADDRESS,
          currentConfig.wrappedNativeToken
        );
        if (pair) {
          wbtcWrappedPairAddress = pair;
          setTokenPairAddress(pair); // Update state for future use
          console.log(`✅ Fetched WBTC/${wrappedSymbol} pair address:`, pair);
        } else {
          console.warn(`⚠️ No pair found for WBTC/${wrappedSymbol}`);
        }
      } catch (e) {
        console.warn("Failed to get WBTC/wrapped pair:", e);
      }
    }

    // Get WBTC/USDC pair address dynamically
    let wbtcStablecoinPairAddress = "";
    if (TOKEN_ADDRESS && ethers.utils.isAddress(TOKEN_ADDRESS)) {
      try {
        const pair = await getPairOrPoolAddress(
          TOKEN_ADDRESS,
          currentConfig.stablecoinAddress
        );
        if (pair) {
          wbtcStablecoinPairAddress = pair;
        } else {
          console.warn(`⚠️ No pair found for WBTC/${stableSymbol}`);
        }
      } catch (e) {
        console.warn("Failed to get WBTC/stablecoin pair:", e);
      }
    }

    const PAIRS = [
      { label: `WBTC/${wrappedSymbol}`, address: wbtcWrappedPairAddress },
      { label: `${wrappedSymbol}/${stableSymbol}`, address: stablecoinPairAddress },
      { label: `WBTC/${stableSymbol}`, address: wbtcStablecoinPairAddress },
    ].filter(p => p.address); // Only include pairs with valid addresses

    console.log(`📊 Fetched pairs:`, PAIRS.map(p => `${p.label}: ${p.address ? '✅' : '❌'}`));

    const ALL = [
      ...PAIRS,
      ...customBots.map((b) => ({ label: b.key, address: b.pairAddress })),
    ];

    const readDecimals = async (addr) => {
      const k = (addr || "").toLowerCase();
      if (!k) return 18;
      if (decimalsCacheRef.current[k] != null) return decimalsCacheRef.current[k];
      try {
        const c = new ethers.Contract(addr, ERC20_META_ABI, provider);
        const d = Number(await c.decimals());
        const n = Number.isFinite(d) ? Math.max(0, Math.min(18, d)) : 18;
        decimalsCacheRef.current[k] = n;
        return n;
      } catch {
        decimalsCacheRef.current[k] = 18;
        return 18;
      }
    };

    const attachPair = async ({ label, address }) => {
      if (!address || attachedPairs[address]) return;
      
      // Detect if this is a V3 pool
      const isV3 = selectedDex === "uniswapV3";
      const poolAbi = isV3 ? V3_POOL_ABI : PAIR_ABI;
      const pair = new ethers.Contract(address, poolAbi, provider);
      liveContractsRef.current.push(pair);
      attachedPairs[address] = true;

      const token0Addr = (await pair.token0()).toLowerCase();
      const token1Addr = (await pair.token1()).toLowerCase();

      if (!currentConfig) return;

      const wrappedNativeLower = currentConfig.wrappedNativeToken.toLowerCase();
      const stablecoinLower = currentConfig.stablecoinAddress.toLowerCase();
      const stablecoinDecimals = (chainConfig.defaultStablecoin === "USDC" || chainConfig.defaultStablecoin === "USDC.e") ? 6 : 
                                 chainConfig.defaultStablecoin === "USDT" ? 6 : 18;

      const d0 =
        token0Addr === wrappedNativeLower
          ? chainConfig.nativeCurrency.decimals
          : token0Addr === stablecoinLower
          ? stablecoinDecimals
          : await readDecimals(token0Addr);

      const d1 =
        token1Addr === wrappedNativeLower
          ? chainConfig.nativeCurrency.decimals
          : token1Addr === stablecoinLower
          ? stablecoinDecimals
          : await readDecimals(token1Addr);

      const compute = async () => {
        try {
          let raw;
          const wrappedSymbol = chainConfig.nativeCurrency.symbol;
          const stableSymbol = chainConfig.defaultStablecoin;

          if (isV3 && Number(currentConfig.chainId) === 42161 && label === `${wrappedSymbol}/${stableSymbol}`) {
            const marketKey = `${wrappedSymbol}-${stableSymbol}`;
            const stablePerWrapped = await getWethUsdcPriceForCore({
              marketKey,
              usage: "price-listener",
              fallbackPrice: markets.find((m) => m.key === marketKey)?.price || lastPriceRef.current[marketKey] || 0,
            });
            if (stablePerWrapped > 0 && isFinite(stablePerWrapped)) {
              usdPerWcroRef.current = stablePerWrapped;
              updateMarketPrice(marketKey, stablePerWrapped);
              updatePosition(chainConfig.nativeCurrency.symbol, { price: stablePerWrapped });
            }
            return;
          }
          
          if (isV3) {
            // V3: Use slot0() to get sqrtPriceX96, then calculate price
            const slot0 = await pair.slot0();
            const sqrtPriceX96 = slot0.sqrtPriceX96;
            
            // Convert sqrtPriceX96 to actual price
            // price = (sqrtPriceX96 / 2^96)^2
            // Adjust for token decimals: price = (sqrtPriceX96 / 2^96)^2 * (10^d0 / 10^d1)
            const Q96 = ethers.BigNumber.from(2).pow(96);
            const sqrtPrice = Number(sqrtPriceX96.toString()) / Number(Q96.toString());
            const priceRatio = (sqrtPrice * sqrtPrice);
            
            // Adjust for decimals: token1 per token0
            const decimalAdjustment = Math.pow(10, d0) / Math.pow(10, d1);
            raw = priceRatio * decimalAdjustment;
          } else {
            // V2: Use getReserves()
            const [r0, r1] = await pair.getReserves();
            const R0 = normalize(r0, d0);
            const R1 = normalize(r1, d1);
            if (!isFinite(R0) || !isFinite(R1) || R0 <= 0 || R1 <= 0) return;
            raw = R1 / R0;
          }
          
          if (!isFinite(raw) || raw <= 0) return;

          // wrappedNativeLower and stablecoinLower are already declared in outer scope

          if (label === `${wrappedSymbol}/${stableSymbol}`) {
            const stablePerWrapped =
              token0Addr === wrappedNativeLower ? raw : 1 / raw;
            if (stablePerWrapped > 0 && isFinite(stablePerWrapped)) {
              usdPerWcroRef.current = stablePerWrapped;
              updateMarketPrice(`${wrappedSymbol}-${stableSymbol}`, stablePerWrapped);
              updatePosition(chainConfig.nativeCurrency.symbol, { price: stablePerWrapped });
            }
            return;
          }

          if (label === `WBTC/${wrappedSymbol}`) {
            const tokenLower = TOKEN_ADDRESS.toLowerCase();
            const wrappedPerWbtc =
              token0Addr === tokenLower ? raw : 1 / raw;
            if (wrappedPerWbtc > 0 && isFinite(wrappedPerWbtc)) {
              wcroPerWbtcRef.current = wrappedPerWbtc;
              updateMarketPrice(`WBTC-${wrappedSymbol}`, wrappedPerWbtc);
              console.log(`✅ WBTC/${wrappedSymbol} price updated:`, wrappedPerWbtc);
            } else {
              console.warn(`⚠️ Invalid price for WBTC/${wrappedSymbol}:`, wrappedPerWbtc, `token0: ${token0Addr}, token1: ${token1Addr}, TOKEN_ADDRESS: ${tokenLower}`);
            }
            return;
          }

          if (label === `WBTC/${stableSymbol}`) {
            // Calculate WBTC price in stablecoin (e.g., WBTC/USDC)
            const tokenLower = TOKEN_ADDRESS.toLowerCase();
            const stablePerWbtc =
              token0Addr === tokenLower ? raw : 1 / raw;
            if (stablePerWbtc > 0 && isFinite(stablePerWbtc)) {
              updateMarketPrice(`WBTC-${stableSymbol}`, stablePerWbtc);
            }
            return;
          }

          // Fallback for other pairs
          const priceInNative =
            token0Addr === wrappedNativeLower ? 1 / raw : raw;
          if (priceInNative > 0 && isFinite(priceInNative)) {
            updateMarketPrice(label, priceInNative);
          }
        } catch (e) {
          console.error(label, "compute error", e);
        }
      };

      await compute();
      // V3 pools only have Swap events, V2 has both Swap and Sync
      pair.on("Swap", () => compute());
      if (!isV3) {
        pair.on("Sync", () => compute());
      }
    };

    try {
      for (const p of ALL) {
        await attachPair(p);
      }
    } catch (e) {
      console.error("Listener attach error:", e?.message || e);
    }

    if (!window.__sniperHeartbeats) window.__sniperHeartbeats = {};
    if (!window.__sniperHeartbeats[botInstanceId]) {
      window.__sniperHeartbeats[botInstanceId] = setInterval(() => {}, 20000);
    }
  };

  // Universal Router address on Arbitrum
  const UNIVERSAL_ROUTER_ADDRESS = "0xA51afAFe0263b40EdaEf0Df8781eA9aa03E381a3";
  // SwapRouter02 address (fallback for direct V3 swaps)
  const SWAP_ROUTER_02_ADDRESS = "0x68b3465833fb72A70ecDF485E0e4C7bD8665Fc45";
  // Permit2 contract address (same on all chains) - required for Universal Router token transfers
  const PERMIT2_ADDRESS = "0x000000000022D473030F116dDEE9F6B43aC78BA3";

  /** Uniswap V3 QuoterV2 — tuple must use `tuple(...)` in ethers v5; returns 4 values (amountOut is [0]) */
  const QUOTER_V2_ABI = [
    "function quoteExactInputSingle(tuple(address tokenIn, address tokenOut, uint256 amountIn, uint24 fee, uint160 sqrtPriceLimitX96) params) external returns (uint256 amountOut, uint160 sqrtPriceX96After, uint32 initializedTicksCrossed, uint256 gasEstimate)",
  ];
  /** QuoterV2 only — NOT legacy Quoter (0xb273…). Same create2 address on mainnet / L2s per Uniswap docs. */
  const quoterV2AddressForChain = (chainId) => {
    const m = {
      1: "0x61fFE014bA17989E743c5F6cB21bF9697530B21e",
      10: "0x61fFE014bA17989E743c5F6cB21bF9697530B21e",
      137: "0x61fFE014bA17989E743c5F6cB21bF9697530B21e",
      42161: "0x61fFE014bA17989E743c5F6cB21bF9697530B21e",
    };
    return m[chainId] || null;
  };

  const quoteWethUsdcExecutablePrice = async ({ side, amount, poolPrice, cfg }) => {
    if (Number(currentConfig?.chainId) !== 42161) {
      throw new Error("External executable pricing is only configured for Arbitrum.");
    }
    const readProvider = providerRef.current;
    const quoterAddress = quoterV2AddressForChain(42161);
    if (!readProvider || !quoterAddress) throw new Error("Arbitrum QuoterV2 is unavailable.");

    const normalizedSide = String(side).toUpperCase();
    const tokenIn = normalizedSide === "BUY"
      ? currentConfig.stablecoinAddress
      : currentConfig.wrappedNativeToken;
    const tokenOut = normalizedSide === "BUY"
      ? currentConfig.wrappedNativeToken
      : currentConfig.stablecoinAddress;
    const amountIn = ethers.utils.parseUnits(
      toDecimalStringSafe(amount, normalizedSide === "BUY" ? 6 : 18),
      normalizedSide === "BUY" ? 6 : 18
    );
    const quoter = new ethers.Contract(quoterAddress, QUOTER_V2_ABI, readProvider);
    const quotePromise = quoter.callStatic.quoteExactInputSingle([
      ethers.utils.getAddress(tokenIn),
      ethers.utils.getAddress(tokenOut),
      amountIn,
      500,
      0,
    ]);
    const quoteResult = await Promise.race([
      quotePromise,
      new Promise((_, reject) => setTimeout(() => reject(new Error("QuoterV2 timed out")), 1200)),
    ]);
    const amountOut = quoteResult?.amountOut ?? quoteResult?.[0];
    if (!amountOut || amountOut.lte(0)) throw new Error("QuoterV2 returned no output.");

    const inputNumber = Number(ethers.utils.formatUnits(amountIn, normalizedSide === "BUY" ? 6 : 18));
    const outputNumber = Number(ethers.utils.formatUnits(amountOut, normalizedSide === "BUY" ? 18 : 6));
    const executablePrice = normalizedSide === "BUY"
      ? inputNumber / outputNumber
      : outputNumber / inputNumber;
    if (!(executablePrice > 0) || !Number.isFinite(executablePrice)) {
      throw new Error("QuoterV2 produced an invalid executable price.");
    }

    const quoteGas = Number((quoteResult?.gasEstimate ?? quoteResult?.[3])?.toString?.() || 0);
    const maxFeeGwei = Math.max(0, Number(maxFeePerGasAmount) || 0);
    const estimatedGasUsd = quoteGas > 0 && poolPrice > 0
      ? (quoteGas * maxFeeGwei / 1e9) * poolPrice
      : 0;
    return {
      executablePrice,
      expectedAmountOut: outputNumber,
      quoteGas,
      gasUsd: Math.max(
        estimatedGasUsd,
        Number(cfg.externalSignalFallbackGasUsd ?? defaultBotCfg.externalSignalFallbackGasUsd)
      ),
      feeTier: 500,
    };
  };

  const updateExternalThresholdPersistence = ({ marketKey, side, advice, cfg, bypassReason }) => {
    const now = Date.now();
    const key = `${marketKey}:${String(side).toUpperCase()}`;
    const direction = Math.sign(Number(advice?.recommendedThresholdAdjustmentBps) || 0);
    const qualifying = advice?.thresholdEligible === true && direction !== 0 && !bypassReason;
    if (!qualifying) {
      delete externalThresholdStateRef.current[key];
      return {
        episodeId: "",
        sampleCount: 0,
        firstSeenAt: 0,
        lastSeenAt: now,
        persistent: false,
        direction,
      };
    }

    const minSamples = Math.max(
      1,
      Math.round(Number(cfg.externalSignalPhase2MinSamples ?? defaultBotCfg.externalSignalPhase2MinSamples))
    );
    const minPersistenceMs = Math.max(
      0,
      Number(cfg.externalSignalPhase2MinPersistenceMs ?? defaultBotCfg.externalSignalPhase2MinPersistenceMs)
    );
    const maxSampleGapMs = Math.max(
      1000,
      Number(cfg.externalSignalPhase2MaxSampleGapMs ?? defaultBotCfg.externalSignalPhase2MaxSampleGapMs)
    );
    const previous = externalThresholdStateRef.current[key];
    const continuesEpisode =
      previous &&
      previous.direction === direction &&
      now - Number(previous.lastSeenAt || 0) <= maxSampleGapMs;
    const state = continuesEpisode
      ? {
          ...previous,
          sampleCount: Number(previous.sampleCount || 0) + 1,
          lastSeenAt: now,
        }
      : {
          episodeId: `phase2-${marketKey}-${String(side).toLowerCase()}-${now}`,
          direction,
          sampleCount: 1,
          firstSeenAt: now,
          lastSeenAt: now,
        };
    state.persistent =
      state.sampleCount >= minSamples &&
      now - state.firstSeenAt >= minPersistenceMs;
    externalThresholdStateRef.current[key] = state;
    return state;
  };

  const evaluateExternalTradeSizing = async ({
    marketKey,
    side,
    baseTradeAmount,
    baseTradeUsd,
    poolPrice,
    availableUsd,
    cfg,
    bypassReason = "",
    thresholdContext = {},
  }) => {
    const wrappedSymbol = chainConfig?.nativeCurrency?.symbol || "ETH";
    const stableSymbol = chainConfig?.defaultStablecoin || "USDC";
    const supportedMarket =
      Number(currentConfig?.chainId) === 42161 &&
      marketKey === `${wrappedSymbol}-${stableSymbol}`;
    const externalConfig = {
      minSignalQuality: clampNumber(Number(cfg.externalSignalMinQuality ?? defaultBotCfg.externalSignalMinQuality), 0.5, 0.95),
      minSizeMultiplier: clampNumber(Number(cfg.externalSignalMinSizeMultiplier ?? defaultBotCfg.externalSignalMinSizeMultiplier), 0.8, 1),
      maxSizeMultiplier: clampNumber(Number(cfg.externalSignalMaxSizeMultiplier ?? defaultBotCfg.externalSignalMaxSizeMultiplier), 1, 1.2),
      minDirectionalQuality: clampNumber(Number(cfg.externalSignalMinDirectionalQuality ?? defaultBotCfg.externalSignalMinDirectionalQuality), 0.5, 0.95),
      minDirectionalLeadBps: clampNumber(Number(cfg.externalSignalMinDirectionalLeadBps ?? defaultBotCfg.externalSignalMinDirectionalLeadBps), 0, 50),
      minDirectionalMomentumBps: clampNumber(Number(cfg.externalSignalMinDirectionalMomentumBps ?? defaultBotCfg.externalSignalMinDirectionalMomentumBps), 0, 50),
      minDirectionalAgreement: clampNumber(Number(cfg.externalSignalMinDirectionalAgreement ?? defaultBotCfg.externalSignalMinDirectionalAgreement), 0.5, 1),
      minMomentumConsistency: clampNumber(Number(cfg.externalSignalMinMomentumConsistency ?? defaultBotCfg.externalSignalMinMomentumConsistency), 0, 1),
      executionToleranceBps: clampNumber(Number(cfg.externalSignalExecutionToleranceBps ?? defaultBotCfg.externalSignalExecutionToleranceBps), 0, 25),
      maxUpsizeWithoutNetOpportunity: clampNumber(Number(cfg.externalSignalMaxUpsizeWithoutNetOpportunity ?? defaultBotCfg.externalSignalMaxUpsizeWithoutNetOpportunity), 1, 1.1),
      maxThresholdAdjustmentPct: clampNumber(Number(cfg.externalSignalPhase2MaxAdjustmentPct ?? defaultBotCfg.externalSignalPhase2MaxAdjustmentPct), 0, 0.1),
      estimatedExitCostBps: clampNumber(Number(cfg.externalSignalEstimatedExitCostBps ?? defaultBotCfg.externalSignalEstimatedExitCostBps), 0, 100),
      safetyMarginBps: clampNumber(Number(cfg.externalSignalSafetyMarginBps ?? defaultBotCfg.externalSignalSafetyMarginBps), 0, 100),
      fallbackGasUsd: clampNumber(Number(cfg.externalSignalFallbackGasUsd ?? defaultBotCfg.externalSignalFallbackGasUsd), 0, 10),
    };
    externalSignalServiceRef.current.configure(externalConfig);
    const snapshot = externalSignalServiceRef.current.getSnapshot();
    const featureEnabled = cfg.externalSignalEnabled !== false;
    const shadowMode = cfg.externalSignalShadowMode !== false;
    const phase1Enabled = cfg.externalSignalPhase1SizingEnabled === true;
    const phase2Enabled = cfg.externalSignalPhase2ThresholdEnabled !== false;
    const canInfluenceSizing =
      supportedMarket && featureEnabled && phase1Enabled && !shadowMode && !bypassReason;
    let quote = null;
    let quoteError = "";
    if (supportedMarket && featureEnabled && snapshot.status === "healthy") {
      try {
        quote = await quoteWethUsdcExecutablePrice({
          side,
          amount: baseTradeAmount,
          poolPrice,
          cfg,
        });
      } catch (error) {
        quoteError = error?.message || String(error);
      }
    }

    const currentAdaptiveThresholdBps = String(side).toUpperCase() === "BUY"
      ? Number(cfg.adaptiveBuyMovePct || (1 - Number(cfg.buyTarget || 1))) * 10000
      : Number(cfg.adaptiveSellMovePct || (Number(cfg.sellTarget || 1) - 1)) * 10000;
    const advice = buildExternalSignalAdvice({
      snapshot,
      side,
      poolPrice,
      dexExecutablePrice: quote?.executablePrice,
      baseTradeUsd,
      currentAdaptiveThresholdBps,
      gasUsd: quote?.gasUsd,
      config: externalConfig,
    });
    const sizing = applyExternalSizeOverlay({
      baseTradeUsd,
      advice,
      enabled: canInfluenceSizing,
      shadowMode,
      minTradeUsd: Number(cfg.minTradeUsd ?? defaultBotCfg.minTradeUsd),
      maxTradeUsd: Number(cfg.maxTradeUsd ?? defaultBotCfg.maxTradeUsd),
      availableUsd,
    });
    const persistence = updateExternalThresholdPersistence({
      marketKey,
      side,
      advice,
      cfg,
      bypassReason,
    });
    const threshold = applyExternalThresholdOverlay({
      side,
      currentPrice: poolPrice,
      currentTargetPrice: thresholdContext.originalTargetPrice,
      currentThresholdBps: currentAdaptiveThresholdBps,
      economicFloorPrice: thresholdContext.economicFloorPrice,
      advice,
      enabled: supportedMarket && featureEnabled && phase2Enabled,
      shadowMode,
      persistent: persistence.persistent,
      bypassReason,
      maxAdjustmentPct: externalConfig.maxThresholdAdjustmentPct,
    });
    const mode = !featureEnabled
      ? "disabled"
      : shadowMode
        ? "shadow"
        : phase1Enabled && phase2Enabled
          ? "live-sizing+threshold"
          : phase2Enabled
            ? "live-threshold"
            : phase1Enabled
              ? "live-sizing"
              : "observe-only";
    const reason = bypassReason || quoteError || advice.reason;
    const decision = recordExternalSignalDecision({
      timestamp: Date.now(),
      marketKey,
      side: String(side).toUpperCase(),
      mode,
      status: supportedMarket ? advice.status : "unsupported-market",
      fairPrice: snapshot.fairWethUsdc || 0,
      fairBid: snapshot.fairBid || 0,
      fairAsk: snapshot.fairAsk || 0,
      poolPrice,
      poolAddress: ARBITRUM_WETH_USDC_POOL_ADDRESS,
      dexExecutablePrice: quote?.executablePrice || 0,
      signalQuality: snapshot.signalQuality || 0,
      entryEdgeBps: advice.entryEdgeBps || 0,
      rawDirectionalLeadBps: advice.rawDirectionalLeadBps || 0,
      directionalLeadBps: advice.directionalLeadBps || 0,
      neutralBandBps: advice.neutralBandBps || 0,
      opportunityMagnitudeBps: advice.opportunityMagnitudeBps || 0,
      directionalExecutableEdgeBps: advice.directionalExecutableEdgeBps || 0,
      netExecutableOpportunityBps: advice.netExecutableOpportunityBps || 0,
      opportunityDirection: advice.opportunityDirection || "neutral",
      agreement: snapshot.agreement || 0,
      dispersionBps: snapshot.dispersionBps,
      momentumBps: snapshot.momentumBps || 0,
      directionalMomentumBps: advice.directionalMomentumBps || 0,
      signalConfirmed: advice.signalConfirmed === true,
      executionAcceptable: advice.executionAcceptable === true,
      supportScore: advice.supportScore || 0,
      feedFreshnessMs: snapshot.feedFreshnessMs,
      sourceCount: snapshot.sourceCount || 0,
      sources: snapshot.sources || [],
      rejectedSources: snapshot.rejectedSources || [],
      sizeMultiplier: advice.sizeMultiplier || 1,
      appliedMultiplier: sizing.appliedMultiplier,
      baseTradeSizeUsd: baseTradeUsd,
      recommendedTradeSizeUsd: advice.recommendedTradeUsd || baseTradeUsd,
      finalTradeSizeUsd: sizing.finalTradeUsd,
      recommendedThresholdAdjustmentBps: advice.recommendedThresholdAdjustmentBps || 0,
      thresholdAdjustmentBps: threshold.appliedAdjustmentBps,
      currentAdaptiveThresholdBps,
      recommendedFinalThresholdBps: threshold.recommendedThresholdBps,
      finalThresholdBps: threshold.adjustedThresholdBps,
      originalTargetPrice: threshold.originalTargetPrice,
      recommendedTargetPrice: threshold.recommendedTargetPrice,
      adjustedTargetPrice: threshold.adjustedTargetPrice,
      currentDistanceFromTargetBps:
        threshold.originalTargetPrice > 0 && poolPrice > 0
          ? (String(side).toUpperCase() === "BUY"
              ? ((poolPrice - threshold.originalTargetPrice) / threshold.originalTargetPrice) * 10000
              : ((threshold.originalTargetPrice - poolPrice) / threshold.originalTargetPrice) * 10000)
          : 0,
      originalWouldTrigger: threshold.originalWouldTrigger,
      recommendedWouldTrigger: threshold.recommendedWouldTrigger,
      adjustedWouldTrigger: threshold.adjustedWouldTrigger,
      thresholdEligible: threshold.eligible,
      thresholdPersistent: threshold.persistent,
      thresholdBlockedReason: threshold.blockedReason,
      thresholdDirection: advice.thresholdDirection || "neutral",
      thresholdSignalSampleCount: persistence.sampleCount,
      thresholdSignalFirstSeenAt: persistence.firstSeenAt,
      thresholdSignalLastSeenAt: persistence.lastSeenAt,
      candidateEpisodeId: persistence.episodeId,
      phase1ChangedAnything: sizing.changedAnything,
      phase2ChangedAnything: threshold.changedAnything,
      changedAnything: sizing.changedAnything || threshold.changedAnything,
      constrainedByLimits: sizing.constrainedByLimits,
      candidateKind: thresholdContext.candidateKind || "trade-trigger",
      regime: thresholdContext.regime || "",
      avgEntry: Number(thresholdContext.avgEntry) || 0,
      holdingsWeth: Number(thresholdContext.holdingsWeth) || 0,
      usdcBalance: Number(thresholdContext.usdcBalance) || 0,
      costBasis: Number(thresholdContext.costBasis) || 0,
      buyDistanceFactor: Number(cfg.adaptiveBuyDistanceFactor ?? defaultBotCfg.adaptiveBuyDistanceFactor),
      reason,
      quoteError,
      feeTier: quote?.feeTier || 500,
      gasBps: advice.gasBps || 0,
      costsBps: advice.costsBps || 0,
    });
    return {
      decision,
      finalTradeUsd: sizing.finalTradeUsd,
      adjustedTargetPrice: threshold.adjustedTargetPrice,
      threshold,
    };
  };

  // Helper to check if using Universal Router
  const isUniversalRouter = (routerAddress) => {
    return routerAddress && routerAddress.toLowerCase() === UNIVERSAL_ROUTER_ADDRESS.toLowerCase();
  };

  // Helper to construct Universal Router V3_SWAP_EXACT_IN command
  // Universal Router command 0x00 = V3_SWAP_EXACT_IN
  // According to Uniswap docs: (address recipient, uint256 amountIn, uint256 amountOutMin, bytes path, bool payerIsUser)
  // Path format: tokenIn (20 bytes) + fee (uint24, 3 bytes) + tokenOut (20 bytes)
  const encodeUniversalRouterV3Swap = (recipient, amountIn, amountOutMin, tokenIn, fee, tokenOut, payerIsUser = true) => {
    // Get checksummed addresses
    const tokenInAddr = ethers.utils.getAddress(tokenIn);
    const tokenOutAddr = ethers.utils.getAddress(tokenOut);
    
    // Build path: tokenIn (20 bytes) + fee (3 bytes) + tokenOut (20 bytes) = 43 bytes
    const tokenInBytes = ethers.utils.hexZeroPad(tokenInAddr, 20);
    const feeBytes = ethers.utils.hexZeroPad(ethers.BigNumber.from(fee).toHexString(), 3);
    const tokenOutBytes = ethers.utils.hexZeroPad(tokenOutAddr, 20);
    const path = ethers.utils.hexConcat([tokenInBytes, feeBytes, tokenOutBytes]);
    
    // Encode: (address recipient, uint256 amountIn, uint256 amountOutMin, bytes path, bool payerIsUser)
    const input = ethers.utils.defaultAbiCoder.encode(
      ["address", "uint256", "uint256", "bytes", "bool"],
      [ethers.utils.getAddress(recipient), amountIn, amountOutMin, path, payerIsUser]
    );
    
    return { command: "0x00", input };
  };

  // Helper to encode WRAP_ETH command for Universal Router
  // Command 0x0b = WRAP_ETH
  // Input format: recipient (address) - wraps all ETH sent with the transaction
  const encodeWrapETH = (recipient) => {
    const input = ethers.utils.defaultAbiCoder.encode(
      ["address"],
      [recipient]
    );
    return { command: "0x0b", input };
  };

  // Helper to encode PERMIT2_PERMIT command for Universal Router
  // Command 0x0a = PERMIT2_PERMIT
  // Input format: (PermitSingle permitSingle, bytes signature)
  // Based on successful DEX transaction format
  const encodePermit2Permit = async (token, amount, owner, spender, deadline) => {
    // Get the actual nonce from Permit2 contract
    const provider = new ethers.providers.Web3Provider(window.ethereum);
    const permit2Abi = [
      "function nonces(address owner, address spender) external view returns (uint256)"
    ];
    let nonce = 0;
    try {
      const permit2Contract = new ethers.Contract(PERMIT2_ADDRESS, permit2Abi, provider);
      // Note: Permit2 uses a different nonce structure, but for simplicity use 0
      // The actual nonce might be token-specific or use a different mapping
      nonce = 0;
    } catch (e) {
      console.warn("Could not fetch nonce from Permit2, using 0:", e.message);
      nonce = 0;
    }
    
    // PermitSingle struct according to Permit2:
    // details: { token (address), amount (uint256), expiration (uint48), nonce (uint256) }
    // spender: address
    // sigDeadline: uint256
    // expiration must be uint48 (max value: 0xffffffffffff = 281474976710655)
    const maxUint48 = ethers.BigNumber.from("0xffffffffffff"); // Max uint48 value
    const expiration = deadline.gt(maxUint48) ? maxUint48 : deadline;
    
    const permitSingle = {
      details: {
        token: ethers.utils.getAddress(token),
        amount: amount,
        expiration: expiration, // uint48
        nonce: nonce,
      },
      spender: ethers.utils.getAddress(spender),
      sigDeadline: deadline, // uint256
    };
    
    // For on-chain approvals, use empty signature (0x)
    // The successful transaction shows a signature, but with on-chain approvals we use empty
    const signature = "0x";
    
    // Encode: (PermitSingle, bytes)
    const input = ethers.utils.defaultAbiCoder.encode(
      [
        "tuple(tuple(address token, uint256 amount, uint48 expiration, uint256 nonce) details, address spender, uint256 sigDeadline) permitSingle",
        "bytes signature"
      ],
      [permitSingle, signature]
    );
    
    return { command: "0x0a", input };
  };

  const getWeb3SignerAndRouter = async () => {
    if (!(window && window.ethereum)) throw new Error("No injected wallet");
    if (!currentConfig) throw new Error("Configuration not ready");
    
    const web3 = new ethers.providers.Web3Provider(window.ethereum);
    
    // Check and switch network if needed
    const network = await web3.getNetwork();
    const expectedChainId = currentConfig.chainId;
    
    if (network.chainId !== expectedChainId) {
      console.log(`⚠️ Wallet is on chain ${network.chainId}, but need chain ${expectedChainId}. Attempting to switch...`);
      try {
        // Try to switch network
        await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: `0x${expectedChainId.toString(16)}` }],
        });
        // Wait a bit for the switch to complete
        await new Promise(resolve => setTimeout(resolve, 1000));
      } catch (switchError) {
        // If the chain doesn't exist, try to add it
        if (switchError.code === 4902) {
          const chainConfigForAdd = CHAIN_CONFIGS[selectedChain];
          if (chainConfigForAdd) {
            const rpcUrls = chainConfigForAdd.rpcUrls;
            const blockExplorerUrls = chainConfigForAdd.blockExplorerUrls || [];
            
            await window.ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [{
                chainId: `0x${expectedChainId.toString(16)}`,
                chainName: chainConfigForAdd.name,
                nativeCurrency: chainConfigForAdd.nativeCurrency,
                rpcUrls: [rpcUrls.default || rpcUrls.public],
                blockExplorerUrls: blockExplorerUrls,
              }],
            });
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        } else {
          const chainName = CHAIN_CONFIGS[selectedChain]?.name || 'the correct network';
          throw new Error(`Failed to switch network: ${switchError.message}. Please switch to ${chainName} manually.`);
        }
      }
    }
    
    const signer = web3.getSigner();
    const router = new ethers.Contract(currentConfig.routerAddress, ROUTER_ABI, signer);
    const from = await signer.getAddress();
    return { signer, router, from, web3 };
  };

  const readErc20Decimals = async (addr) => {
    if (!addr) return 18;
    const meta = new ethers.Contract(addr, ERC20_META_ABI, provider);
    try {
      const d = await meta.decimals();
      const n = Number(d);
      return Number.isFinite(n) ? Math.max(0, Math.min(18, n)) : 18;
    } catch {
      return 18;
    }
  };

  const ensureAllowance = async (tokenAddr, owner, spender, amountInWei) => {
    if (!currentConfig) {
      throw new Error("Configuration not ready for allowance check");
    }
    const token = new ethers.Contract(
      tokenAddr,
      ERC20_ABI,
      new ethers.providers.Web3Provider(window.ethereum).getSigner()
    );
    const cur = await token.allowance(owner, spender);
    if (cur.gte(amountInWei)) return;
    const tx = await token.approve(spender, ethers.constants.MaxUint256);
    await tx.wait();
  };

  /** Permit2 requires TWO steps: ERC20→Permit2, then Permit2.approve(token, router, …) or the Universal Router cannot pull tokens. */
  const PERMIT2_ROUTER_ABI = [
    "function allowance(address owner, address token, address spender) view returns (uint160 amount, uint48 expiration, uint48 nonce)",
    "function approve(address token, address spender, uint160 amount, uint48 expiration) external",
  ];

  const ensurePermit2RouterAllowance = async (signer, tokenAddress, routerAddress, minAmountWei) => {
    const permit2 = new ethers.Contract(PERMIT2_ADDRESS, PERMIT2_ROUTER_ABI, signer);
    const owner = await signer.getAddress();
    const res = await permit2.allowance(owner, tokenAddress, routerAddress);

    const allowed = res.amount !== undefined ? res.amount : res[0];
    const expirationRaw = res.expiration !== undefined ? res.expiration : res[1];
    const expiration = ethers.BigNumber.from(expirationRaw).toNumber();
    const now = Math.floor(Date.now() / 1000);
    const expiresSoon = expiration <= now + 600; // refresh if expired or expiring within 10 minutes

    const minBn = ethers.BigNumber.from(minAmountWei);
    const maxUint160 = ethers.BigNumber.from(2).pow(160).sub(1);
    const minCapped = minBn.gt(maxUint160) ? maxUint160 : minBn;

    console.log(
      `[Permit2] token=${tokenAddress} spender=${routerAddress} allowed=${allowed.toString()} expiration=${expiration} now=${now}`
    );

    if (allowed.gte(minCapped) && !expiresSoon) {
      console.log("✅ [Permit2] Universal Router allowance sufficient and not expired");
      return;
    }

    const maxUint48 = ethers.BigNumber.from(2).pow(48).sub(1);
    console.log(
      `🔐 [Permit2] Approving Universal Router to spend token via Permit2 (${
        allowed.lt(minCapped) ? "amount too low" : "expired/expiring soon"
      })…`
    );
    const tx = await permit2.approve(tokenAddress, routerAddress, maxUint160, maxUint48);
    await tx.wait();
    console.log("✅ [Permit2] permit2.approve confirmed for router");
  };

  const reanchorTargetsFor = (marketKey) => {
    const price = markets.find((m) => m.key === marketKey)?.price || 0;
    const pos = getPositionForDecisions(marketKey);
    if (!(price > 0)) return;

    const cfg = getEffectiveBotCfg(marketKey);
    const buyPct = cfg.buyTarget ?? defaultBotCfg.buyTarget;
    const sellPct = cfg.sellTarget ?? defaultBotCfg.sellTarget;
    const book = getMarketBook(marketKey, pos, price, positionsRef.current, chainConfig);
    const useSpot = shouldUseSpotGridMode({
      cfg,
      pos,
      price,
      baseBalanceUsd: book.baseBalance,
      totalBookUsd: book.totalBookUsd,
    });
    const t = computeGridTargets({
      price,
      avgEntry: pos.avgEntry,
      buyPct,
      sellPct,
      useSpotAnchor: useSpot,
    });
    t.priceTargetSell = Math.max(
      t.priceTargetSell,
      getNormalSellFloorPrice({
        avgEntry: pos.avgEntry,
        minProfitBps: cfg.normalSellMinProfitBps,
      })
    );
    console.log(
      `[Reanchor] ${marketKey} → ${useSpot ? "spot" : "entry"} grid (buy ${safeFix(t.priceTargetBuy)}, sell ${safeFix(t.priceTargetSell)})`
    );
    updatePosition(marketKey, {
      priceTargetBuy: t.priceTargetBuy,
      priceTargetSell: t.priceTargetSell,
    });
    emitBotResetDiscord(marketKey, "MANUAL_REANCHOR", {
      buyTarget: t.priceTargetBuy,
      sellTarget: t.priceTargetSell,
      details: useSpot ? "Manual reanchor → spot grid" : "Manual reanchor → entry grid",
    });
  };

  const maybeSyncRecoveryGrid = (key, price) => {
    const pos = getPositionForDecisions(key);
    if (!pos || !(price > 0)) return;
    const cfg = getEffectiveBotCfg(key);
    const now = Date.now();
    const book = getMarketBook(key, pos, price, positionsRef.current, chainConfig);
    const recoveryPnlHoldings = Number(pnlDataRef.current[key]?.holdings) || 0;
    const recoveryPnlCostBasis = Number(pnlDataRef.current[key]?.costBasis) || 0;
    const recoveryPnlAvgEntry =
      recoveryPnlHoldings > DUST && recoveryPnlCostBasis > 0
        ? recoveryPnlCostBasis / recoveryPnlHoldings
        : 0;
    const recoveryEconomicAvgEntry = recoveryPnlAvgEntry > 0
      ? recoveryPnlAvgEntry
      : Number(pos.avgEntry) || 0;
    const safeSellTarget = Math.max(
      Number(pos.priceTargetSell) || 0,
      getNormalSellFloorPrice({
        avgEntry: recoveryEconomicAvgEntry,
        minProfitBps: cfg.normalSellMinProfitBps,
      })
    );
    const unsafeSellTarget = safeSellTarget > (Number(pos.priceTargetSell) || 0) + DUST;
    if (unsafeSellTarget) {
      updatePosition(key, { priceTargetSell: safeSellTarget });
      console.warn("[SELL TARGET REPAIRED]", {
        marketKey: key,
        oldSellTarget: pos.priceTargetSell,
        safeSellTarget,
        avgEntry: recoveryEconomicAvgEntry,
      });
    }
    if (
      !shouldUseSpotGridMode({
        cfg,
        pos,
        price,
        baseBalanceUsd: book.baseBalance,
        totalBookUsd: book.totalBookUsd,
      })
    ) {
      return;
    }
    const buyPct = cfg.buyTarget ?? defaultBotCfg.buyTarget;
    const sellPct = cfg.sellTarget ?? defaultBotCfg.sellTarget;
    const t = computeGridTargets({
      price,
      avgEntry: pos.avgEntry,
      buyPct,
      sellPct,
      useSpotAnchor: true,
    });
    const buyStuck = (pos.priceTargetBuy || 0) > t.priceTargetBuy * 1.01;
    if (!buyStuck) return;
    const regimeSnap = marketRegimeRef.current[key];
    const timeframeMetrics = getMarketTimeframeMetrics(key);
    const samples15m = (priceHistoryRef.current[key] || []).filter(
      (sample) => sample.t >= now - 15 * 60 * 1000 && sample.p > 0
    );
    const midpoint15m =
      timeframeMetrics.high15m > timeframeMetrics.low15m
        ? (timeframeMetrics.high15m + timeframeMetrics.low15m) / 2
        : 0;
    const rangePosition = (high, low) =>
      high > low ? Math.max(0, Math.min(1, (price - low) / (high - low))) : 0.5;
    const oneHourPosition = Number(
      regimeSnap?.metrics?.m1h?.positionInRange ??
        rangePosition(timeframeMetrics.high1h, timeframeMetrics.low1h)
    );
    const fourHourPosition = Number(
      regimeSnap?.metrics?.m4h?.positionInRange ??
        rangePosition(timeframeMetrics.high4h, timeframeMetrics.low4h)
    );
    const trend15m = Number(
      regimeSnap?.metrics?.m15?.changePct ?? timeframeMetrics.trend15m ?? 0
    );
    const recoveryConfirmed =
      samples15m.length >= 4 &&
      midpoint15m > 0 &&
      price >= midpoint15m &&
      !regimeSnap?.fresh15mLow &&
      trend15m >= -0.0015 &&
      oneHourPosition > 0.45 &&
      fourHourPosition > 0.15;
    if (!recoveryConfirmed) {
      const lastLogAt = Number(lastRecoveryDecisionLogRef.current[key] || 0);
      if (now - lastLogAt >= 5 * 60 * 1000) {
        lastRecoveryDecisionLogRef.current[key] = now;
        console.log("[GRID RESET SKIPPED]", {
          marketKey: key,
          reason: "RECOVERY_GRID",
          skipReason: "recovery confirmation missing",
          samples15m: samples15m.length,
          above15mMidpoint: midpoint15m > 0 && price >= midpoint15m,
          trend15m,
          oneHourPosition,
          fourHourPosition,
          fresh15mLow: !!regimeSnap?.fresh15mLow,
        });
      }
      return;
    }
    const persistedGridHistory = Array.isArray(pos.recoveryGridHistory)
      ? pos.recoveryGridHistory
      : [];
    const gridHist = (
      recoveryGridHistoryRef.current[key]?.length
        ? recoveryGridHistoryRef.current[key]
        : persistedGridHistory
    ).filter((timestamp) => timestamp >= now - 6 * 60 * 60 * 1000);
    if (gridHist.length >= GRID_RESET_RECOVERY_MAX_PER_6H) {
      console.log("[GRID RESET SKIPPED]", {
        marketKey: key,
        reason: "RECOVERY_GRID",
        skipReason: "six-hour recovery reset budget exhausted",
        recentRecoveryGridCount: gridHist.length,
        maxRecoveryGrids: GRID_RESET_RECOVERY_MAX_PER_6H,
      });
      return;
    }
    if (shouldSkipGridReset({
      key,
      reason: "RECOVERY_GRID",
      price,
      proposedBuy: t.priceTargetBuy,
      proposedSell: safeSellTarget,
      cooldownSeconds: GRID_RESET_RECOVERY_COOLDOWN_SECONDS,
      minPriceMoveBps: GRID_RESET_RECOVERY_MIN_PRICE_MOVE_BPS,
      minTargetChangeBps: GRID_RESET_RECOVERY_MIN_TARGET_CHANGE_BPS,
      hysteresisBps: GRID_RESET_RECOVERY_HYSTERESIS_BPS,
    })) {
      return;
    }
    console.log(
      `[RecoveryGrid] ${key} trailing BUY target to spot while preserving cost-aware SELL (drawdown ${(getDrawdownPct(pos.avgEntry, price) * 100).toFixed(2)}%, USDC ${((book.baseBalance / Math.max(book.totalBookUsd, 1)) * 100).toFixed(0)}%)`
    );
    const cooldownMs = Number(cfg.recoveryBuyCooldownMs ?? defaultBotCfg.recoveryBuyCooldownMs) || defaultBotCfg.recoveryBuyCooldownMs;
    const nextGridHistory = [...gridHist, now];
    recoveryGridHistoryRef.current[key] = nextGridHistory;
    updatePosition(key, {
      priceTargetBuy: t.priceTargetBuy,
      priceTargetSell: safeSellTarget,
      recoveryModeActive: true,
      lastRecoveryGridAt: now,
      recoveryBuyBlockedUntil: now + cooldownMs,
      recoveryGridHistory: nextGridHistory,
    });
    recordGridReset({
      key,
      reason: "RECOVERY_GRID",
      price,
      newBuy: t.priceTargetBuy,
      newSell: safeSellTarget,
      regime: regimeSnap?.regime,
    });
    emitBotResetDiscord(key, "RECOVERY_GRID", {
      buyTarget: t.priceTargetBuy,
      sellTarget: safeSellTarget,
      details: "Recovery confirmed: BUY trailed to spot; cost-aware SELL preserved",
      cooldownMs,
      regime: regimeSnap?.regime,
      recoveryBreakout: regimeSnap?.recoveryBreakoutActive,
    });
  };

  // Parse actual amounts from transaction receipt logs
  const parseTradeAmounts = (receipt, context) => {
    if (!receipt) {
      console.warn("[parseTradeAmounts] No receipt provided");
      return null;
    }
    
    if (!receipt.logs || !Array.isArray(receipt.logs) || receipt.logs.length === 0) {
      console.warn("[parseTradeAmounts] No logs in receipt", {
        receipt: receipt ? "present" : "missing",
        hasLogs: receipt?.logs ? "yes" : "no",
        logsLength: receipt?.logs?.length || 0,
      });
      return null;
    }
    
    const TRANSFER_TOPIC = "0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef";
    const userAddress = (executionAddress || receipt.from || "").toLowerCase();
    
    if (!currentConfig || !chainConfig) {
      console.warn("[parseTradeAmounts] Missing config", {
        hasCurrentConfig: !!currentConfig,
        hasChainConfig: !!chainConfig,
      });
      return null;
    }
    
    const wrappedNativeToken = currentConfig.wrappedNativeToken?.toLowerCase();
    const stablecoinAddress = currentConfig.stablecoinAddress?.toLowerCase();
    const side = context.side || "";
    
    // Determine market type
    const marketKey = context.marketKey || "";
    const wrappedSymbol = chainConfig.nativeCurrency.symbol;
    const stableSymbol = chainConfig.defaultStablecoin;
    const isWcroUsdc = marketKey === `${wrappedSymbol}-${stableSymbol}`;
    
    // Find all Transfer events
    const transfers = receipt.logs.filter(log => 
      log.topics && log.topics[0]?.toLowerCase() === TRANSFER_TOPIC.toLowerCase()
    );
    
    let amountIn = 0;
    let amountOut = 0;
    let tokenInAddress = "";
    let tokenOutAddress = "";
    
    // For WETH-USDC.e market, we know:
    // BUY: user sends USDC.e, receives WETH
    // SELL: user sends WETH, receives USDC.e
    
    if (isWcroUsdc) {
      // Parse transfers specifically for WETH-USDC.e market
      for (const log of transfers) {
        const logAddress = log.address?.toLowerCase();
        if (!log.topics || log.topics.length < 3) continue;
        
        const from = "0x" + log.topics[1]?.slice(-40).toLowerCase();
        const to = "0x" + log.topics[2]?.slice(-40).toLowerCase();
        const value = log.data || "0x0";
        
        const isFromUser = from === userAddress;
        const isToUser = to === userAddress;
        
        // Determine decimals
        let decimals = 18;
        if (logAddress === stablecoinAddress) {
          decimals = (stableSymbol === "USDC" || stableSymbol === "USDC.e" || stableSymbol === "USDT") ? 6 : 18;
        } else if (logAddress === wrappedNativeToken) {
          decimals = chainConfig.nativeCurrency.decimals || 18;
        }
        
        const amount = Number(ethers.utils.formatUnits(value, decimals));
        
        if (side === "BUY") {
          // BUY: USDC.e sent, WETH received
          if (isFromUser && logAddress === stablecoinAddress) {
            // User sent USDC.e
            if (amount > amountIn) {
              amountIn = amount;
              tokenInAddress = logAddress;
            }
          } else if (isToUser && logAddress === wrappedNativeToken) {
            // User received WETH
            if (amount > amountOut) {
              amountOut = amount;
              tokenOutAddress = logAddress;
            }
          }
        } else if (side === "SELL") {
          // SELL: WETH sent, USDC.e received
          if (isFromUser && logAddress === wrappedNativeToken) {
            // User sent WETH
            if (amount > amountIn) {
              amountIn = amount;
              tokenInAddress = logAddress;
            }
          } else if (isToUser && logAddress === stablecoinAddress) {
            // User received USDC.e
            if (amount > amountOut) {
              amountOut = amount;
              tokenOutAddress = logAddress;
            }
          }
        }
      }
    } else {
      // For other markets, use generic logic
      for (const log of transfers) {
        const logAddress = log.address?.toLowerCase();
        if (!log.topics || log.topics.length < 3) continue;
        
        const from = "0x" + log.topics[1]?.slice(-40).toLowerCase();
        const to = "0x" + log.topics[2]?.slice(-40).toLowerCase();
        const value = log.data || "0x0";
        
        let decimals = 18;
        if (logAddress === stablecoinAddress) {
          decimals = (stableSymbol === "USDC" || stableSymbol === "USDC.e" || stableSymbol === "USDT") ? 6 : 18;
        } else if (logAddress === wrappedNativeToken) {
          decimals = chainConfig.nativeCurrency.decimals || 18;
        }
        
        const isFromUser = from === userAddress;
        const isToUser = to === userAddress;
        
        if (isFromUser) {
          const amount = Number(ethers.utils.formatUnits(value, decimals));
          if (amount > amountIn) {
            amountIn = amount;
            tokenInAddress = logAddress;
          }
        } else if (isToUser) {
          const amount = Number(ethers.utils.formatUnits(value, decimals));
          if (amount > amountOut) {
            amountOut = amount;
            tokenOutAddress = logAddress;
          }
        }
      }
    }
    
    // Also check native ETH transfer if present
    if (receipt.value && ethers.BigNumber.from(receipt.value).gt(0)) {
      const nativeAmount = Number(ethers.utils.formatEther(receipt.value));
      if (side === "BUY" && isWcroUsdc) {
        // Buying with native ETH
        amountIn = nativeAmount;
      }
    }
    
    // CRITICAL FIX: Validate that we found both amounts before returning
    // If either is missing, return null to trigger fallback calculation
    if (amountIn <= 0 || amountOut <= 0 || !tokenInAddress || !tokenOutAddress) {
      console.warn(`[parseTradeAmounts] Incomplete parsing for ${side}:`, {
        amountIn,
        amountOut,
        tokenInAddress,
        tokenOutAddress,
        transfersFound: transfers.length,
        hasReceiptValue: !!(receipt.value && ethers.BigNumber.from(receipt.value).gt(0)),
      });
      return null;
    }
    
    const result = {
      amountIn,
      amountOut,
      tokenInAddress,
      tokenOutAddress,
      tokenInDecimals: tokenInAddress === stablecoinAddress ? 6 : 18,
      tokenOutDecimals: tokenOutAddress === stablecoinAddress ? 6 : 18,
    };
    
    console.log(`[parseTradeAmounts] Result for ${side}:`, {
      marketKey: context.marketKey,
      isWcroUsdc,
      amountIn,
      amountOut,
      tokenInAddress,
      tokenOutAddress,
      transfersFound: transfers.length,
      userAddress,
    });
    
    return result;
  };

  // Calculate and update PnL for a trade
  const updatePnL = (marketKey, side, amountIn, amountOut, price) => {
    if (!marketKey || !amountIn || !amountOut) return;
    
    setPnlData(prev => {
      // CRITICAL FIX: Sync PnL with position state if PnL is out of sync
      // This handles cases where position was updated optimistically but PnL wasn't
      const pos = positions[marketKey];
      const existingPnl = prev[marketKey] || {
        costBasis: 0,
        holdings: 0,
        realizedPnL: 0,
        totalTrades: 0,
        buyCount: 0,
        sellCount: 0,
      };
      
      // CRITICAL FIX: Ensure all numeric fields are actually numbers (handle cases where they might be strings from localStorage or undefined)
      existingPnl.costBasis = Number(existingPnl.costBasis) || 0;
      existingPnl.holdings = Number(existingPnl.holdings) || 0;
      existingPnl.realizedPnL = Number(existingPnl.realizedPnL) || 0;
      existingPnl.totalTrades = Number(existingPnl.totalTrades) || 0;
      existingPnl.buyCount = Number(existingPnl.buyCount) || 0;
      existingPnl.sellCount = Number(existingPnl.sellCount) || 0;
      
      // CRITICAL FIX: Sync PnL with position state to keep them aligned
      // This handles cases where position was updated but PnL wasn't, or vice versa
      
      // Case 1: PnL shows 0 holdings but position shows holdings - sync from position
      // CRITICAL FIX: Sync PnL from position state if:
      // - PnL is empty (holdings=0, costBasis=0)
      // - Position has data (holdings>0, avgEntry>0)
      // - We're processing a trade (hasActiveTrades OR side is defined, meaning we're in active trading)
      // This ensures PnL is synced before processing trades, especially SELLs that need cost basis
      const hasActiveTrades = existingPnl.totalTrades > 0;
      const isProcessingTrade = side !== undefined; // If side is defined, we're processing a trade
      
      if (existingPnl.holdings === 0 && existingPnl.costBasis === 0 && pos && pos.holdings > 0 && pos.avgEntry > 0 && (hasActiveTrades || isProcessingTrade)) {
        console.log(`[PnL Sync] Syncing PnL from position state: holdings=${pos.holdings}, avgEntry=${pos.avgEntry}, side=${side || 'N/A'}`);
        // Initialize cost basis from position
        existingPnl.holdings = pos.holdings;
        existingPnl.costBasis = pos.holdings * pos.avgEntry; // For WETH-USDC: holdings in WETH, avgEntry in USDC per WETH
      } else if (existingPnl.holdings === 0 && existingPnl.costBasis === 0 && pos && pos.holdings > 0 && pos.avgEntry > 0 && !hasActiveTrades && !isProcessingTrade) {
        // Position has data but PnL is empty and no trades yet - this is likely stale Moralis data
        // Don't sync to avoid initializing costBasis from stale data
        console.warn(`[PnL Sync Warning] Skipping sync from position state - likely stale Moralis data. PnL has no trades yet. Position: holdings=${pos.holdings}, avgEntry=${pos.avgEntry}`);
      }
      
      // Case 2: Position's avgEntry is 0 but PnL's costBasis is not 0 - reset costBasis
      // This can happen if avgEntry was reset (e.g., zero-holdings reset) but PnL wasn't updated yet
      // CRITICAL FIX: Atomic sync - ensure both avgEntry and costBasis are reset together
      if (pos && pos.avgEntry === 0 && pos.holdings === 0 && existingPnl.costBasis > 0) {
        console.log(`[PnL Sync] Position avgEntry reset to 0, resetting PnL costBasis from ${existingPnl.costBasis} to 0 (atomic sync)`);
        existingPnl.costBasis = 0;
        existingPnl.holdings = 0;
        // Validation: Log if sync was successful
        if (existingPnl.costBasis !== 0 || existingPnl.holdings !== 0) {
          console.error(`[PnL Sync Error] Failed to reset costBasis/holdings for ${marketKey}`);
        }
      }
      
      // Case 3: Position has holdings but avgEntry is 0 - this shouldn't happen, but sync if it does
      // CRITICAL FIX: Atomic sync - ensure avgEntry is derived from costBasis and both are consistent
      // Also recalculate targets to prevent percentage drift
      if (pos && pos.holdings > 0 && pos.avgEntry === 0 && existingPnl.costBasis > 0) {
        console.warn(`[PnL Sync Warning] Position has holdings but avgEntry is 0 - syncing avgEntry from PnL costBasis (atomic sync).`);
        // Derive avgEntry from PnL's costBasis
        const derivedAvgEntry = existingPnl.costBasis / existingPnl.holdings;
        if (derivedAvgEntry > 0 && isFinite(derivedAvgEntry)) {
          // CRITICAL FIX: Recalculate targets when fixing avgEntry to prevent percentage drift
          const cfg = getEffectiveBotCfg(marketKey);
          const buyPct  = cfg.buyTarget  ?? defaultBotCfg.buyTarget;
          const sellPct = cfg.sellTarget ?? defaultBotCfg.sellTarget;
          
          // Atomic update: Update position state synchronously with both avgEntry and targets
          updatePosition(marketKey, { 
            avgEntry: derivedAvgEntry,
            // Recalculate targets from new avgEntry to maintain correct percentages
            priceTargetBuy:  derivedAvgEntry * buyPct,
            priceTargetSell: derivedAvgEntry * sellPct,
          });
          // Validation: Verify sync was successful
          const costBasisNum = Number(existingPnl.costBasis) || 0;
          const holdingsNum = Number(existingPnl.holdings) || 0;
          console.log(`[PnL Sync] Derived avgEntry=${derivedAvgEntry.toFixed(6)} from costBasis=${costBasisNum.toFixed(2)} / holdings=${holdingsNum.toFixed(6)}, recalculated targets`);
        } else {
          console.error(`[PnL Sync Error] Invalid derived avgEntry for ${marketKey}: ${derivedAvgEntry}`);
        }
      }
      
      // Case 4: Validation - Ensure costBasis and avgEntry are consistent
      // If both exist, verify they match (within tolerance)
      // CRITICAL FIX: Don't auto-correct PnL costBasis if discrepancy is large - it might mean pos.avgEntry is wrong/stale
      // PnL is calculated from actual trade receipts and is more reliable than position state
      // Only log warnings, don't overwrite PnL with potentially stale position data
      if (pos && pos.holdings > 0 && pos.avgEntry > 0 && existingPnl.costBasis > 0 && existingPnl.holdings > 0) {
        const expectedCostBasis = pos.holdings * pos.avgEntry;
        const costBasisDiff = Math.abs(existingPnl.costBasis - expectedCostBasis);
        const costBasisDiffPct = expectedCostBasis > 0 ? (costBasisDiff / expectedCostBasis) * 100 : 0;
        
        // If discrepancy is > 1%, log warning (likely sync issue)
        if (costBasisDiffPct > 1) {
          const costBasisNum = Number(existingPnl.costBasis) || 0;
          const expectedAvgEntry = existingPnl.holdings > 0 ? existingPnl.costBasis / existingPnl.holdings : 0;
          console.warn(`[PnL Sync Warning] Cost basis mismatch for ${marketKey}: PnL=${costBasisNum.toFixed(2)} (avgEntry=${expectedAvgEntry.toFixed(2)}), Position=${expectedCostBasis.toFixed(2)} (avgEntry=${pos.avgEntry.toFixed(2)}), Diff=${costBasisDiffPct.toFixed(2)}%`);
          // CRITICAL FIX: Don't auto-correct - PnL is calculated from actual receipts and is more reliable
          // Large discrepancies likely mean pos.avgEntry is stale/wrong, not that PnL is wrong
          // If correction is needed, it should sync position FROM PnL, not the other way around
        }
      }
      
      const marketPnl = existingPnl;
      
      const wrappedSymbol = chainConfig?.nativeCurrency?.symbol || "ETH";
      const stableSymbol = chainConfig?.defaultStablecoin || "USDC";
      const isWcroUsdc = marketKey === `${wrappedSymbol}-${stableSymbol}`;
      
      let newCostBasis = marketPnl.costBasis;
      let newHoldings = marketPnl.holdings;
      let newRealizedPnL = marketPnl.realizedPnL;
      
      if (side === "BUY") {
        // BUY: Spending base currency (USDC.e) to get token (WETH)
        // Cost basis increases by amount spent
        // Holdings increase by amount received
        if (isWcroUsdc) {
          // For WETH-USDC.e: spending USDC.e, getting WETH
          newCostBasis = marketPnl.costBasis + amountIn; // USDC.e spent
          newHoldings = marketPnl.holdings + amountOut;  // WETH received
          
          console.log(`[PnL Debug] BUY: amountIn=${amountIn} USDC.e, amountOut=${amountOut} WETH, newCostBasis=${newCostBasis}, newHoldings=${newHoldings}`);
        } else {
          // For other markets, use price to convert
          newCostBasis = marketPnl.costBasis + amountIn;
          newHoldings = marketPnl.holdings + amountOut;
        }
        marketPnl.buyCount += 1;
      } else if (side === "SELL") {
        // SELL: Spending token (WETH) to get base currency (USDC.e)
        // Calculate realized PnL based on average cost basis
        
        // CRITICAL FIX: Sync PnL from position state before processing SELL if PnL is reset but position has data
        // This ensures profit is calculated even if PnL was reset (e.g., after page reload or data sync issue)
        if (marketPnl.holdings === 0 && marketPnl.costBasis === 0 && pos && pos.holdings > 0 && pos.avgEntry > 0) {
          console.log(`[PnL Sync] SELL: Syncing PnL from position state before calculating profit: holdings=${pos.holdings}, avgEntry=${pos.avgEntry}`);
          marketPnl.holdings = pos.holdings;
          marketPnl.costBasis = pos.holdings * pos.avgEntry; // For WETH-USDC: holdings in WETH, avgEntry in USDC per WETH
        }
        
        if (isWcroUsdc) {
          // For WETH-USDC.e: spending WETH, getting USDC.e
          const avgCostPerToken = marketPnl.holdings > 0 
            ? marketPnl.costBasis / marketPnl.holdings 
            : 0;
          
          // CRITICAL FIX: Ensure all values are numbers before logging/using
          const holdingsNum = Number(marketPnl.holdings) || 0;
          const costBasisNum = Number(marketPnl.costBasis) || 0;
          const avgCostPerTokenNum = Number(avgCostPerToken) || 0;
          const amountInNum = Number(amountIn) || 0;
          const amountOutNum = Number(amountOut) || 0;
          
          console.log(`[PnL Debug] SELL: holdings=${holdingsNum}, costBasis=${costBasisNum}, avgCostPerToken=${avgCostPerTokenNum}, amountIn=${amountInNum}, amountOut=${amountOutNum}`);
          
          // CRITICAL FIX: Use numeric values for calculations
          if (avgCostPerTokenNum > 0 && holdingsNum > 0) {
            // Calculate cost basis of tokens sold
            const costBasisSold = amountInNum * avgCostPerTokenNum; // WETH sold * avg cost per WETH
            // Realized PnL = amount received - cost basis of tokens sold
            const tradePnL = amountOutNum - costBasisSold;
            // CRITICAL FIX: Use numeric values for all calculations
            const realizedPnLNum = Number(marketPnl.realizedPnL) || 0;
            newRealizedPnL = realizedPnLNum + tradePnL;
            
            // Check for profit-based re-anchoring: Reset anchor after realizing 2-3% profit
            const totalCostBasis = costBasisNum; // Original cost basis before this trade (use numeric value)
            if (totalCostBasis > 0) {
              const realizedProfitPct = (newRealizedPnL / totalCostBasis);
              const lastProfitPct = lastRealizedProfitPctRef.current[marketKey] || 0;
              
              // If realized profit crossed the threshold (2.5%), trigger re-anchor
              if (realizedProfitPct >= PROFIT_BASED_REANCHOR_PCT && lastProfitPct < PROFIT_BASED_REANCHOR_PCT) {
                const pos = positions[marketKey];
                if (pos && price > 0) {
                  const cfg = getEffectiveBotCfg(marketKey);
                  const inverted = marketKey === "WCRO-USDC";
                  // CRITICAL FIX: Use avgEntry as anchor (cost basis), NOT current price
                  // avgEntry represents the actual cost basis and should NEVER be reset when holdings exist
                  // Only reset targets to create a fresh grid around the current cost basis
                  const anchor = pos.avgEntry > 0 ? pos.avgEntry : price;
                  const buyPct  = cfg.buyTarget  ?? defaultBotCfg.buyTarget;
                  const sellPct = cfg.sellTarget ?? defaultBotCfg.sellTarget;
                  
                  const realizedProfitPctNum = Number(realizedProfitPct) || 0;
                  console.log(
                    `[ProfitBasedReanchor] ${marketKey} - Realized ${(realizedProfitPctNum * 100).toFixed(2)}% profit → resetting targets around cost basis ${safeFix(anchor)} (NOT resetting avgEntry)`
                  );
                  
                  const pBuy = anchor * (inverted ? sellPct : buyPct);
                  const pSell = anchor * (inverted ? buyPct : sellPct);
                  updatePosition(marketKey, {
                    priceTargetBuy: pBuy,
                    priceTargetSell: pSell,
                  });
                  emitBotResetDiscord(marketKey, "PROFIT_REANCHOR", {
                    buyTarget: pBuy,
                    sellTarget: pSell,
                    details: `Realized profit ${(realizedProfitPctNum * 100).toFixed(2)}% → fresh grid at cost basis`,
                  });
                  lastReanchorAtRef.current[marketKey] = Date.now();
                  lastRealizedProfitPctRef.current[marketKey] = realizedProfitPct;
                }
              } else {
                // Update last profit % even if not crossing threshold
                lastRealizedProfitPctRef.current[marketKey] = realizedProfitPct;
              }
            }
            
            // Reduce cost basis proportionally (use numeric values)
            newCostBasis = Math.max(0, costBasisNum - costBasisSold);
            newHoldings = Math.max(0, holdingsNum - amountInNum);
            
            // CRITICAL FIX: If holdings go to zero, reset cost basis to sync with position state
            // This ensures PnL's costBasis and position's avgEntry stay aligned (both reset to 0)
            if (newHoldings <= 0.0001) {
              console.log(`[PnL] Holdings depleted, resetting cost basis from ${newCostBasis} to 0 (synced with avgEntry reset)`);
              newCostBasis = 0;
              // Also reset realized PnL to start fresh for next cycle
              // Note: We keep realizedPnL as it represents historical performance
            }
            
            console.log(`[PnL Debug] SELL result: costBasisSold=${costBasisSold}, tradePnL=${tradePnL}, newCostBasis=${newCostBasis}, newHoldings=${newHoldings}, newRealizedPnL=${newRealizedPnL}`);
          } else {
            // No cost basis yet or no holdings - this shouldn't happen for a valid sell
            // CRITICAL FIX: Ensure numeric values before calculations
            const holdingsNumWarn = Number(marketPnl.holdings) || 0;
            const costBasisNumWarn = Number(marketPnl.costBasis) || 0;
            const amountInNumWarn = Number(amountIn) || 0;
            console.warn(`[PnL Warning] SELL with no cost basis: holdings=${holdingsNumWarn}, costBasis=${costBasisNumWarn}`);
            // If we're selling but have no cost basis, it means we sold everything already
            // Just record the sale without affecting PnL (likely a dust trade or error)
            newCostBasis = 0;
            newHoldings = Math.max(0, holdingsNumWarn - amountInNumWarn);
            newRealizedPnL = marketPnl.realizedPnL;
          }
        } else {
          // For other markets
          const avgCostPerToken = marketPnl.holdings > 0 
            ? marketPnl.costBasis / marketPnl.holdings 
            : 0;
          
          if (avgCostPerToken > 0 && marketPnl.holdings > 0) {
            const costBasisSold = amountIn * avgCostPerToken;
            const tradePnL = amountOut - costBasisSold;
            newRealizedPnL = marketPnl.realizedPnL + tradePnL;
            newCostBasis = Math.max(0, marketPnl.costBasis - costBasisSold);
            newHoldings = Math.max(0, marketPnl.holdings - amountIn);
          } else {
            newCostBasis = marketPnl.costBasis;
            newHoldings = Math.max(0, marketPnl.holdings - amountIn);
            newRealizedPnL = marketPnl.realizedPnL;
          }
        }
        marketPnl.sellCount += 1;
      }
      
      const updated = {
        ...prev,
        [marketKey]: {
          ...marketPnl,
          costBasis: newCostBasis,
          holdings: newHoldings,
          realizedPnL: newRealizedPnL,
          totalTrades: marketPnl.totalTrades + 1,
          lastUpdated: Date.now(),
        }
      };
      
      // Persist to localStorage
      try {
        localStorage.setItem(
          getBotStorageKey("botPnlData", botInstanceId),
          JSON.stringify(updated)
        );
      } catch (e) {
        console.warn('Failed to save PnL data:', e);
      }
      
      return updated;
    });
  };

  // Receipt accounting must commit and report the same immutable snapshot.
  const commitConfirmedPnlTrade = (
    marketKey,
    side,
    amountIn,
    amountOut,
    price,
    txHash = "",
    meta = {}
  ) => {
    const currentData = pnlDataRef.current || {};
    const previousRecord = normalizePnlRecord(currentData[marketKey]);
    const eligibility = validateConfirmedTradeFill({
      eventType: meta.eventType || "TRADE_FILL",
      status: meta.status || "CONFIRMED",
      side,
      marketKey,
      txHash,
      amountIn,
      amountOut,
      hasReceiptEvidence: meta.hasReceiptEvidence === true,
      processedTxHashes: previousRecord.processedTxHashes,
    });
    if (!eligibility.allowed) {
      console.warn("[PnL Ledger] Event rejected", {
        marketKey,
        side,
        txHash,
        reason: eligibility.reason,
      });
      return previousRecord;
    }
    const result = applyConfirmedTradeToPnlRecord(
      previousRecord,
      side,
      amountIn,
      amountOut,
      txHash
    );

    if (!result.applied) {
      console.warn("[PnL Ledger] Confirmed trade not applied", {
        marketKey,
        side,
        txHash,
        reason: result.reason,
      });
      return result.record;
    }

    result.record.totalGasUsd =
      previousRecord.totalGasUsd + Math.max(0, Number(meta.gasUsd) || 0);

    if (
      String(side).toUpperCase() === "SELL" &&
      isWrappedStableMarket(marketKey) &&
      Number(price) > 0 &&
      result.record.holdings * Number(price) < ZERO_HOLDINGS_THRESHOLD_USD
    ) {
      result.record = {
        ...result.record,
        holdings: 0,
        costBasis: 0,
      };
    }

    const nextData = { ...currentData, [marketKey]: result.record };
    pnlDataRef.current = nextData;
    setPnlData(nextData);
    persistPnlSnapshot(nextData, { immediate: true });

    console.log("[PnL Ledger] Confirmed trade committed", {
      marketKey,
      side,
      txHash,
      amountIn,
      amountOut,
      tradePnL: result.tradePnL,
      realizedPnL: result.record.realizedPnL,
      unrealizedPnL: getUnrealizedPnlForRecord(result.record, price),
      holdings: result.record.holdings,
      costBasis: result.record.costBasis,
      avgEntry: getPnlAverageEntry(result.record),
      totalGasUsd: result.record.totalGasUsd,
      costBasisCoverageRatio: result.costBasisCoverageRatio,
      excludedProceedsUsd: result.excludedProceedsUsd,
    });

    if (
      String(side).toUpperCase() === "SELL" &&
      result.realizedCostRemoved > 0 &&
      result.fullyCostCovered
    ) {
      const cfg = getEffectiveBotCfg(marketKey);
      const reanchor = evaluateProfitReanchor({
        tradePnl: result.tradePnL,
        realizedCostRemoved: result.realizedCostRemoved,
        receivedUsd: amountOut,
        minTradeUsd: cfg.minTradeUsd,
        requiredReturnPct: PROFIT_BASED_REANCHOR_PCT,
      });
      lastRealizedProfitPctRef.current[marketKey] = reanchor.tradeReturnPct;
      if (reanchor.allowed) {
        const pos = getPositionForDecisions(marketKey);
        const cooldownElapsed =
          Date.now() - Number(lastReanchorAtRef.current[marketKey] || 0) >= 60 * 60 * 1000;
        if (pos && price > 0 && cooldownElapsed) {
          const inverted = marketKey === "WCRO-USDC";
          const pnlEntry = getPnlAverageEntry(result.record);
          const anchor = pnlEntry > 0 ? pnlEntry : pos.avgEntry > 0 ? pos.avgEntry : price;
          const buyPct = cfg.buyTarget ?? defaultBotCfg.buyTarget;
          const sellPct = cfg.sellTarget ?? defaultBotCfg.sellTarget;
          const pBuy = anchor * (inverted ? sellPct : buyPct);
          const pSell = Math.max(
            anchor * (inverted ? buyPct : sellPct),
            getNormalSellFloorPrice({ avgEntry: anchor, minProfitBps: cfg.normalSellMinProfitBps })
          );
          updatePosition(marketKey, { priceTargetBuy: pBuy, priceTargetSell: pSell });
          emitBotResetDiscord(marketKey, "PROFIT_REANCHOR", {
            buyTarget: pBuy,
            sellTarget: pSell,
            details: `Meaningful sell returned ${(reanchor.tradeReturnPct * 100).toFixed(2)}% -> fresh grid at cost basis`,
          });
          lastReanchorAtRef.current[marketKey] = Date.now();
        }
      } else if (Number(amountOut) < reanchor.minimumNotionalUsd) {
        console.log("[PROFIT REANCHOR SKIPPED]", {
          marketKey,
          txHash,
          reason: reanchor.reason,
          receivedUsd: Number(amountOut),
          minimumNotionalUsd: reanchor.minimumNotionalUsd,
        });
      }
    } else if (String(side).toUpperCase() === "SELL" && !result.fullyCostCovered) {
      console.warn("[PROFIT REANCHOR SKIPPED]", {
        marketKey,
        txHash,
        reason: "sell is not fully covered by confirmed cost basis",
        costBasisCoverageRatio: result.costBasisCoverageRatio,
        uncoveredAssetAmount: result.uncoveredAssetAmount,
        excludedProceedsUsd: result.excludedProceedsUsd,
      });
    }

    return result.record;
  };

  // Calculate unrealized PnL for current holdings
  const calculateUnrealizedPnL = (marketKey) => {
    const marketPnl = pnlData[marketKey];
    if (!marketPnl) return 0;
    const pos = positions[marketKey];
    const currentPrice = markets.find(m => m.key === marketKey)?.price || pos?.avgEntry || 0;
    return getUnrealizedPnlForRecord(marketPnl, currentPrice);
  };

  const emitBotResetDiscord = async (marketKey, resetKind, extra = {}) => {
    if (!persistencePlayer || !marketKey) return;
    const dedupeKey = `${marketKey}:${resetKind}`;
    const cooldownMs = extra.cooldownMs ?? 10 * 60 * 1000;
    const last = lastDiscordResetNotifyRef.current[dedupeKey] || 0;
    if (Date.now() - last < cooldownMs) return;
    lastDiscordResetNotifyRef.current[dedupeKey] = Date.now();

    const price = markets.find((m) => m.key === marketKey)?.price || 0;
    const pos = positions[marketKey] || {};
    const mp = normalizePnlRecord(pnlDataRef.current[marketKey]);
    const unrealized = getUnrealizedPnlForRecord(mp, price);
    const realized = Number(mp.realizedPnL) || 0;
    const pnlAvgEntry = getPnlAverageEntry(mp);
    const avgEntry = pnlAvgEntry > 0 ? pnlAvgEntry : pos.avgEntry;
    let chainId = chainConfig?.chainId || "unknown";
    if (typeof chainId === "number") chainId = String(chainId);

    const cfgEff = getEffectiveBotCfg(marketKey);
    const regimeSnap = marketRegimeRef.current[marketKey];
    const book = getMarketBook(marketKey, pos, price, positionsRef.current, chainConfig);
    const regimeLine = isRegimeEngineEnabled(marketKey)
      ? formatRegimeCompact(
          { ...regimeSnap, consecBuys: Math.max(0, (pos.buyCounter || 1) - 1) },
          book,
          cfgEff
        )
      : null;
    const details = [extra.details, regimeLine].filter(Boolean).join(" | ");

    try {
      await notifyDiscordBotReset({
        botInstanceId,
        marketKey,
        resetKind,
        chain: chainId,
        wethPriceUsd: price,
        avgEntry,
        buyTarget: extra.buyTarget ?? pos.priceTargetBuy,
        sellTarget: extra.sellTarget ?? pos.priceTargetSell,
        drawdownPct:
          avgEntry > 0 && price > 0 ? getDrawdownPct(avgEntry, price) : undefined,
        targetWethPct: extra.targetWethPct,
        details,
        regime: regimeSnap?.regime,
        recoveryBreakout: regimeSnap?.recoveryBreakoutActive,
        regimeDiagnostics: regimeLine,
        pnl: { realizedPnL: realized, unrealizedPnL: unrealized },
      });
    } catch (e) {
      console.warn("[Discord Reset]", e?.message || e);
    }
  };
  
  // Confirmed receipt accounting is authoritative for cost basis and avgEntry.
  // RPC balances remain authoritative for the actual wallet holdings.
  useEffect(() => {
    if (!chainConfig) return;
    markets.forEach(({ key, price }) => {
      if (!isWrappedStableMarket(key)) return;
      if (isMarketTradeInFlight(key)) return;
      const pos = positionsRef.current[key] || {};
      const marketPnl = normalizePnlRecord(pnlDataRef.current[key]);
      if (!(marketPnl.totalTrades > 0)) return;

      const actualHoldings = Math.max(0, Number(pos.holdings) || 0);
      const ledgerHoldings = Math.max(0, Number(marketPnl.holdings) || 0);
      const holdingsDifference = Math.abs(actualHoldings - ledgerHoldings);
      if (holdingsDifference > 0.00001) {
        console.warn("[PnL Reconciliation] Wallet and receipt-ledger holdings differ", {
          marketKey: key,
          positionHoldings: actualHoldings,
          ledgerHoldings,
          difference: holdingsDifference,
        });
      }

      const accounting = getAuthoritativeAverageEntry({
        costBasis: marketPnl.costBasis,
        ledgerHoldings,
        positionAvgEntry: pos.avgEntry,
      });
      if (accounting.source !== "confirmed-fill-ledger") return;
      const currentAvgEntry = Math.max(0, Number(pos.avgEntry) || 0);
      const avgDifferenceBps = currentAvgEntry > 0
        ? Math.abs(accounting.avgEntry - currentAvgEntry) / currentAvgEntry * 10000
        : Infinity;
      if (avgDifferenceBps < 0.1) return;

      const cfg = getEffectiveBotCfg(key);
      const livePrice = Number(price) || Number(lastPriceRef.current[key]) || accounting.avgEntry;
      const buyPct = cfg.buyTarget ?? defaultBotCfg.buyTarget;
      const sellPct = cfg.sellTarget ?? defaultBotCfg.sellTarget;
      const targets = getSafeTimeReanchorTargets({
        currentPrice: livePrice,
        avgEntry: accounting.avgEntry,
        buyMultiplier: buyPct,
        sellMultiplier: sellPct,
        minimumSellPrice: getNormalSellFloorPrice({
          avgEntry: accounting.avgEntry,
          minProfitBps: cfg.normalSellMinProfitBps,
        }),
      });
      updatePosition(key, {
        avgEntry: accounting.avgEntry,
        priceTargetBuy: targets.priceTargetBuy,
        priceTargetSell: targets.priceTargetSell,
        accountingReconciledAt: Date.now(),
      });
      console.warn("[POSITION ACCOUNTING REPAIRED]", {
        marketKey: key,
        oldAvgEntry: currentAvgEntry,
        confirmedAvgEntry: accounting.avgEntry,
        avgDifferenceBps,
        buyTarget: targets.priceTargetBuy,
        sellTarget: targets.priceTargetSell,
      });
    });
  }, [positions, pnlData, markets, chainConfig, botConfigs]);

  // Repair older snapshots only when confirmed receipt replay exactly explains the wallet holdings.
  useEffect(() => {
    if (!executionAddress || !provider || !currentConfig || !chainConfig || tradeHistory.length === 0) return;

    const wrappedSymbol = chainConfig.nativeCurrency.symbol;
    const stableSymbol = chainConfig.defaultStablecoin;
    const marketKey = `${wrappedSymbol}-${stableSymbol}`;
    const baseKey = `${marketKey}_BASE`;
    if (!Object.prototype.hasOwnProperty.call(positions, baseKey)) return;
    if (normalizePnlRecord(pnlDataRef.current?.[marketKey]).accountingCheckpointAt > 0) {
      return;
    }
    if (
      (pendingTradesRef.current[marketKey] || []).length > 0 ||
      pendingTradeSubmissionsRef.current[marketKey]
    ) {
      return;
    }

    const entries = tradeHistory
      .filter((entry) => entry?.marketKey === marketKey && entry?.txHash)
      .filter((entry, index, all) =>
        all.findIndex((candidate) =>
          String(candidate.txHash).toLowerCase() === String(entry.txHash).toLowerCase()
        ) === index
      )
      .sort((a, b) => Number(a.ts || 0) - Number(b.ts || 0));
    if (entries.length < 2) return;

    const fingerprint = entries
      .map((entry) => String(entry.txHash).toLowerCase())
      .join(":");
    if (
      pnlLedgerRepairRef.current.fingerprint === fingerprint &&
      ["running", "complete", "incomplete"].includes(pnlLedgerRepairRef.current.status)
    ) {
      return;
    }
    pnlLedgerRepairRef.current = { fingerprint, status: "running" };

    let cancelled = false;
    (async () => {
      let rebuilt = normalizePnlRecord({});
      for (const entry of entries) {
        if (cancelled) return;
        let actualAmountIn = finitePnlNumber(entry.actualAmountIn);
        let actualAmountOut = finitePnlNumber(entry.actualAmountOut);

        if (!(actualAmountIn > 0) || !(actualAmountOut > 0)) {
          const receipt = await provider.getTransactionReceipt(entry.txHash);
          if (!receipt || receipt.status === 0) {
            pnlLedgerRepairRef.current = { fingerprint, status: "incomplete" };
            console.warn("[PnL Ledger Repair] Missing successful receipt", {
              marketKey,
              txHash: entry.txHash,
            });
            return;
          }
          const parsed = parseTradeAmounts(receipt, {
            marketKey,
            side: entry.side,
            initiator: entry.initiator,
            tokenAddress: entry.token,
          });
          actualAmountIn = finitePnlNumber(parsed?.amountIn);
          actualAmountOut = finitePnlNumber(parsed?.amountOut);
        }

        if (!(actualAmountIn > 0) || !(actualAmountOut > 0)) {
          pnlLedgerRepairRef.current = { fingerprint, status: "incomplete" };
          console.warn("[PnL Ledger Repair] Receipt amounts unavailable", {
            marketKey,
            txHash: entry.txHash,
          });
          return;
        }

        if (
          String(entry.side).toUpperCase() === "SELL" &&
          actualAmountIn > rebuilt.holdings + 0.00001
        ) {
          pnlLedgerRepairRef.current = { fingerprint, status: "incomplete" };
          console.warn("[PnL Ledger Repair] History starts after the opening inventory; preserving current PnL", {
            marketKey,
            txHash: entry.txHash,
            ledgerHoldings: rebuilt.holdings,
            sellAmount: actualAmountIn,
          });
          return;
        }

        const applied = applyConfirmedTradeToPnlRecord(
          rebuilt,
          entry.side,
          actualAmountIn,
          actualAmountOut,
          entry.txHash
        );
        if (!applied.applied) {
          pnlLedgerRepairRef.current = { fingerprint, status: "incomplete" };
          return;
        }
        rebuilt = applied.record;
      }

      const chainHoldings = Math.max(0, finitePnlNumber(positions[marketKey]?.holdings));
      const holdingsTolerance = Math.max(0.00001, chainHoldings * 0.002);
      if (Math.abs(chainHoldings - rebuilt.holdings) > holdingsTolerance) {
        pnlLedgerRepairRef.current = { fingerprint, status: "incomplete" };
        console.warn("[PnL Ledger Repair] Rebuilt holdings do not match the wallet; preserving current PnL", {
          marketKey,
          walletHoldings: chainHoldings,
          rebuiltHoldings: rebuilt.holdings,
          tolerance: holdingsTolerance,
        });
        return;
      }

      if (cancelled) return;
      const currentData = pnlDataRef.current || {};
      const repairedRecord = {
        ...rebuilt,
        holdings: chainHoldings <= PNL_ASSET_DUST ? 0 : rebuilt.holdings,
        costBasis: chainHoldings <= PNL_ASSET_DUST ? 0 : rebuilt.costBasis,
        rebuiltFromReceiptHistoryAt: Date.now(),
      };
      const nextData = { ...currentData, [marketKey]: repairedRecord };
      pnlDataRef.current = nextData;
      setPnlData(nextData);
      persistPnlSnapshot(nextData, { immediate: true });
      pnlLedgerRepairRef.current = { fingerprint, status: "complete" };
      console.log("[PnL LEDGER REBUILT]", {
        marketKey,
        trades: repairedRecord.totalTrades,
        realizedPnL: repairedRecord.realizedPnL,
        holdings: repairedRecord.holdings,
        costBasis: repairedRecord.costBasis,
        avgEntry: getPnlAverageEntry(repairedRecord),
      });
    })().catch((error) => {
      pnlLedgerRepairRef.current = { fingerprint, status: "incomplete" };
      console.warn("[PnL Ledger Repair] Failed; preserving current PnL", error);
    });

    return () => {
      cancelled = true;
      if (
        pnlLedgerRepairRef.current.fingerprint === fingerprint &&
        pnlLedgerRepairRef.current.status === "running"
      ) {
        pnlLedgerRepairRef.current = { fingerprint: "", status: "idle" };
      }
    };
  }, [executionAddress, provider, currentConfig, chainConfig, tradeHistory, positions]);

  const processTradeReceipt = async (receipt, amount, side, context = {}) => {
    if (!receipt) {
      console.warn("[processTradeReceipt] No receipt provided");
      return;
    }

    const marketKey =
      context.marketKey ||
      (context.initiator === "CRO"
        ? "WBTC-WCRO"
        : context.initiator === "USDC" || context.initiator === "USDC.e"
        ? "WBTC-USDC"
        : `${chainConfig?.nativeCurrency?.symbol || "ETH"}-${chainConfig?.defaultStablecoin || "USDC"}`);

    const txHash =
      receipt.transactionHash ||
      receipt.hash ||
      (Array.isArray(receipt) && receipt[0] && receipt[0].transactionHash) ||
      "";

    if (receipt.status === 0 || receipt.status === false) {
      console.error(`[processTradeReceipt] On-chain failure (status=${receipt.status}) for ${marketKey}`, { txHash });
      if (context.pendingId) rollbackPending(marketKey, context.pendingId);
      delete pendingTradeSubmissionsRef.current[marketKey];
      tradeLockRef.current[marketKey] = false;
      return;
    }
    if (!txHash && (!Array.isArray(receipt.logs) || receipt.logs.length === 0)) {
      console.error(
        `[processTradeReceipt] Invalid receipt (no tx hash and no logs) for ${marketKey} — skipping PnL / history`
      );
      if (context.pendingId) rollbackPending(marketKey, context.pendingId);
      delete pendingTradeSubmissionsRef.current[marketKey];
      tradeLockRef.current[marketKey] = false;
      return;
    }

    if (txHash) {
      const txKey = txHash.toLowerCase();
      if (processedTradeTxHashesRef.current[txKey]) {
        console.warn("[processTradeReceipt] Duplicate receipt skipped", {
          marketKey,
          txHash,
          firstProcessedAt: processedTradeTxHashesRef.current[txKey],
        });
        if (context.pendingId) commitPending(marketKey, context.pendingId);
        delete pendingTradeSubmissionsRef.current[marketKey];
        tradeLockRef.current[marketKey] = false;
        return;
      }
      processedTradeTxHashesRef.current[txKey] = Date.now();
    }

    let externalSignalForHistory = context.externalSignal || null;
    if (!externalSignalForHistory) {
      const snapshot = externalSignalServiceRef.current.getSnapshot();
      const receiptPoolPrice = Number(markets.find((market) => market.key === marketKey)?.price) || 0;
      externalSignalForHistory = recordExternalSignalDecision({
        timestamp: Date.now(),
        marketKey,
        side: String(side).toUpperCase(),
        mode: "bypass",
        status: snapshot.status,
        fairPrice: snapshot.fairWethUsdc || 0,
        fairBid: snapshot.fairBid || 0,
        fairAsk: snapshot.fairAsk || 0,
        poolPrice: receiptPoolPrice,
        poolAddress: ARBITRUM_WETH_USDC_POOL_ADDRESS,
        dexExecutablePrice: 0,
        signalQuality: snapshot.signalQuality || 0,
        opportunityMagnitudeBps: 0,
        directionalExecutableEdgeBps: 0,
        opportunityDirection: "neutral",
        agreement: snapshot.agreement || 0,
        dispersionBps: snapshot.dispersionBps,
        momentumBps: snapshot.momentumBps || 0,
        feedFreshnessMs: snapshot.feedFreshnessMs,
        sourceCount: snapshot.sourceCount || 0,
        sources: snapshot.sources || [],
        sizeMultiplier: 1,
        appliedMultiplier: 1,
        baseTradeSizeUsd: 0,
        recommendedTradeSizeUsd: 0,
        finalTradeSizeUsd: 0,
        recommendedThresholdAdjustmentBps: 0,
        thresholdAdjustmentBps: 0,
        changedAnything: false,
        reason: "Manual, risk-control, or legacy execution bypassed external sizing.",
        txHash,
        blockNumber: receipt.blockNumber ?? null,
      });
    }

    console.log(`[processTradeReceipt] Processing ${side} trade for ${marketKey}`, {
      receipt: receipt ? "present" : "missing",
      logs: receipt?.logs?.length || 0,
      txHash,
      context,
    });

    // Parse actual amounts from receipt logs
    const tradeAmounts = parseTradeAmounts(receipt, { ...context, side });
    
    console.log(`[processTradeReceipt] Parsed amounts:`, {
      tradeAmounts,
      amountIn: tradeAmounts?.amountIn,
      amountOut: tradeAmounts?.amountOut,
      hasValidAmounts: tradeAmounts && tradeAmounts.amountIn > 0 && tradeAmounts.amountOut > 0,
    });
    
    // ETH-USDC core accounting uses the configured V3 pool price at the receipt block when possible.
    const marketPriceFallback = markets.find(m => m.key === marketKey)?.price || lastPriceRef.current[marketKey] || 0;
    const currentPrice = await getWethUsdcPriceForCore({
      marketKey,
      blockTag: receipt.blockNumber,
      usage: "trade-receipt:pnl-notification-benchmark",
      fallbackPrice: marketPriceFallback,
    });
    let confirmedGasUsd = 0;
    try {
      const gasUsedRaw = receipt.gasUsed;
      const gasPriceRaw = receipt.effectiveGasPrice || receipt.gasPrice;
      if (gasUsedRaw != null && gasPriceRaw != null && currentPrice > 0) {
        const gasNative = Number(
          ethers.utils.formatEther(
            ethers.BigNumber.from(gasUsedRaw).mul(ethers.BigNumber.from(gasPriceRaw))
          )
        );
        const gasUsd = gasNative * currentPrice;
        if (Number.isFinite(gasUsd) && gasUsd > 0) {
          confirmedGasUsd = gasUsd;
          recentGasCostUsdRef.current[marketKey] = {
            gasUsd,
            observedAt: Date.now(),
            txHash,
          };
          console.log("[TRADE GAS COST]", { marketKey, txHash, gasNative, gasUsd });
        }
      }
    } catch (gasError) {
      console.warn("[TRADE GAS COST] Unable to parse receipt gas", {
        marketKey,
        txHash,
        error: gasError?.message || gasError,
      });
    }
    if (tradeAmounts?.amountIn > 0 && tradeAmounts?.amountOut > 0) {
      if (side === "SELL" && context.fromStuckStop) {
        recordConfirmedStuckStop({
          marketKey,
          txHash,
          blockNumber: receipt.blockNumber,
          currentPrice,
          tradeAmounts,
          context,
        });
      } else if (side === "BUY") {
        recordFirstRebuyAfterStuckStop({
          marketKey,
          txHash,
          blockNumber: receipt.blockNumber,
          currentPrice,
          tradeAmounts,
        });
      }
    }
    if (side === "SELL" && context.fullExitTransition) {
      const exitCfg = getEffectiveBotCfg(marketKey);
      const exitTargets = computeGridTargets({
        price: currentPrice,
        avgEntry: 0,
        buyPct: exitCfg.buyTarget ?? defaultBotCfg.buyTarget,
        sellPct: exitCfg.sellTarget ?? defaultBotCfg.sellTarget,
        useSpotAnchor: true,
      });
      updatePosition(marketKey, {
        avgEntry: 0,
        priceTargetBuy: exitTargets.priceTargetBuy,
        priceTargetSell: exitTargets.priceTargetSell,
        buyCounter: 1,
        flatSince: Number(context.fullExitAt || Date.now()),
        fullExitPrice: currentPrice,
        fullExitTxHash: txHash,
        flatReentryNearMissSince: 0,
      });
      console.log("[FLAT BOOK CONFIRMED]", {
        marketKey,
        txHash,
        fullExitPrice: currentPrice,
        buyTarget: exitTargets.priceTargetBuy,
      });
    } else if (side === "BUY") {
      const flatBuyPatch = {
        flatSince: 0,
        flatReentryNearMissSince: 0,
      };
      if (context.fromFlatReentry) {
        flatBuyPatch.lastFlatReentryAt = Date.now();
      }
      updatePosition(marketKey, flatBuyPatch);
    }
    const preStableBalance = Number.isFinite(Number(context.preStableBalance))
      ? Number(context.preStableBalance)
      : NaN;
    const preWrappedBalance = Number.isFinite(Number(context.preWrappedBalance))
      ? Number(context.preWrappedBalance)
      : NaN;
    
    // Keep the submission active through PnL reconciliation, notification, and
    // wallet refresh. Clearing it here allowed another market tick to trade on
    // the optimistic position before confirmed state had settled.
    if (pendingTradeSubmissionsRef.current[marketKey]) {
      const pendingTrade = pendingTradeSubmissionsRef.current[marketKey];
      const receiptDelay = Date.now() - pendingTrade.submittedAt;
      console.log(`[processTradeReceipt] Trade receipt confirmed after ${receiptDelay}ms for ${marketKey}`);
    }
    
    // Update receipt confirmation timestamp for spacing checks
    lastTradeReceiptAtRef.current[marketKey] = Date.now();

    // Reconcile any wallet transfer that happened since the prior balance
    // snapshot before applying this trade. This keeps withdrawals/deposits out
    // of realized PnL and makes the notification use the corrected ledger.
    if (
      isWrappedStableMarket(marketKey) &&
      Number.isFinite(preStableBalance) &&
      Number.isFinite(preWrappedBalance) &&
      currentPrice > 0
    ) {
      reconcileExternalCapitalBalances({
        marketKey,
        stableBalance: preStableBalance,
        assetBalance: preWrappedBalance,
        price: currentPrice,
        txHash,
        blockNumber: receipt.blockNumber ?? null,
        source: "pre-trade-balance-snapshot",
      });
    }
    
    // Update PnL if we have actual amounts
    if (tradeAmounts && tradeAmounts.amountIn > 0 && tradeAmounts.amountOut > 0) {
      console.log(`[processTradeReceipt] Updating PnL for ${marketKey}:`, {
        side,
        amountIn: tradeAmounts.amountIn.toFixed(6),
        amountOut: tradeAmounts.amountOut.toFixed(6),
        price: currentPrice.toFixed(6),
      });
      
      const wrappedSymbol = chainConfig?.nativeCurrency?.symbol || "ETH";
      const stableSymbol = chainConfig?.defaultStablecoin || "USDC";
      // Commit once from the receipt-backed ledger, then use that immutable snapshot everywhere below.
      const committedPnl = commitConfirmedPnlTrade(
        marketKey,
        side,
        tradeAmounts.amountIn,
        tradeAmounts.amountOut,
        currentPrice,
        txHash,
        {
          eventType: "TRADE_FILL",
          status: "CONFIRMED",
          hasReceiptEvidence: true,
          gasUsd: confirmedGasUsd,
          blockNumber: receipt.blockNumber ?? null,
        }
      );
      const updatedRealizedPnL =
        committedPnl.realizedPnL - committedPnl.reportingRealizedPnlOffset;
      const updatedHoldings = committedPnl.holdings;
      const updatedCostBasis = committedPnl.costBasis;
      const updatedBuyCount = committedPnl.buyCount;
      const updatedSellCount = committedPnl.sellCount;

      const latestPosition = positionsRef.current[marketKey] || {};
      const confirmedAvgEntry = getPnlAverageEntry(committedPnl);
      const confirmedPositionPatch = {
        holdings: committedPnl.holdings,
        avgEntry: confirmedAvgEntry,
        accountingReconciledAt: Date.now(),
        accountingTxHash: txHash,
      };
      const cfgConfirmed = getEffectiveBotCfg(marketKey);
      const confirmedBuyPct = cfgConfirmed.buyTarget ?? defaultBotCfg.buyTarget;
      const confirmedSellPct = cfgConfirmed.sellTarget ?? defaultBotCfg.sellTarget;

      if (committedPnl.holdings <= DUST || !(confirmedAvgEntry > 0)) {
        const flatTargets = computeGridTargets({
          price: currentPrice,
          avgEntry: 0,
          buyPct: confirmedBuyPct,
          sellPct: confirmedSellPct,
          useSpotAnchor: true,
        });
        confirmedPositionPatch.avgEntry = 0;
        confirmedPositionPatch.priceTargetBuy = flatTargets.priceTargetBuy;
        confirmedPositionPatch.priceTargetSell = flatTargets.priceTargetSell;
      } else if (side === "BUY") {
        const positionsForBook = {
          ...positionsRef.current,
          [marketKey]: {
            ...latestPosition,
            holdings: committedPnl.holdings,
            avgEntry: confirmedAvgEntry,
          },
        };
        const confirmedBook = getMarketBook(
          marketKey,
          positionsForBook[marketKey],
          currentPrice,
          positionsForBook,
          chainConfig
        );
        const useSpotBuy = shouldUseSpotGridMode({
          cfg: cfgConfirmed,
          pos: positionsForBook[marketKey],
          price: currentPrice,
          baseBalanceUsd: confirmedBook.baseBalance,
          totalBookUsd: confirmedBook.totalBookUsd,
        });
        const confirmedGrid = computeGridTargets({
          price: currentPrice,
          avgEntry: confirmedAvgEntry,
          buyPct: confirmedBuyPct,
          sellPct: confirmedSellPct,
          useSpotAnchor: false,
        });
        if (useSpotBuy) confirmedGrid.priceTargetBuy = currentPrice * confirmedBuyPct;
        confirmedGrid.priceTargetSell = Math.max(
          confirmedGrid.priceTargetSell,
          getNormalSellFloorPrice({
            avgEntry: confirmedAvgEntry,
            minProfitBps: cfgConfirmed.normalSellMinProfitBps,
          })
        );
        confirmedPositionPatch.priceTargetBuy = confirmedGrid.priceTargetBuy;
        confirmedPositionPatch.priceTargetSell = confirmedGrid.priceTargetSell;
        confirmedPositionPatch.lastBuyPrice = tradeAmounts.amountIn / tradeAmounts.amountOut;
      } else {
        confirmedPositionPatch.priceTargetSell = Math.max(
          Number(latestPosition.priceTargetSell) || 0,
          getNormalSellFloorPrice({
            avgEntry: confirmedAvgEntry,
            minProfitBps: cfgConfirmed.normalSellMinProfitBps,
          })
        );
      }
      updatePosition(marketKey, confirmedPositionPatch);
      console.log("[POSITION ACCOUNTING RECONCILED]", {
        marketKey,
        txHash,
        side,
        holdings: committedPnl.holdings,
        costBasis: committedPnl.costBasis,
        avgEntry: confirmedPositionPatch.avgEntry,
        previousPositionAvgEntry: latestPosition.avgEntry,
      });
      
      // Send Discord notification with UPDATED PnL data (for both BUY and SELL)
      try {
        // Determine token symbols based on trade side and market
        let tokenInSymbol, tokenOutSymbol;
        if (side === "BUY") {
          // BUY: Spending stablecoin to get wrapped native token
          tokenInSymbol = stableSymbol;
          tokenOutSymbol = wrappedSymbol;
        } else {
          // SELL: Spending wrapped native token to get stablecoin
          tokenInSymbol = wrappedSymbol;
          tokenOutSymbol = stableSymbol;
        }
        
        // Get chain ID or name (convert chainId number to string if needed)
        let chainId = chainConfig?.chainId || currentConfig?.chainId || "unknown";
        if (typeof chainId === "number") {
          chainId = String(chainId);
        }
        
        const wethPriceUsd = Number(currentPrice) || 0;
        const updatedUnrealizedPnL = getUnrealizedPnlForRecord(committedPnl, wethPriceUsd);
        const reportedGasUsd = Math.max(
          0,
          committedPnl.totalGasUsd - committedPnl.reportingGasUsdOffset
        );

        const cfgTrade = getEffectiveBotCfg(marketKey);
        const posTrade = getPositionForDecisions(marketKey);
        const regimeSnapTrade = marketRegimeRef.current[marketKey];
        const bookTrade = getMarketBook(marketKey, posTrade, wethPriceUsd, positionsRef.current, chainConfig);
        const benchmarkTrade = isWrappedStableMarket(marketKey)
          ? getWethBenchmarkResult(marketKey, wethPriceUsd)
          : null;
        const benchmarkLineTrade = benchmarkTrade?.result
          ? `WETH Benchmark: initial ${benchmarkTrade.result.initialTotalValueInWeth.toFixed(6)} WETH | current ${benchmarkTrade.result.currentTotalValueInWeth.toFixed(6)} WETH | delta ${benchmarkTrade.result.wethBenchmarkDelta >= 0 ? "+" : ""}${benchmarkTrade.result.wethBenchmarkDelta.toFixed(6)} WETH (${benchmarkTrade.result.wethBenchmarkDeltaPct >= 0 ? "+" : ""}${(benchmarkTrade.result.wethBenchmarkDeltaPct * 100).toFixed(2)}%) | vs HODL ${benchmarkTrade.result.botVsWethHoldUsd >= 0 ? "+" : ""}$${benchmarkTrade.result.botVsWethHoldUsd.toFixed(2)}`
          : null;
        const regimeLineTrade = isRegimeEngineEnabled(marketKey)
          ? formatRegimeCompact(
              { ...regimeSnapTrade, consecBuys: Math.max(0, (posTrade.buyCounter || 1) - 1) },
              bookTrade,
              cfgTrade
            )
          : null;

        void notifyDiscordTrade({
          botInstanceId,
          type: side,
          marketKey,
          txHash,
          amountIn: tradeAmounts.amountIn,
          amountOut: tradeAmounts.amountOut,
          tokenInSymbol,
          tokenOutSymbol,
          price: currentPrice,
          wethPriceUsd,
          chain: chainId,
          tradeConfirmedAt: new Date().toISOString(),
          regime: regimeSnapTrade?.regime,
          recoveryBreakout: regimeSnapTrade?.recoveryBreakoutActive,
          regimeDiagnostics: [regimeLineTrade, benchmarkLineTrade].filter(Boolean).join("\n") || null,
          pnl: {
            realizedPnL: updatedRealizedPnL,
            unrealizedPnL: updatedUnrealizedPnL,
            totalPnL: updatedRealizedPnL + updatedUnrealizedPnL - reportedGasUsd,
            totalTrades: committedPnl.totalTrades,
            buyCount: updatedBuyCount,
            sellCount: updatedSellCount,
            holdings: updatedHoldings,
            costBasis: updatedCostBasis,
            totalGasUsd: reportedGasUsd,
            accountingStatus: committedPnl.accountingStatus,
            wethBenchmark: benchmarkTrade?.result
              ? {
                  initialWeth: benchmarkTrade.result.initialTotalValueInWeth,
                  currentWeth: benchmarkTrade.result.currentTotalValueInWeth,
                  deltaWeth: benchmarkTrade.result.wethBenchmarkDelta,
                  deltaPct: benchmarkTrade.result.wethBenchmarkDeltaPct,
                  botVsHoldUsd: benchmarkTrade.result.botVsWethHoldUsd,
                  botVsHoldPct: benchmarkTrade.result.botVsWethHoldPct,
                }
              : null,
          },
        })
          .then(() => console.log(`[Discord Notification] Sent ${side} trade notification for ${marketKey}`))
          .catch((discordError) => {
            console.warn(`[Discord Notification] Failed to send notification:`, discordError);
          });
      } catch (discordError) {
        // Don't let Discord failures break trade processing
        console.warn(`[Discord Notification] Failed to send notification:`, discordError);
        console.error(`[Discord Notification] Error details:`, discordError.message, discordError.stack);
      }
    } else {
      console.warn(`[processTradeReceipt] Skipping PnL update - invalid amounts:`, {
        marketKey,
        side,
        tradeAmounts,
        amountIn: tradeAmounts?.amountIn,
        amountOut: tradeAmounts?.amountOut,
      });
      
      console.warn("[ACCOUNTING EVENT QUARANTINED] Receipt parsing did not produce confirmed transfer amounts", {
        eventType: "TRADE_FILL",
        status: "ACCOUNTING_INCOMPLETE",
        marketKey,
        side,
        txHash,
        blockNumber: receipt.blockNumber ?? null,
        intendedAmount: Number(amount) || 0,
        reason: "PnL is never estimated from intended amount or spot price",
      });
    }

    if (
      persistencePlayer &&
      externalSignalForHistory?.decisionId &&
      externalSignalForHistory.persistedToMongoDb === false
    ) {
      try {
        externalSignalForHistory = {
          ...externalSignalForHistory,
          txHash,
          blockNumber: receipt.blockNumber ?? null,
          executedAt: Date.now(),
          persistedToMongoDb: true,
        };
        await moralisAddExternalSignalDecision(persistencePlayer, externalSignalForHistory);
        console.log("[EXTERNAL SIGNAL EXECUTION SAVED]", {
          marketKey,
          decisionId: externalSignalForHistory.decisionId,
          txHash,
        });
      } catch (error) {
        console.warn("[EXTERNAL SIGNAL EXECUTION SAVE FAILED]", error?.message || error);
      }
    }

    addTradeToHistory({
      ts: Date.now(),
      side,
      amount: Number(amount),
      token: context.tokenAddress || context.tokenToUse || "",
      initiator: context.initiator || "",
      marketKey,
      txHash,
      botInstanceId,
      // Store actual amounts for reference
      actualAmountIn: tradeAmounts?.amountIn || null,
      actualAmountOut: tradeAmounts?.amountOut || null,
      blockNumber: receipt.blockNumber ?? null,
      externalSignal: externalSignalForHistory,
      externalSignalDecisionId: externalSignalForHistory?.decisionId || "",
    });

    if (externalSignalForHistory?.decisionId) {
      const confirmedAmountIn = Number(tradeAmounts?.amountIn) || 0;
      const confirmedAmountOut = Number(tradeAmounts?.amountOut) || 0;
      const confirmedExecutionPrice =
        confirmedAmountIn > 0 && confirmedAmountOut > 0
          ? (side === "BUY"
              ? confirmedAmountIn / confirmedAmountOut
              : confirmedAmountOut / confirmedAmountIn)
          : Number(currentPrice) || Number(externalSignalForHistory.poolPrice) || 0;
      scheduleExternalSignalOutcomes({
        decision: externalSignalForHistory,
        marketKey,
        side,
        txHash,
        blockNumber: receipt.blockNumber,
        actualAmountIn: confirmedAmountIn,
        actualAmountOut: confirmedAmountOut,
        executionPrice: confirmedExecutionPrice,
        executedAt: Date.now(),
      });
    }

    try {
      if (executionAddress) {
        // Wait a bit for blockchain state to update after transaction
        await new Promise(resolve => setTimeout(resolve, 2000));
        const hasConfirmedAmounts =
          tradeAmounts?.amountIn > 0 && tradeAmounts?.amountOut > 0;
        await getTokenBalance(executionAddress, hasConfirmedAmounts
          ? {
              priceOverride: currentPrice,
              accountingContext: {
                preStableBalance,
                preAssetBalance: preWrappedBalance,
                expectedStableDelta: side === "BUY" ? -tradeAmounts.amountIn : tradeAmounts.amountOut,
                expectedAssetDelta: side === "BUY" ? tradeAmounts.amountOut : -tradeAmounts.amountIn,
                txHash,
                blockNumber: receipt.blockNumber ?? null,
                source: "confirmed-trade-settlement",
              },
            }
          : { priceOverride: currentPrice, skipAccounting: true });
        console.log("[processTradeReceipt] Balances refreshed after trade");
        await logBalanceMismatchDiagnostics({
          marketKey,
          side,
          txHash,
          blockNumber: receipt.blockNumber,
          tradeAmounts,
          preStableBalance,
          preWrappedBalance,
        });
      }
    } catch (e) {
      console.warn("[processTradeReceipt] Failed to refresh balances:", e);
    }

    delete pendingTradeSubmissionsRef.current[marketKey];
    tradeLockRef.current[marketKey] = false;
    console.log("[TRADE SETTLEMENT COMPLETE]", { marketKey, txHash, side });

    // CRITICAL FIX: Don't call reanchorTargetsFor after trades - targets are already updated in buy/sell logic
    // Calling reanchorTargetsFor here can override the correctly calculated targets with stale state
    // Targets are already updated atomically with avgEntry in the trade execution logic
    // No need to re-anchor here - it would cause target drift
  };

  const executeBlockchainTrade = async (
    type,
    amount,
    overrideTokenAddress,
    overrideInitiator,
    ctx = {}
  ) => {
    const side = String(type).toUpperCase();
    let tokenToUse = overrideTokenAddress;
    const initiator = overrideInitiator;
    if (!tokenToUse) {
      console.error("executeBlockchainTrade: missing tokenToUse");
      return false;
    }

    if (!currentConfig || !chainConfig) {
      console.error("executeBlockchainTrade: Configuration not ready");
      return false;
    }

    const wrappedSymbol = chainConfig.nativeCurrency.symbol;
    const nativeSymbol = chainConfig.nativeCurrency.symbol;
    const stableSymbol = chainConfig.defaultStablecoin;
    const wrappedNativeToken = currentConfig.wrappedNativeToken;
    const stablecoinAddress = currentConfig.stablecoinAddress;

    const marketKey =
      ctx.marketKey ||
      inferMarketKeyFromTokenAndInitiator({
        tokenToUse,
        wrappedNativeToken,
        stablecoinAddress,
        wrappedSymbol,
        stableSymbol,
        initiator,
      });

    if (isSecondaryBot && !secondarySessionAuthorized) {
      if (ctx.pendingId) rollbackPending(marketKey, ctx.pendingId);
      delete pendingTradeSubmissionsRef.current[marketKey];
      showTradeNotice("Authorize the secondary bot with the connected primary wallet before trading.", { marketKey, side });
      return false;
    }

    let tradePath;
    // Determine the target token based on marketKey
    const isWcroUsdcMarket = marketKey === `${wrappedSymbol}-${stableSymbol}`;
    // Selling native↔stable pair always spends wrapped native (WETH), never stablecoin address
    if (side === "SELL" && isWcroUsdcMarket) {
      tokenToUse = wrappedNativeToken;
    }
    const isWbtcWcroMarket = marketKey === `WBTC-${wrappedSymbol}`;
    const isWbtcUsdcMarket = marketKey === `WBTC-${stableSymbol}`;
    
    console.log(`[executeBlockchainTrade] marketKey: ${marketKey}, side: ${side}, initiator: ${initiator}`);
    console.log(`[executeBlockchainTrade] isWcroUsdcMarket: ${isWcroUsdcMarket}, isWbtcWcroMarket: ${isWbtcWcroMarket}, isWbtcUsdcMarket: ${isWbtcUsdcMarket}`);
    console.log(`[executeBlockchainTrade] wrappedNativeToken: ${wrappedNativeToken}, stablecoinAddress: ${stablecoinAddress}, tokenToUse: ${tokenToUse}`);
    
    // Build trade path based on market and side, not just initiator
    if (isWcroUsdcMarket) {
      // WCRO-USDC market
      if (side === "BUY") {
        // Buying WCRO with USDC: USDC → WCRO
        tradePath = [stablecoinAddress, wrappedNativeToken];
      } else {
        // Selling WCRO to get USDC: WCRO → USDC
        tradePath = [wrappedNativeToken, stablecoinAddress];
      }
      console.log(`[executeBlockchainTrade] WCRO-USDC ${side} path:`, tradePath);
    } else if (isWbtcWcroMarket) {
      // WBTC-WCRO market
      if (side === "BUY") {
        // Buying WBTC with WCRO: WCRO → WBTC
        tradePath = [wrappedNativeToken, tokenToUse];
      } else {
        // Selling WBTC to get WCRO: WBTC → WCRO
        tradePath = [tokenToUse, wrappedNativeToken];
      }
    } else if (isWbtcUsdcMarket) {
      // WBTC-USDC market
      if (side === "BUY") {
        // Buying WBTC with USDC: USDC → WCRO → WBTC
        tradePath = [stablecoinAddress, wrappedNativeToken, tokenToUse];
      } else {
        // Selling WBTC to get USDC: WBTC → WCRO → USDC
        tradePath = [tokenToUse, wrappedNativeToken, stablecoinAddress];
      }
    } else {
      // Fallback: use initiator-based logic
      if (initiator === chainConfig.nativeCurrency.symbol || initiator === "CRO") {
        tradePath = side === "BUY" ? [wrappedNativeToken, tokenToUse] : [tokenToUse, wrappedNativeToken];
      } else if (initiator === stableSymbol || initiator === "USDC" || initiator === "USDC.e") {
        tradePath = side === "BUY"
          ? [stablecoinAddress, wrappedNativeToken, tokenToUse]
          : [tokenToUse, wrappedNativeToken, stablecoinAddress];
      } else {
        tradePath = side === "BUY" ? [wrappedNativeToken, stablecoinAddress] : [stablecoinAddress, wrappedNativeToken];
      }
    }

    let priceToUse = markets.find((m) => m.key === marketKey)?.price || 0;
    if (!(priceToUse > 0)) priceToUse = lastPriceRef.current[marketKey] || 0;
    if (!(priceToUse > 0)) {
      const errorMsg = `[executeBlockchainTrade] No price for ${marketKey}. Make sure you've started listening and the market has a price.`;
      console.error(errorMsg);
      if (ctx.pendingId) rollbackPending(marketKey, ctx.pendingId);
      delete pendingTradeSubmissionsRef.current[marketKey];
      showTradeNotice(errorMsg, { marketKey, side });
      return false;
    }

    try {
      let preTradeCoreBalances = null;
      if (isWcroUsdcMarket && executionAddress) {
        try {
          preTradeCoreBalances = await readCoreTradeBalances(executionAddress);
        } catch (balanceError) {
          console.warn("[TRADE PRE-BALANCE] RPC snapshot unavailable; receipt accounting will continue", {
            marketKey,
            side,
            error: balanceError?.message || balanceError,
          });
        }
      }

      // Backend wallet path
      if (effectiveUseBackendWallet) {
        if (!currentConfig) {
          throw new Error("Configuration not ready for backend trade");
        }

        // Amount decimals: ETH–USDC SELL is always WETH units (18 dp), regardless of initiator label (ETH vs WETH)
        const amountDecimals =
          side === "SELL" && isWcroUsdcMarket
            ? chainConfig.nativeCurrency.decimals
            : initiator === stableSymbol || initiator === "USDC" || initiator === "USDC.e" || initiator === "USDT"
              ? (chainConfig.defaultStablecoin === "USDC" || chainConfig.defaultStablecoin === "USDC.e" || chainConfig.defaultStablecoin === "USDT" ? 6 : 18)
              : initiator === nativeSymbol || initiator === "CRO" || initiator === "ETH" || initiator === "WETH" || initiator === "WCRO" || initiator === "BNB" || initiator === "MATIC" || initiator === "AVAX"
                ? chainConfig.nativeCurrency.decimals
                : 18;
        
        // String-truncate to chain decimals (avoid Number().toFixed — float corruption / wrong wei)
        const roundedAmount = truncateDecimalStringForPayload(amount, amountDecimals);

        const payload = {
          type: side,
          amount: roundedAmount,
          tokenAddress: tokenToUse,
          routerAddress: currentConfig.routerAddress,
          factoryAddress: currentConfig.factoryAddress,
          chainId: currentConfig.chainId,
          chainName: selectedChain,
          dexName: selectedDex,
          currentPrice: priceToUse,
          maxFeePerGasAmount,
          maxPriorityFeePerGasAmount,
          slippagePercent,
          initiator,
          tradePath,
          wrappedNativeToken: currentConfig.wrappedNativeToken,
          stablecoinAddress: currentConfig.stablecoinAddress,
          sellEntirePosition: side === "SELL" && ctx.sellEntirePosition === true,
          botInstanceId,
          ...(isSecondaryBot ? {
            secondaryAuthorization: {
              message: secondaryAuthorization.message,
              signature: secondaryAuthorization.signature,
            },
          } : {}),
        };

        console.log(
          `⚡ [${side}] ${amount} ${initiator} → ${tokenToUse}\n` +
          `   • marketKey: ${marketKey} | payload.amount: ${roundedAmount}\n` +
          `   • Price: ${safeFix(priceToUse)}\n` +
          `   • Slippage: ${(slippagePercent * 100).toFixed(2)}%\n` +
          `   • Gas cap: ${maxFeePerGasAmount} gwei | Tip: ${maxPriorityFeePerGasAmount} gwei\n` +
          `   • Route: ${tradePath.join(" → ")}\n` +
          `   • Bot: ${botInstanceId.toUpperCase()} | Using: ${effectiveUseBackendWallet ? "Backend Wallet" : "Frontend Wallet"}`
        );
        const resp = await Moralis.Cloud.run("executeTokenTrade", payload);
        const receiptLike = extractTradereceiptFromCloudResponse(resp);
        if (isBackendTradeFailedOrInvalidReceipt(resp, receiptLike)) {
          console.error("[executeBlockchainTrade] Backend trade failed or invalid receipt", { resp, receiptLike });
          if (ctx.pendingId) rollbackPending(marketKey, ctx.pendingId);
          delete pendingTradeSubmissionsRef.current[marketKey];
          showTradeNotice(
            resp?.error ||
              "Trade did not succeed on-chain, or the server returned an invalid receipt. The route may require more slippage than the configured profitable limit.",
            { marketKey, side }
          );
          return false;
        }
        await processTradeReceipt(receiptLike, amount, side, {
          ...ctx,
          initiator,
          tokenAddress: tokenToUse,
          marketKey,
          preStableBalance: preTradeCoreBalances?.stableBalance,
          preWrappedBalance: preTradeCoreBalances?.wrappedBalance,
        });
        if (ctx.pendingId) commitPending(marketKey, ctx.pendingId);
        return true;
      }

      // User wallet path
      if (!currentConfig) {
        throw new Error("Configuration not ready for frontend trade");
      }

      const { signer, router, from: userAddress } = await getWeb3SignerAndRouter();
      const deadline = Math.floor(Date.now() / 1000) + 60 * 40;
      const maxFee = ethers.utils.parseUnits(String(maxFeePerGasAmount), "gwei");
      const maxPrio = ethers.utils.parseUnits(String(maxPriorityFeePerGasAmount), "gwei");
      const tokenDecimalsForTarget = await readErc20Decimals(tokenToUse);
      let tx;
      /** Set when a path already waited for confirmation (e.g. Universal Router SELL fee-tier retries). */
      let tradeReceipt = null;
      const slip = normalizeClientSlippageFraction(slippagePercent);

      // nativeSymbol and stableSymbol are already declared at the top of the function
      const isNativeInitiator = initiator === nativeSymbol || initiator === "CRO" || initiator === "ETH" || initiator === "BNB" || initiator === "MATIC" || initiator === "AVAX";
      const isStablecoinInitiator = initiator === stableSymbol || initiator === "USDC" || initiator === "USDC.e" || initiator === "USDT" || initiator === "DAI" || initiator === "BUSD";
      
      // Check if using Uniswap V3 or V4
      const isV3 = selectedDex === "uniswapV3" || selectedDex === "uniswapV4";
      const isV4 = selectedDex === "uniswapV4";
      const usingUniversalRouter = isUniversalRouter(currentConfig.routerAddress);
      const routerAddressForTrade = currentConfig.routerAddress;
      const routerForTrade = router;
      
      // For V3, try to find the actual fee tier that exists for this pair
      // For V4, use default fee tier (Universal Router will handle routing)
      let feeTier = null;
      if (isV3 && tradePath.length === 2) {
        if (isV4) {
          // V4: Use default fee tier, Universal Router will find the best route
          feeTier = DEX_CONFIGS.uniswapV4?.defaultFeeTier || 500;
        } else {
          // V3: Try to find which fee tier pool actually exists
          try {
            const provider = signer.provider || new ethers.providers.Web3Provider(window.ethereum);
            const factory = new ethers.Contract(
              currentConfig.factoryAddress,
              ["function getPool(address tokenA, address tokenB, uint24 fee) external view returns (address pool)"],
              provider
            );
            const [token0, token1] = tradePath[0].toLowerCase() < tradePath[1].toLowerCase()
              ? [tradePath[0], tradePath[1]]
              : [tradePath[1], tradePath[0]];
            
            const feeTiers = [500, 3000, 10000];
            for (const tier of feeTiers) {
              try {
                const pool = await factory.getPool(token0, token1, tier);
                if (pool && pool !== ethers.constants.AddressZero) {
                  feeTier = tier;
                  console.log(`✅ Found V3 pool with fee tier ${tier} for swap`);
                  break;
                }
              } catch (e) {
                // Continue to next fee tier
              }
            }
            // If no pool found, use default
            if (!feeTier) {
              feeTier = DEX_CONFIGS.uniswapV3?.defaultFeeTier || 500;
              console.warn(`⚠️ No V3 pool found, using default fee tier ${feeTier}`);
            }
          } catch (e) {
            // Fallback to default if factory call fails
            feeTier = DEX_CONFIGS.uniswapV3?.defaultFeeTier || 500;
            console.warn(`⚠️ Could not detect fee tier, using default ${feeTier}:`, e.message);
          }
        }
      }

      if (side === "BUY") {
        if (isNativeInitiator) {
          const valueWei = ethers.utils.parseUnits(toDecimalStringSafe(Number(amount).toFixed(18), 18), 18);
          const estTokens = Number(amount) / priceToUse;
          const minTokens = estTokens * (1 - slip);
          const outMinWei = ethers.utils.parseUnits(
            toDecimalStringSafe(minTokens, tokenDecimalsForTarget),
            tokenDecimalsForTarget
          );

          // Universal Router handles ETH swaps - wrap ETH to WETH first, then swap
          if (usingUniversalRouter && isV3 && tradePath.length === 2) {
            // For Universal Router with ETH: WRAP_ETH (0x0b) + V3_SWAP_EXACT_IN (0x00)
            // Wrap ETH to WETH first (wraps all ETH sent with transaction)
            const wrapCmd = encodeWrapETH(userAddress);
            // Then swap WETH to target token
            // payerIsUser = false because WETH comes from WRAP_ETH command
            const swapCmd = encodeUniversalRouterV3Swap(
              userAddress,
              valueWei,
              outMinWei,
              tradePath[0], // WETH
              feeTier,
              tradePath[1], // target token
              false // payerIsUser = false, WETH comes from WRAP_ETH
            );
            // Commands must be a bytes array: [0x0b, 0x00]
            const commands = ethers.utils.hexConcat([wrapCmd.command, swapCmd.command]);
            const inputs = [wrapCmd.input, swapCmd.input];
            // Arbitrum Universal Router only exposes execute(commands, inputs, deadline) — 2-arg form reverts in simulation
            const iface = new ethers.utils.Interface(ROUTER_ABI);
            const executeData = iface.encodeFunctionData(
              "execute(bytes,bytes[],uint256)",
              [commands, inputs, deadline]
            );
            tx = await signer.sendTransaction({
              to: currentConfig.routerAddress,
              data: executeData,
              value: valueWei,
              gasLimit: 500000, // Increased for multi-command
              maxFeePerGas: maxFee,
              maxPriorityFeePerGas: maxPrio,
            });
          } else {
            tx = await router.swapExactETHForTokens(
              outMinWei,
              tradePath,
              userAddress,
              deadline,
              { value: valueWei, gasLimit: 300000, maxFeePerGas: maxFee, maxPriorityFeePerGas: maxPrio }
            );
          }
        } else if (isStablecoinInitiator) {
          const stablecoinDecimals = chainConfig.defaultStablecoin === "USDC" ? 6 : 
                                     chainConfig.defaultStablecoin === "USDT" ? 6 : 18;
          const inWei = ethers.utils.parseUnits(toDecimalStringSafe(amount, stablecoinDecimals), stablecoinDecimals);
          // For Universal Router, approve Permit2; otherwise approve router directly
          const approvalAddress = usingUniversalRouter ? PERMIT2_ADDRESS : routerAddressForTrade;
          await ensureAllowance(currentConfig.stablecoinAddress, userAddress, approvalAddress, inWei);
          if (usingUniversalRouter) {
            await ensurePermit2RouterAllowance(signer, currentConfig.stablecoinAddress, currentConfig.routerAddress, inWei);
          }

          // Price-based minOut can be wildly wrong if `priceToUse` is stale/mis-scaled.
          // Prefer QuoterV2 for V3 single-hop (exactInputSingle) so amountOutMinimum is in correct decimals.
          const slipBps = Math.min(9999, Math.max(1, Math.round(slip * 10000)));
          let outMinWei;
          if (isV3 && tradePath.length === 2 && feeTier != null) {
            try {
              const quoterAddr = quoterV2AddressForChain(currentConfig.chainId);
              const quoterReadProvider =
                provider || signer.provider || new ethers.providers.Web3Provider(window.ethereum);
              if (quoterAddr && quoterReadProvider) {
                const quoter = new ethers.Contract(quoterAddr, QUOTER_V2_ABI, quoterReadProvider);
                const quoteRes = await quoter.callStatic.quoteExactInputSingle([
                  ethers.utils.getAddress(tradePath[0]),
                  ethers.utils.getAddress(tradePath[1]),
                  inWei,
                  feeTier,
                  0,
                ]);
                const quoted = quoteRes?.amountOut ?? quoteRes?.[0];
                if (quoted && quoted.gt(0)) {
                  outMinWei = quoted.mul(ethers.BigNumber.from(10000 - slipBps)).div(10000);
                  console.log(
                    `[BUY] Quoter expectedOut=${quoted.toString()} raw, minOut=${outMinWei.toString()} (slip ${slipBps} bps)`
                  );
                }
              }
            } catch (qe) {
              console.warn("[BUY] Quoter failed, falling back to price-based minOut:", qe?.message || qe);
            }
          }
          if (!outMinWei) {
            const estTokens = Number(amount) / priceToUse;
            const minTokens = estTokens * (1 - slip);
            outMinWei = ethers.utils.parseUnits(
              toDecimalStringSafe(minTokens, tokenDecimalsForTarget),
              tokenDecimalsForTarget
            );
            console.warn("[BUY] Using price-based minOut fallback (quoter unavailable).");
          }

          if (isV3 && tradePath.length === 2) {
            if (usingUniversalRouter) {
              // With on-chain Permit2 approvals, use V3_SWAP_EXACT_IN directly
              // payerIsUser=true tells it to pull tokens via Permit2
              const deadline = Math.floor(Date.now() / 1000) + 60 * 40;
              
              const swapCmd = encodeUniversalRouterV3Swap(
                userAddress,
                inWei,
                outMinWei,
                tradePath[0],
                feeTier,
                tradePath[1],
                true // payerIsUser = true
              );
              
              const commands = swapCmd.command;
              const inputs = [swapCmd.input];
              const iface = new ethers.utils.Interface(ROUTER_ABI);
              const executeData = iface.encodeFunctionData("execute(bytes,bytes[],uint256)", [commands, inputs, deadline]);
              tx = await signer.sendTransaction({
                to: currentConfig.routerAddress,
                data: executeData,
                gasLimit: 500000,
                maxFeePerGas: maxFee,
                maxPriorityFeePerGas: maxPrio,
              });
            } else {
              // Standard V3: Use exactInputSingle for single-hop swaps
              const params = {
                tokenIn: tradePath[0],
                tokenOut: tradePath[1],
                fee: feeTier,
                recipient: userAddress,
                deadline: deadline,
                amountIn: inWei,
                amountOutMinimum: outMinWei,
                sqrtPriceLimitX96: 0, // No price limit
              };
              tx = await routerForTrade.exactInputSingle(params, {
                gasLimit: 300000,
                maxFeePerGas: maxFee,
                maxPriorityFeePerGas: maxPrio,
              });
            }
          } else {
            // V2: Use swapExactTokensForTokens
            tx = await routerForTrade.swapExactTokensForTokens(
              inWei,
              outMinWei,
              tradePath,
              userAddress,
              deadline,
              { gasLimit: 300000, maxFeePerGas: maxFee, maxPriorityFeePerGas: maxPrio }
            );
          }
        } else {
          // Fallback: wrapped native to stablecoin
          const valueWei = ethers.utils.parseUnits(toDecimalStringSafe(Number(amount).toFixed(chainConfig.nativeCurrency.decimals), chainConfig.nativeCurrency.decimals), chainConfig.nativeCurrency.decimals);
          const stablecoinDecimals = chainConfig.defaultStablecoin === "USDC" ? 6 : 
                                     chainConfig.defaultStablecoin === "USDT" ? 6 : 18;
          const estStablecoin = Number(amount) * priceToUse;
          const minStablecoin = estStablecoin * (1 - slip);
          const outMinWei = ethers.utils.parseUnits(toDecimalStringSafe(minStablecoin, stablecoinDecimals), stablecoinDecimals);
          tx = await router.swapExactETHForTokens(
            outMinWei,
            tradePath,
            userAddress,
            deadline,
            { value: valueWei, gasLimit: 300000, maxFeePerGas: maxFee, maxPriorityFeePerGas: maxPrio }
          );
        }
      } else {
        // SELL: Determine what we're selling and what we're getting
        const isWcroUsdcMarketLocal = marketKey === `${wrappedSymbol}-${stableSymbol}`;
        const isWbtcWcroMarket = marketKey === `WBTC-${wrappedSymbol}`;
        
        // WETH→USDC: must not depend on initiator string === "ETH" only — "WETH" or address mismatch used to skip this branch and hit the wrong swap (MetaMask "likely to fail")
        const tokenIsWrappedNative =
          tokenToUse &&
          wrappedNativeToken &&
          String(tokenToUse).toLowerCase() === String(wrappedNativeToken).toLowerCase();
        const sellingWethForUsdc =
          isWcroUsdcMarketLocal && tokenIsWrappedNative;
        
        // Check if we're selling to get native currency (ETH/CRO) or stablecoin
        const sellingToNative = (isWbtcWcroMarket && initiator === nativeSymbol);
        
        if (sellingToNative) {
          // Selling token to get native currency (e.g., WBTC → CRO)
          const tokenInWei = ethers.utils.parseUnits(
            toDecimalStringSafe(amount, tokenDecimalsForTarget),
            tokenDecimalsForTarget
          );
          const token = new ethers.Contract(tokenToUse, ERC20_ABI, signer);
          const allowance = await token.allowance(userAddress, routerAddressForTrade);
          if (allowance.lt(tokenInWei)) {
            const a = await token.approve(routerAddressForTrade, ethers.constants.MaxUint256);
            await a.wait();
          }

          const estNative = Number(amount) * priceToUse;
          const minNative = estNative * (1 - slip);
          const outMinWei = ethers.utils.parseUnits(toDecimalStringSafe(minNative, chainConfig.nativeCurrency.decimals), chainConfig.nativeCurrency.decimals);

          tx = await routerForTrade.swapExactTokensForETH(
            tokenInWei,
            outMinWei,
            tradePath,
            userAddress,
            deadline,
            { gasLimit: 300000, maxFeePerGas: maxFee, maxPriorityFeePerGas: maxPrio }
          );
        } else if (sellingWethForUsdc || isStablecoinInitiator) {
          const tokenInWei = ethers.utils.parseUnits(
            toDecimalStringSafe(amount, tokenDecimalsForTarget),
            tokenDecimalsForTarget
          );
          const token = new ethers.Contract(tokenToUse, ERC20_ABI, signer);
          // Universal Router requires Permit2 approval, not direct router approval
          const approvalAddress = usingUniversalRouter ? PERMIT2_ADDRESS : routerAddressForTrade;
          const allowance = await token.allowance(userAddress, approvalAddress);
          if (allowance.lt(tokenInWei)) {
            console.log(`⚠️ [SELL] Insufficient allowance for ${usingUniversalRouter ? 'Permit2' : 'Router'}. Current: ${allowance.toString()}, Needed: ${tokenInWei.toString()}. Approving...`);
            const a = await token.approve(approvalAddress, ethers.constants.MaxUint256);
            const approveReceipt = await a.wait();
            console.log(`✅ [SELL] Approval confirmed in block ${approveReceipt.blockNumber} for ${usingUniversalRouter ? 'Permit2' : 'Router'}`);
            // Verify approval went through
            const newAllowance = await token.allowance(userAddress, approvalAddress);
            if (newAllowance.lt(tokenInWei)) {
              throw new Error(`[SELL] Approval failed. New allowance: ${newAllowance.toString()}, needed: ${tokenInWei.toString()}`);
            }
          }
          if (usingUniversalRouter) {
            await ensurePermit2RouterAllowance(signer, tokenToUse, currentConfig.routerAddress, tokenInWei);
          }

          const stablecoinDecimals = chainConfig.defaultStablecoin === "USDC" ? 6 : 
                                     chainConfig.defaultStablecoin === "USDT" ? 6 : 18;
          const estStablecoin = Number(amount) * priceToUse;
          const minStablecoin = estStablecoin * (1 - slip);
          /** Fallback min-out if Quoter is unavailable (same as before) */
          const outMinWeiPriceBased = ethers.utils.parseUnits(toDecimalStringSafe(minStablecoin, stablecoinDecimals), stablecoinDecimals);
          /** Used by non–Universal Router V3 `exactInputSingle` path */
          let outMinWei = outMinWeiPriceBased;

          const wethBal = await token.balanceOf(userAddress);
          if (wethBal.lt(tokenInWei)) {
            throw new Error(
              `[SELL] Insufficient WETH: wallet has ${ethers.utils.formatEther(wethBal)} WETH, need ${ethers.utils.formatEther(tokenInWei)}. ` +
                `Wrap ETH to WETH or reduce the sell amount.`
            );
          }

          if (isV3 && tradePath.length === 2) {
            if (usingUniversalRouter) {
              let swapSuccess = false;
              let lastError = null;
              const feeTiersToTry = [...new Set(feeTier ? [feeTier, 3000, 10000, 500] : [500, 3000, 10000])];
              const quoterAddr = quoterV2AddressForChain(currentConfig.chainId);
              const slipBps = Math.min(9999, Math.max(1, Math.round(slip * 10000)));
              const quoterReadProvider =
                provider || signer.provider || new ethers.providers.Web3Provider(window.ethereum);

              const quoteMinOutForTier = async (tier) => {
                let outMinWei = outMinWeiPriceBased;
                if (quoterAddr && quoterReadProvider) {
                  try {
                    const quoter = new ethers.Contract(quoterAddr, QUOTER_V2_ABI, quoterReadProvider);
                    const quoteRes = await quoter.callStatic.quoteExactInputSingle([
                      ethers.utils.getAddress(tradePath[0]),
                      ethers.utils.getAddress(tradePath[1]),
                      tokenInWei,
                      tier,
                      0,
                    ]);
                    const quoted = quoteRes?.amountOut ?? quoteRes?.[0];
                    if (quoted && quoted.gt(0)) {
                      outMinWei = quoted.mul(ethers.BigNumber.from(10000 - slipBps)).div(10000);
                      console.log(
                        `[SELL] Quoter tier ${tier}: expectedOut=${quoted.toString()} raw, minOut=${outMinWei.toString()} (slip ${slipBps} bps); ` +
                          `price-based min would be ${outMinWeiPriceBased.toString()}`
                      );
                    }
                  } catch (qe) {
                    console.warn(`[SELL] Quoter failed for tier ${tier}, using price-based minOut:`, qe?.message || qe);
                  }
                }
                return outMinWei;
              };

              for (const tier of feeTiersToTry) {
                try {
                  console.log(`🔄 [SELL] Attempting Universal Router swap with fee tier ${tier}...`);
                  const deadline = Math.floor(Date.now() / 1000) + 60 * 40; // 40 minutes from now

                  const outMinWei = await quoteMinOutForTier(tier);

                  const swapCmd = encodeUniversalRouterV3Swap(
                    userAddress,
                    tokenInWei,
                    outMinWei,
                    tradePath[0],
                    tier,
                    tradePath[1],
                    true // payerIsUser = true means pull from user via Permit2
                  );

                  // Commands: V3_SWAP_EXACT_IN (0x00) only - no PERMIT2_PERMIT needed with on-chain approvals
                  const commands = swapCmd.command;
                  const inputs = [swapCmd.input];
                  const iface = new ethers.utils.Interface(ROUTER_ABI);
                  const executeData = iface.encodeFunctionData("execute(bytes,bytes[],uint256)", [commands, inputs, deadline]);
                  tx = await signer.sendTransaction({
                    to: currentConfig.routerAddress,
                    data: executeData,
                    gasLimit: 500000,
                    maxFeePerGas: maxFee,
                    maxPriorityFeePerGas: maxPrio,
                  });
                  const waited = await tx.wait();
                  if (waited.status === 0) {
                    lastError = new Error(`[SELL] Universal Router reverted (fee tier ${tier}, ${tx.hash})`);
                    console.warn(
                      `⚠️ [SELL] Universal Router tx reverted with fee tier ${tier} (${tx.hash}), trying next tier…`
                    );
                    continue;
                  }
                  tradeReceipt = waited;
                  console.log(`✅ [SELL] Confirmed with fee tier ${tier}: ${tx.hash}`);
                  swapSuccess = true;
                  break;
                } catch (error) {
                  lastError = error;
                  console.warn(`⚠️ [SELL] Universal Router swap failed with fee tier ${tier}:`, error.message);
                }
              }

              // Fallback: SwapRouter02 uses classic token.approve(router) — avoids Permit2 pull issues with UR
              if (!swapSuccess) {
                const tierUse = feeTier || feeTiersToTry[0] || 500;
                console.warn(
                  `⚠️ [SELL] Universal Router failed for all tiers; trying SwapRouter02 exactInputSingle (fee ${tierUse})…`,
                  lastError?.message || ""
                );
                const swapRouter02 = new ethers.Contract(SWAP_ROUTER_02_ADDRESS, ROUTER_ABI, signer);
                const allowanceSr = await token.allowance(userAddress, SWAP_ROUTER_02_ADDRESS);
                if (allowanceSr.lt(tokenInWei)) {
                  const a = await token.approve(SWAP_ROUTER_02_ADDRESS, ethers.constants.MaxUint256);
                  await a.wait();
                }
                const minOutFb = await quoteMinOutForTier(tierUse);
                const deadlineFb = Math.floor(Date.now() / 1000) + 60 * 40;
                tx = await swapRouter02.exactInputSingle(
                  {
                    tokenIn: tradePath[0],
                    tokenOut: tradePath[1],
                    fee: tierUse,
                    recipient: userAddress,
                    deadline: deadlineFb,
                    amountIn: tokenInWei,
                    amountOutMinimum: minOutFb,
                    sqrtPriceLimitX96: 0,
                  },
                  {
                    gasLimit: 500000,
                    maxFeePerGas: maxFee,
                    maxPriorityFeePerGas: maxPrio,
                  }
                );
                tradeReceipt = await tx.wait();
                if (tradeReceipt.status === 0) {
                  throw new Error(
                    `[SELL] SwapRouter02 exactInputSingle reverted (${tx.hash}). ` +
                      `UR error: ${lastError?.message || "unknown"}`
                  );
                }
                swapSuccess = true;
                console.log(`✅ [SELL] Confirmed via SwapRouter02 fallback: ${tx.hash}`);
              }

              if (!swapSuccess) {
                throw lastError || new Error("All fee tier attempts failed for Universal Router swap");
              }
            } else {
              // Non-UR path (SwapRouter02 / exactInputSingle): prefer QuoterV2 minOut over price-based.
              try {
                const quoterAddr = quoterV2AddressForChain(currentConfig.chainId);
                const quoterReadProvider =
                  provider || signer.provider || new ethers.providers.Web3Provider(window.ethereum);
                const slipBps = Math.min(9999, Math.max(1, Math.round(slip * 10000)));
                if (quoterAddr && quoterReadProvider && feeTier != null) {
                  const quoter = new ethers.Contract(quoterAddr, QUOTER_V2_ABI, quoterReadProvider);
                  const quoteRes = await quoter.callStatic.quoteExactInputSingle([
                    ethers.utils.getAddress(tradePath[0]),
                    ethers.utils.getAddress(tradePath[1]),
                    tokenInWei,
                    feeTier,
                    0,
                  ]);
                  const quoted = quoteRes?.amountOut ?? quoteRes?.[0];
                  if (quoted && quoted.gt(0)) {
                    outMinWei = quoted.mul(ethers.BigNumber.from(10000 - slipBps)).div(10000);
                    console.log(
                      `[SELL] Quoter expectedOut=${quoted.toString()} raw, minOut=${outMinWei.toString()} (slip ${slipBps} bps); price-based min would be ${outMinWeiPriceBased.toString()}`
                    );
                  }
                }
              } catch (qe) {
                console.warn("[SELL] Quoter failed for non-UR path, using price-based minOut:", qe?.message || qe);
              }
              // V3: Use exactInputSingle for single-hop swaps
              const params = {
                tokenIn: tradePath[0],
                tokenOut: tradePath[1],
                fee: feeTier,
                recipient: userAddress,
                deadline: deadline,
                amountIn: tokenInWei,
                amountOutMinimum: outMinWei,
                sqrtPriceLimitX96: 0, // No price limit
              };
              tx = await routerForTrade.exactInputSingle(params, {
                gasLimit: 300000,
                maxFeePerGas: maxFee,
                maxPriorityFeePerGas: maxPrio,
              });
            }
          } else {
            // V2: Use swapExactTokensForTokens
            tx = await routerForTrade.swapExactTokensForTokens(
              tokenInWei,
              outMinWei,
              tradePath,
              userAddress,
              deadline,
              { gasLimit: 300000, maxFeePerGas: maxFee, maxPriorityFeePerGas: maxPrio }
            );
          }
        } else {
          if (isWcroUsdcMarketLocal || isWbtcUsdcMarket) {
            throw new Error(
              "[SELL] Route not handled for this market in the MetaMask path. " +
                "ETH–USDC sells must use WETH as the sold token (check token + initiator). " +
                "WBTC–USDC multi-hop sells are not implemented here — use Backend Wallet."
            );
          }
          // Fallback: stablecoin → native (legacy single-hop)
          const stablecoinDecimals = chainConfig.defaultStablecoin === "USDC" ? 6 : 
                                     chainConfig.defaultStablecoin === "USDT" ? 6 : 18;
          const stablecoinInWei = ethers.utils.parseUnits(toDecimalStringSafe(amount, stablecoinDecimals), stablecoinDecimals);
          await ensureAllowance(currentConfig.stablecoinAddress, userAddress, currentConfig.routerAddress, stablecoinInWei);

          const estNative = Number(amount) / priceToUse;
          const minNative = estNative * (1 - slip);
          const outMinWei = ethers.utils.parseUnits(toDecimalStringSafe(minNative, chainConfig.nativeCurrency.decimals), chainConfig.nativeCurrency.decimals);

          tx = await routerForTrade.swapExactTokensForETH(
            stablecoinInWei,
            outMinWei,
            tradePath,
            userAddress,
            deadline,
            { gasLimit: 300000, maxFeePerGas: maxFee, maxPriorityFeePerGas: maxPrio }
          );
        }
      }

      const receipt = tradeReceipt != null ? tradeReceipt : await tx.wait();

      // CRITICAL FIX: Check if transaction actually succeeded
      // receipt.status: 1 = success, 0 = failed (reverted)
      if (receipt.status === 0) {
        console.error(`[TRADE ERROR] Transaction reverted on-chain for ${side} trade`);
        const txHash = receipt.transactionHash || receipt.hash || "unknown";
        const errorMsg = `Transaction reverted on-chain. Check Arbiscan: ${txHash}`;
        if (ctx.pendingId) rollbackPending(marketKey, ctx.pendingId);
        delete pendingTradeSubmissionsRef.current[marketKey];
        showTradeNotice(errorMsg, { marketKey, side });
        return false;
      }
      
      // Transaction succeeded - process the receipt
      if (ctx.pendingId) commitPending(marketKey, ctx.pendingId);
      await processTradeReceipt(receipt, amount, side, {
        ...ctx,
        initiator,
        tokenAddress: tokenToUse,
        marketKey,
        preStableBalance: preTradeCoreBalances?.stableBalance,
        preWrappedBalance: preTradeCoreBalances?.wrappedBalance,
      });
      return true;
    } catch (err) {
      console.error(`[TRADE ERROR] ${side}:`, err);
      const errorMsg = err?.message || err?.toString() || "Unknown error";
      if (ctx.pendingId) rollbackPending(marketKey, ctx.pendingId);
      delete pendingTradeSubmissionsRef.current[marketKey];
      showTradeNotice(errorMsg, { marketKey, side });
      return false;
    }
  };

  useEffect(() => {
    if (!executionAddress) return;
    setAddress(executionAddress);
    tradeLockRef.current = {};
    getTokenBalance(executionAddress);
  }, [executionAddress]);

  // Refresh balances when chain or config changes
  useEffect(() => {
    if (!executionAddress || !currentConfig || !chainConfig || !provider) return;
    // Small delay to ensure provider is ready after chain switch
    const timeoutId = setTimeout(() => {
      console.log(`[Chain Change] Refreshing balances for ${chainConfig.name}...`);
      getTokenBalance(executionAddress);
    }, 1000); // 1 second delay to allow chain switch to complete
    
    return () => clearTimeout(timeoutId);
  }, [selectedChain, selectedDex, currentConfig, chainConfig, executionAddress, provider]);

  useEffect(() => {
    if (!TOKEN_ADDRESS) return;
    fetchTokenInfo(TOKEN_ADDRESS);
    (async () => {
      const pair = await getPairAddress(TOKEN_ADDRESS);
      if (pair) setTokenPairAddress(pair);
    })();
  }, [TOKEN_ADDRESS]);

  useEffect(() => {
    if (!isListening || !tokenPairAddress || !tokenDecimals) return;
    fetchTokenPrice3();
  }, [isListening, tokenPairAddress, tokenDecimals, customBots.length]);

  useEffect(() => {
    if (!persistencePlayer || hydratedPlayer !== persistencePlayer) return;
    const keys = new Set(markets.map((m) => m.key));
    setBotConfigs((prev) => {
      const next = { ...prev };
      let changed = false;
      keys.forEach((k) => {
        if (!next[k]) {
          next[k] = { ...instanceDefaultBotCfg };
          changed = true;
        }
      });
      Object.keys(next).forEach((k) => {
        if (!keys.has(k)) {
          delete next[k];
          changed = true;
        }
      });
      if (changed) {
        // persist changed set
        (async () => {
          try {
            if (!persistencePlayer) return;
            const player = persistencePlayer;
            const tasks = Object.entries(next).map(([botKey, cfg]) =>
              moralisSaveBotConfig(player, botKey, cfg)
            );
            await Promise.all(tasks);
          } catch (e) {
            console.error("❌ moralisSaveBotConfig (market-change) failed:", e);
          }
        })();
        return next;
      }
      return prev;
    });
  }, [markets, persistencePlayer, hydratedPlayer]);

  useEffect(() => {
    return () => {
      detachAllListeners();
    };
  }, []);

  useEffect(() => {
    if (!isListening) return;
    seedTargetsIfUnset();
  }, [isListening, buyTarget, sellTarget, markets.length]);

    useEffect(() => {
    const id = setInterval(() => {
      const now = Date.now();

      // Consecutive BUY count is economic state and must survive idle periods and recovery resets.
      savePositions(prev => {
        const next = { ...prev };
        let changed = false;

        Object.keys(next).forEach(key => {
          const pos = next[key];
          if (!pos) return;

          const lastSell = lastSellAtRef.current[key] || 0;

          if (lastSell && now - lastSell > COUNTER_DECAY_MS && (pos.sellCounter || 1) > 1) {
            next[key] = { ...(next[key] || pos), sellCounter: (pos.sellCounter || 1) - 1 };
            lastSellAtRef.current[key] = now;
            changed = true;
          }
        });

        return changed ? next : prev;
      });
    }, 60_000); // every 60s

    return () => clearInterval(id);
  }, []);

  // ---------- actions / helpers needed by Part 2 UI ----------
  const forceBuyFor = async (key) => {
    if (isSecondaryBot && !secondarySessionAuthorized) {
      showTradeNotice("Authorize the secondary bot with the connected primary wallet before trading.", { marketKey: key, side: "BUY" });
      return;
    }
    console.log("🟢 [forceBuyFor] Clicked for key:", key);
    console.log("   currentConfig:", currentConfig);
    console.log("   chainConfig:", chainConfig);
    console.log("   stablecoinAddress:", currentConfig?.stablecoinAddress);
    
    const market = markets.find(m => m.key === key);
    if (!market) {
      console.error(`[forceBuyFor] Market not found: ${key}`);
      alert(`❌ Market not found: ${key}. Make sure you've started listening and the market is available.`);
      return;
    }
    const { token } = market;
    const pos = positions[key] || {};
    const cfg = getEffectiveBotCfg(key);

    if (!currentConfig) return;
    const nativeSymbol = chainConfig.nativeCurrency.symbol;
    const stableSymbol = chainConfig.defaultStablecoin;
    const wrappedSymbol = chainConfig.nativeCurrency.symbol;
    
    // Determine the correct initiator based on the market key
    // For WCRO-USDC: buying WCRO with USDC, so initiator is USDC
    // For WBTC-WCRO: buying WBTC with WCRO, so initiator is WCRO (native)
    // For WBTC-USDC: buying WBTC with USDC, so initiator is USDC
    let actualInitiator;
    let baseBalance;
    
    if (key === `${wrappedSymbol}-${stableSymbol}`) {
      // Buying wrapped native (WETH/WCRO) with stablecoin (USDC)
      // baseBalance should be USDC (what we're spending)
      // holdings track wrapped native (what we're holding)
      actualInitiator = stableSymbol;
      // Get USDC balance from the _BASE key we set in getTokenBalance
      const baseKey = `${wrappedSymbol}-${stableSymbol}_BASE`;
      baseBalance = positions[baseKey]?.holdings || 0;
      
      // Fallback: if _BASE key doesn't exist, try to get stablecoin balance directly
      if (baseBalance <= 0 && currentConfig?.stablecoinAddress) {
        try {
          const stablecoinContract = new ethers.Contract(
            currentConfig.stablecoinAddress,
            ERC20_ABI,
            provider
          );
          const stablecoinDecimals = chainConfig.defaultStablecoin === "USDC" ? 6 : 
                                     chainConfig.defaultStablecoin === "USDT" ? 6 : 18;
          const rawBalance = await stablecoinContract.balanceOf(executionAddress);
          baseBalance = Math.max(0, Number(ethers.utils.formatUnits(rawBalance, stablecoinDecimals)));
          console.log(`   Fetched stablecoin balance directly: ${baseBalance}`);
        } catch (e) {
          console.warn(`   Failed to fetch stablecoin balance:`, e);
        }
      }
      
      console.log(`   Base balance key: ${baseKey}`);
      console.log(`   Base balance value: ${baseBalance}`);
      console.log(`   All positions keys:`, Object.keys(positions));
      console.log(`   Position for ${baseKey}:`, positions[baseKey]);
    } else if (key === `WBTC-${wrappedSymbol}`) {
      // Buying WBTC with wrapped native
      actualInitiator = nativeSymbol;
      baseBalance = positions[nativeSymbol]?.holdings || 0;
      
      // Fallback: get native balance directly if not in positions
      if (baseBalance <= 0 && provider && executionAddress) {
        try {
          const nativeBal = await provider.getBalance(executionAddress);
          baseBalance = Math.max(0, Number(ethers.utils.formatEther(nativeBal)));
          console.log(`   Fetched native balance directly: ${baseBalance}`);
        } catch (e) {
          console.warn(`   Failed to fetch native balance:`, e);
        }
      }
    } else if (key === `WBTC-${stableSymbol}`) {
      // Buying WBTC with stablecoin (USDC)
      // baseBalance should be USDC (what we're spending)
      actualInitiator = stableSymbol;
      const baseKey = `${wrappedSymbol}-${stableSymbol}_BASE`;
      baseBalance = positions[baseKey]?.holdings || 0;
      
      // Fallback: if _BASE key doesn't exist, try to get stablecoin balance directly
      if (baseBalance <= 0 && currentConfig?.stablecoinAddress) {
        try {
          const stablecoinContract = new ethers.Contract(
            currentConfig.stablecoinAddress,
            ERC20_ABI,
            provider
          );
          const stablecoinDecimals = chainConfig.defaultStablecoin === "USDC" ? 6 : 
                                     chainConfig.defaultStablecoin === "USDT" ? 6 : 18;
          const rawBalance = await stablecoinContract.balanceOf(executionAddress);
          baseBalance = Math.max(0, Number(ethers.utils.formatUnits(rawBalance, stablecoinDecimals)));
          console.log(`   Fetched stablecoin balance directly: ${baseBalance}`);
        } catch (e) {
          console.warn(`   Failed to fetch stablecoin balance:`, e);
        }
      }
    } else {
      // Fallback to market's initiator
      actualInitiator = market.initiator;
      if (actualInitiator === stableSymbol || actualInitiator === "USDC") {
        const baseKey = `${wrappedSymbol}-${stableSymbol}_BASE`;
        baseBalance = positions[baseKey]?.holdings || 0;
        
        // Fallback: get stablecoin balance directly
        if (baseBalance <= 0 && currentConfig?.stablecoinAddress) {
          try {
            const stablecoinContract = new ethers.Contract(
              currentConfig.stablecoinAddress,
              ERC20_ABI,
              provider
            );
            const stablecoinDecimals = chainConfig.defaultStablecoin === "USDC" ? 6 : 
                                       chainConfig.defaultStablecoin === "USDT" ? 6 : 18;
            const rawBalance = await stablecoinContract.balanceOf(executionAddress);
            baseBalance = Math.max(0, Number(ethers.utils.formatUnits(rawBalance, stablecoinDecimals)));
          } catch (e) {
            console.warn(`   Failed to fetch stablecoin balance:`, e);
          }
        }
      } else {
        baseBalance = positions[nativeSymbol]?.holdings || 0;
        
        // Fallback: get native balance directly
        if (baseBalance <= 0 && provider && executionAddress) {
          try {
            const nativeBal = await provider.getBalance(executionAddress);
            baseBalance = Math.max(0, Number(ethers.utils.formatEther(nativeBal)));
          } catch (e) {
            console.warn(`   Failed to fetch native balance:`, e);
          }
        }
      }
    }

    if (baseBalance <= 0) {
      alert(`❌ No balance available for ${actualInitiator || 'this trade'}. Please check your wallet balance.`);
      return;
    }

    // Use buyMultiplier directly (no tiers)
    const buyMultiplierPct = cfg.buyMultiplier || 0.1;

    let amountToBuy = cfg.usePercentMode
      ? baseBalance * buyMultiplierPct
      : Number(cfg.manualBuy || 0);

    if (cfg.maxBaseCap && Number(cfg.maxBaseCap) > 0) {
      amountToBuy = Math.min(amountToBuy, Number(cfg.maxBaseCap));
    }

    amountToBuy = Math.max(0, Math.min(amountToBuy, baseBalance));
    if (!(amountToBuy > 0)) return;

    const snapshot = {
      holdings: pos.holdings, avgEntry: pos.avgEntry,
      priceTargetBuy: pos.priceTargetBuy, priceTargetSell: pos.priceTargetSell,
      buyCounter: pos.buyCounter, sellCounter: pos.sellCounter
    };

    const pendingId = startPending(key, snapshot);

    const price = markets.find(m => m.key === key)?.price || 0;
    const isWcroUsdc = key === `${wrappedSymbol}-${stableSymbol}`;

    // Calculate quantity received: For WETH-USDC, amountToBuy is in USDC, price is USDC per WETH
    // So: WETH received = USDC spent / (USDC per WETH) = USDC / price
    // For other markets: amountToBuy is in base token, price is target per base, so divide
    const qty = Number(amountToBuy) / Math.max(price, DUST);
    const newHoldings = (pos.holdings || 0) + qty;
    const newAvgEntry =
      (pos.holdings || 0) > 0
        ? ((pos.avgEntry || 0) * (pos.holdings || 0) + price * qty) / Math.max(newHoldings, DUST)
        : price;

    // CRITICAL FIX: Always use newAvgEntry as anchor for targets, never current price
    // Targets should be calculated from avgEntry (cost basis), not current market price
    // This ensures targets stay fixed relative to entry price
    // If avgEntry is missing, derive anchor from existing targets to prevent drift
    let anchorForTargets;
    if (newAvgEntry > 0) {
      anchorForTargets = newAvgEntry;
    } else if (pos.avgEntry > 0) {
      anchorForTargets = pos.avgEntry;
    } else if (pos.priceTargetBuy > 0 || pos.priceTargetSell > 0) {
      // Derive anchor from existing targets to preserve target stability
      const cfg = getEffectiveBotCfg(key);
      const buyPct  = cfg.buyTarget  ?? defaultBotCfg.buyTarget;
      const sellPct = cfg.sellTarget ?? defaultBotCfg.sellTarget;
      anchorForTargets = pos.priceTargetBuy > 0 ? pos.priceTargetBuy / buyPct : pos.priceTargetSell / sellPct;
    } else {
      // Only use price if no targets exist (initial setup)
      anchorForTargets = price;
    }

    updatePosition(key, {
      holdings: newHoldings,
      avgEntry: newAvgEntry,
      // CRITICAL FIX: Always use avgEntry as anchor, never current price
      // Targets should stay fixed relative to entry price, not drift with market price
      priceTargetBuy:  anchorForTargets * (botConfigs[key]?.buyTarget  ?? defaultBotCfg.buyTarget),
      priceTargetSell: anchorForTargets * (botConfigs[key]?.sellTarget ?? defaultBotCfg.sellTarget),
      buyCounter: (pos.buyCounter || 1) + 1,
      sellCounter: 1,
      lastTradeAt: Date.now(),
      lastBuyAt: Date.now(),
      lastBuyPrice: price,
    });

    // Determine the correct token to use based on what we're buying
    let tokenToUse;
    if (key === `${wrappedSymbol}-${stableSymbol}`) {
      // Buying WCRO with USDC, so target token is WCRO (wrapped native)
      tokenToUse = currentConfig.wrappedNativeToken;
    } else if (key === `WBTC-${wrappedSymbol}`) {
      // Buying WBTC with WCRO, so target token is WBTC
      tokenToUse = token;
    } else if (key === `WBTC-${stableSymbol}`) {
      // Buying WBTC with USDC, so target token is WBTC
      tokenToUse = token;
    } else {
      // Fallback
      tokenToUse = token;
    }

    console.log(`🟢 [${key}] Manual BUY fired!`);
    console.log(`   Amount: ${amountToBuy} ${actualInitiator} (${cfg.usePercentMode ? "percent mode" : "manual"})`);
    console.log(`   Price: ${safeFix(markets.find(m => m.key === key)?.price)}`);
    console.log(`   Counter: ${pos.buyCounter}`);
    console.log(`   New anchor: ${safeFix(pos.avgEntry)}`);
    console.log(`   Initiator: ${actualInitiator} (was: ${market.initiator})`);
    console.log(`   Token to use: ${tokenToUse}`);
    
    try {
      await executeBlockchainTrade("BUY", amountToBuy.toString(), tokenToUse, actualInitiator, { marketKey: key, pendingId });
    } catch (error) {
      console.error(`[forceBuyFor] Error executing trade:`, error);
      showTradeNotice(error?.message || error, { marketKey: key, side: "BUY" });
      if (pendingId) rollbackPending(key, pendingId);
    }
  };

  const forceSellFor = async (key) => {
    if (isSecondaryBot && !secondarySessionAuthorized) {
      showTradeNotice("Authorize the secondary bot with the connected primary wallet before trading.", { marketKey: key, side: "SELL" });
      return;
    }
    const market = markets.find(m => m.key === key);
    if (!market) {
      console.error(`[forceSellFor] Market not found: ${key}`);
      alert(`❌ Market not found: ${key}. Make sure you've started listening and the market is available.`);
      return;
    }
    const { initiator, token } = market;
    const pos = positions[key] || {};
    const cfg = getEffectiveBotCfg(key);

    if (!(pos.holdings > 0)) return;

    // Use sellMultiplier directly (no tiers)
    let sellMultiplierPct = cfg.sellMultiplier || 0.3;
    if (cfg.maxSellCap != null && isFinite(Number(cfg.maxSellCap))) {
      sellMultiplierPct = Math.min(sellMultiplierPct, Number(cfg.maxSellCap));
    }

    let amountToSell = cfg.usePercentMode
      ? (pos.holdings || 0) * sellMultiplierPct
      : Number(cfg.manualSell || 0);

    amountToSell = Math.max(0, Math.min(amountToSell, (pos.holdings || 0)));
    if (!(amountToSell > 0)) return;
    if (!currentConfig) return;

    const snapshot = {
      holdings: pos.holdings, avgEntry: pos.avgEntry,
      priceTargetBuy: pos.priceTargetBuy, priceTargetSell: pos.priceTargetSell,
      buyCounter: pos.buyCounter, sellCounter: pos.sellCounter
    };
    const pendingId = startPending(key, snapshot);

    const nativeSymbol = chainConfig.nativeCurrency.symbol;
    const stableSymbol = chainConfig.defaultStablecoin;
    const wrappedSymbol = chainConfig.nativeCurrency.symbol;

    // Determine the correct initiator and tokenToUse based on what we're selling
    let actualInitiator;
    let tokenToUse;
    
    if (key === `${wrappedSymbol}-${stableSymbol}`) {
      // Selling WCRO to get USDC
      actualInitiator = wrappedSymbol; // We're selling WCRO
      tokenToUse = currentConfig.wrappedNativeToken; // The token we're selling
    } else if (key === `WBTC-${wrappedSymbol}`) {
      // Selling WBTC to get WCRO
      actualInitiator = nativeSymbol;
      tokenToUse = token; // WBTC
    } else if (key === `WBTC-${stableSymbol}`) {
      // Selling WBTC to get USDC
      actualInitiator = stableSymbol;
      tokenToUse = token; // WBTC
    } else {
      // Fallback
      actualInitiator = market.initiator;
      tokenToUse = token;
    }

    const price = markets.find(m => m.key === key)?.price || 0;
    const newHoldings = (pos.holdings || 0) - Number(amountToSell);
    
    // Calculate USD value of remaining holdings to determine if we should reset anchor
    // Zero-holdings reset threshold: Reset if holdings are effectively zero or worth less than threshold
    let remainingHoldingsUsd = 0;
    if (key === `${wrappedSymbol}-${stableSymbol}`) {
      // For WETH-USDC: holdings are in WETH, price is USDC per WETH
      // USD value = holdings * price (since USDC = $1)
      remainingHoldingsUsd = newHoldings * price;
    } else if (key === `WBTC-${stableSymbol}`) {
      // For WBTC-USDC: holdings are in WBTC, price is USDC per WBTC
      remainingHoldingsUsd = newHoldings * price;
    } else {
      // For WBTC-WETH: holdings are in WBTC, need ETH price
      const ethPrice = positions[nativeSymbol]?.price || 0;
      if (ethPrice > 0) {
        remainingHoldingsUsd = newHoldings * price * ethPrice;
      }
      // If ETH price unavailable, we'll still reset if holdings <= DUST (zero check)
    }
    
    const cfgSell = getEffectiveBotCfg(key);
    const buyPct = cfgSell.buyTarget ?? defaultBotCfg.buyTarget;
    const sellPct = cfgSell.sellTarget ?? defaultBotCfg.sellTarget;
    const sellBook = getMarketBook(key, pos, price, positions, chainConfig);
    const sellSpotGrid = shouldUseSpotGridMode({
      cfg: cfgSell,
      pos,
      price,
      baseBalanceUsd: sellBook.baseBalance,
      totalBookUsd: sellBook.totalBookUsd,
    });
    const postSell = resolvePostSellTargetUpdate({
      price,
      newHoldings,
      remainingHoldingsUsd,
      pos,
      cfg: cfgSell,
      buyPct,
      sellPct,
      useSpotGrid: sellSpotGrid,
      resetWhenDepleted: false,
    });

    updatePosition(key, {
      holdings: newHoldings,
      avgEntry: postSell.newAvgEntry,
      priceTargetBuy: postSell.priceTargetBuy,
      priceTargetSell: postSell.priceTargetSell,
      buyCounter: postSell.buyCounter,
      sellCounter: postSell.sellCounter,
      lastTradeAt: Date.now(),
      lastSellAt: Date.now(),
      lastBuyPrice: 0,
      recoveryModeActive: false,
      lastRecoveryGridAt: postSell.depletedWithoutReset ? (pos.lastRecoveryGridAt || 0) : postSell.newAvgEntry === 0 ? 0 : (pos.lastRecoveryGridAt || 0),
      recoveryBuyBlockedUntil: postSell.depletedWithoutReset ? (pos.recoveryBuyBlockedUntil || 0) : postSell.newAvgEntry === 0 ? 0 : (pos.recoveryBuyBlockedUntil || 0),
    });

    console.log(`🔴 [${key}] Manual SELL fired!`);
    console.log(`   Amount: ${amountToSell} ${actualInitiator}`);
    console.log(`   Price: ${safeFix(markets.find(m => m.key === key)?.price)}`);
    console.log(`   Counter: ${pos.sellCounter}`);
    console.log(`   Holdings after: ${newHoldings}`);
    console.log(`   Initiator: ${actualInitiator}, Token: ${tokenToUse}`);

    lastSellAtRef.current[key] = Date.now();

    executeBlockchainTrade("SELL", amountToSell.toString(), tokenToUse, actualInitiator, { marketKey: key, pendingId });
  };


  const forceBuy  = async () => {
    if (!chainConfig) {
      alert("❌ Chain configuration not ready");
      return;
    }
    const wrappedSymbol = chainConfig.nativeCurrency.symbol;
    const stableSymbol = chainConfig.defaultStablecoin;
    const marketKey = `${wrappedSymbol}-${stableSymbol}`;
    await forceBuyFor(marketKey);
  };
  const forceSell = async () => {
    if (!chainConfig) {
      alert("❌ Chain configuration not ready");
      return;
    }
    const wrappedSymbol = chainConfig.nativeCurrency.symbol;
    const stableSymbol = chainConfig.defaultStablecoin;
    const marketKey = `${wrappedSymbol}-${stableSymbol}`;
    await forceSellFor(marketKey);
  };
  const forceHold = () => {
    Object.keys(positions).forEach(k => updatePosition(k, { buyCounter: 1, sellCounter: 1 }));
    // clear tradeHistory locally and in Moralis
    setTradeHistory([]);
    (async () => {
      try {
        if (!persistencePlayer) return;
        await moralisClearTradeHistoryForPlayer(persistencePlayer);
      } catch (e) { console.error("clear trade history failed", e); }
    })();
  };

  const tearDownListeners = async () => {
    try {
      if (tokenPairAddress) {
        const pair = new ethers.Contract(tokenPairAddress, PAIR_ABI, provider);
        pair.removeAllListeners("Swap");
        pair.removeAllListeners("Sync");
      }
      if (!currentConfig) return;
      
      const factory = getFactory();
      
      // Get stablecoin pair address dynamically (WCRO/USDC)
      let stablecoinPairAddress = "";
      try {
        stablecoinPairAddress = await getPairOrPoolAddress(
          currentConfig.wrappedNativeToken,
          currentConfig.stablecoinAddress
        );
      } catch (e) {
        console.warn("Failed to get stablecoin pair in tearDownListeners:", e);
      }
      if (stablecoinPairAddress) {
        const croUsdc = new ethers.Contract(stablecoinPairAddress, PAIR_ABI, provider);
        croUsdc.removeAllListeners("Swap");
        croUsdc.removeAllListeners("Sync");
      }
      
      // Get WBTC/USDC pair address dynamically
      if (TOKEN_ADDRESS && ethers.utils.isAddress(TOKEN_ADDRESS)) {
        try {
          const pair = await getPairOrPoolAddress(
            TOKEN_ADDRESS,
            currentConfig.stablecoinAddress
          );
          if (pair) {
            const wbtcUsdc = new ethers.Contract(pair, PAIR_ABI, provider);
            wbtcUsdc.removeAllListeners("Swap");
            wbtcUsdc.removeAllListeners("Sync");
          }
        } catch (e) {
          console.warn("Failed to get WBTC/stablecoin pair in tearDownListeners:", e);
        }
      }
    } catch (e) {
      console.warn("Listener teardown warning:", e?.message || e);
    }

    try {
      if (factoryContractRef.current) {
        factoryContractRef.current.removeAllListeners();
        factoryContractRef.current = null;
      }
      liveContractsRef.current.forEach(c => { try { c.removeAllListeners(); } catch {} });
      liveContractsRef.current = [];
    } catch {}

    if (window.__sniperHeartbeats?.[botInstanceId]) {
      clearInterval(window.__sniperHeartbeats[botInstanceId]);
      delete window.__sniperHeartbeats[botInstanceId];
    }
    tradeLockRef.current = {};
    if (window.__attachedPairsByBot) {
      window.__attachedPairsByBot[botInstanceId] = {};
    }
  };

  const toggleListening = async () => {
    if (!isListening) {
      if (isSecondaryBot && !secondarySessionAuthorized) {
        showTradeNotice("Authorize the secondary bot with the connected primary wallet before starting it.");
        return;
      }
      await fetchTokenPrice3();
      setIsListening(true);
    } else {
      await tearDownListeners();
      setIsListening(false);
    }
  };

  const addCustomBot = async (tokenAddress) => {
    if (!ethers.utils.isAddress(tokenAddress)) return;
    const pairAddress = await getPairAddress(tokenAddress);
    if (!pairAddress) { alert("No WCRO pair found for this token"); return; }

    const token = new ethers.Contract(tokenAddress, ERC20_META_ABI, provider);
    const symbol = await token.symbol().catch(() => tokenAddress.slice(0, 6));
    const key = `${symbol}-WCRO`;

    if (markets.find(m => m.key === key) || customBots.find(b => b.key === key)) {
      alert("Market already added");
      return;
    }

    const newMarket = { key, price: 0, token: tokenAddress, initiator: "CRO" };
    const newPosition = { holdings: 0, avgEntry: 0, priceTargetBuy: 0, priceTargetSell: 0, buyCounter: 1, sellCounter: 1, enabled: true };

    saveCustomBots(prev => [...prev, { key, tokenAddress, pairAddress }]);
    setMarkets(prev => [...prev, newMarket]);
    savePositions(prev => ({ ...prev, [key]: newPosition }));

    setBotConfig(key, { ...instanceDefaultBotCfg });

    await fetchTokenPrice3();
    if (executionAddress) getTokenBalance(executionAddress);
  };

  const removeCustomBot = (key) => {
    saveCustomBots(prev => prev.filter(b => b.key !== key));
    setMarkets(prev => prev.filter(m => m.key !== key));
    savePositions(prev => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
    saveBotConfigs(prev => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });

    // Also remove tradeHistory entries for that market from Moralis
    (async () => {
      try {
        if (!persistencePlayer) return;
        await moralisDeleteTradeHistoryForMarket(persistencePlayer, key);
      } catch (e) { console.error("deleteTradeHistoryForMarket failed", e); }
    })();
  };

  const readTokenMeta = async (addr) => {
    try {
      const c = new ethers.Contract(addr, ERC20_META_ABI, provider);
      const [symbol, name] = await Promise.all([c.symbol(), c.name()]);
      return { symbol: String(symbol || ""), name: String(name || "") };
    } catch {
      return { symbol: "", name: "" };
    }
  };

  const stopSnipers = () => {
    try {
      if (factoryContractRef.current) factoryContractRef.current.removeAllListeners("PairCreated");
      Object.values(watchedPairsRef.current).forEach(({ pair }) => {
        try {
          pair?.removeAllListeners("Sync");
          pair?.removeAllListeners("Swap");
        } catch {}
      });
      watchedPairsRef.current = {};
    } catch {}
    ambushActiveRef.current = false;
    recklessActiveRef.current = false;
    setSniperStatus("");
  };

  const watchInitialLiquidity = async ({
    tokenAddress,
    minWcroLiquidity,
    autoBuyWcroAmount,
    cooldownMs,
    modeLabel,
  }) => {
    if (!currentConfig) return;
    const tokenLower = tokenAddress.toLowerCase();
    if (watchedPairsRef.current[tokenLower]?.listenerBound) return;

    const pairAddr = await getPairAddress(tokenAddress);
    if (!pairAddr) return;

    const pair = new ethers.Contract(pairAddr, PAIR_ABI, provider);
    watchedPairsRef.current[tokenLower] = { pair, listenerBound: true };

    const token0 = (await pair.token0()).toLowerCase();
    const token1 = (await pair.token1()).toLowerCase();
    const wrappedNativeLower = currentConfig.wrappedNativeToken.toLowerCase();
    const w0 = token0 === wrappedNativeLower;
    const w1 = token1 === wrappedNativeLower;
    if (!w0 && !w1) return;

    const onSync = async () => {
      try {
        const [r0, r1] = await pair.getReserves();
        const nativeDecimals = chainConfig.nativeCurrency.decimals;
        const wrappedReserve = w0 ? Number(r0) / Math.pow(10, nativeDecimals) : Number(r1) / Math.pow(10, nativeDecimals);
        if (wrappedReserve >= minWcroLiquidity) {
          pair.removeAllListeners("Sync");
          pair.removeAllListeners("Swap");
          watchedPairsRef.current[tokenLower] = { pair, listenerBound: false };

          executeBlockchainTrade("BUY", String(autoBuyWcroAmount), tokenAddress, chainConfig.nativeCurrency.symbol);
          setTimeout(() => {}, Math.max(0, cooldownMs || 0));

          setTOKEN_ADDRESS(tokenAddress);
          setTokenPairAddress(pairAddr);
          setIsListening(true);
          setSniperStatus("bought initial liquidity");
        }
      } catch (e) {
        console.error(`${modeLabel}: watch liquidity error`, e?.message || e);
      }
    };

    await onSync();
    pair.on("Sync", onSync);
    pair.on("Swap", onSync);
  };

  const startAmbushSniper = async (opts = {}) => {
    const {
      tokenAddress = ambushToken,
      tickerFilter = ambushTickerFilter || null,
      minWcroLiquidity = ambushMinWcro,
      autoBuyWcroAmount = ambushAutoBuyWcro,
      cooldownMs = ambushCooldownMs,
    } = opts;

    stopSnipers();
    ambushActiveRef.current = true;
    setSniperStatus("searching for initial liquidity");

    if (tokenAddress && ethers.utils.isAddress(tokenAddress)) {
      await watchInitialLiquidity({
        tokenAddress,
        minWcroLiquidity,
        autoBuyWcroAmount,
        cooldownMs,
        modeLabel: "Ambush",
      });
      return;
    }

    if (!currentConfig) return;
    const factory = getFactory();
    factory.on("PairCreated", async (t0, t1) => {
      if (!ambushActiveRef.current) return;

      const wrappedNative = currentConfig.wrappedNativeToken.toLowerCase();
      const t0l = t0.toLowerCase();
      const t1l = t1.toLowerCase();

      let otherToken = null;
      if (t0l === wrappedNative) otherToken = t1;
      else if (t1l === wrappedNative) otherToken = t0;
      else return;

      if (tickerFilter && tickerFilter.trim()) {
        const { symbol, name } = await readTokenMeta(otherToken);
        const needle = tickerFilter.trim().toLowerCase();
        const pass =
          symbol.toLowerCase().includes(needle) || name.toLowerCase().includes(needle);
        if (!pass) return;
      }

      await watchInitialLiquidity({
        tokenAddress: otherToken,
        minWcroLiquidity,
        autoBuyWcroAmount,
        cooldownMs,
        modeLabel: "Ambush",
      });
    });
  };

  const stopAmbushSniper = () => stopSnipers();

  const startRecklessSniper = async (opts = {}) => {
    const {
      tickerFilter = recklessTickerFilter || null,
      minWcroLiquidity = recklessMinWcro,
      autoBuyWcroAmount = recklessAutoBuyWcro,
      cooldownMs = recklessCooldownMs,
    } = opts;

    stopSnipers();
    recklessActiveRef.current = true;
    setSniperStatus("searching for pairs");

    if (!currentConfig) return;
    const factory = getFactory();
    factory.on("PairCreated", async (t0, t1) => {
      if (!recklessActiveRef.current) return;

      const wrappedNative = currentConfig.wrappedNativeToken.toLowerCase();
      const t0l = t0.toLowerCase();
      const t1l = t1.toLowerCase();

      let otherToken = null;
      if (t0l === wrappedNative) otherToken = t1;
      else if (t1l === wrappedNative) otherToken = t0;
      else return;

      if (tickerFilter && tickerFilter.trim()) {
        const { symbol, name } = await readTokenMeta(otherToken);
        const needle = tickerFilter.trim().toLowerCase();
        const pass =
          symbol.toLowerCase().includes(needle) || name.toLowerCase().includes(needle);
        if (!pass) return;
      }

      await watchInitialLiquidity({
        tokenAddress: otherToken,
        minWcroLiquidity,
        autoBuyWcroAmount,
        cooldownMs,
        modeLabel: "Reckless",
      });
    });
  };

  const stopRecklessSniper = () => stopSnipers();

  const runManualTrade = async () => {
    if (!ethers.utils.isAddress(manualTokenAddr)) { alert("❌ Enter a valid ERC-20 address"); return; }
    if (!(manualTradeAmount > 0)) { alert("❌ Enter amount > 0"); return; }

    const market = markets.find(m => (m.token || "").toLowerCase() === manualTokenAddr.toLowerCase());
    const initiator = manualInitiator;
    let amount = Number(manualTradeAmount);

    if (manualTradeSide === "BUY") {
      if (!currentConfig) {
        alert("❌ Configuration not ready. Please wait for chain/DEX to initialize.");
        return;
      }
      const nativeSymbol = chainConfig.nativeCurrency.symbol;
      const stableSymbol = chainConfig.defaultStablecoin;
      const wrappedSymbol = chainConfig.nativeCurrency.symbol;
      // For stablecoin initiator, use _BASE key (stablecoin balance), not wrapped holdings
      let baseBalance;
      if (initiator === stableSymbol || initiator === "USDC" || initiator === "USDC.e") {
        const baseKey = `${wrappedSymbol}-${stableSymbol}_BASE`;
        baseBalance = positions[baseKey]?.holdings || 0;
      } else {
        baseBalance = positions[nativeSymbol]?.holdings || 0;
      }
      if (baseBalance <= 0) { alert("❌ No base balance available for this initiator."); return; }

      if (amount > baseBalance) {
        alert(`⚠️ Amount capped to available base balance: ${baseBalance}`);
        amount = baseBalance;
      }
    } else {
      const posKey = market ? market.key : null;
      const holdings = posKey ? (positions[posKey]?.holdings || 0) : 0;

      if (!posKey) {
        alert("❌ Token not found in tracked markets — cannot determine holdings to sell. Add it as custom bot or use force-sell in bot card.");
        return;
      }

      if (holdings <= 0) { alert("❌ No holdings to sell."); return; }

      if (amount > holdings) {
        alert(`⚠️ Sell amount capped to holdings: ${holdings}`);
        amount = holdings;
      }
    }

    try {
      const succeeded = await executeBlockchainTrade(
        manualTradeSide,
        amount.toString(),
        manualTokenAddr,
        manualInitiator,
        { marketKey: market?.key }
      );
      if (succeeded) {
        showTradeNotice(`Manual ${manualTradeSide} confirmed.`, {
          status: "success",
          title: "Trade executed",
          marketKey: market?.key,
          side: manualTradeSide,
        });
      }
    } catch (err) {
      showTradeNotice(err?.message || err, {
        marketKey: market?.key,
        side: manualTradeSide,
      });
    }
  };

  const getCroUsd = () => {
    const refVal = usdPerWcroRef.current;
    if (refVal && isFinite(refVal) && refVal > 0) return refVal;

    const posVal = positions?.CRO?.price;
    if (posVal && isFinite(posVal) && posVal > 0) return posVal;

    const m = markets.find(m => m.key === "WCRO-USDC");
    const mVal = m?.price;
    if (mVal && isFinite(mVal) && mVal > 0) return mVal;

    return 0;
  };

  const maybeResetRegime = (key, price) => {
    const pos = getPositionForDecisions(key);
    if (!pos || !pos.avgEntry || !price || price <= 0) return;

    const drift = Math.abs(price - pos.avgEntry) / pos.avgEntry;
    if (drift <= MAX_DRIFT_FROM_ANCHOR) return;

    const hasHoldings = (pos.holdings || 0) > DUST;
    if (!hasHoldings) {
      console.log("[GRID RESET SKIPPED]", {
        marketKey: key,
        reason: "REGIME_RESET",
        skipReason: "holdings depleted",
        currentPrice: price,
        avgEntry: pos.avgEntry,
      });
      return;
    }
    const cfg = getEffectiveBotCfg(key);
    const book = getMarketBook(key, pos, price, positionsRef.current, chainConfig);
    const useSpot =
      shouldUseSpotGridMode({
        cfg,
        pos,
        price,
        baseBalanceUsd: book.baseBalance,
        totalBookUsd: book.totalBookUsd,
      }) || !hasHoldings;
    const buyPct = cfg.buyTarget ?? defaultBotCfg.buyTarget;
    const sellPct = cfg.sellTarget ?? defaultBotCfg.sellTarget;
    const t = computeGridTargets({
      price,
      avgEntry: pos.avgEntry,
      buyPct,
      sellPct,
      useSpotAnchor: useSpot,
    });

    t.priceTargetSell = Math.max(
      t.priceTargetSell,
      getNormalSellFloorPrice({
        avgEntry: pos.avgEntry,
        minProfitBps: cfg.normalSellMinProfitBps,
      })
    );

    if (shouldSkipGridReset({
      key,
      reason: "REGIME_RESET",
      price,
      proposedBuy: t.priceTargetBuy,
      proposedSell: t.priceTargetSell,
    })) {
      return;
    }

    console.log(
      `[RegimeReset] ${key} drift=${(drift * 100).toFixed(2)}% → ${useSpot ? "spot" : "entry"} targets (preserving avgEntry ${hasHoldings ? safeFix(pos.avgEntry) : "0"})`
    );

    updatePosition(key, {
      avgEntry: hasHoldings ? pos.avgEntry : 0,
      priceTargetBuy: t.priceTargetBuy,
      priceTargetSell: t.priceTargetSell,
      // Preserve consecutive BUY/SELL counters during grid resets if holdings exist.
      // These resets are target/anchor adjustments, not a completed trade cycle.
      buyCounter: hasHoldings ? (pos.buyCounter || 1) : 1,
      sellCounter: hasHoldings ? (pos.sellCounter || 1) : 1,
    });
    recordGridReset({
      key,
      reason: "REGIME_RESET",
      price,
      newBuy: t.priceTargetBuy,
      newSell: t.priceTargetSell,
      regime: marketRegimeRef.current[key]?.regime,
    });
    emitBotResetDiscord(key, "REGIME_RESET", {
      buyTarget: t.priceTargetBuy,
      sellTarget: t.priceTargetSell,
      details: useSpot ? "12.5%+ drift → spot targets" : "12.5%+ drift → entry targets",
    });
  };

  // Time-based re-anchoring: Reset anchor if no trades for 4-6 hours
  const maybeTimeBasedReanchor = (key, price) => {
    const pos = getPositionForDecisions(key);
    if (!pos || !price || price <= 0) return;
    
    const lastTradeTs = lastTradeAtRef.current[key] || 0;
    const lastReanchorTs = lastReanchorAtRef.current[key] || 0;
    
    // CRITICAL FIX: If lastTradeTs is 0 (uninitialized), don't trigger re-anchoring
    // This prevents false triggers on page load when refs haven't been initialized yet
    if (lastTradeTs === 0) return;
    
    const timeSinceLastTrade = Date.now() - lastTradeTs;
    const timeSinceLastReanchor = Date.now() - lastReanchorTs;
    
    // Only re-anchor if:
    // 1. No trades for TIME_BASED_REANCHOR_MS (5 hours)
    // 2. Haven't re-anchored in the last hour (prevent spam)
    // 3. Have an active position (avgEntry > 0)
    if (timeSinceLastTrade >= TIME_BASED_REANCHOR_MS && 
        timeSinceLastReanchor >= 60 * 60 * 1000 && 
        (pos.avgEntry || 0) > 0) {
      
      // CRITICAL FIX: Only reset avgEntry if holdings are zero
      // If holdings exist, avgEntry represents cost basis and must be preserved
      // Only reset targets to adapt to new price regime
      const hasHoldings = (pos.holdings || 0) > DUST;
      if (!hasHoldings) {
        console.log("[GRID RESET SKIPPED]", {
          marketKey: key,
          reason: "TIME_REANCHOR",
          skipReason: "holdings depleted",
          currentPrice: price,
          avgEntry: pos.avgEntry,
        });
        lastReanchorAtRef.current[key] = Date.now();
        return;
      }
      
      const cfg = getEffectiveBotCfg(key);
      const inverted = key === "WCRO-USDC";
      const anchor = hasHoldings ? pos.avgEntry : price;
      const buyPct  = cfg.buyTarget  ?? defaultBotCfg.buyTarget;
      const sellPct = cfg.sellTarget ?? defaultBotCfg.sellTarget;
      
      const hoursSinceLastTrade = (timeSinceLastTrade / (60 * 60 * 1000)).toFixed(1);
      console.log(
        `[TimeBasedReanchor] ${key} - No trades for ${hoursSinceLastTrade}h → resetting targets around ${hasHoldings ? `cost basis ${safeFix(anchor)} (preserving avgEntry)` : `current price ${safeFix(price)}`}`
      );
      
      const normalSellFloor = getNormalSellFloorPrice({
        avgEntry: anchor,
        minProfitBps: cfg.normalSellMinProfitBps,
      });
      const safeTargets = inverted
        ? {
            priceTargetBuy: Math.min(price * sellPct, anchor * sellPct),
            priceTargetSell: Math.max(price * buyPct, anchor * buyPct),
          }
        : getSafeTimeReanchorTargets({
            currentPrice: price,
            avgEntry: anchor,
            buyMultiplier: buyPct,
            sellMultiplier: sellPct,
            minimumSellPrice: normalSellFloor,
          });
      const buyT = safeTargets.priceTargetBuy;
      const sellT = safeTargets.priceTargetSell;
      if (shouldSkipGridReset({
        key,
        reason: "TIME_REANCHOR",
        price,
        proposedBuy: buyT,
        proposedSell: sellT,
      })) {
        lastReanchorAtRef.current[key] = Date.now();
        return;
      }
      updatePosition(key, {
        avgEntry: hasHoldings ? pos.avgEntry : 0,
        priceTargetBuy: buyT,
        priceTargetSell: sellT,
        // Preserve consecutive BUY/SELL counters during grid resets if holdings exist.
        buyCounter: hasHoldings ? (pos.buyCounter || 1) : 1,
        sellCounter: hasHoldings ? (pos.sellCounter || 1) : 1,
      });
      recordGridReset({
        key,
        reason: "TIME_REANCHOR",
        price,
        newBuy: buyT,
        newSell: sellT,
        regime: marketRegimeRef.current[key]?.regime,
      });
      emitBotResetDiscord(key, "TIME_REANCHOR", {
        buyTarget: buyT,
        sellTarget: sellT,
        details: `${hoursSinceLastTrade}h idle → grid reset around ${hasHoldings ? "cost basis" : "spot"}`,
      });
      lastReanchorAtRef.current[key] = Date.now();
    }
  };

  const getMarketSellExecutionParams = (key, market) => {
    if (!currentConfig || !chainConfig || !market) return null;
    const wrappedSymbol = chainConfig.nativeCurrency.symbol;
    const stableSymbol = chainConfig.defaultStablecoin;
    const nativeSymbol = chainConfig.nativeCurrency.symbol;

    if (key === `${wrappedSymbol}-${stableSymbol}`) {
      return {
        actualInitiator: wrappedSymbol,
        tokenToUse: currentConfig.wrappedNativeToken,
      };
    }
    if (key === `WBTC-${wrappedSymbol}`) {
      return { actualInitiator: nativeSymbol, tokenToUse: market.token };
    }
    if (key === `WBTC-${stableSymbol}`) {
      return { actualInitiator: stableSymbol, tokenToUse: market.token };
    }
    return { actualInitiator: market.initiator, tokenToUse: market.token };
  };

  const maybeStuckPositionStop = async (key, price) => {
    const cfg = getEffectiveBotCfg(key);
    if (!cfg.stuckStopEnabled) return false;

    const pos = getPositionForDecisions(key);
    if (!pos || !(pos.holdings > DUST) || !(pos.avgEntry > 0) || !(price > 0)) return false;

    const now = Date.now();
    const lastTradeTs = lastTradeAtRef.current[key] || pos.lastTradeAt || pos.lastUpdated || 0;
    if (!lastTradeTs) return false;

    const hoursIdle = (now - lastTradeTs) / (60 * 60 * 1000);
    const drawdown = Math.max(0, 1 - price / pos.avgEntry);
    const unrealizedLossUsd = Math.max(0, (pos.avgEntry - price) * pos.holdings);
    const positionCostUsd = Math.max(0, pos.avgEntry * pos.holdings);

    // Align stuck-stop loss gates with the active grid and observed market noise.
    // Convert optional USD floors to position percentages so they cannot bypass the percentage gate.
    const metrics = getMarketTimeframeMetrics(key);
    const buyMovePct = Math.max(0, 1 - Number(cfg.buyTarget ?? defaultBotCfg.buyTarget));
    const sellMovePct = Math.max(0, Number(cfg.sellTarget ?? defaultBotCfg.sellTarget) - 1);
    const gridMovePct = Math.max(buyMovePct, sellMovePct);
    const historicalNoisePct = Math.max(
      Number(metrics.range1h) || 0,
      (Number(metrics.range4h) || 0) * 0.5,
      (Number(metrics.range24h) || 0) / 3
    );
    const configuredPct = (value, fallback) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
    };
    const hasLegacyUsdDefaults =
      Number(cfg.softStuckLossUsd) === 6 &&
      Number(cfg.hardStuckLossUsd) === 8 &&
      Number(cfg.emergencyStuckLossUsd) === 12;
    const usdFloorAsPct = (value) => {
      const parsed = Number(value);
      return !hasLegacyUsdDefaults && positionCostUsd > 0 && Number.isFinite(parsed) && parsed > 0
        ? parsed / positionCostUsd
        : 0;
    };

    const softLossPct = Math.max(
      configuredPct(cfg.softStuckLossPct, defaultBotCfg.softStuckLossPct),
      gridMovePct * 1.5,
      Math.min(historicalNoisePct * 0.75, 0.04),
      usdFloorAsPct(cfg.softStuckLossUsd ?? defaultBotCfg.softStuckLossUsd)
    );
    const hardLossPct = Math.max(
      configuredPct(cfg.hardStuckLossPct, defaultBotCfg.hardStuckLossPct),
      softLossPct * 1.25,
      gridMovePct * 2.5,
      Math.min(historicalNoisePct, 0.06),
      usdFloorAsPct(cfg.hardStuckLossUsd ?? defaultBotCfg.hardStuckLossUsd)
    );
    const emergencyLossPct = Math.max(
      configuredPct(cfg.emergencyStuckLossPct, defaultBotCfg.emergencyStuckLossPct),
      hardLossPct * 1.25,
      gridMovePct * 4,
      Math.min(historicalNoisePct * 1.5, 0.08),
      usdFloorAsPct(cfg.emergencyStuckLossUsd ?? defaultBotCfg.emergencyStuckLossUsd)
    );

    const lastStopTs = lastStuckStopAtRef.current[key] || 0;
    if (now - lastStopTs < 60 * 60 * 1000) return false; // one stuck-stop max per hour

    const tierMatches = (hours, lossPct) =>
      hoursIdle >= Number(hours) && drawdown >= lossPct;

    let tier = null;
    if (tierMatches(
        cfg.emergencyStuckHours || defaultBotCfg.emergencyStuckHours,
        emergencyLossPct
      )) {
      tier = {
        name: "EMERGENCY",
        sellPct: Number(cfg.emergencyStuckSellPct || defaultBotCfg.emergencyStuckSellPct),
      };
    } else if (tierMatches(
        cfg.hardStuckHours || defaultBotCfg.hardStuckHours,
        hardLossPct
      )) {
      tier = {
        name: "HARD",
        sellPct: Number(cfg.hardStuckSellPct || defaultBotCfg.hardStuckSellPct),
      };
    } else if (tierMatches(
        cfg.softStuckHours || defaultBotCfg.softStuckHours,
        softLossPct
      )) {
      tier = {
        name: "SOFT",
        sellPct: Number(cfg.softStuckSellPct || defaultBotCfg.softStuckSellPct),
      };
    }

    if (!tier) return false;

    const sellPct = Math.max(0, Math.min(1, tier.sellPct));
    const amountToSell = Math.min(pos.holdings, pos.holdings * sellPct);
    if (!(amountToSell > 0)) return false;

    const market = markets.find((m) => m.key === key);
    const exec = getMarketSellExecutionParams(key, market);
    if (!market || !exec) return false;

    console.warn("[StuckStop thresholds]", {
      marketKey: key,
      gridMovePct,
      historicalNoisePct,
      softLossPct,
      hardLossPct,
      emergencyLossPct,
      ignoredLegacyUsdDefaults: hasLegacyUsdDefaults,
    });
    console.warn(
      `[StuckStop:${tier.name}] ${key} idle=${hoursIdle.toFixed(1)}h drawdown=${(drawdown * 100).toFixed(2)}% loss=$${unrealizedLossUsd.toFixed(2)} → selling ${(sellPct * 100).toFixed(0)}% (${amountToSell})`
    );

    const snapshot = {
      holdings: pos.holdings,
      avgEntry: pos.avgEntry,
      priceTargetBuy: pos.priceTargetBuy,
      priceTargetSell: pos.priceTargetSell,
      buyCounter: pos.buyCounter,
      sellCounter: pos.sellCounter,
      lastTradeAt: pos.lastTradeAt,
      lastBuyAt: pos.lastBuyAt,
      lastSellAt: pos.lastSellAt,
      flatSince: pos.flatSince,
      fullExitPrice: pos.fullExitPrice,
      fullExitTxHash: pos.fullExitTxHash,
      flatReentryNearMissSince: pos.flatReentryNearMissSince,
      lastFlatReentryAt: pos.lastFlatReentryAt,
    };
    const pendingId = startPending(key, snapshot);

    const cfgNow = getEffectiveBotCfg(key);
    const buyPct = cfgNow.buyTarget ?? defaultBotCfg.buyTarget;
    const sellPctTarget = cfgNow.sellTarget ?? defaultBotCfg.sellTarget;
    const newHoldings = Math.max(0, (pos.holdings || 0) - amountToSell);
    let remainingHoldingsUsd = newHoldings * price;
    const wrappedSymbol = chainConfig?.nativeCurrency?.symbol;
    const stableSymbol = chainConfig?.defaultStablecoin;
    if (key !== `${wrappedSymbol}-${stableSymbol}` && key !== `WBTC-${stableSymbol}`) {
      const ethPrice = positions[wrappedSymbol]?.price || 0;
      if (ethPrice > 0) remainingHoldingsUsd = newHoldings * price * ethPrice;
    }
    const stuckBook = getMarketBook(key, pos, price, positionsRef.current, chainConfig);
    const stuckSpot =
      cfgNow.reanchorStuckOnPartial !== false ||
      shouldUseSpotGridMode({
        cfg: cfgNow,
        pos,
        price,
        baseBalanceUsd: stuckBook.baseBalance,
        totalBookUsd: stuckBook.totalBookUsd,
      });
    const postStuck = resolvePostSellTargetUpdate({
      price,
      newHoldings,
      remainingHoldingsUsd,
      pos,
      cfg: { ...cfgNow, reanchorOnSellWhenUnderwater: true },
      buyPct,
      sellPct: sellPctTarget,
      useSpotGrid: stuckSpot,
    });
    const stuckFullExit =
      newHoldings <= DUST || remainingHoldingsUsd < ZERO_HOLDINGS_THRESHOLD_USD;

    updatePosition(key, {
      holdings: newHoldings,
      avgEntry: postStuck.newAvgEntry,
      priceTargetBuy: postStuck.priceTargetBuy,
      priceTargetSell: postStuck.priceTargetSell,
      buyCounter: postStuck.buyCounter,
      sellCounter: postStuck.sellCounter,
      lastTradeAt: now,
      lastSellAt: now,
      lastStuckStopAt: now,
      flatSince: stuckFullExit ? now : (pos.flatSince || 0),
      fullExitPrice: stuckFullExit ? price : (pos.fullExitPrice || 0),
      fullExitTxHash: stuckFullExit ? "" : (pos.fullExitTxHash || ""),
      flatReentryNearMissSince: stuckFullExit ? 0 : (pos.flatReentryNearMissSince || 0),
    });

    lastStuckStopAtRef.current[key] = now;
    lastSellAtRef.current[key] = now;
    lastTradeAtRef.current[key] = now;

    emitBotResetDiscord(key, `STUCK_STOP_${tier.name}`, {
      buyTarget: postStuck.priceTargetBuy,
      sellTarget: postStuck.priceTargetSell,
      details: `Stuck stop ${tier.name}: partial sell + grid re-anchor (avgEntry preserved)`,
      cooldownMs: 60 * 60 * 1000,
    });

    try {
      await executeBlockchainTrade("SELL", amountToSell.toString(), exec.tokenToUse, exec.actualInitiator, {
        marketKey: key,
        pendingId,
        fromStuckStop: true,
        stuckStopTier: tier.name,
        stuckStopSnapshot: {
          tier: tier.name,
          triggeredAt: now,
          triggerPrice: price,
          avgEntry: pos.avgEntry,
          preStopHoldings: pos.holdings,
          drawdownPct: drawdown,
          hoursIdle,
          sellPct,
        },
        fullExitTransition: stuckFullExit,
        fullExitAt: stuckFullExit ? now : 0,
        fullExitPrice: stuckFullExit ? price : 0,
      });
      return true;
    } catch (error) {
      console.error(`[StuckStop:${tier.name}] ${key} failed:`, error);
      if (pendingId) rollbackPending(key, pendingId);
      return true;
    }
  };

  const maybeStopLossRebalance = async (key, price) => {
    const cfg = getEffectiveBotCfg(key);
    if (!cfg.stopLossEnabled) return false;

    const pos = getPositionForDecisions(key);
    if (!pos || !(pos.holdings > DUST) || !(pos.avgEntry > 0) || !(price > 0)) return false;

    if (!chainConfig) return false;
    const wrappedSymbol = chainConfig.nativeCurrency.symbol;
    const stableSymbol = chainConfig.defaultStablecoin;
    const nativeMarketKey = `${wrappedSymbol}-${stableSymbol}`;
    if (key !== nativeMarketKey && key !== `WBTC-${stableSymbol}`) return false;

    const drawdown = getDrawdownPct(pos.avgEntry, price);
    const threshold = Number(cfg.stopLossDrawdownPct ?? defaultBotCfg.stopLossDrawdownPct);
    if (drawdown < threshold) return false;

    const book = getMarketBook(key, pos, price, positionsRef.current, chainConfig);
    const targetWethPct = Number(cfg.stopLossTargetWethPct ?? defaultBotCfg.stopLossTargetWethPct);
    const amountToSell = computeRebalanceSellQty({
      holdings: pos.holdings,
      price,
      baseBalanceUsd: book.baseBalance,
      targetAssetPct: targetWethPct,
      tolerancePct: 0.02,
    });
    const minTradeUsd = Number(cfg.minTradeUsd ?? defaultBotCfg.minTradeUsd);
    if (!(amountToSell > 0) || amountToSell * price < minTradeUsd * 0.5) return false;

    const now = Date.now();
    const cooldownMs =
      (Number(cfg.stopLossCooldownHours ?? defaultBotCfg.stopLossCooldownHours) || 4) *
      60 *
      60 *
      1000;
    const lastTs = lastStopLossAtRef.current[key] || 0;
    if (now - lastTs < cooldownMs) return false;

    const market = markets.find((m) => m.key === key);
    const exec = getMarketSellExecutionParams(key, market);
    if (!market || !exec) return false;

    const buyPct = cfg.buyTarget ?? defaultBotCfg.buyTarget;
    const sellPct = cfg.sellTarget ?? defaultBotCfg.sellTarget;
    const newHoldings = Math.max(0, (pos.holdings || 0) - amountToSell);
    const postSl = resolveStopLossTargetUpdate({
      price,
      newHoldings,
      pos,
      buyPct,
      sellPct,
    });

    const estUsdcAfter = book.baseBalance + amountToSell * price;
    const estTotalAfter = newHoldings * price + estUsdcAfter;
    const wethPctAfter =
      estTotalAfter > 0 ? ((newHoldings * price) / estTotalAfter) * 100 : 0;

    console.warn(
      `[StopLoss] ${key} drawdown=${(drawdown * 100).toFixed(2)}% (>=${(threshold * 100).toFixed(1)}%) → sell ${amountToSell.toFixed(6)} (~$${(amountToSell * price).toFixed(0)}) toward ${(targetWethPct * 100).toFixed(0)}% WETH (≈${wethPctAfter.toFixed(0)}% after). Grid→spot; PnL/avgEntry preserved.`
    );

    const snapshot = {
      holdings: pos.holdings,
      avgEntry: pos.avgEntry,
      priceTargetBuy: pos.priceTargetBuy,
      priceTargetSell: pos.priceTargetSell,
      buyCounter: pos.buyCounter,
      sellCounter: pos.sellCounter,
      lastTradeAt: pos.lastTradeAt,
    };
    const pendingId = startPending(key, snapshot);

    updatePosition(key, {
      holdings: newHoldings,
      avgEntry: postSl.newAvgEntry,
      priceTargetBuy: postSl.priceTargetBuy,
      priceTargetSell: postSl.priceTargetSell,
      buyCounter: postSl.buyCounter,
      sellCounter: postSl.sellCounter,
      lastTradeAt: now,
      lastSellAt: now,
      lastStopLossAt: now,
    });

    lastStopLossAtRef.current[key] = now;
    lastSellAtRef.current[key] = now;
    lastTradeAtRef.current[key] = now;

    emitBotResetDiscord(key, "STOP_LOSS_REBALANCE", {
      buyTarget: postSl.priceTargetBuy,
      sellTarget: postSl.priceTargetSell,
      targetWethPct,
      details: `Stop-loss: selling toward ${(targetWethPct * 100).toFixed(0)}% WETH / ${(100 - targetWethPct * 100).toFixed(0)}% USDC. avgEntry preserved.`,
      cooldownMs: 60 * 60 * 1000,
    });

    try {
      await executeBlockchainTrade("SELL", amountToSell.toString(), exec.tokenToUse, exec.actualInitiator, {
        marketKey: key,
        pendingId,
        fromStopLoss: true,
      });
      return true;
    } catch (error) {
      console.error(`[StopLoss] ${key} failed:`, error);
      if (pendingId) rollbackPending(key, pendingId);
      return true;
    }
  };

  const maybeDeRisk = (key, price) => {
    const pos = getPositionForDecisions(key);
    if (!pos || !pos.avgEntry || !pos.holdings || !price || price <= 0) return;

    const drawdown = 1 - price / pos.avgEntry; // e.g. 0.5 when down 50%
    if (drawdown <= MAX_UNREALIZED_LOSS) return;

    const lastTs = lastDeRiskAtRef.current[key] || 0;
    // only de-risk once per hour per market
    if (Date.now() - lastTs < 60 * 60 * 1000) return;

    const fractionToSell = 0.25; // sell 25% of bag to free capital
    const amountToSell = pos.holdings * fractionToSell;
    if (!(amountToSell > 0)) return;

    const market = markets.find(m => m.key === key);
    if (!market) return;

    console.log(
      `[DeRisk] ${key} drawdown=${(drawdown * 100).toFixed(2)}% → selling ${(
        fractionToSell * 100
      ).toFixed(0)}% of holdings`
    );

    lastDeRiskAtRef.current[key] = Date.now();
    lastSellAtRef.current[key] = Date.now();

    const snapshot = {
      holdings: pos.holdings,
      avgEntry: pos.avgEntry,
      priceTargetBuy: pos.priceTargetBuy,
      priceTargetSell: pos.priceTargetSell,
      buyCounter: pos.buyCounter,
      sellCounter: pos.sellCounter,
    };
    const pendingId = startPending(key, snapshot);

    executeBlockchainTrade(
      "SELL",
      amountToSell.toString(),
      market.token,
      market.initiator,
      { marketKey: key, pendingId, fromDeRisk: true }
    );
  };

  // ---------- minimal UI for Part 1 ----------
  const allChains = getAllChains();
  const allDexes = getAllDexes();

  // Top balance summary: native gas token + wrapped-native trading inventory + stablecoin base.
  // For Arbitrum ETH-USDC this shows native ETH, WETH, USDC, and total trading value = WETH*price + USDC.
  const topBalanceSummary = useMemo(() => {
    if (!chainConfig) {
      return {
        nativeSymbol: "Native",
        wrappedDisplaySymbol: "Wrapped",
        stableSymbol: "USDC",
        nativeBalance: 0,
        wrappedBalance: 0,
        stableBalance: 0,
        wrappedPriceUsd: 0,
        wrappedValueUsd: 0,
        tradingTotalUsd: 0,
      };
    }

    const nativeSymbol = chainConfig.nativeCurrency?.symbol || "Native";
    const stableSymbol = chainConfig.defaultStablecoin || "USDC";
    const wrappedMarketKey = `${nativeSymbol}-${stableSymbol}`;
    const stableBaseKey = `${wrappedMarketKey}_BASE`;
    const wrappedDisplaySymbol =
      nativeSymbol === "ETH" ? "WETH" : nativeSymbol === "CRO" ? "WCRO" : `W${nativeSymbol}`;

    const nativeBalance = Number(positions[nativeSymbol]?.holdings || 0);
    const wrappedBalance = Number(positions[wrappedMarketKey]?.holdings || 0);
    const stableBalance = Number(positions[stableBaseKey]?.holdings || 0);
    const wrappedPriceUsd = Number(
      markets.find((m) => m.key === wrappedMarketKey)?.price ||
      positions[wrappedMarketKey]?.avgEntry ||
      0
    );

    const wrappedValueUsd = wrappedBalance * wrappedPriceUsd;
    const tradingTotalUsd = stableBalance + wrappedValueUsd;

    return {
      nativeSymbol,
      wrappedDisplaySymbol,
      stableSymbol,
      nativeBalance,
      wrappedBalance,
      stableBalance,
      wrappedPriceUsd,
      wrappedValueUsd,
      tradingTotalUsd,
    };
  }, [chainConfig, markets, positions]);

  const marketEnableCounts = useMemo(() => {
    let activeCount = 0;
    let inactiveCount = 0;
    markets.forEach(({ key }) => {
      if (positions[key]?.enabled !== false) activeCount += 1;
      else inactiveCount += 1;
    });
    return { activeCount, inactiveCount };
  }, [markets, positions]);

  const defaultQuickMarketKey = useMemo(() => {
    if (!chainConfig) return null;
    const wrappedSymbol = chainConfig.nativeCurrency.symbol;
    const stableSymbol = chainConfig.defaultStablecoin;
    const primary = `${wrappedSymbol}-${stableSymbol}`;
    const enabled = markets.filter((m) => positions[m.key]?.enabled !== false);
    if (enabled.some((m) => m.key === primary)) return primary;
    return enabled[0]?.key || markets[0]?.key || primary;
  }, [chainConfig, markets, positions]);

  const [quickControlMarketKey, setQuickControlMarketKey] = useState(null);

  useEffect(() => {
    if (!defaultQuickMarketKey) return;
    setQuickControlMarketKey((prev) => {
      if (prev && markets.some((m) => m.key === prev)) return prev;
      return defaultQuickMarketKey;
    });
  }, [defaultQuickMarketKey, markets]);

  const quickMarketOptions = useMemo(() => {
    return markets.map(({ key, price }) => {
      const pos = positions[key] || {};
      const on = pos.enabled !== false;
      const wrappedSymbol = chainConfig?.nativeCurrency?.symbol || "";
      const stableSymbol = chainConfig?.defaultStablecoin || "USDC";
      let label = key;
      if (key === `${wrappedSymbol}-${stableSymbol}`) {
        label =
          wrappedSymbol === "ETH"
            ? "WETH-USDC"
            : wrappedSymbol === "CRO"
            ? "WCRO-USDC"
            : `${wrappedSymbol}-${stableSymbol}`;
      }
      return {
        key,
        label: `${label}${on ? "" : " (off)"} · $${safeFix(price)}`,
      };
    });
  }, [markets, positions, chainConfig]);

  useEffect(() => {
    console.log(
      `[SniperBot] v${SNIPER_BOT_VERSION} (${SNIPER_BOT_VERSION_DATE}) — ${SNIPER_BOT_VERSION_NOTE}`
    );
  }, []);

  return (
    <DashboardRoot>
      <GlobalStyles />

      <VersionBar
        version={SNIPER_BOT_VERSION}
        date={SNIPER_BOT_VERSION_DATE}
        note={SNIPER_BOT_VERSION_NOTE}
      />

      <Alert
        status={walletProfileError && isSecondaryBot ? "error" : isSecondaryBot && !secondarySessionAuthorized ? "warning" : "info"}
        mb={3}
        borderRadius="lg"
        alignItems="flex-start"
      >
        <AlertIcon mt="2px" />
        <Box flex="1" minW={0}>
          <Text fontWeight="800" fontSize="sm">
            {botInstanceId.toUpperCase()} bot
            {isSecondaryBot ? ` · buy entry ${SECONDARY_BUY_ENTRY_OFFSET_BPS} bps deeper` : " · production profile"}
          </Text>
          <Text fontSize="xs" mt={1} overflowWrap="anywhere">
            {walletProfileLoading
              ? "Reading execution wallet..."
              : executionAddress
                ? `Execution wallet: ${executionAddress}`
                : walletProfileError || "Execution wallet unavailable"}
          </Text>
          {isSecondaryBot && !secondarySessionAuthorized && (
            <HStack mt={2} spacing={2} alignItems="center" flexWrap="wrap">
              <Button
                size="sm"
                colorScheme="orange"
                onClick={authorizeSecondaryBot}
                isLoading={secondaryAuthLoading}
                isDisabled={!account || walletProfileLoading || !walletProfile?.configured}
              >
                Authorize secondary bot
              </Button>
              <Text fontSize="xs">One wallet signature is required before secondary trades.</Text>
            </HStack>
          )}
          {isSecondaryBot && secondaryAuthError && (
            <Text color="red.300" fontSize="xs" mt={1} overflowWrap="anywhere">{secondaryAuthError}</Text>
          )}
        </Box>
      </Alert>

      <HeroHeader
        chainName={chainConfig?.name}
        dexName={DEX_CONFIGS[selectedDex]?.name}
        isListening={isListening}
        killSwitch={killSwitch}
        showDebug={showDebugUI}
        onToggleDebug={() => setShowDebugUI((v) => !v)}
        onToggleKill={() => setKillSwitch((v) => !v)}
        onRefreshBalances={async () => {
          if (executionAddress) await getTokenBalance(executionAddress);
        }}
        account={executionAddress || account}
      />

      <CommandStrip
        account={executionAddress || account}
        isListening={isListening}
        killSwitch={killSwitch}
        quickMarketKey={quickControlMarketKey}
        marketOptions={quickMarketOptions}
        onQuickMarketChange={setQuickControlMarketKey}
        onRefreshPrices={fetchTokenPrice3}
        onQuickBuy={() => {
          if (!quickControlMarketKey) {
            alert("Select a market for quick buy");
            return;
          }
          forceBuyFor(quickControlMarketKey);
        }}
        onQuickSell={() => {
          if (!quickControlMarketKey) {
            alert("Select a market for quick sell");
            return;
          }
          forceSellFor(quickControlMarketKey);
        }}
        onClearStats={forceHold}
        onToggleListening={toggleListening}
        useBackendWallet={effectiveUseBackendWallet}
        onToggleBackendWallet={() => {
          if (!isSecondaryBot) setUseBackendWallet(!useBackendWallet);
        }}
        regimeEngineEnabled={regimeEngineEnabled}
        onToggleRegimeEngine={setRegimeEngineEnabled}
        buyProtectionEnabled={buyProtectionEnabled}
        onToggleBuyProtection={setBuyProtectionEnabled}
      />

      {tradeNotice && (
        <Alert status={tradeNotice.status} mb={3} borderRadius="lg" alignItems="flex-start">
          <AlertIcon mt="2px" />
          <Box flex="1" minW={0}>
            <Text fontWeight="700" fontSize="sm">
              {tradeNotice.title}
              {tradeNotice.side ? `: ${tradeNotice.side}` : ""}
              {tradeNotice.marketKey ? ` · ${tradeNotice.marketKey}` : ""}
            </Text>
            <Text fontSize="xs" mt={1} overflowWrap="anywhere">
              {tradeNotice.message}
            </Text>
          </Box>
          <Button size="xs" variant="ghost" ml={3} onClick={dismissTradeNotice}>
            Dismiss
          </Button>
        </Alert>
      )}

      <Panel
        title="Network & DEX"
        subtitle="Chain, router, and liquidity venue"
        icon={FiZap}
        accent="cyan"
      >
        {configError && (
          <Alert status="error" mb={3} borderRadius="lg">
            <AlertIcon />
            {configError}
          </Alert>
        )}

        <SimpleGrid columns={[1, 2]} spacing={4}>
          <Box>
            <Text fontSize="sm" mb={1} color="white">
              🌐 Blockchain Network
            </Text>
            <Select
              colorScheme="blue"
              variant="outline"
              value={selectedChain}
              onChange={(e) => {
                setSelectedChain(e.target.value);
                const newDexes = getAvailableDexes(e.target.value);
                if (newDexes.length > 0 && !newDexes.includes(selectedDex)) {
                  setSelectedDex(newDexes[0]);
                }
              }}
              bg="gray.800"
              borderColor="gray.600"
              color="white"
            >
              {allChains.map((chain) => (
                <option key={chain.key} value={chain.key}>
                  {chain.name} (Chain ID: {chain.id})
                </option>
              ))}
            </Select>
            {chainConfig && (
              <Text fontSize="xs" color="gray.400" mt={1}>
                Native: {chainConfig.nativeCurrency.symbol} · RPC:{" "}
                {currentConfig?.rpcUrl?.slice(0, 40) || "—"}...
              </Text>
            )}
          </Box>

          <Box>
            <Text fontSize="sm" mb={1} color="white">
              🔄 DEX Protocol
            </Text>
            <Select
              value={selectedDex}
              onChange={(e) => setSelectedDex(e.target.value)}
              bg="gray.800"
              borderColor="gray.600"
              color="white"
              isDisabled={availableDexes.length === 0}
            >
              {availableDexes.length === 0 ? (
                <option value="">No DEX available for this chain</option>
              ) : (
                availableDexes.map((dexId) => {
                  const dex = DEX_CONFIGS[dexId];
                  return (
                    <option key={dexId} value={dexId}>
                      {dex.name}
                    </option>
                  );
                })
              )}
            </Select>
            {currentConfig && (
              <Text fontSize="xs" color="gray.400" mt={1}>
                Router: {currentConfig.routerAddress.slice(0, 10)}... · Factory:{" "}
                {currentConfig.factoryAddress.slice(0, 10)}...
              </Text>
            )}
          </Box>
        </SimpleGrid>

        {currentConfig && (
          <InsightBox variant="success" title="Configuration active">
            <Text fontSize="sm">
              {chainConfig.name} + {DEX_CONFIGS[selectedDex]?.name} · Wrapped{" "}
              {currentConfig.wrappedNativeToken.slice(0, 10)}… · {currentConfig.stablecoinSymbol}{" "}
              {currentConfig.stablecoinAddress.slice(0, 10)}…
            </Text>
          </InsightBox>
        )}
      </Panel>

      <LiveTicker
        active={isListening}
        message={
          isListening && sniperStatus
            ? `Sniper active — ${sniperStatus}`
            : isListening
            ? "Price listeners running"
            : "Connect wallet & start listeners to trade"
        }
      />

      {chainConfig && currentConfig && (
        <BalanceStrip
          items={[
            {
              label: `Gas ${topBalanceSummary.nativeSymbol}`,
              value: safeFix(topBalanceSummary.nativeBalance, 4),
            },
            {
              label: topBalanceSummary.wrappedDisplaySymbol,
              value: safeFix(topBalanceSummary.wrappedBalance, 4),
              sub: `$${safeFix(topBalanceSummary.wrappedValueUsd, 2)}`,
              accent: "cyan.200",
            },
            {
              label: topBalanceSummary.stableSymbol,
              value: safeFix(topBalanceSummary.stableBalance, 2),
              accent: "green.200",
            },
            {
              label: "Trading book",
              value: `$${safeFix(topBalanceSummary.tradingTotalUsd, 2)}`,
              accent: "green.300",
            },
            {
              label: "WBTC",
              value: safeFix(positions[`WBTC-${chainConfig.defaultStablecoin}`]?.holdings ?? 0, 6),
            },
          ]}
        />
      )}

      {!isSecondaryBot && (
        <ConvergenceResearchPanel
          statusPayload={convergenceStatusPayload}
          reportPayload={convergenceReportPayload}
          loading={convergenceMonitorLoading}
          errors={convergenceMonitorErrors}
          horizonMs={convergenceReportHorizonMs}
          onHorizonChange={(nextHorizonMs) => {
            setConvergenceReportPayload(null);
            setConvergenceReportHorizonMs(nextHorizonMs);
          }}
          onRefresh={refreshConvergenceMonitor}
        />
      )}

      {/* Portfolio overview — balances + tracked trade stats */}
      {(() => {
        let totalRealizedPnL = 0;
        let totalCostBasis = 0;
        let totalTrades = 0;
        let totalBuyCount = 0;
        let totalSellCount = 0;
        let totalBuyBase = 0;
        let totalBuyAsset = 0;
        let totalSellBase = 0;
        let totalSellAsset = 0;
        let totalProfit = 0;
        let totalLoss = 0;
        let totalGasUsd = 0;
        let totalNetPnl = 0;
        let totalPnlReferenceUsd = 0;
        let tradeReturns = [];

        Object.keys(pnlData).forEach((marketKey) => {
          const marketPnl = normalizePnlRecord(pnlData[marketKey]);
          if (marketPnl && marketPnl.totalTrades > 0) {
            const reportedRealized =
              marketPnl.realizedPnL - marketPnl.reportingRealizedPnlOffset;
            const reportedGas = Math.max(
              0,
              marketPnl.totalGasUsd - marketPnl.reportingGasUsdOffset
            );
            const unrealized = calculateUnrealizedPnL(marketKey);
            totalRealizedPnL += reportedRealized;
            totalGasUsd += reportedGas;
            totalCostBasis += marketPnl.costBasis || 0;
            totalTrades += marketPnl.totalTrades || 0;
            totalBuyCount += marketPnl.buyCount || 0;
            totalSellCount += marketPnl.sellCount || 0;
            totalBuyBase += marketPnl.totalBuyBase || 0;
            totalBuyAsset += marketPnl.totalBuyAsset || 0;
            totalSellBase += marketPnl.totalSellBase || 0;
            totalSellAsset += marketPnl.totalSellAsset || 0;

            totalProfit += marketPnl.grossProfit || 0;
            totalLoss += marketPnl.grossLoss || 0;
            if (Array.isArray(marketPnl.sellReturnsPct)) {
              tradeReturns.push(...marketPnl.sellReturnsPct.filter(Number.isFinite));
            }

            if (marketPnl.accountingCheckpointAt > 0 && isWrappedStableMarket(marketKey)) {
              const marketPrice =
                Number(markets.find((candidate) => candidate.key === marketKey)?.price) ||
                Number(lastPriceRef.current[marketKey]) ||
                0;
              const marketPosition = positionsRef.current[marketKey] || {};
              const marketBook = getMarketBook(
                marketKey,
                marketPosition,
                marketPrice,
                positionsRef.current,
                chainConfig
              );
              totalNetPnl += calculateFlowAdjustedPnl({
                currentStableBalance: marketBook.baseBalance,
                currentAssetBalance: Number(marketPosition.holdings) || 0,
                assetPrice: marketPrice,
                baselineEquityUsd: marketPnl.accountingBaselineEquityUsd,
                netDepositsUsd: marketPnl.netExternalDepositsUsd,
                netWithdrawalsUsd: marketPnl.netExternalWithdrawalsUsd,
                totalGasUsd: reportedGas,
              }).pnlUsd;
              totalPnlReferenceUsd += Math.max(
                0,
                marketPnl.accountingBaselineEquityUsd +
                  marketPnl.netExternalDepositsUsd -
                  marketPnl.netExternalWithdrawalsUsd
              );
            } else {
              totalNetPnl += reportedRealized + unrealized - reportedGas;
              totalPnlReferenceUsd += marketPnl.totalBuyBase || 0;
            }
          }
        });

        const totalPnL = totalNetPnl;
        const reportedUnrealizedPnL = totalPnL - totalRealizedPnL + totalGasUsd;
        const totalPnLPct = totalPnlReferenceUsd > 0 ? (totalPnL / totalPnlReferenceUsd) * 100 : 0;
        const avgProfitPerTrade = totalTrades > 0 ? totalRealizedPnL / totalTrades : 0;
        const roi = totalPnLPct;
        const buySellRatio =
          totalSellCount > 0
            ? (totalBuyCount / totalSellCount).toFixed(2)
            : totalBuyCount > 0
            ? "∞"
            : "0";

        let sharpeRatio = 0;
        if (tradeReturns.length > 1) {
          const meanReturn = tradeReturns.reduce((a, b) => a + b, 0) / tradeReturns.length;
          const variance =
            tradeReturns.reduce((sum, ret) => sum + Math.pow(ret - meanReturn, 2), 0) / tradeReturns.length;
          const stdDev = Math.sqrt(variance);
          sharpeRatio = stdDev > 0 ? meanReturn / stdDev : 0;
        }

        const avgBuySize = totalBuyCount > 0 ? totalBuyBase / totalBuyCount : 0;
        const avgSellSize = totalSellCount > 0 ? totalSellBase / totalSellCount : 0;
        const avgBuyPrice = totalBuyAsset > 0 ? totalBuyBase / totalBuyAsset : 0;
        const avgSellPrice = totalSellAsset > 0 ? totalSellBase / totalSellAsset : 0;

        if (totalTrades === 0) return null;

        const stableSymbol = chainConfig?.defaultStablecoin || "USDC";
        const baseSymbol = stableSymbol;

        return (
          <>
            <PnLHero
              totalPnL={totalPnL}
              totalPnLPct={totalPnLPct}
              realized={totalRealizedPnL}
              unrealized={reportedUnrealizedPnL}
              totalTrades={totalTrades}
              positive={totalPnL >= 0}
            >
              <HStack justify="flex-end" mb={2}>
                <Switch
                  size="sm"
                  isChecked={manualPnLEnabled}
                  onChange={(e) => setManualPnLEnabled(e.target.checked)}
                />
                <Text fontSize="xs" color="gray.400">
                  Arbiscan sanity check
                </Text>
              </HStack>

              {manualPnLEnabled && (
                <Box mb={4} p={3} borderWidth={1} borderRadius="md" borderColor="yellow.500" bg="blackAlpha.500">
                  <Text fontSize="sm" color="yellow.300" fontWeight="semibold" mb={2}>
                    🔍 PnL Sanity Check (vs Arbiscan)
                  </Text>
                  <HStack spacing={3} align="flex-end">
                    <Box flex={1}>
                      <Text fontSize="xs" color="gray.400" mb={1}>
                        Manual PnL from Arbiscan
                      </Text>
                      <NumberInput
                        value={manualPnL}
                        onChange={(value) => setManualPnL(value)}
                        precision={2}
                        min={-999999}
                        max={999999}
                      >
                        <NumberInputField placeholder="Enter PnL from Arbiscan" borderColor="yellow.500" />
                      </NumberInput>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.400" mb={1}>
                        Calculated PnL
                      </Text>
                      <Text
                        fontSize="lg"
                        color={totalRealizedPnL >= 0 ? "green.400" : "red.400"}
                        fontWeight="bold"
                      >
                        {totalRealizedPnL >= 0 ? "+" : ""}
                        {formatCompact(totalRealizedPnL, 2)} {baseSymbol}
                      </Text>
                    </Box>
                    {manualPnL && !isNaN(parseFloat(manualPnL)) && (
                      <Box>
                        <Text fontSize="xs" color="gray.400" mb={1}>
                          Difference
                        </Text>
                        <Text
                          fontSize="lg"
                          color={Math.abs(parseFloat(manualPnL) - totalRealizedPnL) < 1 ? "green.400" : "red.400"}
                          fontWeight="bold"
                        >
                          {parseFloat(manualPnL) - totalRealizedPnL >= 0 ? "+" : ""}
                          {formatCompact(parseFloat(manualPnL) - totalRealizedPnL, 2)} {baseSymbol}
                        </Text>
                        {Math.abs(parseFloat(manualPnL) - totalRealizedPnL) >= 1 && (
                          <Text fontSize="xs" color="red.400" mt={1}>
                            ⚠️ Mismatch detected!
                          </Text>
                        )}
                      </Box>
                    )}
                  </HStack>
                </Box>
              )}
              <Text fontSize="xs" color="gray.500" mt={2}>
                Cost basis: {formatCompact(totalCostBasis, 2)} {baseSymbol} · Confirmed gas: {formatCompact(totalGasUsd, 2)} {baseSymbol}
              </Text>
            </PnLHero>

            <Panel title="Performance analytics" subtitle="Derived from tracked trades" accent="cyan">
              <SimpleGrid columns={[2, 3, 4]} spacing={4}>
                <Box>
                  <Text fontSize="xs" color="gray.400" mb={1}>
                    Avg Profit/Trade
                  </Text>
                  <Text fontSize="lg" color={avgProfitPerTrade >= 0 ? "green.400" : "red.400"} fontWeight="bold">
                    {avgProfitPerTrade >= 0 ? "+" : ""}
                    {formatCompact(avgProfitPerTrade, 2)} {baseSymbol}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.400" mb={1}>
                    ROI
                  </Text>
                  <Text fontSize="lg" color={roi >= 0 ? "green.400" : "red.400"} fontWeight="bold">
                    {roi >= 0 ? "+" : ""}
                    {roi.toFixed(2)}%
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.400" mb={1}>
                    Sharpe Ratio
                  </Text>
                  <Text
                    fontSize="lg"
                    color={sharpeRatio >= 1 ? "green.400" : sharpeRatio >= 0 ? "yellow.400" : "red.400"}
                    fontWeight="bold"
                  >
                    {sharpeRatio.toFixed(2)}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {sharpeRatio >= 1 ? "Good" : sharpeRatio >= 0 ? "Fair" : "Poor"}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.400" mb={1}>
                    Buy/Sell Ratio
                  </Text>
                  <Text fontSize="lg" color="cyan.300" fontWeight="bold">
                    {buySellRatio}:1
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {totalBuyCount} buys / {totalSellCount} sells
                  </Text>
                </Box>
              </SimpleGrid>

              <Divider my={5} borderColor="whiteAlpha.200" />
              <Text fontSize="md" color="purple.200" fontWeight="bold" mb={3}>
                Trade statistics
              </Text>
              <SimpleGrid columns={[2, 3, 4]} spacing={4}>
                <Box>
                  <Text fontSize="xs" color="gray.400" mb={1}>
                    Total Trades
                  </Text>
                  <Text fontSize="lg" color="cyan.300" fontWeight="bold">
                    {totalTrades}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.400" mb={1}>
                    Buy/Sell Ratio
                  </Text>
                  <Text fontSize="lg" color="cyan.300" fontWeight="bold">
                    {buySellRatio}:1
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.400" mb={1}>
                    Avg Buy Size
                  </Text>
                  <Text fontSize="lg" color="yellow.300" fontWeight="bold">
                    {formatCompact(avgBuySize, 2)} {baseSymbol}
                  </Text>
                  {avgSellSize > 0 && (
                    <Text fontSize="xs" color="gray.500">
                      Avg Sell: {formatCompact(avgSellSize, 2)} {baseSymbol}
                    </Text>
                  )}
                  {avgBuyPrice > 0 && (
                    <Text fontSize="xs" color="gray.500">
                      Avg prices: {formatCompact(avgBuyPrice, 2)} / {formatCompact(avgSellPrice, 2)}
                    </Text>
                  )}
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.400" mb={1}>
                    Total Profit
                  </Text>
                  <Text fontSize="lg" color="green.400" fontWeight="bold">
                    +{formatCompact(totalProfit, 2)} {baseSymbol}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.400" mb={1}>
                    Total Loss
                  </Text>
                  <Text fontSize="lg" color="red.400" fontWeight="bold">
                    -{formatCompact(totalLoss, 2)} {baseSymbol}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.400" mb={1}>
                    Net P&L
                  </Text>
                  <Text
                    fontSize="lg"
                    color={totalRealizedPnL >= 0 ? "green.400" : "red.400"}
                    fontWeight="bold"
                  >
                    {totalRealizedPnL >= 0 ? "+" : ""}
                    {formatCompact(totalRealizedPnL, 2)} {baseSymbol}
                  </Text>
                </Box>
              </SimpleGrid>
              <InsightBox variant="info">
                Average holding time and best/worst trade tracking coming in a future update.
              </InsightBox>
            </Panel>
          </>
        );
      })()}

      {showDebugUI && (
        <InsightBox variant="debug" title="Global target preview">
          Buy {buyTarget} (≈ {(((1 - buyTarget) * 100) || 0).toFixed(2)}%) · Sell {sellTarget} (≈{" "}
          {(((sellTarget - 1) * 100) || 0).toFixed(2)}%)
        </InsightBox>
      )}

      <MarketsSectionHeader
        activeCount={marketEnableCounts.activeCount}
        inactiveCount={marketEnableCounts.inactiveCount}
      />

      <VStack spacing={8} align="stretch" w="100%">
  
  {markets
    .slice()
    .sort((a, b) => {
      const aEnabled = positions[a.key]?.enabled !== false;
      const bEnabled = positions[b.key]?.enabled !== false;
      return aEnabled === bEnabled ? 0 : aEnabled ? -1 : 1;
    })
    .map(({ key, price, token, initiator }) => {
      const pos = positions[key] || {};
      if (price > 0) syncMarketRegime(key, price);
      const inverted = key === "WCRO-USDC";
      const anchor = (pos.holdings > 0 && pos.avgEntry > 0) ? pos.avgEntry : (price || 0);
      const pnlAvgEntryDisplay = getPnlAverageEntry(pnlData[key]);
      const cfg = { ...defaultBotCfg, ...(botConfigs[key] || {}) };
      const effectiveCfg = getEffectiveBotCfg(key);
      const regimeUi = marketRegimeRef.current[key];

      if (!currentConfig) return;
      const nativeSymbol = chainConfig.nativeCurrency.symbol;
      const stableSymbol = chainConfig.defaultStablecoin;
      const wrappedSymbol = chainConfig.nativeCurrency.symbol;
      // Determine baseBalance based on market key, not just initiator
      // For WETH-USDC.e market: base = USDC.e (_BASE key), holdings = WETH
      // For WBTC-USDC.e market: base = USDC.e (_BASE key), holdings = WBTC
      // For WBTC-WETH market: base = ETH (native), holdings = WBTC
      let baseBalance;
      if (key === `${wrappedSymbol}-${stableSymbol}`) {
        // WETH-USDC.e market: base is USDC.e (what you use to buy WETH)
        const baseKey = `${wrappedSymbol}-${stableSymbol}_BASE`;
        baseBalance = positions[baseKey]?.holdings || 0;
      } else if (key === `WBTC-${stableSymbol}`) {
        // WBTC-USDC.e market: base is USDC.e
        const baseKey = `${wrappedSymbol}-${stableSymbol}_BASE`;
        baseBalance = positions[baseKey]?.holdings || 0;
      } else {
        // WBTC-WETH market: base is native ETH
        baseBalance = positions[nativeSymbol]?.holdings || 0;
      }

      const bookDbg = getMarketBook(key, pos, price, positions, chainConfig);
      const spotGridDbg = shouldUseSpotGridMode({
        cfg: effectiveCfg,
        pos,
        price,
        baseBalanceUsd: bookDbg.baseBalance,
        totalBookUsd: bookDbg.totalBookUsd,
      });
      const wantBuyAt = pos.priceTargetBuy > 0 ? pos.priceTargetBuy : anchor * (inverted
        ? (effectiveCfg.sellTarget ?? defaultBotCfg.sellTarget)
        : (effectiveCfg.buyTarget  ?? defaultBotCfg.buyTarget));
      const wantSellAt = pos.priceTargetSell > 0 ? pos.priceTargetSell : anchor * (inverted
        ? (effectiveCfg.buyTarget  ?? defaultBotCfg.buyTarget)
        : (effectiveCfg.sellTarget ?? defaultBotCfg.sellTarget));

      const reservePctDbg = Number(effectiveCfg.minUsdcReservePct ?? defaultBotCfg.minUsdcReservePct);
      const usdcReserveOkDbg =
        bookDbg.totalBookUsd <= 0 || bookDbg.baseBalance / bookDbg.totalBookUsd >= reservePctDbg;

      // Removed profitGateOk - redundant with sellTarget which already ensures profit
      const coreSellOk = (!inverted && price > wantSellAt) || (inverted && price < wantSellAt);

      // CRITICAL FIX: Ensure all values are numbers and validate before calculating percentages
      // Prevent division by zero and handle edge cases that cause extreme percentages (70%/-70%)
      const priceNum = Number(price) || 0;
      const buyTargetNum = Number(pos.priceTargetBuy) || 0;
      const sellTargetNum = Number(pos.priceTargetSell) || 0;
      
      let distToBuy = null;
      let distToSell = null;
      
      if (priceNum > 0 && buyTargetNum > 0) {
        const buyDist = ((priceNum - buyTargetNum) / priceNum) * 100;
        // Clamp to reasonable range (-100% to 100%) to prevent extreme values
        distToBuy = Math.max(-100, Math.min(100, buyDist));
      }
      
      if (priceNum > 0 && sellTargetNum > 0) {
        const sellDist = ((sellTargetNum - priceNum) / priceNum) * 100;
        // Clamp to reasonable range (-100% to 100%) to prevent extreme values
        distToSell = Math.max(-100, Math.min(100, sellDist));
      }

      const notes = [];
      const recentRangePctUi = Number(effectiveCfg.adaptiveRecentRangePct ?? getRecentRangePct(key) ?? 0);
      const buyProtectionUi = getBuyProtectionStatus({
        cfg: effectiveCfg,
        pos,
        currentPrice: price,
        anchorPrice: anchor,
        recentRangePct: recentRangePctUi,
        marketRegime: regimeUi?.regime,
      });
      const regimeBuyGateUi =
        isRegimeEngineEnabled(key) && regimeUi
          ? evaluateRegimeBuyBlocks({
              regimeSnap: regimeUi,
              currentPrice: price,
              consecBuys: buyProtectionUi.consecBuys,
              book: bookDbg,
              plannedBuyUsd: Number(effectiveCfg.minTradeUsd || defaultBotCfg.minTradeUsd),
              pos,
            })
          : { blocked: false, blocks: [] };
      const priceOkForBuy =
        ((!inverted && price < wantBuyAt) || (inverted && price > wantBuyAt));
      const buyReady =
        baseBalance > 0 &&
        usdcReserveOkDbg &&
        priceOkForBuy &&
        !(effectiveCfg.buyProtectionEnabled !== false && buyProtectionUi.blocked) &&
        !regimeBuyGateUi.blocked;

      const sellReady =
        (pos.holdings || 0) > 0 &&
        (pos.sellCounter || 1) < 10 &&
        coreSellOk;

      if (!buyReady) {
        if (!priceOkForBuy) {
          notes.push(`BUY not ready: need price ${inverted ? ">" : "<"} ${safeFix(wantBuyAt)} (now ${safeFix(price)})`);
        }
        if (baseBalance <= 0) {
          const baseLabel = key.includes(`${stableSymbol}`) ? stableSymbol : nativeSymbol;
          notes.push(`no base balance (${baseLabel}: ${formatCompact(baseBalance)})`);
        }
        if (!usdcReserveOkDbg) {
          notes.push(
            `USDC reserve below ${(reservePctDbg * 100).toFixed(0)}% (${((bookDbg.baseBalance / Math.max(bookDbg.totalBookUsd, 1)) * 100).toFixed(1)}% of book)`
          );
        }
        if (effectiveCfg.buyProtectionEnabled !== false && buyProtectionUi.blocked) {
          if (buyProtectionUi.kind === "recoveryCooldown") {
            notes.push(
              `Recovery buy cooldown: ${Math.max(0, Math.ceil((buyProtectionUi.recoveryBuyBlockedUntil - Date.now()) / 60000))}m remaining`
            );
          } else if (buyProtectionUi.kind === "maxConsecutive") {
            notes.push(`Max consecutive buys (${buyProtectionUi.consecBuys}/${buyProtectionUi.maxBuys}) — need SELL`);
          } else if (buyProtectionUi.kind === "fourthMajorDip") {
            notes.push(
              `4th buy requires price <= ${safeFix(buyProtectionUi.majorDipRequiredPrice)} (now ${safeFix(price)})`
            );
          }
        }
        if (regimeBuyGateUi.blocked && regimeBuyGateUi.blocks?.length) {
          regimeBuyGateUi.blocks.forEach((b) => notes.push(b.message));
        }
      }
      if (!sellReady) {
        if ((pos.holdings || 0) <= 0) notes.push(`no holdings`);
        if (!coreSellOk)
          notes.push(`SELL not ready: need price ${inverted ? "<" : ">"} ${safeFix(wantSellAt)} (now ${safeFix(price)})`);
        if ((pos.sellCounter || 1) >= 10) notes.push(`sellCounter cap reached`);
      }

      if (spotGridDbg) {
        notes.push(
          `🧭 Recovery grid active (drawdown ${(getDrawdownPct(pos.avgEntry, price) * 100).toFixed(2)}%, USDC ${((bookDbg.baseBalance / Math.max(bookDbg.totalBookUsd, 1)) * 100).toFixed(0)}%)`
        );
      }



      
      const displayTitle = (() => {
        const wrappedSymbol = chainConfig?.nativeCurrency?.symbol || "ETH";
        const stableSymbol = chainConfig?.defaultStablecoin || "USDC";
        if (key === `${wrappedSymbol}-${stableSymbol}`) {
          const wrappedDisplay =
            selectedChain === "arbitrum"
              ? "WETH"
              : selectedChain === "cronos"
              ? "WCRO"
              : selectedChain === "ethereum"
              ? "WETH"
              : selectedChain === "bsc"
              ? "WBNB"
              : selectedChain === "polygon"
              ? "WMATIC"
              : selectedChain === "avalanche"
              ? "WAVAX"
              : `W${wrappedSymbol}`;
          return `${wrappedDisplay}-${stableSymbol}`;
        }
        return key;
      })();

      return (
        <MarketCard
          key={key}
          displayTitle={displayTitle}
          enabled={pos.enabled !== false}
          onToggle={() => updatePosition(key, { enabled: !(pos.enabled !== false) })}
          price={safeFix(price)}
          buyReady={buyReady}
          sellReady={sellReady}
          recoveryMode={spotGridDbg}
          distToBuy={distToBuy}
          distToSell={distToSell}
          footer={null}
        >
            <MetricRow
              items={[
                { label: "Orientation", value: inverted ? "Inverted" : "Normal", color: inverted ? "orange.300" : "green.300" },
                { label: "Anchor", value: safeFix(anchor), color: "yellow.300" },
                ...(pnlAvgEntryDisplay > 0
                  ? [{ label: "Avg entry", value: safeFix(pnlAvgEntryDisplay), color: "purple.300" }]
                  : []),
                { label: "Buy target", value: safeFix(pos.priceTargetBuy), color: "green.300" },
                { label: "Sell target", value: safeFix(pos.priceTargetSell), color: "red.300" },
                { label: "Base balance", value: formatCompact(baseBalance), color: "cyan.200" },
              ]}
            />

            {cfg.strategyMode === "adaptive" && (
              <InsightBox variant="info" title="Adaptive spacing">
                Buy {(Number(effectiveCfg.adaptiveBuyMovePct || 0) * 100).toFixed(2)}% below · sell{" "}
                {(Number(effectiveCfg.adaptiveSellMovePct || 0) * 100).toFixed(2)}% above · 1h range{" "}
                {(Number(effectiveCfg.adaptiveRecentRangePct || 0) * 100).toFixed(2)}%
              </InsightBox>
            )}

            {effectiveCfg.buyProtectionEnabled !== false && (
              <InsightBox
                variant={buyProtectionUi.blocked ? "warn" : "info"}
                title="Buy protection"
              >
                <Text fontSize="sm">
                  Consecutive buys: {buyProtectionUi.consecBuys} / {buyProtectionUi.maxBuys}
                  {buyProtectionUi.recoveryBuyBlockedUntil > Date.now()
                    ? ` · Recovery cooldown: ${Math.max(
                        0,
                        Math.ceil((buyProtectionUi.recoveryBuyBlockedUntil - Date.now()) / 60000)
                      )}m remaining`
                    : ""}
                  {buyProtectionUi.consecBuys === buyProtectionUi.maxBuys - 1 &&
                  buyProtectionUi.majorDipRequiredPrice > 0
                    ? ` · 4th buy needs price <= ${safeFix(buyProtectionUi.majorDipRequiredPrice)}`
                    : ""}
                </Text>
              </InsightBox>
            )}

            {isRegimeEngineEnabled(key) && regimeUi?.metrics && (
              <InsightBox
                variant={
                  regimeUi.regime === REGIMES.CAPITAL_PROTECTION
                    ? "warn"
                    : regimeUi.regime === REGIMES.RECOVERY_BREAKOUT
                    ? "success"
                    : "info"
                }
                title={`Market regime · ${regimeUi.regime}${regimeUi.rawRegime && regimeUi.rawRegime !== regimeUi.regime ? ` (raw ${regimeUi.rawRegime})` : ""}`}
              >
                <SimpleGrid columns={[1, 2]} spacing={2} fontSize="xs">
                  <Text color="gray.300">
                    Recovery breakout: {regimeUi.recoveryBreakoutActive ? "yes" : "no"}
                    {regimeUi.higherLowConfirmed ? " · higher low ✓" : ""}
                  </Text>
                  <Text color="gray.400">
                    15m Δ {(regimeUi.metrics.m15.changePct * 100).toFixed(2)}% · bounce{" "}
                    {(regimeUi.bounce15mPct * 100).toFixed(2)}%
                  </Text>
                  <Text color="gray.400">
                    1h range {(regimeUi.metrics.m1h.rangePct * 100).toFixed(2)}% · bounce{" "}
                    {(regimeUi.bounce1hPct * 100).toFixed(2)}%
                  </Text>
                  <Text color="gray.400">
                    4h Δ {(regimeUi.metrics.m4h.changePct * 100).toFixed(2)}% · in range{" "}
                    {(regimeUi.metrics.m4h.positionInRange * 100).toFixed(0)}%
                  </Text>
                  <Text color="gray.400">
                    24h Δ {(regimeUi.metrics.m24h.changePct * 100).toFixed(2)}% · DD from high{" "}
                    {(regimeUi.metrics.m24h.drawdownFromHighPct * 100).toFixed(2)}%
                  </Text>
                  <Text color="gray.400">
                    Consecutive buys: {buyProtectionUi.consecBuys} / {buyProtectionUi.maxBuys}
                    {pos.lastBuyPrice > 0 ? ` · last buy $${safeFix(pos.lastBuyPrice)}` : ""}
                  </Text>
                  <Text color="gray.400">
                    Base buy{" "}
                    {(Number(effectiveCfg.adaptiveBaseMoveUser ?? cfg.adaptiveBaseMovePct) * 100).toFixed(2)}% →
                    effective {(Number(effectiveCfg.adaptiveBuyMovePct || 0) * 100).toFixed(2)}% · sell{" "}
                    {(Number(effectiveCfg.adaptiveSellMovePct || 0) * 100).toFixed(2)}%
                  </Text>
                  <Text color="gray.400">
                    Reserve min {(Number(effectiveCfg.minUsdcReservePct || 0) * 100).toFixed(0)}% · max WETH{" "}
                    {(Number(effectiveCfg.maxWethExposurePct || 0) * 100).toFixed(0)}%
                  </Text>
                  {buyProtectionUi.recoveryBuyBlockedUntil > Date.now() && (
                    <Text color="orange.300">
                      Recovery cooldown:{" "}
                      {Math.max(0, Math.ceil((buyProtectionUi.recoveryBuyBlockedUntil - Date.now()) / 60000))}m
                    </Text>
                  )}
                  {pos.capitalProtectionBuyBlockedUntil > Date.now() && (
                    <Text color="red.300">
                      Post-exit cooldown:{" "}
                      {Math.max(
                        0,
                        Math.ceil((pos.capitalProtectionBuyBlockedUntil - Date.now()) / 60000)
                      )}
                      m
                    </Text>
                  )}
                  {regimeUi.recoveryBreakoutActive && (
                    <Text color="green.300">
                      Breakout anchor ${safeFix(regimeUi.recoveryBreakoutAnchorPrice)} · local low $
                      {safeFix(regimeUi.lastLocalLowPrice)}
                    </Text>
                  )}
                  {regimeUi.fresh15mLow && <Text color="orange.300">Fresh 15m low</Text>}
                </SimpleGrid>
              </InsightBox>
            )}

            {/* Config (read-only default, editable via handlers) */}
            <Box  mt={2} p={2} borderWidth="1px" borderColor="gray.600" borderRadius="md" w="100%">
              <HStack justify="space-between" flexWrap="wrap" spacing={3}>
                <HStack>
                  <Text fontSize="sm">Mode</Text>
                  <Switch
                    isChecked={cfg.usePercentMode}
                    onChange={(e) => setBotConfig(key, { usePercentMode: e.target.checked })}
                  />
                  <Text fontSize="xs" color="gray.400">
                    {cfg.usePercentMode ? "Percent" : "Manual"}
                  </Text>
                </HStack>

                <HStack>
                  <Text fontSize="sm">Strategy</Text>
                  <Select
                    size="sm"
                    value={cfg.strategyMode || "static"}
                    onChange={(e) => setBotConfig(key, { strategyMode: e.target.value })}
                    w="130px"
                    bg="gray.800"
                  >
                    <option value="static">Static</option>
                    <option value="adaptive">Adaptive</option>
                  </Select>
                </HStack>

                <HStack>
                  <Text fontSize="sm">Regime engine</Text>
                  <Switch
                    size="sm"
                    isChecked={isRegimeEngineEnabled(key)}
                    onChange={(e) =>
                      setBotConfig(key, { regimeEngineEnabled: e.target.checked })
                    }
                  />
                  <Text fontSize="xs" color="gray.400">
                    {isRegimeEngineEnabled(key) ? "ON" : "OFF"}
                  </Text>
                </HStack>

                <HStack>
                  <Text fontSize="sm">Min Buy Spacing (ms)</Text>
                  <NumberInput
                    value={cfg.minBuySpacingMs}
                    min={10000}
                    step={5000}
                    onChange={(v) => setBotConfig(key, { minBuySpacingMs: parseInt(v || "0", 10) })}
                    w="140px"
                  >
                    <NumberInputField />
                  </NumberInput>
                </HStack>

                <HStack>
                  <Text fontSize="sm">Stuck Stop</Text>
                  <Switch
                    isChecked={cfg.stuckStopEnabled !== false}
                    onChange={(e) => setBotConfig(key, { stuckStopEnabled: e.target.checked })}
                  />
                  <Text fontSize="xs" color="gray.400">{cfg.stuckStopEnabled !== false ? "ON" : "OFF"}</Text>
                </HStack>
              </HStack>

              <SimpleGrid columns={[1, 2]} spacing={3} mt={3}>
                <Box>
                  <Text fontSize="sm">🎯 Buy Target (multiplier)</Text>
                  <NumberInput
                    value={cfg.buyTarget}
                    min={0.5}
                    max={1.0}
                    step={0.001}
                    precision={4}
                    onChange={(v) => setBotConfig(key, { buyTarget: parseFloat(v) })}
                  >
                    <NumberInputField />
                  </NumberInput>
                </Box>

                <Box>
                  <Text fontSize="sm">🎯 Sell Target (multiplier)</Text>
                  <NumberInput
                    value={cfg.sellTarget}
                    min={1.0}
                    max={1.5}
                    step={0.001}
                    precision={4}
                    onChange={(v) => setBotConfig(key, { sellTarget: parseFloat(v) })}
                  >
                    <NumberInputField />
                  </NumberInput>
                </Box>
              </SimpleGrid>

              <SimpleGrid columns={[1, 2, 4]} spacing={3} mt={3}>
                <Box>
                  <Text fontSize="sm">Soft Stop h / loss% / $</Text>
                  <HStack>
                    <NumberInput value={cfg.softStuckHours || defaultBotCfg.softStuckHours} min={1} max={168} step={1} precision={0} onChange={(v) => setBotConfig(key, { softStuckHours: Number(v) || defaultBotCfg.softStuckHours })}>
                      <NumberInputField />
                    </NumberInput>
                    <NumberInput value={(Number(cfg.softStuckLossPct ?? defaultBotCfg.softStuckLossPct) * 100).toFixed(2)} min={0.1} max={20} step={0.1} precision={2} onChange={(v) => setBotConfig(key, { softStuckLossPct: (Number(v) || defaultBotCfg.softStuckLossPct * 100) / 100 })}>
                      <NumberInputField />
                    </NumberInput>
                    <NumberInput value={Number(cfg.softStuckLossUsd ?? defaultBotCfg.softStuckLossUsd)} min={0} max={1000} step={1} precision={2} onChange={(v) => setBotConfig(key, { softStuckLossUsd: Number(v) || defaultBotCfg.softStuckLossUsd })}>
                      <NumberInputField />
                    </NumberInput>
                  </HStack>
                </Box>
                <Box>
                  <Text fontSize="sm">Hard Stop h / loss% / $</Text>
                  <HStack>
                    <NumberInput value={cfg.hardStuckHours || defaultBotCfg.hardStuckHours} min={1} max={168} step={1} precision={0} onChange={(v) => setBotConfig(key, { hardStuckHours: Number(v) || defaultBotCfg.hardStuckHours })}>
                      <NumberInputField />
                    </NumberInput>
                    <NumberInput value={(Number(cfg.hardStuckLossPct ?? defaultBotCfg.hardStuckLossPct) * 100).toFixed(2)} min={0.1} max={30} step={0.1} precision={2} onChange={(v) => setBotConfig(key, { hardStuckLossPct: (Number(v) || defaultBotCfg.hardStuckLossPct * 100) / 100 })}>
                      <NumberInputField />
                    </NumberInput>
                    <NumberInput value={Number(cfg.hardStuckLossUsd ?? defaultBotCfg.hardStuckLossUsd)} min={0} max={1000} step={1} precision={2} onChange={(v) => setBotConfig(key, { hardStuckLossUsd: Number(v) || defaultBotCfg.hardStuckLossUsd })}>
                      <NumberInputField />
                    </NumberInput>
                  </HStack>
                </Box>
                <Box>
                  <Text fontSize="sm">Stop Sell %</Text>
                  <HStack>
                    <NumberInput value={(Number(cfg.softStuckSellPct ?? defaultBotCfg.softStuckSellPct) * 100).toFixed(0)} min={1} max={100} step={1} precision={0} onChange={(v) => setBotConfig(key, { softStuckSellPct: (Number(v) || 33) / 100 })}>
                      <NumberInputField />
                    </NumberInput>
                    <NumberInput value={(Number(cfg.hardStuckSellPct ?? defaultBotCfg.hardStuckSellPct) * 100).toFixed(0)} min={1} max={100} step={1} precision={0} onChange={(v) => setBotConfig(key, { hardStuckSellPct: (Number(v) || 66) / 100 })}>
                      <NumberInputField />
                    </NumberInput>
                  </HStack>
                  <Text fontSize="xs" color="gray.400">Soft / hard percent of WETH bag</Text>
                </Box>
              </SimpleGrid>

              <SimpleGrid columns={[1, 2]} spacing={3} mt={3}>
                <Box>
                  <HStack justify="space-between" mb={1}>
                    <Text fontSize="sm">🟢 Buy {cfg.usePercentMode ? "% of base" : (() => {
                      // Show correct token symbol based on market
                      const wrappedSymbol = chainConfig?.nativeCurrency?.symbol || "ETH";
                      const stableSymbol = chainConfig?.defaultStablecoin || "USDC";
                      if (key === `${wrappedSymbol}-${stableSymbol}` || key === `WBTC-${stableSymbol}`) {
                        return `amount (${stableSymbol})`;
                      }
                      return `amount (${initiator})`;
                    })()}</Text>
                    {!cfg.usePercentMode && (
                      <Text fontSize="xs" color="gray.400">
                        Balance: {formatCompact(baseBalance)} {(() => {
                          // Show correct token symbol based on market
                          const wrappedSymbol = chainConfig?.nativeCurrency?.symbol || "ETH";
                          const stableSymbol = chainConfig?.defaultStablecoin || "USDC";
                          if (key === `${wrappedSymbol}-${stableSymbol}` || key === `WBTC-${stableSymbol}`) {
                            return stableSymbol;
                          }
                          return initiator;
                        })()}
                      </Text>
                    )}
                  </HStack>
                  <VStack align="stretch" spacing={2}>
                    <NumberInput
                      value={cfg.usePercentMode ? cfg.buyMultiplier : cfg.manualBuy}
                      min={0}
                      max={cfg.usePercentMode ? 1 : undefined}
                      step={cfg.usePercentMode ? 0.001 : 0.000001}
                      precision={cfg.usePercentMode ? 4 : 6}
                      onChange={(v) =>
                        setBotConfig(
                          key,
                          cfg.usePercentMode ? { buyMultiplier: parseFloat(v) } : { manualBuy: parseFloat(v) }
                        )
                      }
                    >
                      <NumberInputField 
                        borderColor={
                          !cfg.usePercentMode && Number(cfg.manualBuy) > baseBalance 
                            ? "red.500" 
                            : "gray.600"
                        }
                      />
                    </NumberInput>
                    {(() => {
                      const baseTokenSymbol = (() => {
                        const wrappedSymbol = chainConfig?.nativeCurrency?.symbol || "ETH";
                        const stableSymbol = chainConfig?.defaultStablecoin || "USDC";
                        if (key === `${wrappedSymbol}-${stableSymbol}` || key === `WBTC-${stableSymbol}`) {
                          return stableSymbol;
                        }
                        return initiator;
                      })();
                      
                      const currentBuyAmount = cfg.usePercentMode 
                        ? baseBalance * (cfg.buyMultiplier || 0.1)
                        : Number(cfg.manualBuy || 0);
                      
                      return (
                        <>
                          {!cfg.usePercentMode && (
                            <HStack spacing={2} justify="flex-start" flexWrap="wrap">
                              <Button
                                size="sm"
                                variant="solid"
                                bg="green.500"
                                color="white"
                                _hover={{ bg: "green.600" }}
                                _active={{ bg: "green.700" }}
                                onClick={() => setBotConfig(key, { manualBuy: baseBalance * 0.25 })}
                              >
                                25%
                              </Button>
                              <Button
                                size="sm"
                                variant="solid"
                                bg="green.500"
                                color="white"
                                _hover={{ bg: "green.600" }}
                                _active={{ bg: "green.700" }}
                                onClick={() => setBotConfig(key, { manualBuy: baseBalance * 0.5 })}
                              >
                                50%
                              </Button>
                              <Button
                                size="sm"
                                variant="solid"
                                bg="green.500"
                                color="white"
                                _hover={{ bg: "green.600" }}
                                _active={{ bg: "green.700" }}
                                onClick={() => setBotConfig(key, { manualBuy: baseBalance * 0.75 })}
                              >
                                75%
                              </Button>
                              <Button
                                size="sm"
                                variant="solid"
                                bg="green.500"
                                color="white"
                                _hover={{ bg: "green.600" }}
                                _active={{ bg: "green.700" }}
                                onClick={() => setBotConfig(key, { manualBuy: baseBalance })}
                              >
                                100%
                              </Button>
                  </HStack>
                          )}
                          <Text fontSize="xs" color="gray.400" mt={1}>
                            {cfg.usePercentMode ? (
                              <>
                                {((cfg.buyMultiplier || 0.1) * 100).toFixed(1)}% = {formatCompact(currentBuyAmount, 2)} {baseTokenSymbol}
                              </>
                            ) : (
                              <>
                                Current: {formatCompact(currentBuyAmount, 2)} {baseTokenSymbol} · 
                                25%: {formatCompact(baseBalance * 0.25, 2)} · 
                                50%: {formatCompact(baseBalance * 0.5, 2)} · 
                                75%: {formatCompact(baseBalance * 0.75, 2)} · 
                                100%: {formatCompact(baseBalance, 2)} {baseTokenSymbol}
                              </>
                            )}
                          </Text>
                        </>
                      );
                    })()}
                  </VStack>

                  {!cfg.usePercentMode && (() => {
                    // Get native price for conversion display
                    const nativePrice = positions?.[chainConfig?.nativeCurrency?.symbol]?.price || 0;
                    const stableSymbol = chainConfig?.defaultStablecoin || "USDC";
                    const nativeSymbol = chainConfig?.nativeCurrency?.symbol || "ETH";
                    const wrappedSymbol = chainConfig?.nativeCurrency?.symbol || "ETH";
                    const buyAmount = Number(cfg.manualBuy) || 0;
                    
                    // Calculate estimated output
                    let estimatedOutput = "";
                    if (price > 0 && buyAmount > 0) {
                      const isWcroUsdc = key === `${wrappedSymbol}-${stableSymbol}`;
                      const estimatedTokens = isWcroUsdc ? buyAmount * price : buyAmount / price;
                      let outputSymbol = "";
                      if (key === `${wrappedSymbol}-${stableSymbol}`) {
                        outputSymbol = wrappedSymbol;
                      } else if (key.startsWith("WBTC-")) {
                        outputSymbol = "WBTC";
                    } else {
                        outputSymbol = "tokens";
                      }
                      estimatedOutput = `≈ ${formatCompact(estimatedTokens, 6)} ${outputSymbol}`;
                    }
                    
                    // USD equivalent
                    let usdEcho = "";
                    if (initiator === nativeSymbol || initiator === "CRO" || initiator === "ETH") {
                      usdEcho = nativePrice > 0 ? `≈ $${formatCompact(Number(cfg.manualBuy) * nativePrice, 2)}` : "";
                    } else if (initiator === stableSymbol || initiator === "USDC" || initiator === "USDC.e" || initiator === "USDT") {
                      usdEcho = `≈ $${formatCompact(Number(cfg.manualBuy), 2)}`;
                    } else {
                      usdEcho = nativePrice > 0 ? `≈ $${formatCompact(Number(cfg.manualBuy) * nativePrice, 2)}` : "";
                    }
                    
                    return (
                      <VStack align="start" spacing={0} mt={1}>
                        {estimatedOutput && (
                          <Text fontSize="xs" color="green.400">📊 {estimatedOutput}</Text>
                        )}
                        {usdEcho && (
                          <Text fontSize="xs" color="gray.400">{usdEcho}</Text>
                        )}
                        {buyAmount > baseBalance && (
                          <Text fontSize="xs" color="red.400">⚠️ Amount exceeds balance!</Text>
                        )}
                      </VStack>
                    );
                  })()}

                  <Button
                    size="sm"
                    colorScheme="orange"
                    onClick={() => {
                      savePositions(prev => ({
                        ...prev,
                        [key]: {
                          holdings: 0,
                          avgEntry: 0,
                          priceTargetBuy: 0,
                          priceTargetSell: 0,
                          buyCounter: 1,
                          sellCounter: 1,
                          enabled: true,
                        },
                      }));

                      setBotConfig(key, { ...instanceDefaultBotCfg });

                      setTradeHistory(prev => {
                        const next = prev.filter(trade => trade.marketKey !== key);
                        (async () => {
                          try {
                            if (!persistencePlayer) return;
                            await moralisDeleteTradeHistoryForMarket(persistencePlayer, key);
                          } catch (e) { console.error("deleteTradeHistoryForMarket failed", e); }
                        })();
                        return next;
                      });

                      console.log(`🧹 Cleared bot ${key}`);
                    }}
                  >
                    🧹 Clear Bot
                  </Button>
                </Box>

                <Box>
                  {(() => {
                    // Determine what token is being sold
                    const wrappedSymbol = chainConfig?.nativeCurrency?.symbol || "ETH";
                    const stableSymbol = chainConfig?.defaultStablecoin || "USDC";
                    let sellTokenSymbol = "tokens";
                    
                    if (key === `${wrappedSymbol}-${stableSymbol}`) {
                      // Selling wrapped native (WETH, WCRO, etc.)
                      sellTokenSymbol = wrappedSymbol;
                    } else if (key.startsWith("WBTC-")) {
                      // Selling WBTC
                      sellTokenSymbol = "WBTC";
                    } else {
                      // Custom token or other - try to get from market
                      const market = markets.find(m => m.key === key);
                      if (market && market.token) {
                        // For custom tokens, show "tokens" or try to detect
                        sellTokenSymbol = "tokens";
                      } else {
                        sellTokenSymbol = "tokens";
                      }
                    }
                    
                    const currentHoldings = pos.holdings || 0;
                    const sellAmount = Number(cfg.manualSell) || 0;
                    
                    return (
                      <>
                        <HStack justify="space-between" mb={1}>
                          <Text fontSize="sm">🔴 Sell {cfg.usePercentMode ? "% of holdings" : `amount (${sellTokenSymbol})`}</Text>
                          {!cfg.usePercentMode && (
                            <Text fontSize="xs" color="gray.400">
                              Holdings: {formatCompact(currentHoldings)} {sellTokenSymbol}
                            </Text>
                          )}
                        </HStack>
                        <VStack align="stretch" spacing={2}>
                    <NumberInput
                      value={cfg.usePercentMode ? cfg.sellMultiplier : cfg.manualSell}
                      min={0}
                      max={cfg.usePercentMode ? 1 : undefined}
                      step={cfg.usePercentMode ? 0.001 : 0.000001}
                      precision={cfg.usePercentMode ? 4 : 6}
                      onChange={(v) =>
                        setBotConfig(
                          key,
                          cfg.usePercentMode ? { sellMultiplier: parseFloat(v) } : { manualSell: parseFloat(v) }
                        )
                      }
                    >
                            <NumberInputField 
                              borderColor={
                                !cfg.usePercentMode && sellAmount > currentHoldings 
                                  ? "red.500" 
                                  : "gray.600"
                              }
                            />
                    </NumberInput>
                          {(() => {
                            const currentSellAmount = cfg.usePercentMode
                              ? currentHoldings * (cfg.sellMultiplier || 0.3)
                              : Number(cfg.manualSell || 0);
                            
                            return (
                              <>
                                {!cfg.usePercentMode && (
                                  <HStack spacing={2} justify="flex-start" flexWrap="wrap">
                                    <Button
                                      size="sm"
                                      variant="solid"
                                      bg="red.500"
                                      color="white"
                                      _hover={{ bg: "red.600" }}
                                      _active={{ bg: "red.700" }}
                                      onClick={() => setBotConfig(key, { manualSell: currentHoldings * 0.25 })}
                                    >
                                      25%
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="solid"
                                      bg="red.500"
                                      color="white"
                                      _hover={{ bg: "red.600" }}
                                      _active={{ bg: "red.700" }}
                                      onClick={() => setBotConfig(key, { manualSell: currentHoldings * 0.5 })}
                                    >
                                      50%
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="solid"
                                      bg="red.500"
                                      color="white"
                                      _hover={{ bg: "red.600" }}
                                      _active={{ bg: "red.700" }}
                                      onClick={() => setBotConfig(key, { manualSell: currentHoldings * 0.75 })}
                                    >
                                      75%
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="solid"
                                      bg="red.500"
                                      color="white"
                                      _hover={{ bg: "red.600" }}
                                      _active={{ bg: "red.700" }}
                                      onClick={() => setBotConfig(key, { manualSell: currentHoldings })}
                                    >
                                      100%
                                    </Button>
                  </HStack>
                                )}
                                <Text fontSize="xs" color="gray.400" mt={1}>
                                  {cfg.usePercentMode ? (
                                    <>
                                      {((cfg.sellMultiplier || 0.3) * 100).toFixed(1)}% = {formatCompact(currentSellAmount, 4)} {sellTokenSymbol}
                                    </>
                                  ) : (
                                    <>
                                      Current: {formatCompact(currentSellAmount, 4)} {sellTokenSymbol} · 
                                      25%: {formatCompact(currentHoldings * 0.25, 4)} · 
                                      50%: {formatCompact(currentHoldings * 0.5, 4)} · 
                                      75%: {formatCompact(currentHoldings * 0.75, 4)} · 
                                      100%: {formatCompact(currentHoldings, 4)} {sellTokenSymbol}
                                    </>
                                  )}
                                </Text>
                              </>
                            );
                          })()}
                        </VStack>

                  {!cfg.usePercentMode && (() => {
                          const nativePrice = positions?.[chainConfig?.nativeCurrency?.symbol]?.price || 0;
                          const nativeSymbol = chainConfig?.nativeCurrency?.symbol || "ETH";
                          const stableSymbol = chainConfig?.defaultStablecoin || "USDC";
                          const wrappedSymbol = chainConfig?.nativeCurrency?.symbol || "ETH";
                          
                          // Calculate estimated output
                          let estimatedOutput = "";
                          if (price > 0 && sellAmount > 0) {
                            const isWcroUsdc = key === `${wrappedSymbol}-${stableSymbol}`;
                            let outputAmount, outputSymbol;
                            if (key === `${wrappedSymbol}-${stableSymbol}`) {
                              // Selling wrapped native, getting stablecoin
                              outputAmount = sellAmount * price;
                              outputSymbol = stableSymbol;
                            } else if (key.startsWith("WBTC-")) {
                              // Selling WBTC
                              outputAmount = sellAmount * price;
                              outputSymbol = key.includes(stableSymbol) ? stableSymbol : nativeSymbol;
                    } else {
                              // Other tokens
                              outputAmount = sellAmount * price;
                              outputSymbol = nativeSymbol;
                            }
                            estimatedOutput = `≈ ${formatCompact(outputAmount, 6)} ${outputSymbol}`;
                          }
                          
                          // USD equivalent
                          let usdEcho = "";
                          if (price > 0 && sellAmount > 0 && nativePrice > 0) {
                            let tokenValue = 0;
                            if (key === `${wrappedSymbol}-${stableSymbol}`) {
                              tokenValue = sellAmount * price; // Already in stablecoin
                            } else {
                              tokenValue = sellAmount * price * nativePrice; // Convert to USD
                            }
                            usdEcho = `≈ $${formatCompact(tokenValue, 2)}`;
                          }
                          
                          return (
                            <VStack align="start" spacing={0} mt={1}>
                              {estimatedOutput && (
                                <Text fontSize="xs" color="green.400">📊 {estimatedOutput}</Text>
                              )}
                              {usdEcho && (
                                <Text fontSize="xs" color="gray.400">{usdEcho}</Text>
                              )}
                              {sellAmount > currentHoldings && (
                                <Text fontSize="xs" color="red.400">⚠️ Amount exceeds holdings!</Text>
                              )}
                            </VStack>
                          );
                        })()}
                      </>
                    );
                  })()}
                </Box>
              </SimpleGrid>

              <SimpleGrid columns={[1, 2]} spacing={3} mt={3}>
                <Box>
                  {(() => {
                    const nativeSymbol = chainConfig?.nativeCurrency?.symbol || "ETH";
                    const stableSymbol = chainConfig?.defaultStablecoin || "USDC";
                    const capLabel = initiator === stableSymbol || initiator === "USDC" || initiator === "USDC.e" || initiator === "USDT"
                      ? `Max Base Cap (${stableSymbol})`
                      : `Max Base Cap (${nativeSymbol})`;
                    
                    return (
                      <>
                        <Text fontSize="sm">🔒 {capLabel}</Text>
                  <NumberInput
                    value={cfg.maxBaseCap || 0}
                    min={0}
                    step={0.1}
                    precision={6}
                    onChange={(v) => setBotConfig(key, { maxBaseCap: Number(v) })}
                  >
                    <NumberInputField />
                  </NumberInput>
                  <Text fontSize="xs" color="gray.400">0 = no cap</Text>
                      </>
                    );
                  })()}
                </Box>

                <Box>
                  <Text fontSize="sm">🔒 Max Sell Cap (fraction)</Text>
                  <NumberInput
                    value={cfg.maxSellCap ?? 1}
                    min={0}
                    max={1}
                    step={0.01}
                    precision={2}
                    onChange={(v) => {
                      const val = Math.max(0, Math.min(1, Number(v)));
                      setBotConfig(key, { maxSellCap: val });
                    }}
                  >
                    <NumberInputField />
                  </NumberInput>
                  <Text fontSize="xs" color="gray.400">Fraction of holdings (0.0–1.0)</Text>
                </Box>
              </SimpleGrid>

              {cfg.usePercentMode && isWrappedStableMarket(key) && (
                <Box mt={3} p={3} border="1px solid" borderColor="cyan.800" borderRadius="md">
                  <HStack justify="space-between" mb={3} flexWrap="wrap">
                    <Box>
                      <Text fontSize="sm" fontWeight="semibold">Flow-adjusted compounding</Text>
                      <Text fontSize="xs" color="gray.400">Sizes from confirmed profit; transfers do not change PnL.</Text>
                    </Box>
                    <Switch
                      size="sm"
                      isChecked={cfg.compoundSizingEnabled !== false}
                      onChange={(e) => setBotConfig(key, { compoundSizingEnabled: e.target.checked })}
                    />
                  </HStack>
                  <SimpleGrid columns={[1, 2, 3]} spacing={3}>
                    <Box>
                      <Text fontSize="sm">Base equity %</Text>
                      <NumberInput
                        value={((cfg.compoundBaseTradePct ?? defaultBotCfg.compoundBaseTradePct) * 100).toFixed(0)}
                        min={1}
                        max={50}
                        step={1}
                        precision={0}
                        isDisabled={cfg.compoundSizingEnabled === false}
                        onChange={(v) => setBotConfig(key, { compoundBaseTradePct: Math.max(0.01, Number(v) / 100) })}
                      >
                        <NumberInputField />
                      </NumberInput>
                    </Box>
                    <Box>
                      <Text fontSize="sm">Hard equity cap %</Text>
                      <NumberInput
                        value={((cfg.compoundMaxTradePct ?? defaultBotCfg.compoundMaxTradePct) * 100).toFixed(0)}
                        min={1}
                        max={50}
                        step={1}
                        precision={0}
                        isDisabled={cfg.compoundSizingEnabled === false}
                        onChange={(v) => setBotConfig(key, { compoundMaxTradePct: Math.max(0.01, Number(v) / 100) })}
                      >
                        <NumberInputField />
                      </NumberInput>
                    </Box>
                    <Box>
                      <Text fontSize="sm">Realized profit only</Text>
                      <Switch
                        mt={2}
                        size="sm"
                        isChecked={cfg.compoundRealizedOnly !== false}
                        isDisabled={cfg.compoundSizingEnabled === false}
                        onChange={(e) => setBotConfig(key, { compoundRealizedOnly: e.target.checked })}
                      />
                    </Box>
                  </SimpleGrid>
                </Box>
              )}

              {cfg.usePercentMode && (
                <SimpleGrid columns={[1, 2]} spacing={3} mt={3}>
                  <Box>
                    <Text fontSize="sm">{cfg.compoundSizingEnabled !== false && isWrappedStableMarket(key) ? "Economic Min USD" : "Min Trade USD"}</Text>
                    <NumberInput
                      value={cfg.compoundSizingEnabled !== false && isWrappedStableMarket(key)
                        ? cfg.compoundEconomicMinTradeUsd ?? defaultBotCfg.compoundEconomicMinTradeUsd
                        : cfg.minTradeUsd || defaultBotCfg.minTradeUsd}
                      min={1}
                      step={1}
                      precision={0}
                      onChange={(v) => {
                        const val = Math.max(1, Number(v));
                        setBotConfig(key, cfg.compoundSizingEnabled !== false && isWrappedStableMarket(key)
                          ? { compoundEconomicMinTradeUsd: val, minTradeUsd: val }
                          : { minTradeUsd: val });
                      }}
                    >
                      <NumberInputField />
                    </NumberInput>
                    <Text fontSize="xs" color="gray.400">{`Default $${instanceDefaultBotCfg.compoundEconomicMinTradeUsd}; never overrides the buy cap.`}</Text>
                  </Box>

                  <Box>
                    <Text fontSize="sm">{cfg.compoundSizingEnabled !== false && isWrappedStableMarket(key) ? "Absolute Max USD" : "Max Trade USD"}</Text>
                    <NumberInput
                      value={cfg.compoundSizingEnabled !== false && isWrappedStableMarket(key)
                        ? cfg.compoundAbsoluteMaxTradeUsd ?? defaultBotCfg.compoundAbsoluteMaxTradeUsd
                        : cfg.maxTradeUsd || defaultBotCfg.maxTradeUsd}
                      min={1}
                      step={1}
                      precision={0}
                      onChange={(v) => {
                        const val = Math.max(1, Number(v));
                        setBotConfig(key, cfg.compoundSizingEnabled !== false && isWrappedStableMarket(key)
                          ? { compoundAbsoluteMaxTradeUsd: val, maxTradeUsd: val }
                          : { maxTradeUsd: val });
                      }}
                    >
                      <NumberInputField />
                    </NumberInput>
                    <Text fontSize="xs" color="gray.400">Default $500; the equity percentage cap remains dominant.</Text>
                  </Box>
                </SimpleGrid>
              )}

              {selectedChain === "arbitrum" && key === `${chainConfig.nativeCurrency.symbol}-${chainConfig.defaultStablecoin}` && (
                <Box mt={4}>
                  <Divider borderColor="gray.600" mb={3} />
                  <HStack justify="space-between" align="center" flexWrap="wrap" spacing={3}>
                    <HStack>
                      <Text fontSize="sm">External market advisor</Text>
                      <Switch
                        size="sm"
                        isChecked={cfg.externalSignalEnabled !== false}
                        onChange={(e) => setBotConfig(key, { externalSignalEnabled: e.target.checked })}
                      />
                    </HStack>
                    <HStack>
                      <Text fontSize="sm">Shadow mode</Text>
                      <Switch
                        size="sm"
                        isChecked={cfg.externalSignalShadowMode !== false}
                        isDisabled={cfg.externalSignalEnabled === false}
                        onChange={(e) => setBotConfig(key, { externalSignalShadowMode: e.target.checked })}
                      />
                    </HStack>
                    <HStack>
                      <Text fontSize="sm">Phase 1 sizing</Text>
                      <Switch
                        size="sm"
                        isChecked={cfg.externalSignalPhase1SizingEnabled === true}
                        isDisabled={cfg.externalSignalEnabled === false || cfg.externalSignalShadowMode !== false}
                        onChange={(e) => setBotConfig(key, { externalSignalPhase1SizingEnabled: e.target.checked })}
                      />
                    </HStack>
                    <HStack>
                      <Text fontSize="sm">Phase 2 timing</Text>
                      <Switch
                        size="sm"
                        isChecked={cfg.externalSignalPhase2ThresholdEnabled !== false}
                        isDisabled={cfg.externalSignalEnabled === false || cfg.externalSignalShadowMode !== false}
                        onChange={(e) => setBotConfig(key, { externalSignalPhase2ThresholdEnabled: e.target.checked })}
                      />
                    </HStack>
                    {(() => {
                      const signal = externalSignalServiceRef.current.getSnapshot();
                      const color = signal.status === "healthy" ? "green" : signal.status === "degraded" ? "orange" : "gray";
                      const cexVsPoolPct = price > 0 && signal.fairWethUsdc > 0
                        ? ((signal.fairWethUsdc / price) - 1) * 100
                        : null;
                      const cexComparison = cexVsPoolPct == null
                        ? "CEX comparison unavailable"
                        : Math.abs(cexVsPoolPct) < 0.005
                          ? `CEX $${safeFix(signal.fairWethUsdc)} · aligned`
                          : `CEX $${safeFix(signal.fairWethUsdc)} · ${Math.abs(cexVsPoolPct).toFixed(3)}% ${cexVsPoolPct > 0 ? "higher" : "lower"}`;
                      return (
                        <HStack spacing={2} flexWrap="wrap">
                          <Badge colorScheme={color}>
                            {signal.status} · {signal.sourceCount}/4 · {(signal.signalQuality * 100).toFixed(0)}%
                          </Badge>
                          <Tooltip label="Robust CEX fair price compared with the current Uniswap pool spot price.">
                            <Text fontSize="xs" color="gray.300">
                              {cexComparison}
                            </Text>
                          </Tooltip>
                        </HStack>
                      );
                    })()}
                  </HStack>
                  <SimpleGrid columns={[1, 4]} spacing={3} mt={3}>
                    <Box>
                      <Text fontSize="sm">Minimum size multiplier</Text>
                      <NumberInput
                        value={cfg.externalSignalMinSizeMultiplier ?? defaultBotCfg.externalSignalMinSizeMultiplier}
                        min={0.8}
                        max={1}
                        step={0.01}
                        precision={2}
                        onChange={(v) => setBotConfig(key, { externalSignalMinSizeMultiplier: Math.max(0.8, Math.min(1, Number(v))) })}
                      >
                        <NumberInputField />
                      </NumberInput>
                    </Box>
                    <Box>
                      <Text fontSize="sm">Maximum size multiplier</Text>
                      <NumberInput
                        value={cfg.externalSignalMaxSizeMultiplier ?? defaultBotCfg.externalSignalMaxSizeMultiplier}
                        min={1}
                        max={1.2}
                        step={0.01}
                        precision={2}
                        onChange={(v) => setBotConfig(key, { externalSignalMaxSizeMultiplier: Math.max(1, Math.min(1.2, Number(v))) })}
                      >
                        <NumberInputField />
                      </NumberInput>
                    </Box>
                    <Box>
                      <Text fontSize="sm">Minimum signal quality</Text>
                      <NumberInput
                        value={cfg.externalSignalMinQuality ?? defaultBotCfg.externalSignalMinQuality}
                        min={0.5}
                        max={0.95}
                        step={0.05}
                        precision={2}
                        onChange={(v) => setBotConfig(key, { externalSignalMinQuality: Math.max(0.5, Math.min(0.95, Number(v))) })}
                      >
                        <NumberInputField />
                      </NumberInput>
                    </Box>
                    <Box>
                      <Text fontSize="sm">Phase 2 max nudge</Text>
                      <NumberInput
                        value={(Number(cfg.externalSignalPhase2MaxAdjustmentPct ?? defaultBotCfg.externalSignalPhase2MaxAdjustmentPct) * 100)}
                        min={0}
                        max={10}
                        step={1}
                        precision={0}
                        onChange={(v) => setBotConfig(key, {
                          externalSignalPhase2MaxAdjustmentPct: Math.max(0, Math.min(10, Number(v))) / 100,
                        })}
                      >
                        <NumberInputField />
                      </NumberInput>
                    </Box>
                  </SimpleGrid>
                </Box>
              )}

              <ActionRow>
                <StyledActionButton colorScheme="green" onClick={() => forceBuyFor(key)}>
                  Force buy
                </StyledActionButton>
                <StyledActionButton colorScheme="red" onClick={() => forceSellFor(key)}>
                  Force sell
                </StyledActionButton>
                <StyledActionButton variant="outline" onClick={() => reanchorTargetsFor(key)}>
                  Reanchor grid
                </StyledActionButton>
              </ActionRow>
            </Box>

            {/* Recent Trades Quick View */}
            {(() => {
              const recentTrades = tradeHistory
                .filter(t => t.marketKey === key)
                .slice(0, 3)
                .reverse();
              
              if (recentTrades.length > 0) {
                return (
                  <Box mt={2} p={2} bg="blueAlpha.200" borderRadius="md" w="100%">
                    <Text fontSize="xs" color="blue.300" fontWeight="semibold" mb={1}>
                      📊 Recent Trades ({recentTrades.length})
                    </Text>
                    <VStack align="start" spacing={0.5}>
                      {recentTrades.map((trade, i) => (
                        <HStack key={i} spacing={2} fontSize="xs">
                          <Text color={trade.side === "BUY" ? "green.400" : "red.400"}>
                            {trade.side === "BUY" ? "🟢" : "🔴"}
                          </Text>
                          <Text color="gray.300">
                            {formatCompact(Number(trade.amount || 0), 4)} {trade.initiator || ""}
                          </Text>
                          {trade.txHash && (
                            <Text color="gray.500" fontSize="2xs">
                              {trade.txHash.slice(0, 8)}...
                            </Text>
                          )}
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                );
              }
              return null;
            })()}

            {(notes.length > 0 || showDebugUI) && (
              <InsightBox
                variant={notes.length === 0 ? "success" : "warn"}
                title={notes.length === 0 ? "Ready to trade" : "Why not trading?"}
              >
                {notes.length === 0 ? (
                  <Text fontSize="sm">All gates satisfied — awaiting price tick.</Text>
                ) : (
                  <VStack align="start" spacing={1}>
                    {notes.map((n, i) => (
                      <Text key={i} fontSize="sm" color="gray.300">
                        • {n}
                      </Text>
                    ))}
                  </VStack>
                )}
                {showDebugUI && price > 0 && (pos.holdings || 0) > 0 && (() => {
                  const buyPct = effectiveCfg.buyTarget ?? defaultBotCfg.buyTarget;
                  const sellPct = effectiveCfg.sellTarget ?? defaultBotCfg.sellTarget;
                  const scenarios = buildPriceScenarios({
                    holdings: pos.holdings || 0,
                    avgEntry: pos.avgEntry || 0,
                    currentPrice: price,
                    buyPct,
                    sellPct,
                    useSpotNow: spotGridDbg,
                  });
                  return (
                    <Box mt={2} p={2} bg="purpleAlpha.300" borderRadius="md" w="100%">
                      <Text fontSize="xs" color="purple.200" fontWeight="semibold" mb={1}>
                        📐 Price scenarios (holdings {formatCompact(pos.holdings)} WETH, entry {safeFix(pos.avgEntry)})
                      </Text>
                      <VStack align="stretch" spacing={1}>
                        {scenarios.map((s) => (
                          <Text key={s.price} fontSize="xs" color="gray.300">
                            ETH ${s.price}: value ${s.valueUsd.toFixed(0)} · uPnL {s.unrealized >= 0 ? "+" : ""}{s.unrealized.toFixed(2)} ·
                            sell@{safeFix(s.sellTarget)} ({s.canSell ? "✓ sell" : "✗ sell"}) ·
                            buy@{safeFix(s.buyTarget)} ({s.canBuy ? "✓ buy" : "✗ buy"})
                            {spotGridDbg ? "" : ` · spot sell would be ${safeFix(s.spotSell)}`}
                          </Text>
                        ))}
                      </VStack>
                      <Text fontSize="2xs" color="gray.500" mt={1}>
                        Active mode: {spotGridDbg ? "spot recovery grid" : "entry-anchored grid"}. avgEntry kept for PnL either way.
                      </Text>
                    </Box>
                  );
                })()}

                <Text mt={2} fontSize="xs" color="gray.500">
                  Counters — buy: {pos.buyCounter}, sell: {pos.sellCounter} · Holdings: {formatCompact(pos.holdings)} {(() => {
                    // Show what token the holdings represent
                    const wrappedSymbol = chainConfig?.nativeCurrency?.symbol || "ETH";
                    const stableSymbol = chainConfig?.defaultStablecoin || "USDC";
                    if (key === `${wrappedSymbol}-${stableSymbol}`) {
                      // For WETH-USDC.e: holdings = WETH, base = USDC.e
                      const wrappedDisplay = selectedChain === "arbitrum" ? "WETH" : 
                                           selectedChain === "cronos" ? "WCRO" :
                                           selectedChain === "ethereum" ? "WETH" :
                                           selectedChain === "bsc" ? "WBNB" :
                                           selectedChain === "polygon" ? "WMATIC" :
                                           selectedChain === "avalanche" ? "WAVAX" :
                                           `W${wrappedSymbol}`;
                      return `(${wrappedDisplay})`;
                    } else if (key.startsWith("WBTC-")) {
                      return "(WBTC)";
                    }
                    return "";
                  })()} · Base ({(() => {
                    // Show correct base token based on market key
                    const wrappedSymbol = chainConfig?.nativeCurrency?.symbol || "ETH";
                    const stableSymbol = chainConfig?.defaultStablecoin || "USDC";
                    if (key === `${wrappedSymbol}-${stableSymbol}` || key === `WBTC-${stableSymbol}`) {
                      // For WETH-USDC.e and WBTC-USDC.e: base is USDC.e
                      return stableSymbol;
                    } else {
                      // For WBTC-WETH: base is native token
                      return nativeSymbol;
                    }
                  })()}): {formatCompact(baseBalance)}
                </Text>
                
                {/* PnL Display */}
                {(() => {
                  const rawMarketPnl = pnlData[key];
                  const benchmarkMarket = isWrappedStableMarket(key);
                  if ((!rawMarketPnl || rawMarketPnl.totalTrades === 0) && !benchmarkMarket) return null;
                  const marketPnl = normalizePnlRecord(rawMarketPnl);
                  
                  const openPositionPnl = calculateUnrealizedPnL(key);
                  const reportedRealizedPnL =
                    marketPnl.realizedPnL - marketPnl.reportingRealizedPnlOffset;
                  const reportedGasUsd = Math.max(
                    0,
                    marketPnl.totalGasUsd - marketPnl.reportingGasUsdOffset
                  );
                  const liveBook = getMarketBook(key, pos, price, positionsRef.current, chainConfig);
                  const flowAdjusted = marketPnl.accountingCheckpointAt > 0
                    ? calculateFlowAdjustedPnl({
                        currentStableBalance: liveBook.baseBalance,
                        currentAssetBalance: Number(pos.holdings) || 0,
                        assetPrice: price,
                        baselineEquityUsd: marketPnl.accountingBaselineEquityUsd,
                        netDepositsUsd: marketPnl.netExternalDepositsUsd,
                        netWithdrawalsUsd: marketPnl.netExternalWithdrawalsUsd,
                        totalGasUsd: reportedGasUsd,
                      })
                    : null;
                  const totalPnL = flowAdjusted
                    ? flowAdjusted.pnlUsd
                    : reportedRealizedPnL + openPositionPnl - reportedGasUsd;
                  const unrealizedPnL = marketPnl.accountingCheckpointAt > 0
                    ? totalPnL - reportedRealizedPnL + reportedGasUsd
                    : openPositionPnl;
                  const pnlReferenceUsd = marketPnl.accountingCheckpointAt > 0
                    ? Math.max(
                        0,
                        marketPnl.accountingBaselineEquityUsd +
                          marketPnl.netExternalDepositsUsd -
                          marketPnl.netExternalWithdrawalsUsd
                      )
                    : marketPnl.costBasis;
                  const totalPnLPct = pnlReferenceUsd > 0
                    ? (totalPnL / pnlReferenceUsd) * 100
                    : 0;
                  const sizingCfg = getEffectiveBotCfg(key);
                  const nextBuySizing = benchmarkMarket
                    ? getCompoundSizingPlan({
                        key,
                        book: liveBook,
                        cfg: sizingCfg,
                        availableUsd: liveBook.baseBalance,
                      })
                    : null;
                  
                  const wrappedSymbol = chainConfig?.nativeCurrency?.symbol || "ETH";
                  const stableSymbol = chainConfig?.defaultStablecoin || "USDC";
                  const baseSymbol = (key === `${wrappedSymbol}-${stableSymbol}` || key === `WBTC-${stableSymbol}`) 
                    ? stableSymbol 
                    : nativeSymbol;
                  const benchmarkView = benchmarkMarket ? getWethBenchmarkResult(key, price) : null;
                  const benchmark = benchmarkView?.benchmark;
                  const benchmarkResult = benchmarkView?.result;
                  const benchmarkUnavailable = benchmarkMarket && benchmark && !benchmarkResult;
                  const benchmarkColor = !benchmarkResult
                    ? "gray.400"
                    : benchmarkResult.wethBenchmarkDelta >= 0
                      ? "green.400"
                      : "red.400";
                  const botVsHoldColor = !benchmarkResult
                    ? "gray.400"
                    : benchmarkResult.botVsWethHoldUsd >= 0
                      ? "green.400"
                      : "red.400";
                  
                  return (
                    <Box mt={2} p={2} bg="purpleAlpha.200" borderRadius="md" w="100%">
                      <Text fontSize="xs" color="purple.300" fontWeight="semibold" mb={1}>
                        Profit & Loss{marketPnl.accountingCheckpointAt > 0 ? " since checkpoint" : ""}
                      </Text>
                      <VStack align="start" spacing={1} fontSize="xs">
                        <HStack justify="space-between" w="100%">
                          <Text color="gray.300">Realized PnL:</Text>
                          <Text color={reportedRealizedPnL >= 0 ? "green.400" : "red.400"} fontWeight="semibold">
                            {reportedRealizedPnL >= 0 ? "+" : ""}{formatCompact(reportedRealizedPnL, 2)} {baseSymbol}
                          </Text>
                        </HStack>
                        <HStack justify="space-between" w="100%">
                          <Text color="gray.300">Unrealized PnL:</Text>
                          <Text color={unrealizedPnL >= 0 ? "green.400" : "red.400"} fontWeight="semibold">
                            {unrealizedPnL >= 0 ? "+" : ""}{formatCompact(unrealizedPnL, 2)} {baseSymbol}
                          </Text>
                        </HStack>
                        <HStack justify="space-between" w="100%">
                          <Text color="gray.400">Confirmed gas:</Text>
                          <Text color="gray.400">-{formatCompact(reportedGasUsd, 2)} {baseSymbol}</Text>
                        </HStack>
                        {marketPnl.accountingCheckpointAt > 0 && (
                          <>
                            <HStack justify="space-between" w="100%">
                              <Text color="gray.400">External deposits:</Text>
                              <Text color="gray.400">{formatCompact(marketPnl.netExternalDepositsUsd, 2)} {baseSymbol}</Text>
                            </HStack>
                            <HStack justify="space-between" w="100%">
                              <Text color="gray.400">External withdrawals:</Text>
                              <Text color="gray.400">{formatCompact(marketPnl.netExternalWithdrawalsUsd, 2)} {baseSymbol}</Text>
                            </HStack>
                          </>
                        )}
                        <HStack justify="space-between" w="100%" pt={1} borderTop="1px solid" borderColor="gray.600">
                          <Text color="gray.200" fontWeight="semibold">Net mark PnL:</Text>
                          <Text color={totalPnL >= 0 ? "green.400" : "red.400"} fontWeight="bold" fontSize="sm">
                            {totalPnL >= 0 ? "+" : ""}{formatCompact(totalPnL, 2)} {baseSymbol} ({totalPnLPct >= 0 ? "+" : ""}{totalPnLPct.toFixed(2)}%)
                          </Text>
                        </HStack>
                        {benchmarkMarket && (
                          <Box w="100%" mt={2} pt={2} borderTop="1px solid" borderColor="purple.700">
                            <HStack justify="space-between" w="100%" mb={1}>
                              <Text color="purple.200" fontWeight="semibold">WETH Benchmark</Text>
                              <HStack>
                                <Button size="xs" colorScheme="purple" onClick={() => startWethBenchmark(key)}>
                                  Start
                                </Button>
                                <Button size="xs" variant="outline" colorScheme="purple" onClick={() => resetWethBenchmark(key)}>
                                  Reset
                                </Button>
                              </HStack>
                            </HStack>
                            {!benchmark ? (
                              <Text color="gray.400" fontSize="2xs">No benchmark started.</Text>
                            ) : benchmarkUnavailable ? (
                              <Text color="gray.400" fontSize="2xs">Benchmark unavailable. Missing current WETH price or book value.</Text>
                            ) : (
                              <VStack align="start" spacing={1} w="100%">
                                <HStack justify="space-between" w="100%">
                                  <Text color="gray.400" fontSize="2xs">Started:</Text>
                                  <Text color="gray.400" fontSize="2xs">{new Date(benchmark.startedAt).toLocaleString()}</Text>
                                </HStack>
                                <HStack justify="space-between" w="100%">
                                  <Text color="gray.300">Initial total:</Text>
                                  <Text color="gray.100">{formatCompact(benchmark.initialTotalUsd, 2)} {stableSymbol}</Text>
                                </HStack>
                                <HStack justify="space-between" w="100%">
                                  <Text color="gray.300">Initial WETH price:</Text>
                                  <Text color="gray.100">{formatCompact(benchmark.initialWethPrice, 2)} {stableSymbol}</Text>
                                </HStack>
                                <HStack justify="space-between" w="100%">
                                  <Text color="gray.300">Initial WETH eq:</Text>
                                  <Text color="gray.100">{formatCompact(benchmarkResult.initialTotalValueInWeth, 6)} WETH</Text>
                                </HStack>
                                <HStack justify="space-between" w="100%">
                                  <Text color="gray.300">Current total:</Text>
                                  <Text color="gray.100">{formatCompact(benchmarkView.snapshot.currentTotalUsd, 2)} {stableSymbol}</Text>
                                </HStack>
                                <HStack justify="space-between" w="100%">
                                  <Text color="gray.300">Current WETH eq:</Text>
                                  <Text color="gray.100">{formatCompact(benchmarkResult.currentTotalValueInWeth, 6)} WETH</Text>
                                </HStack>
                                <HStack justify="space-between" w="100%">
                                  <Text color="gray.200" fontWeight="semibold">WETH PnL:</Text>
                                  <Text color={benchmarkColor} fontWeight="bold">
                                    {benchmarkResult.wethBenchmarkDelta >= 0 ? "+" : ""}
                                    {formatCompact(benchmarkResult.wethBenchmarkDelta, 6)} WETH ({benchmarkResult.wethBenchmarkDeltaPct >= 0 ? "+" : ""}
                                    {(benchmarkResult.wethBenchmarkDeltaPct * 100).toFixed(2)}%)
                                  </Text>
                                </HStack>
                                <HStack justify="space-between" w="100%">
                                  <Text color="gray.200" fontWeight="semibold">Bot vs WETH hold:</Text>
                                  <Text color={botVsHoldColor} fontWeight="bold">
                                    {benchmarkResult.botVsWethHoldUsd >= 0 ? "+" : ""}
                                    {formatCompact(benchmarkResult.botVsWethHoldUsd, 2)} {stableSymbol} ({benchmarkResult.botVsWethHoldPct >= 0 ? "+" : ""}
                                    {(benchmarkResult.botVsWethHoldPct * 100).toFixed(2)}%)
                                  </Text>
                                </HStack>
                              </VStack>
                            )}
                          </Box>
                        )}
                        <HStack justify="space-between" w="100%" mt={1}>
                          <Text color="gray.400" fontSize="2xs">Cost Basis:</Text>
                          <Text color="gray.400" fontSize="2xs">{formatCompact(marketPnl.costBasis, 2)} {baseSymbol}</Text>
                        </HStack>
                        <HStack justify="space-between" w="100%">
                          <Text color="gray.400" fontSize="2xs">Trades:</Text>
                          <Text color="gray.400" fontSize="2xs">{marketPnl.buyCount} buys, {marketPnl.sellCount} sells</Text>
                        </HStack>
                        {marketPnl.accountingCheckpointAt > 0 && (
                          <HStack justify="space-between" w="100%">
                            <Text color="gray.400" fontSize="2xs">Checkpoint:</Text>
                            <Text color="gray.400" fontSize="2xs">
                              {new Date(marketPnl.accountingCheckpointAt).toLocaleString()}
                            </Text>
                          </HStack>
                        )}
                        {nextBuySizing && sizingCfg.compoundSizingEnabled !== false && (
                          <HStack justify="space-between" w="100%">
                            <Text color="gray.400" fontSize="2xs">Next base buy:</Text>
                            <Text color={nextBuySizing.allowed ? "cyan.300" : "orange.300"} fontSize="2xs">
                              {nextBuySizing.allowed
                                ? `${formatCompact(nextBuySizing.baseTradeUsd, 2)} ${baseSymbol} (cap ${formatCompact(nextBuySizing.hardMaxUsd, 2)}${nextBuySizing.minimumRelaxed ? "; min relaxed" : ""})`
                                : "No spendable amount under cap"}
                            </Text>
                          </HStack>
                        )}
                        {marketPnl.accountingStatus && (
                          <Text color="gray.500" fontSize="2xs">Accounting: {marketPnl.accountingStatus}</Text>
                        )}
                      </VStack>
                    </Box>
                  );
                })()}
              </InsightBox>
            )}
        </MarketCard>
      );
    })}
      </VStack>

<Panel title="Custom markets" subtitle="Add ERC20 pairs beyond defaults" accent="purple">
  <HStack spacing={4}>
    <Input
      placeholder={`ERC20 Token Address (${chainConfig?.nativeCurrency?.symbol || "Native"} pair)`}
      value={manualTokenAddr}
      onChange={(e) => setManualTokenAddr(e.target.value)}
      bg="gray.800"
      borderColor="gray.600"
    />
    <Button
      colorScheme="teal"
      onClick={() => {
        addCustomBot(manualTokenAddr.trim());
        setManualTokenAddr("");
      }}
    >
      ➕ Add Bot
    </Button>
  </HStack>

  {customBots.length > 0 && (
    <Box mt={4}>
      <Text fontSize="sm" color="gray.400" mb={2}>Custom Bots:</Text>
      {customBots.map(bot => (
        <HStack key={bot.key} spacing={8} wrap="wrap">
          <Text>{bot.key}</Text>
          <Text fontSize="xs" color="gray.500">Pair: {bot.pairAddress}</Text>
          <Button size="sm" colorScheme="red" onClick={() => removeCustomBot(bot.key)}>❌ Remove</Button>
        </HStack>
      ))}
    </Box>
  )}
</Panel>

<Panel title="Global strategy" subtitle="Defaults applied to all markets · use Apply to push live" accent="cyan">
  <HStack spacing={4} align="flex-end" flexWrap="wrap" mb={4}>
    <Box minW="220px">
      <Text fontSize="sm" mb={1}>Strategy preset</Text>
      <Select
        value={selectedTemplate}
        onChange={(e) => setSelectedTemplate(e.target.value)}
        bg="gray.800"
        borderColor="gray.600"
      >
        {Object.entries(TEMPLATE_LABELS).map(([key, label]) => (
          <option key={key} value={key}>
            {label}
          </option>
        ))}
      </Select>
    </Box>

    <Box minW="260px">
      <Text fontSize="sm" mb={1}>Recommended</Text>
      <Badge
        colorScheme={
          recommendedTemplate === "aggressive"
            ? "green"
            : recommendedTemplate === "balanced"
            ? "yellow"
            : "red"
        }
        variant="subtle"
        fontSize="sm"
        px={3}
        py={1}
        mb={2}
      >
        {TEMPLATE_LABELS[recommendedTemplate] || "Balanced"}
      </Badge>
      <Text fontSize="xs" color="gray.400" maxW="320px" lineHeight="tall">
        {BOT_TEMPLATES[recommendedTemplate]?.description}
      </Text>
    </Box>

    <Button size="md" colorScheme="teal" onClick={() => applyTemplateToLocalSettings(selectedTemplate)}>
      Apply preset
    </Button>

    <Checkbox
      isChecked={autoApplyRecommendedTemplate}
      onChange={(e) => setAutoApplyRecommendedTemplate(e.target.checked)}
      colorScheme="cyan"
    >
      Auto apply
    </Checkbox>

    <Checkbox
      isChecked={expertMode}
      onChange={(e) => setExpertMode(e.target.checked)}
      colorScheme="cyan"
    >
      Expert mode
    </Checkbox>
  </HStack>

  {!expertMode && (
    <InsightBox variant="info" title="Standard mode">
      <Text fontSize="sm" color="gray.300">
        Recommended preset is applied automatically when enough price data is available. Enable expert mode to tune the full strategy manually.
      </Text>
    </InsightBox>
  )}

  <SimpleGrid columns={[1, 2, 4]} spacing={4} w="100%">
    <Box>
      <Tooltip label="Static keeps your current fixed thresholds. Adaptive adjusts thresholds from recent intraday volatility." hasArrow>
        <Text fontSize="sm" cursor="help">🧠 Strategy Mode</Text>
      </Tooltip>
      <Select
        value={strategyMode}
        onChange={(e) => setStrategyMode(e.target.value)}
        bg="gray.800"
        borderColor="gray.600"
      >
        <option value="static">Static thresholds</option>
        <option value="adaptive">Adaptive thresholds</option>
      </Select>
    </Box>

    {strategyMode === "adaptive" && (
      <>
        <Box>
          <Tooltip label="Base buy distance below anchor before volatility adjustment. 0.0014 = 0.14%." hasArrow>
            <Text fontSize="sm" cursor="help">Adaptive Base Move</Text>
          </Tooltip>
          <NumberInput value={adaptiveBaseMovePct} step={0.0001} precision={4} min={0.0005} max={0.01} onChange={(v) => setAdaptiveBaseMovePct(parseFloat(v) || defaultBotCfg.adaptiveBaseMovePct)}>
            <NumberInputField />
          </NumberInput>
        </Box>
        <Box>
          <Tooltip label="Extra sell distance above the adaptive buy distance. 0.0004 = +0.04%." hasArrow>
            <Text fontSize="sm" cursor="help">Adaptive Sell Extra</Text>
          </Tooltip>
          <NumberInput value={adaptiveSellExtraPct} step={0.0001} precision={4} min={0} max={0.01} onChange={(v) => setAdaptiveSellExtraPct(parseFloat(v) || 0)}>
            <NumberInputField />
          </NumberInput>
        </Box>
        <Box>
          <Tooltip label="How much recent 1h price range widens thresholds. 0.08 means 8% of the recent range." hasArrow>
            <Text fontSize="sm" cursor="help">Vol Multiplier</Text>
          </Tooltip>
          <NumberInput value={adaptiveVolMultiplier} step={0.01} precision={2} min={0} max={1} onChange={(v) => setAdaptiveVolMultiplier(parseFloat(v) || 0)}>
            <NumberInputField />
          </NumberInput>
        </Box>
      </>
    )}
  </SimpleGrid>

  {expertMode ? (
    <>
      <SettingsBlock
        title="Hybrid grid (spot recovery)"
        description="When underwater or USDC is low, targets re-center on spot so the bot can sell/buy near market. avgEntry stays for PnL."
        accent="purple"
        icon={MdGridOn}
        enabled={hybridGridEnabled}
        onToggle={(e) => setHybridGridEnabled(e.target.checked)}
  >
    <SimpleGrid columns={[1, 2, 3]} spacing={3}>
      <FormField label="Min USDC reserve %" hint="Blocks buys below this share of USDC+WETH book (default 25%)">
        <NumberInput
          value={(minUsdcReservePct * 100).toFixed(0)}
          min={10}
          max={50}
          step={5}
          onChange={(v) => setMinUsdcReservePct((Number(v) || 25) / 100)}
        >
          <NumberInputField {...fieldInputProps} />
        </NumberInput>
      </FormField>
      <FormField label="Underwater → spot grid %" hint="Switch when price is this far below avgEntry (default 3%)">
        <NumberInput
          value={(underwaterSpotPct * 100).toFixed(1)}
          min={1}
          max={15}
          step={0.5}
          onChange={(v) => setUnderwaterSpotPct((Number(v) || 3) / 100)}
        >
          <NumberInputField {...fieldInputProps} />
        </NumberInput>
      </FormField>
    </SimpleGrid>
  </SettingsBlock>

  <SettingsBlock
    title="Adaptive regime engine"
    description="Multi-timeframe metrics (15m–24h), market regime, RECOVERY_BREAKOUT re-entry, and exposure/reserve caps. Off = legacy 1h adaptive only."
    accent="purple"
    icon={MdGridOn}
    enabled={regimeEngineEnabled}
    onToggle={(e) => setRegimeEngineEnabled(e.target.checked)}
  >
    <Text fontSize="xs" color="gray.400">
      Regimes: BULL_ACTIVE · RECOVERY_BREAKOUT · NORMAL_CHOP · DEFENSIVE · CAPITAL_PROTECTION. Does not bypass
      30m recovery-grid buy cooldown.
    </Text>
  </SettingsBlock>

  <SettingsBlock
    title="Buy protection (anti-overbuy)"
    description="Blocks aggressive BUY ladders after recovery or consecutive buys. SELLs are never blocked. Manual Quick Buy bypasses these rules."
    accent="cyan"
    icon={FiShield}
    enabled={buyProtectionEnabled}
    onToggle={(e) => setBuyProtectionEnabled(e.target.checked)}
  >
    <SimpleGrid columns={[1, 2, 4]} spacing={3}>
      <FormField label="Recovery buy cooldown (minutes)" hint="After RECOVERY_GRID, block auto-buys for this long">
        <NumberInput
          value={recoveryBuyCooldownMinutes}
          min={5}
          max={240}
          step={5}
          precision={0}
          onChange={(v) => setRecoveryBuyCooldownMinutes(Number(v) || defaultBotCfg.recoveryBuyCooldownMs / (60 * 1000))}
        >
          <NumberInputField {...fieldInputProps} />
        </NumberInput>
      </FormField>
      <FormField label="Max consecutive buys" hint="5th+ blocked until a SELL">
        <NumberInput
          value={buyProtectionMaxConsecutiveBuys}
          min={2}
          max={10}
          step={1}
          precision={0}
          onChange={(v) => setBuyProtectionMaxConsecutiveBuys(parseInt(v || "4", 10))}
        >
          <NumberInputField {...fieldInputProps} />
        </NumberInput>
      </FormField>
      <FormField label="Major dip for 4th buy (%)" hint="Price must be this far below last buy">
        <NumberInput
          value={(buyProtectionMajorDipFourthBuyPct * 100).toFixed(2)}
          min={0.1}
          max={5}
          step={0.05}
          precision={2}
          onChange={(v) => setBuyProtectionMajorDipFourthBuyPct((Number(v) || 0.75) / 100)}
        >
          <NumberInputField {...fieldInputProps} />
        </NumberInput>
      </FormField>
      <FormField label="High-vol major dip (%)" hint="Used when 1h range is elevated">
        <NumberInput
          value={(buyProtectionMajorDipHighVolPct * 100).toFixed(2)}
          min={0.1}
          max={5}
          step={0.05}
          precision={2}
          onChange={(v) => setBuyProtectionMajorDipHighVolPct((Number(v) || 1) / 100)}
        >
          <NumberInputField {...fieldInputProps} />
        </NumberInput>
      </FormField>
    </SimpleGrid>
  </SettingsBlock>

  <SettingsBlock
    title="Drawdown stop-loss (50/50 rebalance)"
    description="When unrealized drawdown hits your %, sells excess WETH to reach target WETH/USDC split, resets grid to spot, keeps PnL and avgEntry."
    accent="red"
    icon={FiShield}
    enabled={stopLossEnabled}
    onToggle={(e) => setStopLossEnabled(e.target.checked)}
  >
    <SimpleGrid columns={[1, 2, 4]} spacing={3}>
      <FormField label="Drawdown trigger %" hint="Below avgEntry (default 5%)">
        <NumberInput
          value={(stopLossDrawdownPct * 100).toFixed(1)}
          min={1}
          max={25}
          step={0.5}
          onChange={(v) => setStopLossDrawdownPct((Number(v) || 5) / 100)}
        >
          <NumberInputField {...fieldInputProps} />
        </NumberInput>
      </FormField>
      <FormField label="Target WETH % of book" hint="50 = half WETH, half USDC">
        <NumberInput
          value={(stopLossTargetWethPct * 100).toFixed(0)}
          min={30}
          max={70}
          step={5}
          onChange={(v) => setStopLossTargetWethPct((Number(v) || 50) / 100)}
        >
          <NumberInputField {...fieldInputProps} />
        </NumberInput>
      </FormField>
      <FormField label="Cooldown (hours)" hint="Min time between stop-loss sells">
        <NumberInput
          value={stopLossCooldownHours}
          min={1}
          max={72}
          step={1}
          precision={0}
          onChange={(v) => setStopLossCooldownHours(Number(v) || defaultBotCfg.stopLossCooldownHours)}
        >
          <NumberInputField {...fieldInputProps} />
        </NumberInput>
      </FormField>
    </SimpleGrid>
  </SettingsBlock>

  <SettingsBlock
    title="Stuck-position stop"
    description="Triggers after inactivity + underwater WETH inventory. Uses % loss or $ loss, whichever hits first, to free USDC without killing normal mean-reversion trades."
    accent="orange"
    icon={FiAlertTriangle}
    enabled={stuckStopEnabled}
    onToggle={(e) => setStuckStopEnabled(e.target.checked)}
  >
    <SimpleGrid columns={[1, 2, 4]} spacing={3}>
      <Box>
        <Text fontSize="sm">Soft Stop: hours / loss% / $</Text>
        <HStack>
          <NumberInput value={softStuckHours} min={1} max={168} step={1} precision={0} onChange={(v) => setSoftStuckHours(Number(v) || defaultBotCfg.softStuckHours)}>
            <NumberInputField />
          </NumberInput>
          <NumberInput value={(softStuckLossPct * 100).toFixed(2)} min={0.1} max={20} step={0.1} precision={2} onChange={(v) => setSoftStuckLossPct((Number(v) || defaultBotCfg.softStuckLossPct * 100) / 100)}>
            <NumberInputField />
          </NumberInput>
          <NumberInput value={softStuckLossUsd} min={0} max={1000} step={1} precision={2} onChange={(v) => setSoftStuckLossUsd(Number(v) || defaultBotCfg.softStuckLossUsd)}>
            <NumberInputField />
          </NumberInput>
        </HStack>
        <Text fontSize="xs" color="gray.400">default 24h / 1.5% / dynamic history floor</Text>
      </Box>
      <Box>
        <Text fontSize="sm">Hard Stop: hours / loss% / $</Text>
        <HStack>
          <NumberInput value={hardStuckHours} min={1} max={168} step={1} precision={0} onChange={(v) => setHardStuckHours(Number(v) || defaultBotCfg.hardStuckHours)}>
            <NumberInputField />
          </NumberInput>
          <NumberInput value={(hardStuckLossPct * 100).toFixed(2)} min={0.1} max={30} step={0.1} precision={2} onChange={(v) => setHardStuckLossPct((Number(v) || defaultBotCfg.hardStuckLossPct * 100) / 100)}>
            <NumberInputField />
          </NumberInput>
          <NumberInput value={hardStuckLossUsd} min={0} max={1000} step={1} precision={2} onChange={(v) => setHardStuckLossUsd(Number(v) || defaultBotCfg.hardStuckLossUsd)}>
            <NumberInputField />
          </NumberInput>
        </HStack>
        <Text fontSize="xs" color="gray.400">default 36h / 2.5% / dynamic history floor</Text>
      </Box>
      <Box>
        <Text fontSize="sm">Emergency: hours / loss% / $</Text>
        <HStack>
          <NumberInput value={emergencyStuckHours} min={1} max={240} step={1} precision={0} onChange={(v) => setEmergencyStuckHours(Number(v) || defaultBotCfg.emergencyStuckHours)}>
            <NumberInputField />
          </NumberInput>
          <NumberInput value={(emergencyStuckLossPct * 100).toFixed(2)} min={0.1} max={50} step={0.1} precision={2} onChange={(v) => setEmergencyStuckLossPct((Number(v) || defaultBotCfg.emergencyStuckLossPct * 100) / 100)}>
            <NumberInputField />
          </NumberInput>
          <NumberInput value={emergencyStuckLossUsd} min={0} max={1000} step={1} precision={2} onChange={(v) => setEmergencyStuckLossUsd(Number(v) || defaultBotCfg.emergencyStuckLossUsd)}>
            <NumberInputField />
          </NumberInput>
        </HStack>
        <Text fontSize="xs" color="gray.400">default 48h / 4.0% / dynamic history floor</Text>
      </Box>
      <Box>
        <Text fontSize="sm">Soft Sell %</Text>
        <NumberInput value={(softStuckSellPct * 100).toFixed(0)} min={1} max={100} step={1} precision={0} onChange={(v) => setSoftStuckSellPct((Number(v) || 33) / 100)}>
          <NumberInputField />
        </NumberInput>
      </Box>
      <Box>
        <Text fontSize="sm">Hard Sell %</Text>
        <NumberInput value={(hardStuckSellPct * 100).toFixed(0)} min={1} max={100} step={1} precision={0} onChange={(v) => setHardStuckSellPct((Number(v) || 66) / 100)}>
          <NumberInputField />
        </NumberInput>
      </Box>
      <Box>
        <Text fontSize="sm">Emergency Sell %</Text>
        <NumberInput value={(emergencyStuckSellPct * 100).toFixed(0)} min={1} max={100} step={1} precision={0} onChange={(v) => setEmergencyStuckSellPct((Number(v) || 100) / 100)}>
          <NumberInputField {...fieldInputProps} />
        </NumberInput>
      </Box>
    </SimpleGrid>
  </SettingsBlock>
    </>
  ) : (
    <Box p={4} bg="blackAlpha.300" border="1px solid" borderColor="gray.600" borderRadius="lg" mb={4}>
      <Text fontSize="sm" color="gray.300" mb={2}>
        Expert mode is off. Standard mode keeps the recommended preset and core strategy controls streamlined.
      </Text>
      <Text fontSize="xs" color="gray.500">
        Turn on expert mode to access the full advanced risk and trade tuning panel.
      </Text>
    </Box>
  )}

  <Divider my={5} borderColor="whiteAlpha.200" />
  <Text fontSize="sm" color="gray.400" mb={3}>
    Static buy/sell multipliers — push to all markets with Apply below
  </Text>

  <HStack align="start" spacing={8} flexWrap="wrap">
    <Box>
      <Tooltip label="Multiplier to determine when to buy (e.g. 0.99 = buy if price drops 1%)" hasArrow>
        <Text fontSize="sm" cursor="help">🟢 Buy Target ({(buyTarget * 100).toFixed(2)}%)</Text>
      </Tooltip>
      <HStack>
        <NumberInput
          value={buyTarget}
          step={0.001}
          precision={4}
          min={0.7}
          max={1}
          onChange={(v) => setBuyTarget(parseFloat(v))}
        >
          <NumberInputField />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
      </HStack>
    </Box>

    <Box>
      <Tooltip label="Multiplier to determine when to sell (e.g. 1.01 = sell if price rises 1%)" hasArrow>
        <Text fontSize="sm" cursor="help">🔴 Sell Target ({(sellTarget * 100).toFixed(2)}%)</Text>
      </Tooltip>
      <HStack>
        <NumberInput
          value={sellTarget}
          step={0.001}
          precision={4}
          min={1}
          max={1.5}
          onChange={(v) => setSellTarget(parseFloat(v))}
        >
          <NumberInputField />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
      </HStack>
    </Box>
    <StyledActionButton
      size="md"
      leftIcon={<FiZap />}
      onClick={() => {
        const globalPatch = {
          buyTarget,
          sellTarget,
          strategyMode,
          adaptiveBaseMovePct,
          adaptiveSellExtraPct,
          adaptiveVolMultiplier,
          stuckStopEnabled,
          maxConsecutiveBuys,
          softStuckHours,
          softStuckLossPct,
          softStuckLossUsd,
          softStuckSellPct,
          hardStuckHours,
          hardStuckLossPct,
          hardStuckLossUsd,
          hardStuckSellPct,
          emergencyStuckHours,
          emergencyStuckLossPct,
          emergencyStuckLossUsd,
          emergencyStuckSellPct,
          hybridGridEnabled,
          minUsdcReservePct,
          underwaterSpotPct,
          stopLossEnabled,
          stopLossDrawdownPct,
          stopLossTargetWethPct,
          stopLossCooldownHours,
          buyProtectionEnabled,
          recoveryBuyCooldownMs: Math.max(1, Number(recoveryBuyCooldownMinutes) || 30) * 60 * 1000,
          buyProtectionMaxConsecutiveBuys,
          buyProtectionMajorDipFourthBuyPct,
          buyProtectionMajorDipHighVolPct,
          regimeEngineEnabled,
        };

        saveBotConfigs(prev => {
          const next = { ...prev };
          Object.keys(next).forEach(k => {
            next[k] = { ...defaultBotCfg, ...next[k], ...globalPatch };
          });
          return next;
        });

        // Re-anchor visible targets immediately so the selected strategy takes effect now,
        // instead of waiting for the next buy/sell cycle.
        savePositions(prev => {
          const next = { ...prev };
          markets.forEach(({ key, price }) => {
            if (!(price > 0)) return;
            const pos = next[key] || {};
            const mergedCfg = { ...defaultBotCfg, ...(botConfigs[key] || {}), ...globalPatch };
            const recentRangePct = getRecentRangePct(key);
            let effBuy = mergedCfg.buyTarget ?? defaultBotCfg.buyTarget;
            let effSell = mergedCfg.sellTarget ?? defaultBotCfg.sellTarget;
            if (mergedCfg.strategyMode === "adaptive") {
              const buyMove = clampNumber(
                (mergedCfg.adaptiveBaseMovePct || defaultBotCfg.adaptiveBaseMovePct) + recentRangePct * (mergedCfg.adaptiveVolMultiplier || defaultBotCfg.adaptiveVolMultiplier),
                mergedCfg.adaptiveMinMovePct || defaultBotCfg.adaptiveMinMovePct,
                mergedCfg.adaptiveMaxMovePct || defaultBotCfg.adaptiveMaxMovePct
              );
              const sellMove = buyMove + (mergedCfg.adaptiveSellExtraPct || 0);
              effBuy = 1 - buyMove;
              effSell = 1 + sellMove;
            }
            const bookApply = getMarketBook(key, pos, price, next, chainConfig);
            const useSpotApply = shouldUseSpotGridMode({
              cfg: mergedCfg,
              pos,
              price,
              baseBalanceUsd: bookApply.baseBalance,
              totalBookUsd: bookApply.totalBookUsd,
            });
            const tApply = computeGridTargets({
              price,
              avgEntry: pos.avgEntry,
              buyPct: effBuy,
              sellPct: effSell,
              useSpotAnchor: useSpotApply,
            });
            next[key] = {
              ...pos,
              priceTargetBuy: tApply.priceTargetBuy,
              priceTargetSell: tApply.priceTargetSell,
            };
          });
          return next;
        });
        markets.forEach(({ key, price }) => {
          if (price > 0) {
            const pos = positions[key] || {};
            const mergedCfg = { ...defaultBotCfg, ...(botConfigs[key] || {}), ...globalPatch };
            const bookApply = getMarketBook(key, pos, price, positions, chainConfig);
            const useSpotApply = shouldUseSpotGridMode({
              cfg: mergedCfg,
              pos,
              price,
              baseBalanceUsd: bookApply.baseBalance,
              totalBookUsd: bookApply.totalBookUsd,
            });
            let effBuy = mergedCfg.buyTarget ?? defaultBotCfg.buyTarget;
            let effSell = mergedCfg.sellTarget ?? defaultBotCfg.sellTarget;
            if (mergedCfg.strategyMode === "adaptive") {
              const recentRangePct = getRecentRangePct(key);
              const buyMove = clampNumber(
                (mergedCfg.adaptiveBaseMovePct || defaultBotCfg.adaptiveBaseMovePct) +
                  recentRangePct * (mergedCfg.adaptiveVolMultiplier || defaultBotCfg.adaptiveVolMultiplier),
                mergedCfg.adaptiveMinMovePct || defaultBotCfg.adaptiveMinMovePct,
                mergedCfg.adaptiveMaxMovePct || defaultBotCfg.adaptiveMaxMovePct
              );
              effBuy = 1 - buyMove;
              effSell = 1 + buyMove + (mergedCfg.adaptiveSellExtraPct || 0);
            }
            const tApply = computeGridTargets({
              price,
              avgEntry: pos.avgEntry,
              buyPct: effBuy,
              sellPct: effSell,
              useSpotAnchor: useSpotApply,
            });
            emitBotResetDiscord(key, "APPLY_GLOBAL_CONFIG", {
              buyTarget: tApply.priceTargetBuy,
              sellTarget: tApply.priceTargetSell,
              details: "Global bot settings applied — targets recalculated",
              cooldownMs: 5 * 60 * 1000,
            });
          }
        });
      }}
    >
      Apply to all markets
    </StyledActionButton>
  </HStack>

  <Divider my={4} borderColor="whiteAlpha.200" />
  <Text fontSize="sm" color="gray.400" mb={2}>
    Transaction defaults
  </Text>
  <SimpleGrid columns={[1, 2, 3]} spacing={4}>
    <FormField label="Max fee (gwei)" hint="Base gas cap">
      <NumberInput
        value={parseFloat(maxFeePerGasAmount)}
        step={100}
        onChange={(v) => setMaxFeePerGasAmount(v)}
      >
        <NumberInputField {...fieldInputProps} />
      </NumberInput>
    </FormField>
    <FormField label="Priority fee (gwei)" hint="Miner tip">
      <NumberInput
        value={parseFloat(maxPriorityFeePerGasAmount)}
        step={10}
        onChange={(v) => setMaxPriorityFeePerGasAmount(v)}
      >
        <NumberInputField {...fieldInputProps} />
      </NumberInput>
    </FormField>
    <FormField label="Slippage" hint="Max price deviation">
      <NumberInput
        value={slippagePercent}
        step={0.001}
        precision={4}
        onChange={(v) => setSlippagePercent(parseFloat(v))}
      >
        <NumberInputField {...fieldInputProps} />
      </NumberInput>
    </FormField>
  </SimpleGrid>
</Panel>

<Panel title="Execution & snipers" subtitle="Gas, listeners, and legacy sniper modes" accent="purple">

  <HStack spacing={4} mb={4} align="start" flexWrap="wrap">
    <Box minW="220px">
      <Text fontSize="sm" mb={1}>Mode</Text>
      <Select
        value={sniperMode}
        onChange={(e) => setSniperMode(e.target.value)}
        bg="black"
        borderColor="gray.600"
      >
        <option value="ambush">Ambush</option>
        <option value="reckless">Reckless</option>
      </Select>
    </Box>

    <Box minW="260px">
      <Text fontSize="sm" mb={1}>Ticker Filter (optional, substring)</Text>
      <Input
        placeholder="e.g. WBTC"
        value={sniperMode === "ambush" ? ambushTickerFilter : recklessTickerFilter}
        onChange={(e) =>
          sniperMode === "ambush"
            ? setAmbushTickerFilter(e.target.value)
            : setRecklessTickerFilter(e.target.value)
        }
        bg="gray.800"
        borderColor="gray.600"
      />
    </Box>

    <Box minW="220px">
      <Text fontSize="sm" mb={1}>Min WCRO Liquidity</Text>
      <NumberInput
        value={sniperMode === "ambush" ? ambushMinWcro : recklessMinWcro}
        min={0}
        step={10}
        onChange={(v) =>
          sniperMode === "ambush"
            ? setAmbushMinWcro(parseFloat(v))
            : setRecklessMinWcro(parseFloat(v))
        }
      >
        <NumberInputField />
      </NumberInput>
    </Box>

    <Box minW="220px">
      <Text fontSize="sm" mb={1}>Auto-Buy Size (CRO)</Text>
      <NumberInput
        value={sniperMode === "ambush" ? ambushAutoBuyWcro : recklessAutoBuyWcro}
        min={0}
        step={0.1}
        precision={3}
        onChange={(v) =>
          sniperMode === "ambush"
            ? setAmbushAutoBuyWcro(parseFloat(v))
            : setRecklessAutoBuyWcro(parseFloat(v))
        }
      >
        <NumberInputField />
      </NumberInput>
    </Box>

    <Box minW="220px">
      <Text fontSize="sm" mb={1}>Cooldown (ms)</Text>
      <NumberInput
        value={sniperMode === "ambush" ? ambushCooldownMs : recklessCooldownMs}
        min={0}
        step={500}
        onChange={(v) =>
          sniperMode === "ambush"
            ? setAmbushCooldownMs(parseInt(v || "0", 10))
            : setRecklessCooldownMs(parseInt(v || "0", 10))
        }
      >
        <NumberInputField />
      </NumberInput>
    </Box>

    {sniperMode === "ambush" && (
      <Box minW="300px">
        <Text fontSize="sm" mb={1}>Target Token (optional direct address)</Text>
        <Input
          value={ambushToken}
          onChange={(e) => setAmbushToken(e.target.value)}
          placeholder="0x… (leave empty to listen for new pairs)"
          bg="gray.800"
          borderColor="gray.600"
        />
      </Box>
    )}
  </HStack>

  <HStack spacing={4} mt={2} flexWrap="wrap">
    <Button
      colorScheme="purple"
      onClick={async () => {
        if (sniperMode === "ambush") {
          await startAmbushSniper();
          setIsListening(true);
        } else {
          await startRecklessSniper();
          setIsListening(true);
        }
      }}
    >
      🎯 Start {sniperMode === "ambush" ? "Ambush" : "Reckless"} Sniper
    </Button>

    <Button
      colorScheme="orange"
      variant="outline"
      onClick={() => {
        if (sniperMode === "ambush") stopAmbushSniper();
        else stopRecklessSniper();
      }}
    >
      ⏹ Stop {sniperMode === "ambush" ? "Ambush" : "Reckless"}
    </Button>

    <Button
      onClick={() => {
        stopSnipers();
        setIsListening(false);
      }}
    >
      🧹 Stop All + Clear Listeners
    </Button>
  </HStack>

  <Text fontSize="xs" color="gray.500" mt={4}>
    NEW Grid quick actions (refresh, buy, sell, listeners) are in the Command deck at the top of the page.
  </Text>
</Panel>

<Panel title="Manual trade" subtitle="One-off swap outside grid triggers" accent="orange">

  <HStack spacing={6} flexWrap="wrap">
    <Box minW="320px">
      <Text fontSize="sm">Token Address</Text>
      <Input
        placeholder="0x..."
        value={manualTokenAddr}
        onChange={(e) => setManualTokenAddr(e.target.value)}
        bg="gray.800"
        borderColor="gray.600"
      />
    </Box>

    <Box minW="160px">
      <Text fontSize="sm">Pay With (Initiator)</Text>
      <Select
        value={manualInitiator}
        onChange={(e) => setManualInitiator(e.target.value)}
        bg="black"
        borderColor="gray.600"
      >
        {(() => {
          if (!chainConfig) {
            // Fallback to Cronos options if chainConfig not available
            return (
              <>
        <option value="CRO">CRO</option>
        <option value="USDC">USDC</option>
        <option value="wCRO">wCRO</option>
              </>
            );
          }
          const nativeSymbol = chainConfig.nativeCurrency.symbol;
          const stableSymbol = chainConfig.defaultStablecoin;
          const wrappedSymbol = chainConfig.nativeCurrency.symbol; // Same as native for most chains
          
          return (
            <>
              <option value={nativeSymbol}>{nativeSymbol}</option>
              <option value={stableSymbol}>{stableSymbol}</option>
              <option value={`w${nativeSymbol}`}>w{nativeSymbol}</option>
            </>
          );
        })()}
      </Select>
    </Box>

    <Box minW="160px">
      <Text fontSize="sm">Amount ({manualTradeSide === "BUY" ? manualInitiator : "tokens"})</Text>
      <NumberInput
        value={manualTradeAmount}
        min={0}
        step={0.0001}
        precision={6}
        onChange={(v) => setManualTradeAmount(parseFloat(v))}
      >
        <NumberInputField />
      </NumberInput>
    </Box>

    <Box minW="160px">
      <Text fontSize="sm">Side</Text>
      <Select
        value={manualTradeSide}
        onChange={(e) => setManualTradeSide(e.target.value)}
        bg="black"
        borderColor="gray.600"
      >
        <option value="BUY">BUY</option>
        <option value="SELL">SELL</option>
      </Select>
    </Box>

    <StyledActionButton colorScheme="teal" onClick={runManualTrade} alignSelf="end">
      Execute {manualTradeSide}
    </StyledActionButton>
  </HStack>
</Panel>

<Panel title="PnL calculator" subtitle="Independent verification tool" accent="purple">
  <PnLCalculator />
</Panel>
    </DashboardRoot>
  );
}

function SniperBot({ selectedTokenAddress }) {
  const [activeBotInstance, setActiveBotInstance] = useState("primary");

  return (
    <Box>
      <HStack
        spacing={2}
        px={4}
        py={3}
        position="sticky"
        top={0}
        zIndex={20}
        bg="gray.900"
        borderBottom="1px solid"
        borderColor="whiteAlpha.200"
      >
        <Button
          size="sm"
          colorScheme="cyan"
          variant={activeBotInstance === "primary" ? "solid" : "outline"}
          onClick={() => setActiveBotInstance("primary")}
        >
          Primary bot
        </Button>
        <Button
          size="sm"
          colorScheme="orange"
          variant={activeBotInstance === "secondary" ? "solid" : "outline"}
          onClick={() => setActiveBotInstance("secondary")}
        >
          Secondary bot
        </Button>
      </HStack>

      <Box display={activeBotInstance === "primary" ? "block" : "none"}>
        <SniperBotInstance
          selectedTokenAddress={selectedTokenAddress}
          botInstanceId="primary"
        />
      </Box>
      <Box display={activeBotInstance === "secondary" ? "block" : "none"}>
        <SniperBotInstance
          selectedTokenAddress={selectedTokenAddress}
          botInstanceId="secondary"
        />
      </Box>
    </Box>
  );
}

export default SniperBot;
